/** The host. It owns the drawing, the view and the toggles; the chrome is a skin over it.
 *
 * ⚠⚠ **THE CHROME IS 10,660 LINES AND IT IS A GUEST HERE.** `useAutoCadStudioChrome` returns
 * slots — `top`, `rail`, `side`, `status`, `overlay` — and this file decides where they go and
 * what `A` and `T` mean. That seam is the whole reason the chrome could be lifted at all
 * (`chrome/shell-types.ts`), and it is why this page is a few hundred lines rather than another
 * ten thousand.
 *
 * ⚠⚠ **DO NOT ADD A COMMAND LINE OR A STATUS BAR HERE — THE CHROME HAS BOTH.** The first draft
 * of this file rendered its own, which duplicated them down the screen. `AcadCommandLine` already
 * resolves command names, keeps history, repeats the last command on an empty Enter and answers
 * an unknown one in AutoCAD's exact wording, two spaces and all. Anything this page needs to say
 * that is NOT a command response goes in a toast instead.
 *
 * ⚠⚠ **AN UNIMPLEMENTED ACTION MUST NOT THROW.** `api.ts` makes every unbuilt ENDPOINT throw,
 * deliberately, so nobody mistakes a stub for a working feature. That rule cannot cross into the
 * chrome: an action that throws while the ribbon renders takes the whole page down, and a white
 * screen is a worse lie than a stub. `handlers.ts` also calls query-shaped actions and reads a
 * field off the result — `A.selectableKinds().kinds` — so the fallback returns an empty OBJECT,
 * not undefined. Returning undefined blanks the page on the first ribbon render.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
         useSyncExternalStore } from "react";
import { resolveCommand } from "../chrome/catalog";
import { Lisp, lispPrint } from "./lisp/eval";
import type { Lv as LispValue } from "./lisp/read";

import { useAutoCadStudioChrome } from "../chrome/shell";
import type { ShellContext } from "../chrome/shell-types";
import Canvas2D, { setHandVars } from "./Canvas2D";
import type { CanvasApi, DisplayLayer, DisplayList, Pick, View } from "./Canvas2D";
import { Layers, Properties } from "./Panels";
import { applyDelta, applyLocalTransform, cutsOf, deltaFor, keptBy, planLocal } from "./delta";
import { attachImage, attachPdf, editDrawing, exportLayout as exportLayoutApi,
         loadBlockDef, measure as measureApi, pdfPages,
         type EditReport, type Measured, listDrawings, loadFields, loadBlockAtts, loadDisplay, loadGrips, loadLayers,
         patchLayerRow,
         redoDrawing, undoDrawing, loadLayouts, NotLoggedIn, type DrawingRow, type EditOp,
         type Grip, type LayerRow, type LayoutRow, type Who } from "./api";
import { ARC_GIVENS, CIRCLE_GIVENS, ELLIPSE_GIVENS, TANGENT_GIVENS } from "./givens";
import { contOf, continueArc, nearest, objOf, ttrAll, tttAll,
         type Cont, type TanObj } from "./tangency";
import { PROMPTS, SELECT_MODES, SELECT_PROMPT, sanityExtent, selectModeOf,
         type SelectMode }
  from "./commands";
import TextEditor, { type TextBox, type TextSpec } from "./TextEditor";
import { loadText, loadSysvars, loadTextStyles, loadGroups, type SysVar, type GroupRow }
  from "./api";
import { COMMANDS, DIM_KIND, DRAWS, GHOST_KINDS, defOf, solveGiven, PEDIT_OPTION, PEDIT_POINT,
         PEDIT_PROMPT, PICKS,
         SPLINEDIT_OPTION, SPLINEDIT_POINT, SPLINEDIT_PROMPT, VERB,
         nearbyHandles, reachHandles, rectCorners,
         type Affine, type Pt } from "./commands";
import { IMPLEMENTED, maskOf, namesOf, overrideOf, type SnapHit, type SnapKind }
  from "./snap";
import { forgetLocal, localFor } from "./local/registry";
import { standalone } from "./standalone";
import { openFromDisk, saveDwgToDisk, saveToDisk, saveToServer, takeOver, uploadToServer }
  from "./local/open";
import { startupFile, type PickedFile } from "./local/files";
import { publishUX } from "./uxprobe";
import { WORLD_UCS, constrain, isWorldUcs, keywordsOf, matchKeyword, parseTyped,
         ucsFromPoints, ucsToWorld, worldToUcs, type Ucs } from "./coords";
import ArrayDialog, { type PolarSpec, type RectSpec } from "./ArrayDialog";
import BlockDialog from "./BlockDialog";
import DesignCenter from "./DesignCenter";
import HatchDialog, { type HatchSpec } from "./HatchDialog";
import DimStyleDialog from "./DimStyleDialog";
import TextStyleDialog from "./TextStyleDialog";
import XrefDialog from "./XrefDialog";
import LayoutDialog, { type LayoutSpec } from "./LayoutDialog";
import OpenDialog from "./OpenDialog";

/** The AutoCAD status-bar toggles. ⚠ Every one of these is REAL state a drafter changes and that
 *  this app can honestly own, unlike the commands — so they are implemented, not stubbed. */
/** SNAPUNIT — the grid step F9 rounds to. ⚠ A DRAWING variable now, not a constant: AutoCAD
 *  reads it from the file and its dialog sets X and Y separately (this app snaps to one square
 *  step and writes the header point with X == Y). 10 mm, this office's detail spacing, is still
 *  the default a drawing that never set it gets. */
const SNAP_UNIT = 10;

/** Where named origins live. ⚠ `localStorage`, not the drawing — see the `ucs` state. Keyed
 *  without a drawing id on purpose: a working origin on a rotated grid is reused across the
 *  whole job, and per-drawing named origins would have to be re-made in every sheet of it. */
const UCS_KEY = "pdcad.ucs.named";

/** The CURRENT origin — READ OUT OF THE DRAWING, since 2026-09-09.
 *
 *  ⚠⚠ **IT USED TO LIVE IN `localStorage`, AND THAT WAS THE DEFECT.** The argument was that one
 *  drafter's working origin is not a property of the building and should not travel into
 *  everybody else's copy or into the exported DXF. AutoCAD keeps it in the file header
 *  (`$UCSORG`, `$UCSXDIR`) and the bar is AutoCAD's behaviour, so it lives in the drawing now:
 *  it undoes with Ctrl+Z, it exports, and the next person to open the sheet is where you were.
 *  ⚠ The header spells it as an origin and an X DIRECTION; this app works in an origin and a
 *  rotation, so the angle is the direction's `atan2` and nothing else needs to know. */
function ucsFromVars(vars: SysVar[] | null | undefined): Ucs {
  const val = (n: string): number[] | null => {
    const row = (vars ?? []).find((v) => v.name === n);
    const raw = row?.value as unknown;
    if (!Array.isArray(raw)) return null;
    const nums = raw.map((v) => Number(v));
    // ⚠ Validated, not trusted: one NaN out of a client's header would place every subsequent
    // point at NaN — a canvas that accepts clicks and stores nothing, which reads as dead.
    return nums.every((v) => Number.isFinite(v)) ? nums : null;
  };
  const org = val("UCSORG");
  const xd = val("UCSXDIR");
  if (!org && !xd) return WORLD_UCS;
  const rot = xd && (Math.abs(xd[0]) > 1e-12 || Math.abs(xd[1]) > 1e-12)
    ? Math.atan2(xd[1], xd[0]) : 0;
  return { x: org?.[0] ?? 0, y: org?.[1] ?? 0, rot };
}

/** CIRCLE's prompt offers `[3P/2P/Ttr (tan tan radius)]` and its second offers `[Diameter]`.
 *  ⚠ Keyed by the keyword EXACTLY as the prompt spells it, because `coords.matchKeyword`
 *  returns the prompt's own spelling — one string, one place it is written down. */
const CIRCLE_KEYWORD: Record<string, string> = {
  "3P": "3p", "2P": "2p", "Ttr (tan tan radius)": "ttr",
  // ⚠ `Ttt` needs THREE straight lines selected first, same noun-verb grammar as `Ttr`'s two.
  "Ttt (tan tan tan)": "ttt", Diameter: "cd",
};

/** Which given a keyword switches to, keyed by **the given you are in, the prompt you are at,
 *  and the word** — `"<given>:<index>:<Word>"`.
 *
 * ⚠⚠ **THE SAME LETTER MEANS DIFFERENT GIVENS DEPENDING ON WHERE YOU ARE.** `C` before any pick
 * means "centre first" — `cse`; `C` after the start point is down means "start, centre, end" —
 * `sce`, a different pick order for the same arc. Collapsing them loses the point already picked
 * and puts the arc somewhere else.
 *
 * ⚠⚠ **AND THE GIVEN IS IN THE KEY BECAUSE PROMPT 2 IS A DIFFERENT QUESTION IN EACH OF THEM.**
 * The first version keyed on the index alone, which was right while only three keywords existed
 * and becomes wrong the moment two givens offer different options at the same step: in `sce`
 * prompt 2 is the END POINT and in `sed` it is the tangent DIRECTION. `Angle` means `sca` in one
 * and `sea` in the other.
 *
 * ⚠ All eleven arc givens already existed with real solvers — `sca`, `scl`, `csa`, `csl`, `sea`,
 * `ser` were reachable from the ribbon flyout and from nowhere else. Nothing here is new
 * geometry; the prompts simply never offered what the app could already draw.
 *
 * ⚠ Switching the given does NOT re-arm the tool: the mode stays `arcv`, so the effect keyed on
 * `[mode]` does not fire and the points already picked survive. That is the whole reason this
 * can be done mid-command, and why the new prompt is spoken here by hand. */
const ARC_KEYWORD: Record<string, string> = {
  "3p:0:Center": "cse", "3p:1:Center": "sce", "3p:1:End": "sed",
  // start, centre, then the third thing — the end point, an included angle, or a chord length
  "sce:2:Angle": "sca", "sce:2:chord Length": "scl",
  "cse:2:Angle": "csa", "cse:2:chord Length": "csl",
  // start, end, then how it curves — a direction (where you already are), an angle, or a radius
  "sed:2:Angle": "sea", "sed:2:Radius": "ser",
};

/** `[Reference]`'s two questions, per command. ⚠ ROTATE subtracts and SCALE divides — the pair
 *  of prompts and the arithmetic belong to the same command, so they are written down together
 *  rather than inferred from the word "Reference" in two places. */
const REF_ASK: Record<string, [string, string]> = {
  rotate: ["Specify the reference angle <0>:", "Specify the new angle:"],
  scale: ["Specify reference length <1>:", "Specify new length:"],
};

/** RECTANG's three options, and the question each one asks.
 *
 * ⚠⚠⚠ **THESE PERSIST BETWEEN RECTANGLES, BECAUSE AutoCAD'S DO.** Set a fillet once and every
 * RECTANG is filleted until you set it back to zero. This app deliberately did NOT copy that —
 * the comment here argued that "a rectangle that comes out rounded because of something typed
 * twenty minutes ago is a bug report, not a feature", and it is a good argument about a product
 * this is not allowed to be. The owner's bar of 2026-09-05 is EXACT, so the trap is the
 * specification and copying it is the work.
 * ⚠ Held in a REF, not in the draw state: the draw state is rebuilt every time a tool arms, and
 * that rebuild is exactly what used to clear them.
 * ⚠ Per SESSION, not per drawing. AutoCAD keeps them in the drawing; putting them in the header
 * would need a sysvar pair that DXF does not define for RECTANG, so the difference that remains
 * is that they reset when the tab is reloaded — smaller than the one being closed, and stated
 * rather than hidden. */
/** The kinds that HAVE a text height. ⚠ Named rather than "anything with text": a DIMENSION
 *  carries text too, and its height comes from its dimension STYLE — writing `height` on one
 *  would be changing a property it does not own. */
const TEXTISH = new Set(["TEXT", "MTEXT", "ATTRIB", "ATTDEF", "LEADER", "MULTILEADER"]);

/** RECTANG's rotation, in RADIANS, out of the options bag it is remembered in.
 *  ⚠ One reader, so the degrees-to-radians conversion happens in exactly one place. Doing it at
 *  each call site is how a preview ends up at 30 radians while the geometry is at 30 degrees. */
function rectRot(opt: Record<string, unknown> | undefined): number {
  return ((Number(opt?.rot) || 0) * Math.PI) / 180;
}

//: How far the pointer must travel, in SCREEN pixels, before a freehand stroke takes another
//: sample. ⚠ Screen and not drawing units, so a traced cloud is equally smooth at every zoom —
//: in drawing units it would be a scribble on a detail and a straight line on a site plan.
const FREEHAND_STEP = 9;

const RECT_OPT: Record<string, string> = {
  Chamfer: "chamfer", Fillet: "fillet", Width: "width",
  // ⚠⚠ **`Dimensions` IS NOT A RIDE-ALONG LIKE THE OTHER THREE.** Chamfer, Fillet and Width are
  // sent to the server, which does the geometry in `_rect_corners`. A LENGTH and a WIDTH are
  // answered entirely here: a rectangle is authored from two corners, so knowing both sides
  // means the second corner is DERIVED and the click only chooses which way it goes. Putting it
  // in `opt` would send the server two numbers it has never heard of.
  Dimensions: "dimlength",
  // ⚠ `Area` is the same shape as `Dimensions` — answered here, never sent. Give an area and one
  // side and the other side is arithmetic; the click then only chooses which way it goes.
  Area: "area",
  // ⚠⚠ These three DO go to the server, like Chamfer and Fillet — and like them they are
  // REMEMBERED between rectangles, which is the whole reason they live in `opt`. `Rotated` is
  // read back out for the rubber band as well, so the angle only exists in one place.
  // ⚠ `rot` is in DEGREES here and everywhere the drafter can see it; only `rectCorners` and the
  // server's `_rect_corners` work in radians.
  Rotated: "rot", Elevation: "elevation", Thickness: "thickness",
};
/** ZOOM's options, and ONLY the ones something answers.
 *  ⚠⚠ AutoCAD's own prompt also offers Center, Dynamic, Object and a scale factor in paper
 *  units (nXP). None of those are built, so none of them are printed — the rule the 31 removed
 *  keywords were removed for, and the reason `test/keywords.test.ts` exists.
 *  ⚠ `All` really is Extents here: this app has no separate drawing LIMITS to zoom to, and the
 *  ribbon's own note has said so since the shell was lifted. */
const ZOOM_OPTION: Record<string, string> = {
  a: "all", all: "all",
  e: "extents", extents: "extents",
  p: "previous", prev: "previous", previous: "previous",
  w: "window", window: "window",
  s: "scale", scale: "scale",
  // ⚠⚠ Added 2026-09-07 against a REFERENCE rather than a memory: BricsCAD V26 prints
  // `[zoom In/zoom Out/All/Center/Dynamic/Extents/Left/Previous/Right/Scale/Window/ENtity]`,
  // harvested from the running program — `qa/reference/bricscad-v26-grammar.md`. pdcad offered
  // five of the twelve.
  // ⚠ `in`/`out` abbreviate to I and O — BricsCAD capitalises the I of "zoom In", not the Z.
  i: "in", in: "in",
  o: "out", out: "out",
  n: "entity", en: "entity", entity: "entity",
  // ⚠⚠ **Center, Left and Right are ONE prompt shape: a point, then a HEIGHT in drawing units.**
  // Built 2026-09-15 and printed in the same edit as the code that answers them, which is this
  // file's own rule. Left and Right take the LOWER corner — that is what AutoCAD's two legacy
  // options mean, and the canvas does the anchoring because only it knows its own width.
  // ⚠ `Dynamic` is still NOT printed: it is a view box dragged over a thumbnail of the whole
  // drawing, nothing here does that, and a prompt is a promise.
  c: "center", cen: "center", center: "center", centre: "center",
  l: "left", left: "left",
  r: "right", right: "right",
};

/** What a ZOOM Center / Left / Right is waiting for. ⚠ A ref rather than state: the point prompt
 *  needs no re-render, and `pedWantRef` beside it is armed the same way for the height. */
type ZoomAnchor = { anchor: "center" | "left" | "right"; p: { x: number; y: number } | null };
const ZOOM_PROMPT = PROMPTS.zoom;

const RECT_OPT_PROMPT: Record<string, string> = {
  chamfer: "Specify first chamfer distance for rectangles — or both, as `8,5`:",
  fillet: "Specify fillet radius for rectangles:",
  width: "Specify line width for rectangles:",
  // ⚠ Two prompts, in BricsCAD's order — it asks the length and then the width rather than
  // taking a pair, and a drafter who types `3000` expects to be asked the other one.
  dimlength: "Specify length for rectangles:",
  dimwidth: "Specify width for rectangles:",
  // ⚠ BricsCAD asks the area and then `[Length/Width]` before the number. That sub-choice is
  // NOT built and is NOT printed — the two answers are the same arithmetic with the sides
  // swapped, and the quadrant click already decides which way the rectangle runs.
  area: "Specify area of rectangle:",
  arealen: "Specify length of rectangle — the other side is derived from the area:",
  rot: "Specify rotation angle for rectangles, in degrees:",
  elevation: "Specify elevation (the Z the rectangle sits at) for rectangles:",
  thickness: "Specify thickness (extrusion height) for rectangles:",
};

/** AutoCAD's grip modes, in the order SPACE walks them.
 *
 * ⚠ `cmd: null` is STRETCH and is not a `COMMANDS` entry: it drags ONE vertex, which is the one
 * thing the transform machinery cannot express — that machinery applies a single affine to the
 * whole selection, and stretching a rectangle's corner moves that corner only.
 * ⚠⚠ **AutoCAD OFFERS `[Base point/Copy/Undo/eXit]` AT EVERY ONE OF THESE AND THIS DOES NOT,
 * DELIBERATELY.** Nothing here answers Base point or Copy, and this project has twice shipped
 * prompts advertising keywords no code could hear — eleven on the MODIFY panel, thirty-one in
 * `DRAWS`. A prompt is a promise. `[Reference]` survives on ROTATE and SCALE because
 * `commitTyped` genuinely answers it; the rest are absent until they are built, and ESC and
 * Ctrl+Z already do what eXit and Undo would.
 */
const GRIP_MODES: { label: string; cmd: string | null; prompt: string }[] = [
  { label: "STRETCH", cmd: null, prompt: "Specify stretch point:" },
  { label: "MOVE", cmd: "move", prompt: "Specify move point:" },
  // ⚠ ROTATE and SCALE keep `[Reference]` because `commitTyped` ANSWERS it — see `REF_ASK`.
  { label: "ROTATE", cmd: "rotate", prompt: "Specify rotation angle or [Reference]:" },
  { label: "SCALE", cmd: "scale", prompt: "Specify scale factor or [Reference]:" },
  { label: "MIRROR", cmd: "mirror", prompt: "Specify second point of mirror line:" },
];

const TOGGLES = {
  grid: true, snapmode: false, ortho: false, polar: false, osnap: true, otrack: false,
  lwt: false, dynin: true, infer: false, transparency: false, annovis: true, annomon: false,
  autoscale: false, gfx: false, lockui: false, iso: false, qp: false, pending: false,
  //: ⚠ AutoCAD ships selection cycling OFF, and so does this: with it on, a second click on a
  //: stack selects the second item, which is surprising if you did not ask for it. TAB steps
  //: through the stack either way.
  cycling: false,
  //: ⚠ AutoCAD's `FRAME`, and it ships ON (1) — an underlay you cannot see the edge of is one
  //: you cannot tell is there when the fade is high, which is exactly when you need to.
  uframe: true,
};
type Toggles = typeof TOGGLES;

/** The running object snaps that are ON by default.
 *
 * ⚠⚠ **A LIST OF NAMES, NOT A BOOLEAN.** `T.snaps` is read as an array in two places —
 * `(T?.snaps || []).join(",")` builds the handler memo's signature and `snaps.includes(m.snap)`
 * lights the fourteen ticks in the OSNAP drop-up. Handing it `true` threw
 * `.join is not a function` while the ribbon was rendering and blanked the whole page: the first
 * screenshot of this studio was a plain dark rectangle.
 * ⚠ The names are `catalog.ts`'s own `snap:` column, which is the single table the drop-up, the
 * tooltip and the handler all read. `centre` is spelled the way that table spells it.
 */
const SNAPS_ON = ["endpoint", "midpoint", "centre", "intersection", "extension"];

/** ⚠⚠ **AutoCAD'S `PICKADD` IS 2 BY DEFAULT AND IT IS NOT WHAT ANY OTHER PROGRAM DOES.** In
 *  Illustrator, in a browser, in a file manager, clicking a second thing REPLACES the first and
 *  Shift adds. In AutoCAD clicking a second thing **ADDS** it, and **Shift+click REMOVES**.
 *  Thirty years of drafters select five walls by clicking five walls, with no modifier.
 *
 *  ⇒ Mimicking that is the whole point of this app, so it is mimicked exactly. Clicking empty
 *  space clears the set, which is the same in both worlds and the escape hatch either habit
 *  reaches for. `PICKADD` itself is a system variable and will be settable when SETVAR is. */
function pickadd(cur: Pick[], got: Pick[], shift: boolean): Pick[] {
  if (!got.length) return [];                        // picked nothing → clear, in both worlds
  const by = new Map(cur.map((p) => [p.handle, p]));
  for (const p of got) {
    if (shift) by.delete(p.handle);
    else by.set(p.handle, p);
  }
  return [...by.values()];
}

/** What a DRAW tool's typed answer defaults to. ⚠ Worked out from the DRAWING, not a constant:
 *  a text height of 2.5 is right on a millimetre detail and invisible on a 115-metre site plan.
 *  Module scope so `finishDraw` and `onDrawPoint` cannot disagree about it. */
function defaultAsk(def: (typeof DRAWS)[string], i: number, extent = 0,
                    pts: readonly Pt[] = []): number {
  const ask = def.asks?.[i];
  if (!ask || ask.kind !== "number") return 0;
  // ⚠ The PICKS matter to a flyout given — "Start, Centre, Length" defaults its chord to the
  // distance between the two points already on screen, which no function of the extent knows.
  return def.defaultOf ? def.defaultOf(ask.key, extent, pts) : 1;
}

/** ⚠ WHICH flyout the mode belongs to. The arc and the circle have their own given, and a
 *  single "given" string would make pressing an arc variant change what the circle button does
 *  next — a state bug a drafter would read as the program forgetting. */
/** Every object in the same group as anything picked.
 *
 * ⚠⚠ **A GROUP WHOSE MEMBERS SELECT ONE AT A TIME IS INDISTINGUISHABLE FROM NO GROUP AT ALL**,
 * which is why AutoCAD's PICKSTYLE is on out of the box. ⚠ Honours each group's OWN `selectable`
 * flag (DXF code 71) rather than one global switch: a drawing can carry both kinds and does. */
function expandGroups(got: Pick[], groups: GroupRow[], dl: DisplayList | null): Pick[] {
  if (!got.length || !groups.length) return got;
  const picked = new Set(got.map((p) => p.handle));
  const want = new Set<string>();
  for (const g of groups) {
    if (!g.selectable) continue;
    if (g.members.some((h) => picked.has(h))) for (const h of g.members) want.add(h);
  }
  if (!want.size) return got;
  const out = [...got];
  const have = new Set(picked);
  // ⚠ The KIND and LAYER come from the display list, because a Pick carries them and a group
  // stores only handles. A member that is not drawn — an off layer — is not added: you cannot
  // select what you cannot see, which is the same rule `hit()` follows.
  const meta = new Map<string, Pick>();
  for (const [li, aci, , owner, kind] of dl?.polys ?? []) {
    const ly = dl?.layers[li];
    if (ly && ly.on && !ly.frozen && !meta.has(owner)) {
      meta.set(owner, { handle: owner, kind, layer: ly.name, aci });
    }
  }
  for (const h of want) {
    if (have.has(h)) continue;
    const m = meta.get(h);
    if (m) { out.push(m); have.add(h); }
  }
  return out;
}

/** The direction of the entity `handle`, as a unit vector, or null if it has no single one.
 *
 * ⚠⚠ **THE SEGMENT NEAREST THE CLICK, NOT THE FIRST ONE** — which is what AutoCAD's XLINE
 * `Parallel` does and the only reading that makes sense on a polyline. "Parallel to that wall"
 * means the bit of it under the cursor; taking segment 0 would answer with a different wall on
 * every L-shaped room, and be right often enough to look like a fluke rather than a bug.
 * ⚠ A circle, an arc or a spline has no direction and is refused BY NAME. Picking the closest
 * thing to a direction out of a curve would draw a construction line the drafter never asked for
 * and could not predict.
 */
/** BricsCAD's TEXT justification keywords → DXF (halign, valign).
 *  ⚠ Keyed on the FULL spelling the prompt prints, because that is what `parseTyped` returns —
 *  matching on a prefix here would quietly accept a word the prompt never offered. */
const TEXT_JUSTIFY: Record<string, [number, number]> = {
  "Center horizontally": [1, 0],
  "Middle": [4, 0],
  "Right justify": [2, 0],
  // ⚠ These two span TWO points; the others take one. `TEXT_SPANS` is what the point loop reads.
  "Align on line": [3, 0],
  "Fit between points": [5, 0],
};
/** The halign values that need a SECOND point. ⚠ Mirrors `TEXT_TWO_POINT` on the server and
 *  `TWO_POINT` in `textjust.ts`: three places have to agree, and a bare `=== 3 || === 5` in each
 *  is three places to forget one. */
const TEXT_SPANS = new Set([3, 5]);

function directionOf(dl: DisplayList | null, handle: string,
                     at: Pt | undefined): { x: number; y: number } | null {
  if (!dl) return null;
  // ⚠ Another construction line first: parallel-to-an-XLINE is the commonest use of all, and its
  // direction is stored rather than derived, so there is nothing to choose between.
  for (const r of dl.rays ?? []) if (r[7] === handle) return { x: r[4], y: r[5] };
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  for (const [, , pts, owner] of dl.polys ?? []) {
    if (owner !== handle) continue;
    for (let i = 0; i + 3 < pts.length; i += 2) {
      const ax = pts[i], ay = pts[i + 1], bx = pts[i + 2], by = pts[i + 3];
      const len = Math.hypot(bx - ax, by - ay);
      if (len < 1e-9) continue;
      // Distance from the click to this segment, clamped to its ends.
      const t = at ? Math.max(0, Math.min(1, ((at.x - ax) * (bx - ax)
                                            + (at.y - ay) * (by - ay)) / (len * len))) : 0;
      const qx = ax + (bx - ax) * t, qy = ay + (by - ay) * t;
      const d = at ? Math.hypot(at.x - qx, at.y - qy) : 0;
      if (d < bestD) { bestD = d; best = { x: (bx - ax) / len, y: (by - ay) / len }; }
    }
  }
  return best;
}

function givenFor(mode: string,
                  s: { arcGiven: string; circGiven: string; ellGiven: string }): string {
  return mode === "circv" ? s.circGiven : mode === "arcv" ? s.arcGiven
       : mode === "ellipse" ? s.ellGiven : "";
}

function askPrompt(def: (typeof DRAWS)[string], i: number, d: number): string {
  return (def.asks?.[i]?.prompt ?? "").replace("{d}", String(d));
}

/** ⚠⚠ **GONE — `defOf` RESOLVES THE WHOLE TOOL, NOT JUST ITS `kind`.** The dimension flyout is
 *  eight commands wearing one mode and they take different PICKS, so translating only the kind
 *  string left a radius dimension collecting three points and sending them to a command that
 *  wants one. See `DIM_KIND` in `commands.ts` — the old
 *  app's names for these are inverted relative to AutoCAD's. */


/** What PEDIT and SPLINEDIT are waiting for. ⚠ `op` is which command is asking — the two share
 *  every piece of prompt machinery, and without it Enter at the option prompt would always run
 *  PEDIT's table. */
interface PedWant {
  //: ⚠ `zoom` rides this because ZOOM is the same shape — a command that prints a bracket and
  //: takes a letter — and a second copy of the prompt machinery is how the two come to disagree
  //: about what Escape does.
  op: "pedit" | "splinedit" | "zoom" | "patharray";
  mode: string;
  kind: "point" | "width" | "option";
}

/** ⚠⚠⚠ **THE DYNAMIC-INPUT BUFFER LIVES OUTSIDE REACT'S TREE, ON PURPOSE.**
 *
 *  It was `useState` in `Studio`, so every character typed at a prompt re-rendered the whole
 *  studio — and a typed command is seven of those steps. Measured on drawing 46 in Chrome: 7.0
 *  renders a command at 2.8 ms each, inside a command that costs 120 ms against BricsCAD's 35.
 *  Nothing but the box at the cursor shows this string; nothing else needs to render when it
 *  changes. Every READER goes through `live.current.typed`, which is an accessor onto this —
 *  so the 68 `setTyped` calls and the 20-odd `s.typed` reads did not have to change, and the
 *  value is visible in the SAME TICK it is set, which the input queue depends on. */
const typedStore = { v: "", subs: new Set<() => void>() };
const setTypedValue = (v: string) => {
  if (v === typedStore.v) return;
  typedStore.v = v;
  typedStore.subs.forEach((f) => f());
};
const subscribeTyped = (f: () => void) => {
  typedStore.subs.add(f);
  return () => { typedStore.subs.delete(f); };
};
/** ⚠ `useSyncExternalStore`, not a `useState` mirror: the box has to show what the store holds
 *  at the moment React renders it, and a mirror updated in an effect is a frame behind. */
const useTyped = () => useSyncExternalStore(subscribeTyped, () => typedStore.v,
                                            () => typedStore.v);

export default function Studio({ who, onSignOut }: { who: Who; onSignOut: () => void }) {
  // ⚠⚠⚠ **WHAT A RENDER OF THIS COMPONENT COSTS, because it is now the biggest thing left.**
  // A typed command is seven steps — `LINE ⏎ 0,0 ⏎ 1000,0 ⏎ Esc` — and each one sets state
  // here, so each one re-renders the WHOLE studio. Measured in Chrome on drawing 46: the edit
  // itself is 15.6 ms and the command takes 133; the gaps between the input queue's steps are a
  // 9 ms median, which is this. Nothing in the app reads these numbers — `RENDERER` prints them
  // and `/var/tmp/pacecheck.py` reads them — and they cost two `performance.now()` a render.
  const renderT0 = performance.now();
  useLayoutEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    const r = (w.__pdcadRender ||= { n: 0, ms: 0, max: 0 }) as
      { n: number; ms: number; max: number };
    const d = performance.now() - renderT0;
    r.n += 1; r.ms += d; if (d > r.max) r.max = d;
    // ⚠⚠ **WHICH STATE MOVED, because "4 renders an edit" is not a finding until you know why.**
    // Every render of this component costs ~11 ms on a million-object drawing and drags a ribbon
    // render with it, and the only way to remove one is to know what asked for it. A compact
    // signature per render, diffed by whoever is reading; bounded like `__pdcadEdits`.
    const sig: Record<string, unknown> = {
      dl: dlTick.current, sel: sel.length, pending: !!pending, busy: busy ?? "",
      cmd: cmd ? cmd.name : "", draw: draw ? draw.mode : "", mode,
      rows: layerRows.length, grips: grips ? grips.length : -1, hot: hotGrip ?? -1,
      space: space ?? "", ms: Math.round(d * 10) / 10,
    };
    const lg = (w.__pdcadRenderWhy ||= []) as unknown[];
    lg.push(sig);
    if (lg.length > 64) lg.splice(0, lg.length - 64);
  });
  const [rows, setRows] = useState<DrawingRow[]>([]);
  const [id, setId] = useState<number | null>(null);
  const [dl, setDl] = useState<DisplayList | null>(null);
  //: A counter that moves when `dl` does, so a render signature can say "the list changed"
  //: without holding the list. ⚠ A REF bumped during render: it is a derived fact about the
  //: value this render is using, not state.
  const dlTick = useRef(0);
  const dlWas = useRef<DisplayList | null>(null);
  if (dlWas.current !== dl) { dlWas.current = dl; dlTick.current += 1; }
  //: The layer table as the DOCUMENT holds it. ⚠ Not `dl.layers`, which is the render state and
  //: has no primary key — and an id is exactly what a PATCH needs. The chrome's own layer
  //: commands (LAYON, LAYISO, LAYLCK) read this list through `ctx.plan.layers`.
  const [layerRows, setLayerRows] = useState<LayerRow[]>([]);
  // ⚠⚠ **THE SPACE, AND WHY IT IS NOT `undefined`.** Until now the studio asked for the WHOLE
  // drawing and drew every space at once — so a client file with twenty title-blocked sheets
  // piled all twenty on the plan, at paper-millimetre coordinates near the origin of a model
  // eighty metres wide. It read as debris in the corner of the drawing, not as paper space.
  // `null` is model space; a string is a layout's name.
  const [space, setSpace] = useState<string | null>(null);
  const [layoutRows, setLayoutRows] = useState<LayoutRow[]>([]);
  const [layoutDlg, setLayoutDlg] = useState<"new" | "rename" | "delete" | null>(null);
  const [dimDlg, setDimDlg] = useState(false);
  const [txtDlg, setTxtDlg] = useState(false);
  const [xrefDlg, setXrefDlg] = useState(false);
  /** ARRAY's dialog, and — for a polar one — the numbers it collected while the CENTRE is
   *  still being picked on the drawing. ⚠ The centre is a place, so it comes off the geometry
   *  and not out of a text box. */
  const [arrayDlg, setArrayDlg] = useState<"rect" | "polar" | null>(null);
  const [polarWant, setPolarWant] = useState<PolarSpec | null>(null);
  const polarWantRef = useRef<PolarSpec | null>(null);
  /** VPSCALE is waiting for its 1:n. ⚠ One number, so it is asked on the command line the way
   *  AutoCAD asks it — a dialog for a single integer is a dialog nobody wants twice. */
  const [vpAsk, setVpAsk] = useState<string[] | null>(null);
  /** PEDIT or SPLINEDIT is waiting for the point or the number its option needs.
   *  ⚠⚠ **ONE STATE FOR BOTH, KEYED BY `op`.** They are the same shape of command — a modal
   *  sub-menu that asks for a letter, then sometimes a point and sometimes a number — and a
   *  second copy of the prompt machinery is a second place for Escape, Enter and the option
   *  line to drift out of agreement. */
  const [pedWant, setPedWant] =
    useState<PedWant | null>(null);
  // ⚠ A REF for the same reason `vpAskRef` is one: the key handler and `onPoint` are bound once.
  const pedWantRef = useRef<PedWant | null>(null);
  //: ZOOM Center/Left/Right, mid-prompt — see `ZoomAnchor`.
  //: ⚠⚠ **STATE AND NOT ONLY A REF, because `picking` is a RENDER decision.** A ref-only version
  //: printed "Specify center point:" and then swallowed the click as a selection: the canvas
  //: reports a POINT only while `picking` is true, and a ref cannot make it true. Same shape as
  //: the ZOOM Window defect this file already records one layer down.
  const [zoomAnchor, setZoomAnchor] = useState<ZoomAnchor | null>(null);
  const zoomAnchorRef = useRef<ZoomAnchor | null>(null);
  // ⚠ A ref, because `acceptPeditOption` is bound into the key handler above where `pedit` is
  // declared — the same not-hoisted problem `commit` has, solved the same way `sayRef` is.
  const peditRef = useRef<((op: string, extra?: Record<string, unknown>) => void) | null>(null);
  const splineditRef = useRef<((extra?: Record<string, unknown>) => void) | null>(null);
  //: ⚠ Same not-hoisted problem: `acceptPeditOption` is declared above `runZoom`.
  const zoomRef = useRef<((what: string, f?: number) => void) | null>(null);
  // ⚠ Same not-hoisted problem as `peditRef`: the mode effect runs above where `real` is built.
  const runCmdRef = useRef<((n: string, extra?: Record<string, unknown>) => void) | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [openDlg, setOpenDlg] = useState(false);
  // ⚠ Read ONCE. A flag that can change under a mounted canvas would have to tear down a GL
  // context mid-drawing, and there is no reason to: this is a measurement switch.
  // ⚠⚠⚠ **ON BY DEFAULT. IT WAS NEVER BROKEN — THE CAMERA WAS.**
  // It was turned off twice on the strength of screenshots showing a blank canvas, and
  // `qa/shot.py` launched Chrome with `--enable-unsafe-webgpu` and WITHOUT `--enable-gpu`, which
  // renders the WebGL layer perfectly and never composites it into a page screenshot. The
  // canvas's own `toDataURL()` returned the complete drawing throughout. Five hypotheses were
  // tested and discarded against a photograph that could not show the answer; the sixth
  // instrument in this project to give a confident wrong answer was the camera itself.
  // ⚠ The opaque-sheet fix that came out of that hunt is real and stays: the page is drawn by
  // the GPU layer in band -1 and `drawSheet` no longer paints it. Layouts render.
  //
  // Measured on drawing 1158 (252,797 polys), p95 over 24 zoom frames at a working zoom:
  //   Canvas2D  42.6 median / 46.1 p95      GPU canvas  33.3 median / 33.4 p95 — FLAT
  // Fidelity: model space 4.08/255, layout A-51-1 5.00/255 through `gpu_diff --app --space`.
  //
  // ⚠ `?gpu=0` or `localStorage.pdcad.gpu = "0"` turns it OFF without a rebuild.
  // ⚠ Read ONCE. A flag that changed under a mounted canvas would have to tear down a GL
  // context mid-drawing.
  const [gpuOn] = useState(() => {
    try {
      const q = new URLSearchParams(location.search).get("gpu");
      if (q === "1") return true;
      if (q === "0") return false;
      return localStorage.getItem("pdcad.gpu") !== "0";
    } catch { return true; }
  });

  const [dark, setDark] = useState(true);
  const [tog, setTog] = useState<Toggles>({ ...TOGGLES });
  const [mode, setMode] = useState("select");
  //: ⚠ Seeded from `OSMODE` when a drawing opens — see the effect below. `SNAPS_ON` is only
  //: the value before any drawing is loaded.
  const [snaps, setSnaps] = useState<string[]>(SNAPS_ON);
  //: ⚠⚠⚠ **AN EDIT DRAWN BEFORE THE SERVER HAS CONFIRMED IT.** Non-null while a MOVE is in
  //: flight that the studio has already applied locally — see `commit`. It exists to stand two
  //: snaps down, not to draw anything: `cen` and `ins` carry NO OWNER (`display.py` says so
  //: where it declares them), the browser cannot tell whose centre a point is, and so it does
  //: not move them. Offering a CENTER snap on an object that is drawn in its new place and whose
  //: centre is still recorded in the old one is precisely the defect that file warns about —
  //: *"the drafter would snap to where it used to be, which no count and no picture can see."*
  const [pending, setPending] = useState<Set<string> | null>(null);
  const [sel, setSel] = useState<Pick[]>([]);
  //: The command in progress. ⚠⚠ **THIS IS AutoCAD'S WHOLE GRAMMAR IN ONE FIELD.** A drafter
  //: selects, names a command, and THEN answers its prompts — so while it is set, a click on
  //: the drawing means "here", not "select this". `Canvas2D.picking` is the same idea seen from
  //: the canvas.
  //: ⚠ `held` is a number the command captured BEFORE its points — OFFSET's distance. See
  //: `CmdDef.scalarFirst`.
  //: ⚠ `ref` is ROTATE's and SCALE's `[Reference]` option in progress: `undefined` means the
  //: command wants its scalar the ordinary way, `null` means the drafter typed R and the
  //: reference value has not arrived yet, and a NUMBER is the reference waiting for the new
  //: one. Three states, because "no reference" and "reference of 0" are different answers and
  //: a single number could not tell them apart.
  const [cmd, setCmdRaw] = useState<{ name: string; pts: Pt[]; held?: number;
                                   //: ⚠ COPY's `[mode]`. `askMode` is the sub-prompt waiting for
                                   //: Single or Multiple; `multi` is the answer. BricsCAD's own
                                   //: default is Multiple — `<Multiple>` in its prompt — so an
                                   //: empty Enter at the sub-prompt means many.
                                   askMode?: boolean; multi?: boolean;
                                   ref?: number | null } | null>(null);
  //: What the drafter has typed into the dynamic-input box, as text. ⚠ TEXT, not a number: "1."
  //: and "-" are states a person passes through, and `Number("1.")` is 1, so parsing on every
  //: keystroke would fight them for the decimal point.
  //: ⚠ Not state — see `typedStore`. The setter keeps the `useState` shape (a value or an
  //: updater) because 68 call sites use both.
  const setTyped = (v: string | ((p: string) => string)) => {
    setTypedValue(typeof v === "function" ? v(typedStore.v) : v);
    // ⚠ The one render that still has to follow the buffer — see `preview`.
    if (live.current?.cmd?.pts.length) setTypedGen((n) => n + 1);
  };
  const [typedGen, setTypedGen] = useState(0);
  //: The DRAW tool in progress and the points it has so far. ⚠ Separate from `cmd`: a Modify
  //: command acts on a selection and a Draw tool creates from nothing, so they can never both
  //: be running and keeping one field for both would make that an invariant to remember.
  /** ⚠⚠⚠ **THE SHAPE A DRAFTER JUST DREW, HELD ON SCREEN UNTIL THE SERVER SENDS ITS OWN.**
   *
   *  `Canvas2D` rubber-bands the real geometry at 60 fps for the whole gesture — and then the
   *  final click clears `draw`, the band goes out, and NOTHING is on screen where the object
   *  belongs until the POST returns. Measured 2026-09-09 on drawing 10919: the POST is **118 ms**
   *  of which 113 is `apply_op`, so a drafter drawing a wall watches it disappear for an eighth
   *  of a second and reappear, once per line. `commit`'s own note says exactly this about MOVE —
   *  *"Worse than waiting: the drag preview is gone the instant you commit and the object SNAPS
   *  BACK for the whole POST, then jumps. That is not slowness, it is a wrong picture in
   *  between"* — and MOVE was fixed with `applyLocalTransform` while every DRAW was left with it.
   *
   *  ⚠ This is NOT the optimistic display-list splice MOVE uses, deliberately. A new object has
   *  no handle until the server allocates one, and `applyDelta` splices by INDEX into the old
   *  array — so a synthesised row would put every later index out by one and corrupt the very
   *  delta meant to replace it. The ghost is drawn OVER the display list and owns no row in it,
   *  so it cannot be wrong about anything except its own pixels, and it lives for one round trip.
   *
   *  ⚠ It is the points that were POSTED, so it is a picture of the request. Cleared when the
   *  report lands (the real geometry is in `dl` by then) and cleared on failure, where the
   *  honest picture is the one without the object. */
  // ⚠⚠ **A LIST, NOT ONE SLOT.** LINE and POINT re-arm and commit segment by segment, and each
  // segment's server patch can be SECONDS on a million-object drawing. A single slot held only the
  // newest band, so every earlier segment VANISHED the instant the next was drawn and did not come
  // back until its own slow patch landed or a reload rebuilt the scene — the "only the last line
  // shows" the owner reported (2026-09-21). Each ghost is dropped by its OWN seq when its report
  // lands or fails (see `dropGhost`), so all the in-flight ones stay on screen together.
  const [ghosts, setGhosts] = useState<{ kind: string; pts: Pt[]; seq: number;
                                         rect?: { dims?: { l: number; w: number };
                                                  square?: boolean; rot?: number } }[]>([]);
  const ghostSeq = useRef(0);
  //: ⚠ `ask` is which of the tool's typed answers it is waiting for, and `got` the ones it
  //: already has. TEXT is placed, then given a height, then given its string.
  const [draw, setDrawRaw] = useState<{ mode: string; pts: Pt[]; ask?: number;
                                     //: ⚠ The object a radius/diameter/arc dimension measures,
                                     //: captured when the tool armed — see the arm effect.
                                     ref?: string;
                                     //: ⚠⚠ PLINE's `A` option: the indices of the SEGMENTS the
                                     //: drafter drew as arcs, and whether the NEXT one is one.
                                     //: The bulge itself is the server's — see `_tangent_bulges`
                                     //: — because tangent continuation is geometry, and geometry
                                     //: lives in one place in this app.
                                     arcs?: number[];
                                     arcing?: boolean;
                                     //: ⚠ RECTANG's Chamfer/Fillet/Width, which are options of
                                     //: the COMMAND and not of the object: AutoCAD builds one
                                     //: polyline with cut corners rather than drawing a
                                     //: rectangle and then filleting it, so the undo is one step.
                                     opt?: Record<string, unknown>;
                                     //: ⚠ Which of those three is being typed right now. Not the
                                     //: tool's own `ask` list: `asks` is fixed per tool and runs
                                     //: after the points, and these run BEFORE them and only if
                                     //: the drafter asks for them.
                                     optWant?: string;
                                     //: ⚠ XLINE's and RAY's `[Horizontal/Vertical]`. The
                                     //: direction IS the answer, so the command completes at its
                                     //: FIRST point and `onDrawPoint` derives the through point
                                     //: from this rather than asking for one.
                                     //: ⚠⚠ A DIRECTION, NOT A NAME. It was `"h" | "v"` while
                                     //: those were the only two; `Angle` makes it a vector and
                                     //: H and V become `(1,0)` and `(0,1)` — one code path for
                                     //: three options instead of a widening enum.
                                     dir?: { x: number; y: number };
                                     //: XLINE/RAY `Angle` is waiting for a number, and `Bisect`
                                     //: for three picks rather than the catalogue's two.
                                     angWant?: boolean; bisect?: boolean;
                                     //: REVCLOUD's gesture, once NAMED rather than inferred from
                                     //: how many points arrived. `rect` finishes on the second.
                                     shape?: "rect" | "poly";
                                     //: An `asks` answer given UP FRONT — BricsCAD's order for
                                     //: REVCLOUD's arc length. `preWant` is the key being asked.
                                     pre?: Record<string, unknown>; preWant?: string;
                                     //: SPLINE's `[Method]` sub-prompt, Fit against CV.
                                     askMethod?: boolean;
                                     //: TEXT's `use defined Style` — waiting for a style NAME.
                                     styleWant?: boolean;
                                     //: ⚠ A sub-prompt whose answers are WORDS rather than a
                                     //: number — SPLINE's `Knots` and REVCLOUD's `Style`. The
                                     //: answer lands in `pre` and rides to the server with the
                                     //: rest. Generic because two options wanting the same shape
                                     //: is where a third one gets written differently.
                                     subWant?: { key: string; label: string;
                                                 opts: [string, string][] };
                                     //: XLINE/RAY `Parallel` is waiting for an OBJECT, so the
                                     //: next click is an argument and not a point.
                                     parWant?: boolean;
                                     //: CIRCLE's `Multiple` — the tool re-arms after each one.
                                     multi?: boolean;
                                     //: REVCLOUD's `Freehand` — the path follows the pointer
                                     //: while the button is down instead of taking picks.
                                     freehand?: boolean;
                                     //: RECTANG's `Square` — the second click gives a DIRECTION
                                     //: and the longer of its two offsets becomes both sides.
                                     square?: boolean;

                                     //: ⚠ RECTANG's `[Dimensions]` — a length and a width, so the
                                     //: second corner is derived and the click only picks which
                                     //: quadrant it goes in. Not in `opt`: the server never sees
                                     //: these, it is given two corners as always.
                                     //: ⚠ `a` is only set while `Area` is being answered —
                                     //: it is the area waiting for a side, not a third dimension.
                                     dims?: { l: number; w: number; a?: number };
                                     got?: Record<string, unknown> } | null>(null);
  //: ⚠ The dimension flyout's kind, set by the chrome through `A.setDimKind` — see `DIM_KIND`.
  //: RECTANG's Chamfer/Fillet/Width, kept between commands — see `RECT_OPT`.
  const rectOpt = useRef<Record<string, unknown>>({});
  const [dimKind, setDimKind] = useState("ortholinear");
  //: ⚠⚠ **THE ARC AND CIRCLE FLYOUTS, AS ONE STRING EACH.** `handlers.ts` calls
  //: `A.setArcGiven`/`A.setCircGiven` with the name of the GIVEN — "sce", "ttr" — and calls no
  //: `setMode` at all, so the adapter has to arm the mode as well. Both were optional calls
  //: landing on the Proxy's do-nothing fallback, which is why every flyout entry pressed and
  //: did nothing at all: no error, no armed tool, no message.
  //: ⚠⚠ **THE IN-PLACE TEXT EDITOR, OPENED BY DOUBLE-CLICK AND BY NOTHING ELSE** — which is
  //: AutoCAD's only gesture for it. `null` when closed; the spec is fetched RAW from the server
  //: rather than read off the display list, which strips every inline code.
  const [textEdit, setTextEdit] = useState<{ spec: TextSpec; box: TextBox } | null>(null);
  //: ⚠⚠ **AutoCAD'S SELECTION GRAMMAR, WHICH HAS NO BUTTON AND NEVER HAS HAD ONE.** `SELECT`
  //: opens "Select objects:" and a letter chooses how — F for a fence, WP for a window polygon,
  //: P for the previous set. `mode` is null while the letter is still being waited for.
  /** SELECT's state. ⚠ `then` is what makes VERB-FIRST work: AutoCAD lets you press ERASE and
   *  THEN pick, and this app used to answer "Select objects first." and stop — which is half of
   *  how AutoCAD is driven, refused. The command to run once the selection is confirmed is
   *  carried here rather than re-fired through the chrome, because the chrome's handler table is
   *  memoised on the selection and would still be reading the empty one. */
  const [selWant, setSelWant] = useState<
    { mode: SelectMode | null; pts: Pt[]; then?: [string, Record<string, unknown> | undefined] }
    | null>(null);
  //: ⚠⚠ **SETVAR — the variable being asked about, or "" while the NAME is still wanted.**
  //: System variables are how AutoCAD is configured and scripted, and `$LTSCALE` in particular
  //: decides what every dashed line in the drawing looks like: measured across the files
  //: imported here it is 10.0 in half of them and 400 in one.
  /** ⚠ `then` is what makes ELEV one command instead of two: it names the variable to ask for
   *  after this one is answered, which is how AutoCAD's own ELEV prompts for the elevation and
   *  then the thickness. */
  const [varWant, setVarWant] =
    useState<{ name: string; now: unknown; then?: string } | null>(null);
  const [sysvars, setSysvars] = useState<SysVar[] | null>(null);
  //: ⚠ The PREVIOUS selection set, for `SELECT` ▸ Previous. Kept as handles rather than picks
  //: because a re-imported drawing renumbers nothing but a deleted object must not come back.
  const prevSel = useRef<Pick[]>([]);
  //: ATTDEF's prompt chain — the tag, then the prompt, then the default, then a click.
  const [attWant, setAttWant] = useState<{ handle: string; step: "tag" | "prompt" | "def";
                                           tag: string; prompt: string; def: string } | null>(null);
  //: ⚠ Where the attribute goes, once the three answers are in. `null` when not placing.
  const [attPlace, setAttPlace] = useState<{ handle: string; tag: string; prompt: string;
                                             def: string } | null>(null);
  //: AutoCAD's PICKSTYLE — whether clicking one member of a group selects them all.
  const [groupPick, setGroupPick] = useState(true);
  //: ⚠ The drawing's groups, loaded once per open. Clicking a member has to select the whole
  //: group and that decision is made per click, so it cannot be a round trip.
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [arcGiven, setArcGiven] = useState("3p");
  const [circGiven, setCircGiven] = useState("3p");
  // ⚠ ELLIPSE's given — `axis` or `c`. It is NOT reset when the tool re-arms, which matches the
  // arc and circle flyouts: AutoCAD remembers the gesture you last chose.
  const [ellGiven, setEllGiven] = useState("axis");
  /** ⚠⚠ **THE THREE GESTURES THAT PICK OBJECTS.** TTR, TTT and ARC Continue were armed buttons
   *  that printed "not built yet" and returned — see `tangency.ts`. They collect their objects
   *  from the SELECTION, because pdcad is noun-verb, and then take one click to say which of
   *  the up-to-four answers was meant. */
  const [tanWant, setTanWant] = useState<
    { given: "ttr" | "ttt" | "cont"; objs: TanObj[]; r: number } | null>(null);
  /** The end point and end TANGENT of the last line or arc drawn — what ARC Continue continues
   *  from. ⚠ `canContinueArc` was read by the ribbon and never written by anything, so the
   *  button was permanently greyed saying "nothing has been drawn yet". */
  const [cont, setCont] = useState<Cont | null>(null);
  /** IMAGEATTACH — the chosen file, waiting for the two corners of its frame. */
  //: ⚠ ONE placer for both IMAGEATTACH and PDFATTACH — the gesture is identical (a file, then
  //: two corners) and a second copy of it would be a second place for the corner order to be
  //: got wrong. `kind` is the only difference and it is decided when the file is chosen.
  const [imgWant, setImgWant] =
    useState<{ file: File; pts: Pt[]; kind?: "image" | "pdf"; page?: number;
               //: How many pages the chosen PDF has, read by the probe when the file was
               //: picked. ⚠ Held so the prompt can SAY it — "page 1 of 12" is the difference
               //: between a drafter knowing to ask for another and not knowing there are any.
               pages?: number } | null>(null);
  /** The clipboard, and MATCHPROP's source.
   *
   * ⚠⚠ **INSIDE ONE DRAWING, CUT-AND-PASTE IS A MOVE AND COPY-AND-PASTE IS A COPY.** That is not
   * an approximation, it is the same result: cut removes the objects and paste puts them back at
   * the new point, which is where a move would have left them. So the clipboard holds HANDLES and
   * a flag, the paste runs `move` or `copy`, and the undo is one step rather than two.
   * ⚠ The visible difference from AutoCAD, stated because it is real: the objects do not vanish
   * between the cut and the paste. Making them vanish would mean holding the geometry outside the
   * drawing, which is also what a paste into ANOTHER drawing needs and which this app has nowhere
   * to put yet. `clipPasteBlock` is unbuilt for the same reason.
   * ⚠ `base` is the point the paste places BY. Plain Copy uses the selection's bottom-left; COPY
   * WITH BASE POINT asks, because for a door leaf the corner of the bounding box is nowhere near
   * the hinge. */
  const [clip, setClip] = useState<
    { handles: string[]; cut: boolean; base: Pt;
      //: ⚠⚠ **WHICH DRAWING IT CAME FROM, WHICH IS WHAT MAKES TWO PASTES OUT OF ONE COMMAND.**
      //: Inside one drawing a paste is a MOVE or a COPY — exact, one op, one undo step. Into
      //: ANOTHER drawing a handle means nothing, so the geometry has to travel: `rows` are
      //: `_freeze` records and `blocks` the definitions any INSERT among them names, nested ones
      //: included. Fetched on COPY rather than on paste, because by then the source may be
      //: closed.
      from: number | null; rows?: unknown[]; blocks?: unknown[] } | null>(null);
  //: MATCHPROP's source object — the one clicked FIRST. ⚠ A ref and not state: the click handler
  //: that reads it is bound into the canvas, and a stale closure would paint the wrong properties.
  const matchFrom = useRef<Pick | null>(null);
  //: COPYBASE is waiting for the point the paste will place BY.
  const [clipBase, setClipBase] = useState(false);
  //: MATCHPROP is armed. ⚠ Stays armed across targets — AutoCAD paints until Escape.
  const [matching, setMatching] = useState(false);
  //: ⚠⚠ **THE UCS IS A VIEWING AID AND LIVES IN THE BROWSER, NOT ON THE DRAWING.** Where a
  //: drafter is working from this afternoon is not a property of the building; putting it on the
  //: plan would push one person's working origin into everybody else's copy — and into the DXF.
  const [ucs, setUcs] = useState<Ucs>(WORLD_UCS);
  //: UCS Previous. ⚠ A REF, not state: nothing renders from the history, and making it state
  //: would re-render the whole canvas every time the origin moved.
  const ucsStack = useRef<Ucs[]>([]);
  //: ⚠ The stack itself is a ref, but its DEPTH is state: `ucsCanPrev` greys a button from it,
  //: and a ref does not re-render, so the button would stay grey until something unrelated
  //: happened to redraw the ribbon.
  const [ucsDepth, setUcsDepth] = useState(0);
  //: The UCS tool, mid-gesture: the origin is down and the X axis is not.
  //: ZOOM Window's first corner. ⚠⚠ **`setMode("zoomwin")` ARMED A MODE NOTHING HANDLED** — the
  //: command answered, the ribbon button lit, and the view never moved: the exact "live button
  //: that does nothing" this project refuses everywhere else. Found by a peer session's UX
  //: survey, whose fixture framed the view with ZOOM W and silently got no framing at all.
  const [zoomWant, setZoomWant] = useState<Pt | null>(null);
  const [ucsWant, setUcsWant] = useState<{ origin: Pt | null } | null>(null);
  //: ARRAYPATH is waiting for the PATH — the second argument, which is a pick and not part of
  //: the selection. ⚠ Holds the objects being arrayed, because the path pick clears nothing and
  //: the selection must survive it.
  const [pathWant, setPathWant] = useState<{ items: Pick[] } | null>(null);
  //: PASTEBLOCK — the next paste wraps its objects in one anonymous block.
  const [pasteBlock, setPasteBlock] = useState(false);
  /** ISOLATEOBJECTS / UNISOLATEOBJECTS — the handles hidden from the canvas.
   *
   *  ⚠⚠ **NOT THE SAME COMMAND AS LAYISO, WHICH IS WHY IT IS A SET OF HANDLES.** LAYISO turns
   *  LAYERS off and is already built; this hides the OBJECTS you did not select and leaves every
   *  layer alone — a drafter uses it to work on one detail inside a busy layer, which layer
   *  visibility cannot express.
   *  ⚠ A view state, not a document one: it is not written to the drawing and it goes when the
   *  drawing is closed, exactly as AutoCAD's does. */
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  //: ⚠ A ref beside the state: the actions object is memoised and would otherwise close over the
  //: set as it was when the drawing opened — isolating twice would forget the first one.
  const hiddenRef = useRef<Set<string>>(hidden);
  hiddenRef.current = hidden;
  const imgPick = useRef<HTMLInputElement | null>(null);
  const lspPick = useRef<HTMLInputElement | null>(null);
  //: Which command opened the file picker. ⚠ A REF: it is read inside the input's `onChange`,
  //: which fires from the OS dialog long after the render that set it, and state would be a
  //: re-render of the whole canvas to remember one word.
  const pickKind = useRef<"image" | "pdf">("image");
  //: ⚠ The third kind of command — see `PICKS`. It asks for OBJECTS after it starts, which
  //: neither `cmd` (points over an existing selection) nor `draw` (create from points) can hold.
  /** ⚠⚠ `at` — WHERE each object was clicked — is not decoration. FILLET between a line and an
   *  arc has up to four answers of the same radius and between two arcs up to eight; which one
   *  the drafter meant is decided by which side of each curve they clicked and by nothing else.
   *  The server takes the object midpoints when this is empty, which is the old line-only
   *  behaviour, so an older caller still gets the old answer. */
  const [objCmd, setObjCmd] = useState<
    { name: string; got: Pick[]; at: Pt[]; held?: number } | null>(null);
  /** ⚠⚠ OFFSET's SECOND question. The object is picked, and the side is a separate click —
   *  AutoCAD asks them one after the other, and this app used to ask for one side and apply it
   *  to a whole selection. Offsetting a wall's two lines outwards is one click on each. */
  const [sideWant, setSideWant] = useState<{ handle: string; dist: number } | null>(null);
  /** ⚠ DIMCONTINUE/DIMBASELINE's `Select`: the next click names the dimension to continue FROM,
   *  not a point. It is a click on an object in the middle of a command that otherwise reads
   *  clicks as points, which is why it has to turn `picking` off for exactly one pick. */
  const [basePick, setBasePick] = useState(false);
  // ⚠ `"gradient"` rather than a second boolean: the two are mutually exclusive and a pair of
  // flags can be set at once, which is a dialog showing a pattern list and a colour picker.
  const [hatchDlg, setHatchDlg] = useState<false | true | "gradient">(false);
  //: ⚠ The NAME of the block open for editing, or null. The ribbon reads it to turn Block
  //: Editor into "press again to save", and the state lives on the SERVER (`Drawing.bedit`) —
  //: this is the browser's copy of it, refreshed with the drawing.
  const [bedit, setBedit] = useState<string | null>(null);
  //: ⚠ The pattern to fill with while HATCH is armed for internal points — see `applyHatch`.
  const [hatchWant, setHatchWant] = useState<HatchSpec | null>(null);
  //: The block dialog, and what the base-point click that follows it should do. ⚠ Two states,
  //: because the dialog closes BEFORE the click: AutoCAD's "Pick point" dismisses its dialog
  //: too, and there is nothing to come back for.
  const [blockDlg, setBlockDlg] = useState<"create" | "insert" | null>(null);
  const [blockWant, setBlockWant] = useState<
    { mode: "create" | "insert"; name: string; scale: number; rotation: number;
      //: ⚠ Set only when the block came from ANOTHER drawing — see `DesignCenter`. Its presence
      //: is what decides between `insert` (a name this drawing already has) and `importblock`
      //: (a definition that has to travel first), which is a real difference and not a flavour.
      from?: number } | null>(null);
  const [adcOpen, setAdcOpen] = useState(false);
  const [clipWant, setClipWant] = useState<{ pts: Pt[] } | null>(null);
  //: BREAK / BREAKATPOINT — one object already selected, now collecting one or two points.
  //: ⚠ `want` is how many: 1 splits, 2 cuts a gap. AutoCAD spells BREAKATPOINT as BREAK with
  //: the second point equal to the first, which is a trap for anybody reading the code.
  const [breakWant, setBreakWant] = useState<{ want: 1 | 2; pts: Pt[] } | null>(null);
  //: AREA / DIST / ANGLE with PICKED points. ⚠ Most rooms in this office's drawings have no
  //: closed object to select — they are four overshooting wall lines — so picking is the
  //: primary route, not a fallback.
  const [measWant, setMeasWant] =
    useState<{ kind: "area" | "distance" | "angle" | "panpts";
               want: number; pts: Pt[] } | null>(null);
  //: ⚠ The layer the next line lands on — the ribbon's dropdown reads and writes it through
  //: `T.drawLayer` and `A.setDrawLayer`. See `handlers.ts` "layer.control".
  const [drawLayer, setDrawLayer] = useState("0");
  //: The selected object's real grips, and which one is HOT. ⚠ Only ever for a SINGLE selection:
  //: AutoCAD shows grips for everything picked, but dragging one of forty objects' grips is a
  //: different command (it stretches the whole set), and offering the squares without that
  //: behaviour would be a control that lies.
  const [grips, setGrips] = useState<Grip[] | null>(null);
  const [hotGrip, setHotGrip] = useState<number | null>(null);
  /** The grip edit in progress, and which of AutoCAD's five modes SPACE has cycled to.
   *
   * ⚠⚠ **CLICK A GRIP, PRESS SPACE TWICE, YOU ARE ROTATING ABOUT THAT POINT.** It is the
   * fastest edit AutoCAD has — no command, no base-point pick — and the reason a drafter picks
   * an object before deciding what to do to it. Only STRETCH existed here, so a grip was a
   * one-trick control and every other edit cost a trip to the ribbon.
   * ⚠ `at` is the grip's WORLD position, kept because the non-stretch modes hand it to the
   * ordinary command machinery as the base point already picked — `cmd.pts[0]`. That is what
   * makes MOVE/ROTATE/SCALE/MIRROR from a grip free: preview, typed angles, the Reference
   * option and the undo record are the existing ones, not a second implementation. */
  const [gripEdit, setGripEdit] = useState<{ at: Pt; mode: number } | null>(null);
  const gripEditRef = useRef<{ at: Pt; mode: number } | null>(null);
  gripEditRef.current = gripEdit;
  /** Which grip was clicked. ⚠ Remembered separately from `hotGrip` because the four transform
   *  modes CLEAR `hotGrip` — the click belongs to the command then, not to the vertex — and
   *  cycling back round to STRETCH has to know which vertex it was. */
  const gripIdxRef = useRef<number | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  //: Where the pointer is in SCREEN pixels, for the dynamic-input box. ⚠ A ref, not state: it
  //: moves with every pointer event and the box only needs it at render time.
  const cursorPx = useRef<{ x: number; y: number } | null>(null);
  // ⚠ State, not a ref, because the STATUS BAR shows it — `shell.tsx` builds `_endp` and the
  // rest out of `st.snap.kind`. The canvas only reports a CHANGE, so this is not a per-move
  // render; without that guard it would be sixty a second.
  const [snapHit, setSnapHit] = useState<SnapHit | null>(null);
  /** A SINGLE-SHOT object snap — AutoCAD's `MID`, `END`, `CEN` typed at a point prompt.
   *
   * ⚠⚠ **IT REPLACES THE RUNNING SNAPS FOR EXACTLY ONE PICK**, and that is the whole point: a
   * drafter who needs one midpoint should not have to turn MIDPOINT on for the rest of the day.
   * ⚠ `"none"` is a value, not an absence — `NON` turns snapping off for one pick, which is the
   * only way to put a point NEAR a line without landing on it. So the state is a union and the
   * test for "is an override armed" is `!== null`, never truthiness. */
  const [snapOnce, setSnapOnce] = useState<SnapKind | "none" | null>(null);
  const snapOnceRef = useRef<SnapKind | "none" | null>(null);
  snapOnceRef.current = snapOnce;
  /** POLARANG — the increment F10 locks to. ⚠ The drop-up has been calling `A.setPolarAng`
   *  since the chrome was lifted and nothing defined it, so picking 15 deg did nothing and the
   *  tooltip went on saying 15 whatever you chose. AutoCAD's own default is 90. */
  // ⚠ POLARANG, and 90 is AutoCAD's default. Seeded from the drafter's profile when a drawing
  // opens — see `applyMine`.
  const [polarAng, setPolarAng] = useState(90);
  /** SNAPUNIT — the grid step F9 rounds to. ⚠ From the DRAWING, unlike the four above. */
  const [snapUnit, setSnapUnit] = useState(SNAP_UNIT);
  const snapUnitRef = useRef(snapUnit);
  snapUnitRef.current = snapUnit;

  /** The drafter's own system variables, applied where they are read.
   *
   * ⚠⚠ **FOUR OF THEM LIVE IN THE CANVAS AS MODULE STATE, AND THAT IS DELIBERATE.** `hit()`
   * reads PICKBOX on every mouse move; a prop threaded through three components would be
   * re-read on every one of those moves for a value that changes when somebody types its name.
   * `setHandVars` is the one door. POLARANG is React state because the drop-up shows a tick
   * beside it.
   * ⚠ Tolerant of an old server that does not send `mine`: everything keeps its default.
   */
  const applyMine = useCallback((mine?: Record<string, number>) => {
    if (!mine) return;
    setHandVars(mine);
    if (typeof mine.POLARANG === "number" && mine.POLARANG > 0) setPolarAng(mine.POLARANG);
    if (typeof mine.PICKFIRST === "number") pickFirst.current = mine.PICKFIRST !== 0;
  }, []);

  /** PICKFIRST — whether a selection made BEFORE a command is used by it.
   *
   * ⚠⚠ **1 IS NOUN/VERB AND IS WHAT EVERY DRAFTER USES**: pick the wall, press M, move it.
   * AutoCAD's 0 makes every command ask *Select objects:* even when something is already picked,
   * and a few offices set it that way deliberately to stop a stray pick becoming a stray edit.
   * ⚠ Held in a ref because `runCmd` reads it in the same tick as the keystroke.
   */
  const pickFirst = useRef(true);
  const [, setView] = useState<View>({ x: 0, y: 0, scale: 1 });
  /** ZOOM Previous — a STACK, not a slot.
   *  ⚠ A drafter zooms in three times looking for something and expects Previous to walk back
   *  out, not to jump to wherever they were first.
   *  ⚠ Capped, because every wheel notch is a view change and an uncapped history is a memory
   *  leak that grows for as long as the drawing is open. AutoCAD keeps ten. */
  const viewHist = useRef<View[]>([]);
  const lastView = useRef<View | null>(null);
  const api = useRef<CanvasApi | null>(null);

  /** ⚠⚠ **A 401 IS NOT A TOAST, IT IS A SIGN-OUT.** A session can expire while pdcad is open —
   *  it is a two-week cookie on a machine people leave logged in — and the failure a drafter
   *  actually sees is a MOVE that reports an error. Showing that as red text leaves them on a
   *  drawing that has silently stopped saving. */
  const fail = useCallback((e: unknown) => {
    if (e instanceof NotLoggedIn) { onSignOut(); return; }
    setErr(String(e));
    // ⚠ IN THE TRANSCRIPT TOO, for the same reason `warn` says as well as toasting: an error
    // banner is dismissed and gone, and AutoCAD puts every failure in the command line where it
    // can be scrolled back to. ⚠ Stripped of the `Error: ` prefix, which is noise to a drafter.
    sayRef.current?.(`  ${String(e).replace(/^Error:\s*/, "")}`);
  }, [onSignOut]);

  const toast = useCallback((s: string | null) => {
    setNote(s);
    if (s) window.setTimeout(() => setNote((c) => (c === s ? null : c)), 4000);
  }, []);

  // ⚠ With no server there is no drawing LIST — the drawings are the files on this machine, and
  // `OPENDWG` is how they arrive. Asking anyway would report a failure that is not one.
  useEffect(() => {
    if (standalone()) return;
    listDrawings().then(setRows).catch(fail);
  }, [fail]);


  const open = useCallback(async (row: DrawingRow) => {
    setOpenDlg(false); setErr(null);
    setBusy(`Opening ${row.name} — ${row.entities.toLocaleString()} objects…`);
    const t0 = performance.now();
    try {
      // ⚠ Both, together. The layer table is small and the display list is not; fetching them
      // in parallel costs one round trip rather than two on a link where that is the expense.
      // ⚠⚠ **THE SYSTEM VARIABLES COME WITH IT, BECAUSE THE RUNNING SNAPS ARE ONE OF THEM.**
      // `OSMODE` is stored in the drawing's header, so reopening a file must restore the snaps
      // it was saved with — that is what makes them the drawing's setting rather than the app's.
      // ⚠⚠ **THE SPACE COMES FROM THE ROW, BECAUSE IT IS NEEDED BEFORE ANY OF THESE ANSWER.**
      // `open()` used to ask for "Model" as a literal, so 53 of the 138 drawings imported here —
      // every one saved on a sheet, which is what TILEMODE 0 means — came back to model space
      // and read as a layout that had been lost. Resolving it server-side keeps this one fetch.
      const want = row.opens_in || "Model";
      const [d, rows, los, sv] = await Promise.all([
        loadDisplay(row.id, want), loadLayers(row.id), loadLayouts(row.id),
        loadSysvars(row.id).catch(() => null)]);
      const tFetched = performance.now();
      // ⚠⚠⚠ **THE LAYER TABLE IS THE DOCUMENT; THE DISPLAY LIST IS A PICTURE OF IT.** A layer's
      // on/frozen state no longer invalidates the cached picture — it changes no row, and
      // invalidating for it made the next fetch of `ysl-hn-center-2025` **4,935 ms instead of
      // 165** for one click (`views.LayerViewSet.ROW_FIELDS`). So the cached `layers` array can
      // be a toggle or two behind, and the table just fetched beside it is the truth. Both come
      // out of the same `Promise.all`, so this is exact rather than a race.
      // ⚠ By NAME: the picture's index order is the build's and the table's is the queryset's.
      setDl(withLiveLayers(d, rows)); setId(row.id); setLayerRows(rows);
      // ⚠⚠ **THE WORKING ORIGIN COMES BACK, PER DRAWING.** It used to reset to the world on
      // every reload, so a drafter who set the origin on a rotated wall lost it the moment they
      // pressed F5 or the tab reloaded — and had to re-pick two points to get back to where
      // they were already working. ⚠ Read from the DRAWING's header now ($UCSORG/$UCSXDIR),
      // not from `localStorage` — see `ucsFromVars`. The NAMED origins are still local, which
      // is a different question: a name is a shortcut this drafter made, not a property of the
      // building.
      // ⚠ The Previous stack is NOT restored. "Previous" means previous in THIS session; a
      // stack surviving a reload would step back to somewhere the drafter cannot remember.
      setUcs(ucsFromVars(sv?.vars));
      ucsStack.current = [];
      setUcsDepth(0);
      // ⚠ Groups come with the drawing for the same reason the layers do: clicking a member
      // must select the whole group, and that is decided per click.
      void loadGroups(row.id).then((g) => setGroups(g.groups)).catch(() => setGroups([]));
      if (sv) {
        setSysvars(sv.vars);
        const osmode = sv.vars.find((v) => v.name === "OSMODE");
        if (osmode) setSnaps(namesOf(Number(osmode.value) || 0));
        // ⚠ Same reasoning as OSMODE: pen weights are the DRAWING's setting, stored in its
        // header as $LWDISPLAY, so reopening a file must show it the way it was saved.
        const lwd = sv.vars.find((v) => v.name === "LWDISPLAY");
        if (lwd) setTog((t) => ({ ...t, lwt: !!Number(lwd.value) }));
        // ⚠⚠ **THE DRAFTER'S OWN VARIABLES, APPLIED — the pick box, the aperture, the grip
        // size, the wheel step and the polar increment.** They are not in the drawing (see
        // `sysvars.USER_SCOPED`); they arrive with it because this is the one fetch that
        // happens when a drawing opens, and the canvas reads them on every mouse move.
        applyMine(sv.mine);
        const snapUnit = sv.vars.find((v) => v.name === "SNAPUNIT");
        if (snapUnit) setSnapUnit(Number(snapUnit.value) || 10);
      }
      // ⚠ `null` IS model space here — see the `space` state. A layout is its own name, and
      // passing "Model" through as a name would put a phantom tab beside the real ones.
      setSpace(want === "Model" ? null : want);
      setLayoutRows(los.layouts);
      // ⚠ `replaceState`, not `pushState`. Opening five drawings should not bury the back
      // button under five entries of the same page.
      window.history.replaceState(null, "", `?d=${row.id}`);
      toast(`${d.counts.polys.toLocaleString()} polylines · ` +
            `${d.counts.texts.toLocaleString()} text · ` +
            `${d.counts.points.toLocaleString()} points · ` +
            `${((performance.now() - t0) / 1000).toFixed(2)}s`);
      // ⚠⚠ **HOW LONG OPENING A DRAWING ACTUALLY TAKES, SPLIT WHERE THE TIME GOES.**
      // `docs/GAP.md` has carried "open time is UNMEASURED" for three passes because the only
      // number anyone had was a harness's own `wait_for_timeout(7000)` — all three drawings
      // read 7.1 s, which is what a stopwatch measuring a stopwatch reads.
      // ⚠⚠ **TWO `requestAnimationFrame`s, NOT ONE.** The first fires BEFORE the frame that
      // applies this state is composited; the second is the first callback after it is on
      // screen. Recording at one rAF measures React, not the picture, and would report a
      // 47,000-object drawing as instant.
      // ⚠ `fetch` is the network and the server; `paint` is this machine turning the answer
      // into a drawing. They are different problems with different fixes, and one total hides
      // which one you have.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        (window as unknown as Record<string, unknown>).__pdcadOpen = {
          id: row.id, name: row.name, space: want,
          entities: row.entities,
          fetch: Math.round(tFetched - t0),
          paint: Math.round(performance.now() - tFetched),
          total: Math.round(performance.now() - t0),
          counts: d.counts,
          // ⚠⚠ **THE DRAWING'S OWN GROUND, because a harness that hardcodes coordinates ages
          // badly.** `qa/bench_actions.py`'s MTEXT case drew a 40,000 x 10,000 box sized for a
          // default text height of `extent / 200` — about 2,500 when it was written. This
          // drawing's extents have since grown to 6.7 million wide, the default height with
          // them to 33,332, and the command refused with "that box is too narrow for text of
          // this height": a correct refusal, a row missing from the table, and nothing wrong
          // with MTEXT. `qa/splice_shot.py` wanted the same number and had to fall back to
          // ZOOM EXTENTS for want of it.
          extents: d.extents ?? null,
        };
      }));
    } catch (e) {
      fail(e);
    } finally { setBusy(null); }
  }, [toast, fail]);

  /** OPENDWG's body, on its own, because the DESKTOP APP opens a drawing WITHOUT the command:
   *  double-clicking a .dwg in a file manager launches `pdcad-desktop plan.dwg`, and there is no
   *  command line to type into yet when that happens. One path, two doors. */
  const openPicked = useCallback(async (picked?: PickedFile | null) => {
    const got = await openFromDisk(picked);
    // ⚠ a row of its own, so everything that takes a `DrawingRow` — the title bar, the file
    // tabs, `open()` — works without knowing this drawing has no server.
    const row: DrawingRow = {
      id: got.id, name: got.name, source_ext: got.name.split(".").pop() || "dwg",
      source_name: got.name, dxfversion: "", entities: got.entities,
      layer_count: got.layers, created_at: new Date().toISOString(),
      opens_in: got.spaces[0] || "Model",
    };
    // ⚠⚠⚠ **THE DRAWINGS THIS ONE DISPLACED GO WITH IT.** The engine holds ONE document — see
    // `forgetAllLocal` — so a local drawing left in the list is a tab whose name now addresses
    // this file's geometry. Leaving it there is how a drafter opens "the electrical plan" and
    // gets the elevation, and the only reason the owner SAW it was two differently-named
    // layouts: `no layout called "CGR-DD-ARC-ELC-053"`. Two files that both call their sheet
    // `Layout1` would simply have drawn the wrong drawing.
    const gone = new Set(got.dropped || []);
    setRows((r) => [row, ...r.filter((x) => x.id !== row.id && !gone.has(x.id))]);
    sayRef.current?.(`  ${got.name} — ${got.entities.toLocaleString()} objects, `
                     + `${got.layers} layers, read in this tab (${got.ms} ms)`);
    // ⚠ Said, not silent. A tab disappearing with no explanation is worse than the limit itself.
    if (gone.size) {
      sayRef.current?.(`  closed ${gone.size} drawing${gone.size === 1 ? "" : "s"} held in this `
                       + `tab — the browser engine holds one at a time`);
    }
    await open(row);
  }, [open]);

  // ⚠⚠ **THE DRAWING THE APP WAS LAUNCHED WITH.** A drafter opens a drawing by double-clicking
  // it, not by typing OPENDWG — so an app that can only be fed through its own dialog is an app
  // that ignores the way its file type is used. `startupFile()` is `null` in a browser.
  // ⚠ Once. A ref rather than an empty dep list, because React 19 mounts effects twice in
  // development and opening the drafter's drawing twice would put two tabs on one file.
  const launched = useRef(false);
  useEffect(() => {
    if (launched.current || !standalone()) return;
    launched.current = true;
    void (async () => {
      try {
        const picked = await startupFile();
        if (picked) await openPicked(picked);
      } catch (e) { fail(e); }
    })();
  }, [openPicked, fail]);

  const current = rows.find((r) => r.id === id) || null;

  // ⚠ `?d=<id>` OPENS THAT DRAWING. AutoCAD has no address bar and a web app does; a drawing
  // nobody can link to is one that gets described in chat as "the one with the stairs". It is
  // also what makes a screenshot one step instead of three, which is why it exists today.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || !rows.length) return;
    const want = Number(new URLSearchParams(window.location.search).get("d"));
    const row = rows.find((r) => r.id === want);
    if (row) { opened.current = true; void open(row); }
  }, [rows, open]);

  // ⚠ Memoised. The canvas repaints when this changes identity, and building a new Set on every
  // render would repaint a 47,000-object drawing on every mouse move.
  const selHandles = useMemo(() => new Set(sel.map((p) => p.handle)), [sel]);

  // ⚠ A NEW DRAWING CLEARS THE SELECTION. Handles are per-drawing, so a stale set silently
  // highlights whatever object in the new file happens to share a handle — a wrong answer that
  // looks like a right one.
  useEffect(() => { setSel([]); setCmd(null); }, [id]);

  /** ⚠⚠ **THE MODE IS THE CHROME'S, AND IT IS WHAT ARMS A DRAWING TOOL.** `handlers.ts` maps
   *  every Draw button to `setMode` through `MODE_OF`, so the ribbon, the pull-down menus, the
   *  classic toolbars and a typed `LINE` all arrive here — one effect instead of twenty
   *  handlers, and none of them can disagree about which tool is armed.
   *  ⚠ A mode this app has not built says so and falls back to select, rather than arming a
   *  tool that swallows clicks. */
  // ⚠⚠ **A LAYOUT EFFECT, AND IT IS WORTH A WHOLE ROUND TRIP ON EVERY COMMAND.** Arming a tool
  // is the one step of a typed command that CANNOT be collapsed into the task that dispatched
  // it: the chrome sets a mode, React commits, and only then does this effect open the prompt.
  // A passive effect runs AFTER the browser has painted, so the command line's own echo gets a
  // frame to itself before the tool exists; a layout effect runs inside the commit. Same change
  // and same reason as `Canvas2D`'s scene build, which is measured in its own note.
  useLayoutEffect(() => {
    // ⚠ HATCH is a MODE in the lifted chrome (`draw.hatch` → "bucket") and a SELECTION command
    // here, because pdcad fills the closed objects you have picked rather than tracing a
    // boundary from an internal point. So the mode opens the dialog and hands the keyboard back.
    // ⚠ `block.insert` is a MODE in the lifted chrome ("furniture" — the old app inserted
    // catalogue furniture) and a dialog here, because pdcad's blocks are this drawing's own.
    if (mode === "furniture") {
      setBlockDlg("insert");
      setMode("select");
      return;
    }
    // ⚠⚠ **AutoCAD'S HATCH OPENS WITH *"Pick internal point or [Select objects]"*, AND THE
    // FIRST HALF IS WHAT ANYBODY ACTUALLY USES.** This used to refuse outright with "select the
    // closed objects first" — which is not a gesture a drafter has, and in a plan drawn as
    // separate overshooting wall lines there is no closed object to select at all. Both routes
    // now open the same dialog; which one runs is decided by whether anything is picked, which
    // is a question the app can already answer.
    if (mode === "bucket") {
      setHatchDlg(true);
      setMode("select");
      return;
    }
    // ⚠⚠ **A MODIFY COMMAND CAN ARRIVE AS A MODE TOO, AND STRETCH DOES.** The lifted chrome
    // has `mod.stretch` in `MODE_OF`, so pressing it armed a drawing tool called "stretch" that
    // no table answered to — and the studio dutifully reported "STRETCH is not built yet" about
    // a command that was. Anything in `COMMANDS` is routed there instead of being called
    // missing; the alternative is a name list to keep in step with two other name lists.
    // ⚠ SELECT is not a draw tool and not a modify command — it BUILDS the selection the next
    // command will act on, which is what noun-verb means. Its own branch, before both.
    // ⚠ `SETVAR` asks for a NAME; `LTSCALE` typed on its own asks for that variable's value
    // directly, which is what AutoCAD does and why both routes land here.
    // ⚠⚠ **GROUP AND UNGROUP ACT ON THE SELECTION, LIKE EVERY OTHER MODIFY COMMAND.** They are
    // in `MODE_OF` because the ribbon puts them there, not because they are drawing tools.
    if (mode === "group" || mode === "ungroup" || mode === "groupedit"
        || mode === "pickstyle" || mode === "attdef" || mode === "attsync") {
      const name = mode;
      setMode("select");
      void runGroupish(name);
      return;
    }
    if (mode === "setvar" || mode.startsWith("var:")) {
      const want = mode.startsWith("var:") ? mode.slice(4) : "";
      setMode("select");
      void openVar(want);
      return;
    }
    // ⚠⚠ ELEV — AutoCAD's own pair, asked in AutoCAD's own order. It is not a dialog and it is
    // not two commands: `$ELEVATION` is the Z every new object is drawn at and `$THICKNESS` is
    // how far it extrudes, and a drafter sets them together or not at all.
    if (mode === "elev") {
      setMode("select");
      void openVar("ELEVATION", "THICKNESS");
      return;
    }
    if (mode === "selectobjects") {
      setMode("select");
      setSelWant({ mode: null, pts: [] });
      setTyped("");
      sayRef.current?.(SELECT_PROMPT);
      return;
    }
    if (!defOf(mode, live.current.dimKind, givenFor(mode, live.current)) && COMMANDS[mode]) {
      const name = mode;
      setMode("select");
      runCmdRef.current?.(name);
      return;
    }
    // ⚠ PASTE is a MODE that waits for a POINT, like the image placer — not a draw tool, so it
    // has no `DrawDef` and would otherwise fall into the "not built yet" branch below.
    if (mode === "paste") {
      setDraw(null);
      sayRef.current?.(live.current.clip
        ? "Specify insertion point:" : "  The clipboard is empty.");
      if (!live.current.clip) setMode("select");
      return;
    }
    // ⚠⚠ **THREE LIFTED MODES THAT NAMED TOOLS THIS APP DOES NOT HAVE.** `MODE_OF` sends
    // AREA and DIST to canvas modes "area" and "dist", and `PICK_OF` sends BREAK to "break" —
    // all three from the design app, none of them a `DrawDef` here, so all three fell through
    // to "X is not built yet" about commands whose server half was built. Routed like
    // `furniture` and `bucket` above, which are the same story.
    if (mode === "area" || mode === "dist" || mode === "break"
        || mode === "boundary" || mode === "gradient" || mode === "panpts"
        || mode === "centermark" || mode === "centerline") {
      const which = mode;
      setMode("select");
      if (which === "panpts") A.panPoints?.();
      else if (which === "area") A.measureArea?.();
      else if (which === "dist") A.measureDist?.();
      else if (which === "break") A.breakObject?.(false);
      else if (which === "boundary") A.traceBoundary?.();
      else if (which === "centermark") A.centreMark?.();
      else if (which === "centerline") A.centreLine?.();
      else A.fillGradient?.();
      return;
    }
    // ⚠⚠⚠ **PAN — THE `BREAKLINE` BUG A THIRD TIME, ON THE COMMAND A DRAFTER TYPES MOST.**
    // `MODE_OF["view.pan"] = "pan"` armed this mode and its ribbon note said *"drag to pan — the
    // middle mouse button pans inside any command"*, and then nothing answered to `"pan"`, so
    // `defOf` fell through and the app printed **"PAN is not built yet"**. The alias `P` is
    // bound to it, which is muscle memory for every drafter alive. The 2026-09-04 survey scored
    // it one of only two commands in the whole ribbon that are genuinely not built.
    // ⚠ It STAYS ARMED. Every other entry in this function is one-shot; AutoCAD's PAN holds the
    // hand tool until Esc or Enter, and a pan that cancelled itself after one drag would be a
    // worse answer than the error message it replaces.
    if (mode === "pan") {
      setDraw(null);
      sayRef.current?.("Press Esc or Enter to exit, or right-click to display shortcut menu.");
      return;
    }
    // ⚠⚠ **ZOOM WINDOW — THE `PAN`/`BREAKLINE` SHAPE A FOURTH TIME.** `runZoom` armed `zoomwin`
    // and nothing answered to it, so `defOf` fell through and the app said "ZOOMWIN is not built
    // yet" — while the ribbon button lit and the command line printed a prompt. It takes two
    // corners and draws nothing, so it has no `DrawDef`, exactly like `axes` below.
    // ⚠ It STAYS ARMED: `onPick` keys on this mode for the first corner, and resetting to
    // `select` here is what made the whole feature unreachable. `runZoom` has already spoken the
    // prompt, so this branch says nothing — a second prompt would print twice.
    if (mode === "zoomwin") {
      setDraw(null);
      return;
    }
    // ⚠ UCS takes two points and draws nothing, so it has no `DrawDef` — same shape as PASTE.
    if (mode === "axes") {
      setDraw(null);
      setUcsWant({ origin: null });
      setMode("select");
      sayRef.current?.(PROMPTS.ucs);
      return;
    }
    const def = defOf(mode, live.current.dimKind, givenFor(mode, live.current));
    if (!def) {
      setDraw(null);
      if (mode !== "select") {
        sayRef.current?.(`  ${mode.toUpperCase()} is not built yet`);
        setMode("select");
      }
      return;
    }
    // ⚠⚠ **A RADIUS DIMENSION MEASURES SOMETHING, SO IT CANNOT CLEAR THE SELECTION.** Every
    // other draw tool does — AutoCAD's do too — and applying that rule uniformly meant the
    // three radial dimensions threw away the arc they were about to measure between the button
    // press and the first click. The picked object IS the first prompt for these.
    let ref: string | undefined;
    if (def.ref) {
      const hit = live.current.sel.find((p) => def.ref!.includes(p.kind));
      if (!hit) {
        sayRef.current?.(`  Select ${def.ref.length > 1 ? "an arc or a circle"
                                                        : "an arc"} first.`);
        setMode("select");
        return;
      }
      ref = hit.handle;
    }
    // ⚠ The RECTANG options come back with the tool — see `RECT_OPT`. Every other tool gets an
    // empty `opt`, so nothing else can inherit a rectangle's fillet.
    setDraw({ mode, pts: [], ref, ...(mode === "rect" ? { opt: rectOpt.current } : {}) });
    setObjCmd(null);
    // ⚠⚠ **REMEMBERED BEFORE IT IS CLEARED, AND THAT IS WHAT MAKES THE NOUN-VERB OPTIONS WORK
    // FROM THE COMMAND LINE.** AutoCAD clears the selection when a draw tool arms, and so does
    // this. But three of CIRCLE's options address objects that were picked FIRST — `Ttr`, `Ttt`
    // and `turn Arc into circle` — and typing `CIRCLE ⏎ A ⏎` armed the tool, wiped the arc from
    // the selection, and then told the drafter to select an arc they had just selected. From the
    // RIBBON the same three worked, because a button does not arm the tool first.
    // ⚠ Cleared on Escape and on a new pick, so it can never be a STALE selection acted on by a
    // command that arrived minutes later.
    armSel.current = live.current.sel;
    if (!def.ref) setSel([]);         // ⚠ AutoCAD clears the selection when a draw tool arms
    sayRef.current?.(def.prompts[0]);
  }, [mode]);

  // ⚠ The chrome's layer commands write through `designLayersApi` directly and then call
  // `ctx.reload()`; that is `refreshLayers` here, not a whole re-open — see `refresh`.
  

  /** ⚠⚠ **A LAYER TOGGLE IS LOCAL AND MUST BE.** It changes what is DRAWN, not the document —
   *  and the display list is what the canvas draws, so it is the right place to change. Sending
   *  it to `/api/layers/` would make turning a layer off a round trip AND would write a view
   *  preference into the client's file. AutoCAD's on/off is per-session too. */
  const patchLayer = useCallback((name: string, patch: Partial<DisplayLayer & LayerRow>) => {
    // ⚠⚠ **LOCAL FIRST, THEN THE SERVER.** A layer toggle has to be instant — it is how a
    // drafter reads a busy drawing, flicking layers on and off while looking — and a round trip
    // per click makes that unusable. So the picture changes now and the document catches up.
    setDl((d) => (d ? { ...d, layers: d.layers.map(
      (l) => (l.name === name ? { ...l, ...patch } : l)) } : d));
    setLayerRows((rows) => rows.map((r) => (r.name === name ? { ...r, ...patch } : r)));
    const row = live.current.rows.find((r) => r.name === name);
    if (!row) return;
    // ⚠ `lw` is the display list's name for it and `lineweight` is the document's. Translated
    // here rather than making the canvas speak the API's field names.
    const { lw, rgb, ...rest } = patch as any;
    const body: any = { ...rest };
    if (lw !== undefined) body.lineweight = lw;
    if (rgb !== undefined) body.true_colour = rgb;
    patchLayerRow(row.id, body).catch((e) => {
      // ⚠⚠ **A FAILED WRITE MUST NOT LEAVE THE SCREEN LOOKING RIGHT.** The optimistic update
      // above has already changed the picture; if the server refused, the drawing on screen is
      // now a claim about the document that is false. Say so and put it back.
      sayRef.current?.(`  layer ${name} NOT saved — ${e}`);
      void refreshLayers();
    });
  }, []);

  /** Start a TRIM / EXTEND / FILLET / CHAMFER. ⚠ It clears the selection, because from here on
   *  a click on the drawing hands the object to the COMMAND rather than picking it. */
  const armPick = useCallback((name: string) => {
    const def = PICKS[name];
    setSel([]);
    setCmd(null);
    setDraw(null);
    setObjCmd({ name, got: [], at: [] });
    if (def.scalar) {
      setTyped(String(def.scalar.def));
      sayRef.current?.(def.scalar.prompt.replace("{d}", def.scalar.def.toFixed(4)));
    } else {
      sayRef.current?.(def.prompts[0]);
    }
  }, []);

  /** ⚠ A ref, because `A` is memoised ABOVE the chrome that provides `say` — the same
   *  temporal-dead-zone shape `shell.tsx` solves with `chromeRef`. Reading `chrome` from inside
   *  the memo would be a use-before-declaration crash on the first render. */
  const sayRef = useRef<((s: string) => void) | undefined>(undefined);
  const fireRef = useRef<((id: string) => void) | undefined>(undefined);
  /** The selection as it stood when a draw tool armed — see the note where it is set. */
  const armSel = useRef<Pick[]>([]);
  const givenRef = useRef<((g: string) => void) | undefined>(undefined);
  /** ⚠ The chrome's Plot dialog, through the same ref trick as `sayRef` — `ctx` is built above
   *  the chrome that owns it. */
  const plotRef = useRef<((w: "drawing" | "set") => void) | undefined>(undefined);

  /** The ONE viewport that is selected, or null. ⚠⚠ **VPLAYER AND VPLOCK HAVE NO MEANING
   *  WITHOUT IT** — "freeze this layer in a viewport" needs to know which window, and with two
   *  selected there is no answer. AutoCAD greys its own VP columns for exactly the same reason.
   *  ⚠ Declared HERE, above the `live` ref, because that ref carries it — the first version put
   *  it beside the other callbacks and TypeScript caught the use-before-declaration. */
  const pickedViewport = useMemo(() => {
    const vps = (dl?.sheets ?? []).flatMap((sh) => sh.viewports);
    const hit = sel.filter((p) => vps.some((v) => v.h === p.handle));
    return hit.length === 1 ? vps.find((v) => v.h === hit[0].handle) ?? null : null;
  }, [dl, sel]);

  /** ⚠⚠ **SELECT A WINDOW AND YOU ARE IN ITS COORDINATE SYSTEM — AutoCAD's UCSVP.**
   *
   *  `do_vpucs` writes a viewport's own UCS into the file and `display._sheets` reads it back,
   *  so the value survives a reload for everyone. This is the half a drafter actually feels:
   *  the coordinate readout, the crosshair, ortho and every typed relative coordinate follow
   *  the window that is picked, and go back to the drawing's UCS when it is not.
   *
   *  ⚠ A viewport UCS does NOT rotate the geometry — that is `view_twist_angle`, which
   *  `_compose` already applies. Confusing the two would turn every window with a UCS on it,
   *  which is a picture nobody asked for.
   *  ⚠ `pushUcs` is deliberately NOT used: this is a restore, not a new origin, and pushing it
   *  would fill the 16-deep UCS Previous stack with entries the drafter never set.
   */
  useEffect(() => {
    const u = pickedViewport?.ucs;
    if (!u) return;
    const [x, y, dx, dy] = u;
    if (![x, y, dx, dy].every(Number.isFinite)) return;
    const next = { x, y, rot: Math.atan2(dy, dx) };
    const now = live.current.ucs;
    if (Math.abs(now.x - next.x) < 1e-9 && Math.abs(now.y - next.y) < 1e-9
        && Math.abs(now.rot - next.rot) < 1e-12) return;
    setUcs(next);
    sayRef.current?.(`  UCS is this window's own — origin ${x.toFixed(1)}, ${y.toFixed(1)}`
                     + (Math.abs(next.rot) > 1e-12
                        ? ` at ${(next.rot * 180 / Math.PI).toFixed(1)}°` : ""));
  }, [pickedViewport]);

  /** ⚠⚠ **THE LIVE STATE, IN A REF, SO `A` NEVER REBUILDS.** `shell.tsx` measured it: with the
   *  canvas state in the handler memo's dependency list, all 450 handlers were rebuilt and every
   *  ribbon button reconciled — 3.48 ms of main-thread script — for a mouse that had moved four
   *  pixels. Putting `sel`, `id` and `cmd` straight into `A`'s deps would bring that back for
   *  every click. The actions read the ref instead, which is always current. */
  const live = useRef<{ sel: Pick[]; id: number | null; cmd: typeof cmd; dl: DisplayList | null;
                       rows: LayerRow[]; typed: string; cursor: Pt | null;
                       draw: typeof draw; drawLayer: string;
                       grips: Grip[] | null; hotGrip: number | null; dimKind: string;
                       arcGiven: string; circGiven: string; ellGiven: string;
                       tanWant: { given: "ttr" | "ttt" | "cont"; objs: TanObj[];
                                  r: number } | null;
                       cont: Cont | null;
                       imgWant: { file: File; pts: Pt[]; kind?: "image" | "pdf";
                                  page?: number; pages?: number } | null;
                       clip: { handles: string[]; cut: boolean; base: Pt; from: number | null;
                               rows?: unknown[]; blocks?: unknown[] } | null;
                       clipBase: boolean; matching: boolean; mode: string;
                       pathWant: { items: Pick[] } | null; pasteBlock: boolean;
                       ucs: Ucs; ucsWant: { origin: Pt | null } | null; zoomWant: Pt | null;
                       clipWant: { pts: Pt[] } | null;
                       breakWant: { want: 1 | 2; pts: Pt[] } | null;
                       measWant: { kind: "area" | "distance" | "angle" | "panpts";
                                   want: number; pts: Pt[] } | null;
                       pickedVp: { h: string; lock?: boolean } | null;
                       textEdit: { spec: TextSpec; box: TextBox } | null;
                       selWant: { mode: SelectMode | null; pts: Pt[];
                                  then?: [string, Record<string, unknown> | undefined] } | null;
                       varWant: { name: string; now: unknown; then?: string } | null;
                       sysvars: SysVar[] | null;
                       hatchWant: HatchSpec | null;
                       attWant: { handle: string; step: "tag" | "prompt" | "def"; tag: string;
                                  prompt: string; def: string } | null;
                       attPlace: { handle: string; tag: string; prompt: string;
                                   def: string } | null;
                       groupPick: boolean;
                       groups: GroupRow[];
                       selKinds: { kinds: string[]; hasBlocks: boolean };
                       extent: number; objCmd: typeof objCmd; sideWant: typeof sideWant;
                       basePick: boolean;
                       blockWant: typeof blockWant;
                       space: string | null }>(
    { sel: [], id: null, cmd: null, dl: null, rows: [], typed: "", cursor: null,
      draw: null, drawLayer: "0", grips: null, hotGrip: null, dimKind: "ortholinear",
      arcGiven: "3p", circGiven: "3p", ellGiven: "axis",
      tanWant: null, cont: null, pickedVp: null,
      imgWant: null, clip: null, clipBase: false, matching: false, mode: "select",
      pathWant: null, pasteBlock: false, ucs: WORLD_UCS, ucsWant: null, zoomWant: null,
      clipWant: null,
      breakWant: null, measWant: null,
      textEdit: null, selWant: null,
      varWant: null, sysvars: null, hatchWant: null, attWant: null, attPlace: null,
      groupPick: true, groups: [],
      selKinds: { kinds: [], hasBlocks: false },
      extent: 0, objCmd: null, sideWant: null, basePick: false, blockWant: null, space: null });
  // ⚠ `shownDl`, not `dl` — see its own note. Everything that asks "what is in this drawing"
  // has to get the same answer as the canvas, or a hidden object is still selectable.
  /** The display list with the isolated-away objects removed.
   *
   *  ⚠⚠ **FILTERED ONCE AND USED EVERYWHERE, OR A HIDDEN OBJECT IS STILL SELECTABLE.** The
   *  canvas draws `dl`, the snaps read `dl`, `everything()` reads `dl` and TRIM's edge filter
   *  reads `dl`. Filtering only the copy the canvas draws would leave a window selection picking
   *  up objects nobody can see — which is the same failure as a layer that is off still being
   *  selectable, and AutoCAD does not have it.
   *  ⚠ `layers` is NOT filtered: the indices in every other channel point into it. */
  //: The kind counts the memo below carries forward, with the list and layer table they were
  //: taken from. ⚠ A REF, because it is a cache of a derived fact and must not itself cause a
  //: render; the memo owns it and nothing else reads it.
  const kindCounts = useRef<{ dl: DisplayList; layers: DisplayList["layers"];
                              counts: Map<string, number> } | null>(null);

  const shownDl = useMemo(() => {
    if (!dl || !hidden.size) return dl;
    const keep = (h: string) => !hidden.has(h);
    return {
      ...dl,
      polys: dl.polys.filter((r) => keep(r[3])),
      texts: dl.texts.filter((r) => keep(r[7])),
      ...(dl.fills ? { fills: dl.fills.filter((r) => keep(r[3])) } : {}),
      ...(dl.marks ? { marks: dl.marks.filter((r) => keep(r[4])) } : {}),
      ...(dl.rays ? { rays: dl.rays.filter((r) => keep(r[7])) } : {}),
    };
  }, [dl, hidden]);

  /** The distinct entity kinds on screen, and whether any of them is a block reference.
   *
   * ⚠⚠⚠ **THE RIBBON ASKS FOR THIS ON EVERY RENDER, AND IT USED TO WALK THE WHOLE DRAWING TO
   * ANSWER.** `selectableKinds` called `everything()` — which visits all four display channels
   * and allocates a `Pick` object per owner, 336,000 rows and 19,763 objects on drawing 1158 —
   * and then threw every one of those objects away to keep a handful of STRINGS. Profiled inside
   * the post-edit commit with the sourcemap resolved, `everything()` was **~35 ms of every
   * edit**, spent while the drafter is still looking at the old picture. It is why the scene
   * build appeared to start 44 ms late: that gap is the rest of React's commit, and this was
   * most of it.
   * ⇒ Memoised on the display list, so it runs once per EDIT instead of once per RENDER, and the
   * scan collects kinds into a `Set` without allocating anything per row.
   * ⚠ `selectBy` and `selectSimilar` still call `everything()`, and should: they need the
   * objects, and they run when a drafter asks for them, not while the ribbon draws.
   */
  const selKinds = useMemo(() => {
    const _k0 = performance.now();
    const d = shownDl;
    //: ⚠ The four channels that carry a kind, as `[channel, layerIndex, kindIndex]`.
    const CH = [["polys", 0, 4], ["texts", 0, 8], ["marks", 0, 5], ["rays", 0, 8]] as const;
    const prev = kindCounts.current;
    let counts: Map<string, number> | null = null;
    let how = "full";
    // ⚠⚠⚠ **A COUNT PER KIND, UPDATED FROM THE DELTA — NOT A SET REBUILT FROM THE DRAWING.**
    // The full pass below is 16.7 ms of the 52.8 ms render an edit costs on
    // `ysl-hn-center-2025`, and it spends all of it learning that a drawing which had LINEs
    // before still has LINEs. `applyDelta` records the cuts that made each channel array
    // (`cutsOf`), so the rows that LEFT can be subtracted and the rows that ARRIVED added, which
    // is exact and costs only what changed. A count rather than a set because a kind leaves the
    // picker exactly when its last row does, and a set cannot tell.
    // ⚠ The visibility rule below reads `d.layers`, so an inheriting pass needs the SAME layer
    // table — a layer turned off changes which kinds are selectable without touching a row. And
    // `shownDl` builds fresh arrays when anything is hidden, which carry no cuts, so that case
    // falls back here on its own.
    if (d && prev && prev.layers === d.layers) {
      const next = new Map(prev.counts);
      let ok = true;
      for (const [ch, li, ki] of CH) {
        const now = (d as unknown as Record<string, unknown[][]>)[ch] ?? [];
        const was = (prev.dl as unknown as Record<string, unknown[][]>)[ch] ?? [];
        if (now === was) continue;
        const cuts = cutsOf(now);
        if (!cuts) { ok = false; break; }
        const bump = (rows: unknown[][], from: number, to: number, by: number) => {
          for (let i = from; i < to; i++) {
            const r = rows[i] as unknown[];
            if (!r) continue;
            const ly = d.layers[r[li] as number];
            if (!ly || !ly.on || ly.frozen) continue;
            const k = r[ki] as string;
            const v = (next.get(k) ?? 0) + by;
            if (v > 0) next.set(k, v); else next.delete(k);
          }
        };
        for (const [at, remove, rows] of cuts) {
          bump(was, at, at + remove, -1);
          bump(rows as unknown[][], 0, (rows as unknown[][]).length, +1);
        }
      }
      if (ok) { counts = next; how = "delta"; }
    }
    if (!counts) {
      counts = new Map<string, number>();
      if (d) {
        // ⚠ the same visibility rule `everything()` applies — a kind only on a frozen layer is
        // not selectable, and offering it in the picker would answer "0 found" every time.
        const on = (li: number) => { const ly = d.layers[li]; return !!ly && ly.on && !ly.frozen; };
        const add = (k: string) => counts!.set(k, (counts!.get(k) ?? 0) + 1);
        for (const r of d.polys) if (on(r[0])) add(r[4]);
        for (const t of d.texts) if (on(t[0])) add(t[8]);
        for (const m of d.marks ?? []) if (on(m[0])) add(m[5]);
        for (const r of d.rays ?? []) if (on(r[0])) add(r[8]);
      }
    }
    kindCounts.current = d ? { dl: d, layers: d.layers, counts } : null;
    // ⚠ Published because the full pass is a WHOLE PASS over the drawing and it runs inside the
    // same ~150 ms post-edit task as the splice and the band fold. `how` says which path ran —
    // a 0.1 ms number with no way to tell an inheriting pass from an empty drawing is not a
    // finding. Nothing reads these but a harness.
    const w = window as unknown as Record<string, unknown>;
    w.__pdcadKindsMs = Math.round((performance.now() - _k0) * 10) / 10;
    w.__pdcadKindsHow = how;
    const out = [...counts.keys()].sort();
    // ⚠⚠⚠ **THE INHERITING PATH IS CHECKED AGAINST THE FULL PASS, ON DEMAND.** A kind list that
    // is wrong does not fail — it offers QSELECT a type the drawing no longer has, which answers
    // "0 found", or hides one it does have, which is worse and silent. So `qa/kinds_check.py`
    // sets `__pdcadKindsVerify` and every memo from then on recomputes from scratch and records
    // any disagreement. ⚠ Off in a drafter's browser: it is the very pass being avoided.
    if (w.__pdcadKindsVerify && d) {
      const chk = new Set<string>();
      const on = (li: number) => { const ly = d.layers[li]; return !!ly && ly.on && !ly.frozen; };
      for (const r of d.polys) if (on(r[0])) chk.add(r[4]);
      for (const t of d.texts) if (on(t[0])) chk.add(t[8]);
      for (const m of d.marks ?? []) if (on(m[0])) chk.add(m[5]);
      for (const r of d.rays ?? []) if (on(r[0])) chk.add(r[8]);
      const want = [...chk].sort();
      const log = (w.__pdcadKindsBad ||= []) as unknown[];
      if (want.join(",") !== out.join(",")) {
        log.push({ how, got: out, want, missing: want.filter((k) => !out.includes(k)),
                   extra: out.filter((k) => !want.includes(k)) });
      }
      w.__pdcadKindsChecked = ((w.__pdcadKindsChecked as number) ?? 0) + 1;
    }
    return { kinds: out, hasBlocks: counts.has("INSERT") };
  }, [shownDl]);

  // ⚠⚠ **THE SELECTION, FOR A HARNESS — and it exists because a benchmark lied without it.**
  // `qa/bench_session.py`'s "ERASE all + UNDO" reported a 47 ms median while committing 0 edits
  // in four reps of five: the step was measuring a SELECT that found nothing and an ERASE that
  // therefore did nothing, and a fast number for work not done is the worst kind. There is no
  // other way in from outside — the command line has no testid and `sel` is React state.
  (window as unknown as Record<string, unknown>).__pdcadSel = sel.length;
  live.current = { sel, id, cmd, dl: shownDl, rows: layerRows,
                   // ⚠ An ACCESSOR, so every existing read and write of `live.current.typed`
                   // goes to the store — including the input queue's, which needs the value in
                   // the tick it was written and cannot wait for a render.
                   get typed() { return typedStore.v; },
                   set typed(v: string) { setTypedValue(v); },
                   cursor, draw, drawLayer,
                   grips, hotGrip, dimKind, arcGiven, circGiven, ellGiven, tanWant, cont,
                   pickedVp: pickedViewport, imgWant, clip, clipBase, matching, mode,
                   pathWant, pasteBlock, ucs, ucsWant, zoomWant, clipWant, breakWant, measWant,
                   textEdit, selWant,
                   varWant, sysvars, hatchWant, attWant, attPlace, groupPick, groups,
                   objCmd, sideWant, basePick, blockWant, space, selKinds,
                   // ⚠ The bigger side of the drawing, BOUNDED — see `sanityExtent`, which
                   // carries the 2e+210 that poisoned every height default on drawing 10448.
                   extent: sanityExtent(dl?.extents) };

  /** ⚠⚠⚠ **THE COMMAND STATE IS MIRRORED INTO `live` THE MOMENT IT IS SET, NOT A RENDER LATER.**
   *
   *  Every handler in this file reads `live.current`, which is assigned during RENDER — so after
   *  `onDrawPoint` takes a point, nothing can act on the new point count until React has
   *  committed. The input queue therefore had to WAIT after every Enter, and a typed command is
   *  seven of them: measured on drawing 46 in Chrome, a `LINE` costs 120 ms of which the edit is
   *  15.6, the renders 20, the paints 25 — and the rest is seven sequential round trips through
   *  the scheduler, each one ~10 ms, for a command BricsCAD answers in 35.
   *
   *  ⇒ The setters write the ref as well as the state. The ref is what the command engine reads;
   *  the state is what the screen shows, and it lands when React is ready. `typed` has been
   *  mirrored this way since the queue landed and it is the same reasoning.
   *  ⚠ An UPDATER form is resolved against the REF, not against React's pending state — the ref
   *  is by definition the more recent of the two, and mixing them would apply an update to a
   *  value already superseded. */
  const setDraw: typeof setDrawRaw = (v) => {
    const next = typeof v === "function"
      ? (v as (p: typeof draw) => typeof draw)(live.current.draw) : v;
    live.current.draw = next;
    setDrawRaw(next);
  };
  const setCmd: typeof setCmdRaw = (v) => {
    const next = typeof v === "function"
      ? (v as (p: typeof cmd) => typeof cmd)(live.current.cmd) : v;
    live.current.cmd = next;
    setCmdRaw(next);
  };

  /** ⚠ Read inside canvas callbacks, which close over the FIRST render's values. `live` exists
   *  for exactly this and these three did not fit its shape — `tog` is the toggle bag the
   *  status bar owns, `snapRef` is the live object-snap hit, and `polarAngRef` the increment
   *  its drop-up sets. */
  const togRef = useRef(tog);
  togRef.current = tog;
  const snapRef = useRef<SnapHit | null>(null);
  const polarAngRef = useRef(polarAng);
  polarAngRef.current = polarAng;

  /** Where a click or the coordinate readout should really land.
   *
   * ⚠⚠ **AN OBJECT SNAP BEATS ORTHO, AND GETTING THAT ROUND THE WRONG WAY IS WORSE THAN NO
   * ORTHO AT ALL.** AutoCAD lets a running snap override the constraint: you turn ORTHO on to
   * keep a wall horizontal and still snap its end to the corner of something that is not. If
   * ortho ran afterwards it would drag that endpoint off the corner it had just found — the
   * snap would light up on the right point and place the line somewhere else.
   */
  /** Every selectable object in the drawing, as picks. ⚠⚠ **ONE WALK, FOUR CALLERS.** SELECT
   *  ALL, QSELECT, SELECTSIMILAR and FIND all have to agree about what is in this drawing, and
   *  four hand-written loops over the same four channels is how three of them come to miss a
   *  channel the fourth remembers.
   *  ⚠ Points and construction lines are their OWN channels — `marks` and `rays` in `Canvas2D`,
   *  not `polys`. "All" meaning "everything except the three kinds that do not live in `polys`"
   *  is the kind of quiet wrong answer a drafter only finds after an ERASE ALL.
   *  ⚠ A layer that is off or frozen is not selectable, which is AutoCAD's rule and the reason
   *  turning a layer off is a safe way to work rather than a display trick. */
  /** A display list with its layer table's on/frozen taken from the DOCUMENT's layer rows.
   *  ⚠ See the note in `open()`. Returns the list unchanged when there is nothing to reconcile,
   *  so an identity comparison upstream still says "same list". */
  const withLiveLayers = (d: DisplayList, rows: LayerRow[]): DisplayList => {
    if (!d?.layers?.length || !rows?.length) return d;
    const by = new Map(rows.map((r) => [r.name, r]));
    let moved = false;
    const layers = d.layers.map((l) => {
      const q = by.get(l.name);
      if (!q || (q.on === l.on && q.frozen === l.frozen)) return l;
      moved = true;
      return { ...l, on: q.on, frozen: q.frozen };
    });
    return moved ? { ...d, layers } : d;
  };

  const everything = useCallback((): Pick[] => {
    const d = live.current.dl;
    if (!d) return [];
    const by = new Map<string, Pick>();
    const add = (li: number, aci: number, owner: string, kind: string) => {
      const ly = d.layers[li];
      if (!ly || !ly.on || ly.frozen || by.has(owner)) return;
      by.set(owner, { handle: owner, kind, layer: ly.name, aci });
    };
    for (const [li, aci, , owner, kind] of d.polys) add(li, aci, owner, kind);
    for (const t of d.texts) add(t[0], t[1], t[7], t[8]);
    for (const m of d.marks ?? []) add(m[0], m[1], m[4], m[5]);
    for (const r of d.rays ?? []) add(r[0], r[1], r[7], r[8]);
    return [...by.values()];
  }, []);

  /** The bottom-left corner of what these handles occupy, from the display list.
   *  ⚠ Every channel, for the same reason `everything()` walks four: a selection of POINTs would
   *  otherwise have no extent at all and paste at the origin. */
  const cornerOf = useCallback((handles: string[]): Pt => {
    const d = live.current.dl;
    const want = new Set(handles);
    let x = Infinity, y = Infinity;
    const eat = (px: number, py: number) => {
      if (px < x) x = px;
      if (py < y) y = py;
    };
    if (d) {
      for (const [, , pts, owner] of d.polys) {
        if (!want.has(owner)) continue;
        for (let i = 0; i + 1 < pts.length; i += 2) eat(pts[i], pts[i + 1]);
      }
      for (const f of d.fills ?? []) {
        if (!want.has(f[3])) continue;
        for (let i = 0; i + 1 < f[2].length; i += 2) eat(f[2][i], f[2][i + 1]);
      }
      for (const t of d.texts) if (want.has(t[7])) eat(t[2], t[3]);
      for (const m of d.marks ?? []) if (want.has(m[4])) eat(m[2], m[3]);
      for (const r of d.rays ?? []) if (want.has(r[7])) eat(r[2], r[3]);
    }
    return Number.isFinite(x) ? { x, y } : { x: 0, y: 0 };
  }, []);

  const applyConstraint = useCallback((p: Pt): Pt => {
    if (snapRef.current) return p;
    const s = live.current;
    const last = s.draw?.pts.length ? s.draw.pts[s.draw.pts.length - 1]
               : s.cmd?.pts.length ? s.cmd.pts[s.cmd.pts.length - 1] : null;
    const t = togRef.current;
    if (!t.ortho && !t.polar && !t.snapmode) return p;
    // ⚠⚠ **ORTHO, POLAR AND SNAP ALL FOLLOW THE UCS, WHICH IS THE WHOLE REASON TO SET ONE.** A
    // drafter puts the origin on a wall running at 37° precisely so that ortho draws ALONG that
    // wall. Constraining in world axes would leave the one command that makes a rotated UCS
    // worth having behaving as if it were not set — and it would look like ortho was broken.
    // ⚠ Constrained in UCS space and mapped back, rather than teaching `constrain` about the
    // UCS: that function is pure arithmetic with its own tests, and it stays that way.
    const u = live.current.ucs;
    const q = constrain(worldToUcs(p, u), last ? worldToUcs(last, u) : null,
                        { ortho: t.ortho, polar: t.polar, polarAng: polarAngRef.current,
                          snap: t.snapmode, snapX: snapUnitRef.current });
    return ucsToWorld(q, u);
  }, []);

  /** UCSFOLLOW — turn the VIEW to match the UCS, if the drawing asks for it.
   *
   *  ⚠⚠ **THE ONE UCS VARIABLE THIS APP CAN HONESTLY HONOUR, AND THE ONE THAT MATTERS IN 2D.**
   *  It is the whole reason a drafter sets an origin on a wall running at 37°: to detail that
   *  wall as if it were horizontal. AutoCAD's own `UCSFOLLOW` does exactly this.
   *  ⚠ Read from the DRAWING's header, not from a preference — it is `$UCSFOLLOW`, it arrives
   *  with the client's file and goes back out in it. Default 0, which is AutoCAD's.
   *  ⚠ `$UCSORG`/`$UCSXDIR` are deliberately NOT stored — see `UCS_KEY`. A working origin is
   *  not a property of the building.
   */
  const followUcs = useCallback((u: Ucs) => {
    const on = Number(live.current.sysvars?.find((v) => v.name === "UCSFOLLOW")?.value || 0);
    if (!on) return;
    const api2 = api.current;
    if (!api2) return;
    api2.setView({ ...api2.view, rot: u.rot });
    sayRef.current?.(Math.abs(u.rot) > 1e-12
      ? `  view turned to ${(u.rot * 180 / Math.PI).toFixed(1)}° — UCSFOLLOW is on`
      : "  view back to level — UCSFOLLOW is on");
  }, []);

  /** ⚠ `refresh` is declared BELOW `pushUcs` and `pushUcs` needs it — the same arrangement
   *  `sayRef` already uses for `chrome.say`. A direct call is a use-before-declaration and tsc
   *  says so; hoisting `refresh` instead would move a 40-line callback to satisfy one caller. */
  const refreshRef = useRef<(() => Promise<void>) | undefined>(undefined);

  /** Move the origin, remembering where it was. ⚠ 16 deep, which is AutoCAD's own UCS Previous
   *  depth — deep enough to walk back through a detail, shallow enough not to be a second undo
   *  stack nobody can see the top of. */
  const pushUcs = useCallback((next: Ucs) => {
    const now = live.current.ucs;
    if (Math.abs(now.x - next.x) < 1e-9 && Math.abs(now.y - next.y) < 1e-9
        && Math.abs(now.rot - next.rot) < 1e-12) return;
    ucsStack.current = [...ucsStack.current, now].slice(-16);
    setUcsDepth(ucsStack.current.length);
    setUcs(next);
    // ⚠⚠ **ON A SHEET WITH ONE WINDOW SELECTED, THE UCS BELONGS TO THAT WINDOW.** AutoCAD's
    // UCSVP is per viewport for exactly this reason: two windows onto the same model at
    // different rotations each need their own origin, and one drawing-wide UCS cannot express
    // that. It goes into the FILE (`ucs_per_viewport`, groups 110–112), not into localStorage,
    // so it survives a reload for everyone rather than for this browser.
    const s2 = live.current;
    const vp = s2.space !== "Model" && s2.sel.length === 1
               && s2.sel[0].kind === "VIEWPORT" ? s2.sel[0].handle : null;
    if (vp && s2.id) {
      void (async () => {
        try {
          await editDrawing(s2.id!, "vpucs", [vp],
                            { origin: [next.x, next.y, 0],
                              xaxis: [Math.cos(next.rot), Math.sin(next.rot)] });
          sayRef.current?.("  this window's own UCS — saved on the viewport");
          await refreshRef.current?.();
        } catch (e) { fail(e); }
      })();
    } else if (s2.id != null) {
      // ⚠ One edit, not three SETVARs: a drafter who typed UCS once expects ONE Ctrl+Z to put
      // the origin back. `do_ucs` writes the three header variables together.
      void editDrawing(s2.id, "ucs", [],
                       { origin: [next.x, next.y, 0], rot: next.rot }).catch(fail);
    }
    followUcs(next);
    // ⚠ `refresh` is reached through a ref because it is declared BELOW this — see
    // `refreshRef`. `fail` is declared above and is an ordinary dependency.
  }, [fail]);

  /** The layout tabs the chrome draws, in tab order and WITHOUT model space — the chrome always
   *  draws a Model tab of its own. ⚠ Memoised: `ShellContext.layouts` is a `useEffect`
   *  dependency in `shell.tsx`, and a fresh array every render is a fresh identity every render,
   *  which would reset the current tab back to Model on every keystroke. */
  const layoutTabs = useMemo(
    () => layoutRows.filter((l) => !l.model).map((l, i) => ({ id: i, name: l.name })),
    [layoutRows]);

  /** Re-read the layer table from the server. ⚠ Used after a write FAILED — see `patchLayer`. */
  const refreshLayers = useCallback(async () => {
    const s = live.current;
    if (!s.id) return;
    const [rows, d] = await Promise.all([loadLayers(s.id),
                                         loadDisplay(s.id, s.space ?? "Model")]);
    setLayerRows(rows);
    setDl(d);
  }, []);

  /** The snaps that are actually RUNNING. ⚠ Filtered by what `snap.ts` implements: F3 off gives
   *  an empty set, and a ticked-but-unbuilt snap must not reach the canvas and silently do
   *  nothing there. Memoised because the canvas repaints when its identity changes. */
  /** What a ZOOM EXTENTS is owed to. ⚠ It was the drawing ID alone, so switching from Model to
   *  an A1 sheet kept the model-space view — an A1 at a plan's zoom is a blank screen, and a
   *  blank screen after clicking a tab reads as a layout that failed to load. The SPACE belongs
   *  in the key for exactly the same reason the drawing does. */
  const fitKey = useMemo(() => `${id ?? ""}/${space ?? ""}`, [id, space]);

  const running = useMemo(
    // ⚠⚠ **AN OVERRIDE BEATS F3.** AutoCAD honours a typed `MID` with OSNAP switched off — the
    // drafter asked for that one point by name, which is a stronger statement than the toggle.
    () => {
      const base = snapOnce !== null
        ? new Set(snapOnce === "none" ? [] : [snapOnce])
        : new Set(tog.osnap ? snaps.filter((s) => IMPLEMENTED.has(s)) : []);
      // ⚠ See `pending`. Endpoint, midpoint and the rest come out of `polys`, which the local
      // transform DID move, so they stay live; these two do not and must not.
      if (pending) { base.delete("centre"); base.delete("insertion"); }
      return base;
    },
    [tog.osnap, snaps, snapOnce, pending]);

  /** ⚠⚠ **WHICH ROUTE PUT THIS LIST ON SCREEN — `delta`, `optimistic` or `refetch`.** The GPU
   *  scene caches a fill's triangles on its point array's IDENTITY, so a REFETCH throws the whole
   *  cache away: measured on drawing 1158, that is **253 ms of re-triangulation** against 12 ms
   *  for a spliced list. Two builds in six were paying it and nothing said which two. Counted
   *  here rather than guessed at from the outside — `qa/edit_time.py` reads it. */
  const dlWay = useRef<Record<string, number>>({});
  const noteDl = useCallback((how: string) => {
    dlWay.current[how] = (dlWay.current[how] ?? 0) + 1;
    (window as unknown as Record<string, unknown>).__pdcadDl = { ...dlWay.current };
  }, []);

  /** Bring the picture up to date after the server changed the drawing.
   *  ⚠ NOT `open()`: that resets the view to extents, and a MOVE that zooms the drawing out
   *  from under you loses the place you were working.
   *
   *  ⚠⚠⚠ **PASS THE EDIT'S REPORT AND IT SPLICES INSTEAD OF REFETCHING.** Editing drawing 1158
   *  refetched a **46 MB** display list after every MOVE — the server had just finished
   *  computing exactly which rows changed and threw it away. With the report, the same edit
   *  carries an **88.6 KB** delta and this applies it to the list already on screen.
   *  ⚠ Without a report — or when the server declined to produce a delta, which it does
   *  whenever it cannot be certain — this refetches exactly as it always did. That fallback is
   *  the reason the delta path is allowed to be selective. */
  /** The half of `refresh` that needs no `await` — `true` when it has done the whole job.
   *
   * ⚠⚠⚠ **SPLIT OUT BECAUSE `await refresh(rep)` COST A WHOLE EXTRA RENDER OF THE STUDIO.**
   * React batches the state changes made in ONE task; every `await` starts a new one. `author`
   * did `say(...)`, then `await refresh(rep)`, then cleared `pending`/`busy` in its `finally` —
   * three batches for one logical update. Measured on `ysl-hn-center-2025`: **4 Studio renders
   * an edit, 42-55 ms of the ~145 ms the drafter waits after the server answers**, and each one
   * drags a ribbon render with it.
   * ⇒ The delta path touches no promise at all, so `author` can splice it, say its line and
   * clear its spinner in ONE batch. Only a REFETCH — a network call by definition — is awaited.
   */
  const refreshNow = (rep?: unknown): boolean => {
    if (!id) return true;
    if (keptBy(rep)) return true;
    const d = deltaFor(rep, live.current.space ?? "Model");
    // ⚠ Nothing on screen to splice INTO — a delta is a description of a change, not a drawing.
    // Only possible if a report arrives before the first load; the caller refetches.
    if (!d || !live.current.dl) return false;
    const t0 = performance.now();
    noteDl("delta");
    (window as unknown as Record<string, unknown>).__pdcadCommit = { set: t0 };
    setDl((prev) => {
      if (!prev) return prev;
      const s0 = performance.now();
      const next = applyDelta(prev, d);
      const w = window as unknown as Record<string, unknown>;
      (w.__pdcadCommit as Record<string, number>).apply = performance.now() - s0;
      return next;
    });
    const t1 = performance.now();
    requestAnimationFrame(() => requestAnimationFrame(() => {
      (window as unknown as Record<string, unknown>).__pdcadRefresh = {
        request: Math.round((t1 - t0) * 10) / 10,
        splice: Math.round(((window as unknown as Record<string, Record<string, number>>)
                            .__pdcadCommit?.apply ?? 0) * 10) / 10,
        paint: Math.round(performance.now() - t1),
        total: Math.round(performance.now() - t0),
      };
    }));
    return true;
  };

  /** Refetch the whole list. ⚠ The half of `refresh` that IS a network call. */
  const refreshFetch = useCallback(async () => {
    if (!id) return;
    noteDl("refetch:refresh");
    setDl(await loadDisplay(id, live.current.space ?? "Model"));
  }, [id, noteDl]);

  const refresh = useCallback(async (rep?: unknown) => {
    if (!id) return;
    // ⚠ The server kept the picture — a GROUP, a COPYCLIP, a SETVAR of something the display
    // never reads. The list on screen is still exactly right; fetching 46 MB to learn that is
    // the cost this exists to remove. See `keptBy`.
    if (keptBy(rep)) return;
    const d = deltaFor(rep, live.current.space ?? "Model");
    // ⚠⚠⚠ **`had` WAS READ BEFORE REACT HAD RUN THE UPDATER, SO IT WAS ALWAYS FALSE AND THIS
    // SPLICED AND THEN REFETCHED ANYWAY.** `setDl(prev => { had = !!prev; ... })` does not call
    // that function then and there — React calls it during the next render — so the `if (had)
    // return` below ran while `had` was still its initial `false`, every single time. The whole
    // point of the delta path is to avoid a 46 MB refetch on drawing 1158, and it was doing the
    // splice AND the refetch: `qa/edit_time.py` counted `{optimistic: 3, delta: 6,
    // refetch:refresh: 6}` over six edits. The refetch also hands back entirely fresh rows,
    // which throws away `gpu/build.ts`'s fill cache — the 253 ms of re-triangulation this file
    // and `delta.ts` both have long notes about.
    // ⇒ Ask the REF, which is assigned during render and is therefore already true.
    const had = !!live.current.dl;
    if (d) {
      const t0 = performance.now();
      noteDl("delta");
      // ⚠ the instant the state change is REQUESTED — see `Canvas2D`'s note, which uses it to
      // split React's commit out of what was being reported as the dynamic import's cost.
      (window as unknown as Record<string, unknown>).__pdcadCommit = { set: t0 };
      // ⚠⚠⚠ **`splice` BELOW WAS MEASURING THE `setDl` CALL, NOT THE SPLICE, AND REPORTED 0 ms
      // FOR YEARS.** `setDl(fn)` does not run `fn` then and there — React runs it during the
      // next render — which is the very trap the paragraph above records for `had`. So the work
      // is timed INSIDE the updater, where it happens: on `ysl-hn-center-2025` that is a copy
      // and a splice of a 1,511,031-row array, inside the ~150 ms task every edit pays.
      setDl((prev) => {
        if (!prev) return prev;
        const s0 = performance.now();
        const next = applyDelta(prev, d);
        const w = window as unknown as Record<string, unknown>;
        (w.__pdcadCommit as Record<string, number>).apply = performance.now() - s0;
        return next;
      });
      // ⚠⚠ **HOW LONG THE PICTURE TAKES TO CATCH UP WITH THE EDIT, ON THIS MACHINE.** The server
      // reports its own phases; nothing reported the browser's. `splice` is `applyDelta`, and
      // `paint` is everything after it — React, the GPU scene rebuild, the upload — up to the
      // first frame after the new list is on screen. Two rAFs, for the reason `open()` gives.
      // Read by `qa/edit_time.py`.
      const t1 = performance.now();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        (window as unknown as Record<string, unknown>).__pdcadRefresh = {
          // ⚠ `request` is how long `setDl` took to be CALLED — near zero by construction. The
          // splice itself is `__pdcadCommit.apply`, measured inside the updater above.
          request: Math.round((t1 - t0) * 10) / 10,
          splice: Math.round(((window as unknown as Record<string, Record<string, number>>)
                              .__pdcadCommit?.apply ?? 0) * 10) / 10,
          paint: Math.round(performance.now() - t1),
          total: Math.round(performance.now() - t0),
        };
      }));
      // ⚠ Nothing on screen to splice INTO — a delta is a description of a change, not a
      // drawing. Only possible if a report arrives before the first load; refetch and move on.
      if (had) return;
    }
    noteDl("refetch:refresh");
    setDl(await loadDisplay(id, live.current.space ?? "Model"));
  }, [id, noteDl]);

  /** Switch to model space or to a layout — AutoCAD's layout tabs, and PSPACE/MSPACE.
   *
   * ⚠ Selection is DROPPED on the way. A handle picked in model space is not in this space, and
   * carrying it across leaves the Properties palette describing an object that is no longer on
   * screen — with grips drawn at paper coordinates for a wall eighty metres away.
   */
  const goSpace = useCallback(async (name: string | null) => {
    const s = live.current;
    if (!s.id || name === s.space) return;
    setBusy(name === null ? "Model" : `Layout ${name}…`);
    // ⚠⚠⚠ **A TOOL ARMED IN ORDER TO GET HERE MUST SURVIVE THE TRIP.** `handlers.tool()` runs
    // `toPlan()` and then `setMode(...)`, so pressing LINE on a drawing that opens on a LAYOUT
    // says *"Returned to Model space to draw"*, arms the tool — and then this function, which is
    // async, lands and throws it away. The clicks that follow fall through to SELECTION: on
    // drawing 10951 (which opens on a sheet) a two-click line survived 1 attempt in 6, and the
    // transcript read `CIRCLE F7D805 — 1 of 2 here` where it should have read `LINE drawn`.
    // ⚠ Clearing is still right for a drafter who CHOOSES another tab — their armed tool
    // belonged to the space they left — so what is preserved is the MODE, re-armed once the new
    // list is on screen, and only when one is armed at all.
    const armedMode = live.current.mode;
    try {
      // ⚠ The rows already in hand — `goSpace` fetches a picture, not the document.
      const d = withLiveLayers(await loadDisplay(s.id, name ?? "Model"), live.current.rows);
      setSpace(name);
      setSel([]); setGrips([]); setCmd(null); setDraw(null); setObjCmd(null);
      setDl(d);
      // ⚠⚠ **`__pdcadOpen` DESCRIBED THE SPACE THE DRAWING OPENED IN, FOREVER.** It is written
      // once, by `open()`, so after a layout tab — or `__pdcadSpace` — every harness reading it
      // was told the old space and the old counts. `qa/bench_session.py` printed `space 'E1.0'`
      // through four rounds in which the app was fetching `?space=Model`, which is how a
      // benchmark comes to measure one space and name another.
      {
        const w = window as unknown as Record<string, unknown>;
        const o = w.__pdcadOpen as Record<string, unknown> | undefined;
        // ⚠⚠ **AND `extents` WITH THEM, FOR EXACTLY THE SAME REASON.** It was left describing
        // the space the drawing OPENED in, so after a switch every harness sizing geometry from
        // it worked in the other space's units: `qa/bench_actions.py`'s MTEXT read sheet `E1.0`'s
        // title-block extents while the command's default height came from Model's, drew a box
        // three orders of magnitude too narrow, and the app refused it — correctly, and the row
        // vanished from the table. Anything published here has to describe the space it is in.
        if (o) { o.space = name ?? "Model"; o.counts = d.counts; o.extents = d.extents ?? null; }
      }
      // ⚠ Zoomed to the sheet, not left where model space was. Paper is millimetres and a model
      // is metres; keeping the view would open an A1 sheet at 1:200 000 — a blank screen, which
      // is exactly how a layout that failed to load looks.
      requestAnimationFrame(() => api.current?.zoomExtents());
      sayRef.current?.(name === null ? "  model space" : `  layout "${name}"`);
      // ⚠ Bounced through `select`, because the mode EFFECT is what arms a tool and React runs
      // it on a change — setting the same value again is not one.
      // ⚠⚠⚠ **THE TOOL THAT ARMED *DURING* THE TRIP IS THE ONE TO RESTORE, AND `armedMode`
      // NEVER SEES IT.** It is read at entry, before the caller's `setMode` has run, so for a
      // command that changes space IN ORDER to draw it is always `select` — the re-arm below
      // then did nothing while `setDraw(null)` above threw the armed tool away.
      // ⚠⚠ And the half that was worse: `mode` was left at `line` with NO `draw`. ESC resets
      // the mode only while a draw is live (see its handler), so the next `LINE` set the mode
      // to the value React already held, no effect ran, and **the command was dead for the rest
      // of the session**. Measured on drawing 10951, which opens on sheet `E1.0`: the first
      // LINE armed and lost its box 200 ms later, and no LINE after it ever armed again.
      // ⇒ Read the mode as it stands NOW, beside the `draw` being cleared, so the two can never
      // disagree — which is the invariant, not a special case for one command.
      const nowMode = live.current.mode;
      const restore = nowMode !== "select" ? nowMode : armedMode;
      if (restore && restore !== "select") {
        setMode("select");
        setTimeout(() => setMode(restore), 0);
      } else if (nowMode !== "select") {
        setMode("select");
      }
    } catch (e) { fail(e); } finally { setBusy(null); }
  }, [fail]);

  /** ⚠ A REF, because the key handler is bound once and would otherwise close over the first
   *  `vpAsk` forever — the same reason `live` exists. */
  const vpAskRef = useRef<string[] | null>(null);
  vpAskRef.current = vpAsk;
  pedWantRef.current = pedWant;
  zoomAnchorRef.current = zoomAnchor;
  polarWantRef.current = polarWant;
  // ⚠ Read INSIDE `runLayout`, which the dialog calls after `setLayoutDlg(null)` has already
  // been queued — reading the state variable there would race the close.
  const layoutDlgRef = useRef<typeof layoutDlg>(null);
  layoutDlgRef.current = layoutDlg;


  /** Write a property onto the whole selection — the Properties palette's one job.
   *
   * ⚠ Goes through `commit`-shaped plumbing rather than straight to `editDrawing` so it lands on
   * the same undo stack as everything else: a drafter who moves forty walls onto the wrong layer
   * presses Ctrl+Z, and there is no reason for the palette to be the one command that cannot be
   * taken back.
   */
  /** ATTEDIT — write attribute VALUES onto the selected block references, by tag.
   *  ⚠ Its own path, not `setprop`: an ATTRIB is a SUB-ENTITY of the INSERT, so the server op
   *  is different and so is what it reports — `attributes` counts attributes, `changed` counts
   *  blocks, and they are usually different numbers. */
  const setAttr = useCallback(async (values: Record<string, string>) => {
    const s = live.current;
    if (!s.id || !s.sel.length) { sayRef.current?.("  select a block first"); return; }
    try {
      const rep = await editDrawing(s.id, "attedit", s.sel.map((p) => p.handle), { values });
      sayRef.current?.(`  ${rep.attributes ?? 0} attribute(s) on ${rep.changed ?? 0} block(s)`
                       + (rep.without_the_tag ? `, ${rep.without_the_tag} without the tag` : ""));
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  const setProp = useCallback(async (patch: Record<string, unknown>) => {
    const s = live.current;
    if (!s.id || !s.sel.length) { sayRef.current?.("  select something first"); return; }
    try {
      const rep = await editDrawing(s.id, "setprop", s.sel.map((p) => p.handle), patch);
      const what = Object.keys(patch).join(", ");
      sayRef.current?.(`  ${rep.changed ?? 0} object(s) — ${what}`
                       + (rep.refused_count ? `, ${rep.refused_count} refused` : ""));
      noteDl("refetch:setprop");
      const fresh = await loadDisplay(s.id, s.space ?? "Model");
      setDl(fresh);
      // ⚠⚠ **THE SELECTION IS RE-DERIVED, OR THE PALETTE SHOWS WHAT IT JUST CHANGED AWAY FROM.**
      // A `Pick` carries the layer and colour it had when it was picked; after a write those are
      // stale, so the palette reported `*VARIES*` on twelve objects that had all just been moved
      // onto WALL. Re-reading them from the refreshed display list is the only version that
      // cannot drift — an optimistic patch here would be a second opinion about the drawing.
      const by = new Map<string, { layer: string; aci: number; kind: string }>();
      for (const [li, aci, , owner, kind] of fresh.polys) {
        if (!by.has(owner)) by.set(owner, { layer: fresh.layers[li]?.name ?? "0", aci, kind });
      }
      for (const [li, aci, , , , , , owner, kind] of fresh.texts) {
        if (!by.has(owner)) by.set(owner, { layer: fresh.layers[li]?.name ?? "0", aci, kind });
      }
      setSel(s.sel.map((p) => {
        const now = by.get(p.handle);
        return now ? { ...p, layer: now.layer, aci: now.aci } : p;
      }));
    } catch (e) { fail(e); }
  }, [refresh, fail]);


  /** XREF ▸ BIND — pull a referenced drawing's geometry into this one. */
  const bindXref = useCallback(async (block: string, from: number) => {
    const s = live.current;
    if (!s.id) return;
    setBusy(`Binding ${block}…`);
    try {
      const rep = await editDrawing(s.id, "bindxref", [], { block, from });
      sayRef.current?.(`  "${block}" bound from ${rep.from} — ${rep.bound ?? 0} objects`);
      await refresh(rep);
    } catch (e) { fail(e); } finally { setBusy(null); }
  }, [refresh, fail]);

  /** Write a dimension style, and apply it to the selection if there is one.
   *
   * ⚠ Applying to the selection is the half AutoCAD does with a separate "Set current" — but a
   * style edited while forty dimensions are selected is almost always being edited FOR them,
   * and a manager that changes a style and leaves the drawing alone reads as having done
   * nothing. The transcript says which of the two happened.
   */
  const applyDimStyle = useCallback(async (name: string,
                                           vars: Record<string, string | number>,
                                           base?: string) => {
    const s = live.current;
    if (!s.id) return;
    try {
      const rep = await editDrawing(s.id, "dimstyle", [], { name, vars, base });
      sayRef.current?.(`  dimension style "${name}" ${rep.made ? "created" : "updated"}`);
      const dims = s.sel.filter((p) => p.kind.startsWith("DIM"));
      if (dims.length) {
        const r2 = await editDrawing(s.id, "setdimstyle", dims.map((p) => p.handle),
                                     { style: name });
        sayRef.current?.(`  ${r2.changed ?? 0} dimension(s) set to "${name}"`);
        if (r2.note) sayRef.current?.(`  ⚠ ${r2.note}`);
      }
      await refresh();
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** LAYOUT ▸ New / Rename / Delete — the dialog hands back what it collected and the work
   *  happens here, because a layout is a row in the drawing and the dialog is a form. */
  const runLayout = useCallback(async (spec: LayoutSpec) => {
    const s = live.current;
    const which = layoutDlgRef.current;
    if (!s.id || !which) return;
    setLayoutDlg(null);
    try {
      if (which === "new") {
        await editDrawing(s.id, "newlayout", [],
                          { name: spec.name, page: spec.page, landscape: spec.landscape });
      } else if (which === "rename") {
        await editDrawing(s.id, "renamelayout", [], { old: s.space, new: spec.name });
      } else {
        await editDrawing(s.id, "dellayout", [], { name: s.space });
      }
      const los = (await loadLayouts(s.id)).layouts;
      setLayoutRows(los);
      // ⚠ A new sheet is OPENED, a deleted one falls back to Model, a renamed one follows its
      // name. Leaving the drafter on a tab that no longer exists — or making them find the sheet
      // they just created — is the part AutoCAD gets right without being asked.
      if (which === "new") await goSpace(spec.name);
      else if (which === "rename") await goSpace(spec.name);
      else await goSpace(null);
      sayRef.current?.(`  layout ${which === "new" ? "created" :
                                   which === "rename" ? "renamed" : "deleted"}`);
    } catch (e) { fail(e); }
  }, [fail, goSpace]);


  /** VPSCALE — the typed 1:n, applied to every selected window. */
  const acceptVpScale = useCallback(async () => {
    const s = live.current;
    const handles = vpAskRef.current;
    const n = Number(s.typed);
    setVpAsk(null); setTyped("");
    if (!s.id || !handles?.length) return;
    if (!Number.isFinite(n) || n <= 0) { sayRef.current?.("  a scale is 1:n, n > 0"); return; }
    try {
      const rep = await editDrawing(s.id, "vpscale", handles, { scale: n });
      sayRef.current?.(`  ${rep.scaled ?? 0} viewport(s) at 1:${rep.scale}`);
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** Send one command to the server and say what it did.
   *
   *  ⚠⚠ **THE REFUSAL COUNT IS PRINTED, ALWAYS.** `edits.py` will not move an entity it cannot
   *  prove it understands — an OLE frame, a paper-space viewport — and a command that quietly
   *  left eight objects behind is precisely the failure this app exists to not have. AutoCAD
   *  prints its own leftovers the same way.
   */
  /** Put the clipboard down with its base point at `to`.
   *  ⚠ `move` for a cut and `copy` for a copy — the same result AutoCAD reaches by removing the
   *  objects and putting them back, in one op and therefore one undo step. */
  const pasteAt = useCallback(async (to: Pt, ontoDrawLayer = false) => {
    const s2 = live.current;
    const c2 = s2.clip;
    if (!s2.id || !c2 || !c2.handles.length) return;
    const delta = [to.x - c2.base.x, to.y - c2.base.y, 0];
    // ⚠⚠ **A PASTE INTO ANOTHER DRAWING IS A DIFFERENT COMMAND, AND THE HANDLES ARE USELESS.**
    // They belong to the drawing it was copied FROM. The frozen rows go instead, with the block
    // definitions any INSERT among them names.
    // ⚠ PASTEBLOCK takes the same road as a cross-drawing paste even when it is staying put: the
    // server wraps the FROZEN ROWS, and a same-drawing copy/move never produces any.
    const asBlock = s2.pasteBlock;
    if (asBlock) setPasteBlock(false);
    // ⚠ A CUT has no handles any more — the objects were removed when it was cut, as AutoCAD
    // removes them — so it pastes from its ROWS exactly like a paste from another drawing does.
    if (c2.from !== s2.id || asBlock || !c2.handles.length) {
      if (!c2.rows?.length) {
        sayRef.current?.("  that copy did not carry its geometry — copy it again");
        return;
      }
      try {
        const rep = await editDrawing(s2.id, "clippaste", [],
                                      { rows: c2.rows, blocks: c2.blocks || [], delta,
                                        space: s2.space ?? "Model", as_block: asBlock });
        const n = rep.pasted ?? 0;
        const carried = (rep.refused || {})["blocks carried"] || 0;
        if (asBlock) {
          sayRef.current?.(`  ${rep.wrapped ?? n} object${(rep.wrapped ?? n) === 1 ? "" : "s"}`
                           + ` pasted as block ${rep.block ?? ""}`);
        } else {
          sayRef.current?.(`  ${n} object${n === 1 ? "" : "s"} pasted`
                           + (c2.from !== s2.id ? " from another drawing" : "")
                           + (carried ? `, ${carried} block definition`
                                        + `${carried === 1 ? "" : "s"} carried across` : ""));
        }
        await refresh();
      } catch (e) { fail(e); }
      return;
    }
    try {
      const rep = await editDrawing(s2.id, c2.cut ? "move" : "copy", c2.handles, { delta });
      const made: string[] = rep.handles || [];
      if (ontoDrawLayer && made.length) {
        // ⚠ PASTESPEC's one honest meaning here: onto the layer you are DRAWING on. Pasting a
        // dimension back onto A-DIMS while you are working on A-FURN is the same mistake.
        await editDrawing(s2.id, "setprop", made, { layer: s2.drawLayer });
      }
      const n = rep.moved ?? rep.copied ?? c2.handles.length;
      sayRef.current?.(`  ${n} object${n === 1 ? "" : "s"} pasted`
                       + (ontoDrawLayer ? ` onto ${s2.drawLayer}` : ""));
      if (c2.cut) setClip(null);          // ⚠ a cut is spent once it lands
      // ⚠⚠ **NOT `refresh(rep)` — TWO EDITS, ONE REFRESH.** The `setprop` above runs AFTER the
      // paste and has its own delta; splicing only the paste's would leave the layer change off
      // the screen. A refetch is the honest answer whenever a handler makes more than one edit.
      await refresh();
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  const commit = useCallback(async (op: EditOp, params: Record<string, unknown>,
                                    clears = false) => {
    const s = live.current;
    if (!s.id || !s.sel.length) return;
    setBusy(`${op[0].toUpperCase()}${op.slice(1)}…`);
    // ⚠⚠ **A REF BESIDE THE STATE, because the INPUT QUEUE has to see this in the same tick.**
    // `busy` is state and lands a render later; the replay decides whether to press the next key
    // NOW. Without it COPY and MIRROR — the two commands that CREATE, so the `L` after them must
    // name an object that does not exist until the commit lands — took 13 and 14 of a 20-command
    // burst instead of 20.
    committing.current += 1;

    // ⚠⚠⚠ **DRAW THE MOVE NOW, NOT IN 345 ms — THE STRUCTURAL DIFFERENCE FROM AutoCAD.**
    // AutoCAD is instant here because the geometry is already in memory; this waited for a round
    // trip. Worse than waiting: `setCmd(null)` runs BEFORE this, so the drag preview is gone the
    // instant you commit and the object SNAPS BACK to where it was for the whole POST, then
    // jumps. That is not slowness, it is a wrong picture in between, and it is what made an edit
    // feel slow rather than merely be slow.
    // ⇒ The transform is applied to the display list locally and the round trip reconciles
    // behind it: `applyDelta`'s `[at, remove, rows]` are positions, a translation changes no
    // row's position and no channel's length, so the authoritative delta lands exactly as it
    // would have and replaces these rows outright.
    // ⚠⚠ ONLY a pure translation, and ONLY kinds `qa/optimistic_check.mjs` proves exact — see
    // `LOCALLY_EXACT_KINDS`. An INSERT is not one, a HATCH regenerates its pattern on a grid and
    // comes back with a DIFFERENT NUMBER OF LINES, and either would leave the drafter looking at
    // geometry the server never agreed to.
    // ⚠⚠⚠ **ROTATE AND SCALE ARE AFFINE TOO, AND ONLY MOVE WAS EVER WIRED UP.**
    // `applyLocalTransform` has always taken a full matrix and `qa/optimistic_check.mjs` has
    // always taken `--op=`; what was missing was this. So a drafter rotating a wall watched it
    // sit still for the whole round trip while moving the same wall was instant — measured on
    // ysl-hn-center-2025 at 73 ms against MOVE's 43, for the same one object.
    // ⚠ The kinds are per op (`LOCALLY_EXACT_BY_OP`) and come from the gate, not from reasoning
    // about which ones ought to work: a CIRCLE's radius lives in `cen`, which this may not
    // touch, and a DIMENSION re-measures itself when it turns.
    // ⚠ `[a, b, c, d, e, f]` is `x' = ax + cy + e`, `y' = bx + dy + f` — see `applyLocalTransform`.
    const matrixFor = (): [number, number, number, number, number, number] | null => {
      const base = params.base as number[] | undefined;
      const bx = Array.isArray(base) ? Number(base[0]) : NaN;
      const by = Array.isArray(base) ? Number(base[1]) : NaN;
      if (op === "move") {
        const d = params.delta as number[] | undefined;
        // ⚠ A z of anything is refused: the display list is a plan and this cannot show a lift.
        return Array.isArray(d) && d.length >= 2 && !(d[2] ?? 0)
          ? [1, 0, 0, 1, Number(d[0]), Number(d[1])] : null;
      }
      if (op === "rotate") {
        const t = (Number(params.angle) * Math.PI) / 180;
        if (!Number.isFinite(t) || !Number.isFinite(bx) || !Number.isFinite(by)) return null;
        const cs = Math.cos(t), sn = Math.sin(t);
        return [cs, sn, -sn, cs, bx - cs * bx + sn * by, by - sn * bx - cs * by];
      }
      if (op === "scale") {
        const k = Number(params.factor);
        if (!(k > 0) || !Number.isFinite(bx) || !Number.isFinite(by)) return null;
        return [k, 0, 0, k, bx * (1 - k), by * (1 - k)];
      }
      return null;
    };
    const mx = matrixFor();
    const handles = s.sel.map((p) => p.handle);
    let guessed = false;
    // ⚠ ONE pass: `planLocal` both decides whether this may be drawn early and records the rows
    // it would touch — see its note. The two used to be separate scans of the whole drawing.
    const plan = mx && s.dl ? planLocal(s.dl, handles, op) : null;
    if (mx && s.dl && plan) {
      const owners = new Set(handles.map((h) => h.toUpperCase()));
      noteDl("optimistic");
      setDl((prev) => (prev ? applyLocalTransform(prev, owners, mx, plan) : prev));
      // ⚠⚠⚠ **THE GRIPS MOVE WITH THE GEOMETRY, OR THE HANDLES STAY BEHIND.** They are FETCHED
      // from the server on every `dl` change, so an optimistically-moved object arrived in its
      // new place with its grip squares still marking the old one for the whole round trip.
      // `qa/optimistic_shot.py` photographed exactly that: the line white in both frames — moved
      // — and the grips red at the old position and cyan at the new. A drafter sees the object
      // land and its handles lag, which reads as the edit not having taken.
      // ⚠ The SAME matrix, so they cannot disagree with the geometry; the refetch that follows
      // overwrites them with the server's own a moment later, and agrees.
      setGrips((g) => (g ? g.map((q) => ({
        ...q,
        p: [mx[0] * q.p[0] + mx[2] * q.p[1] + mx[4],
            mx[1] * q.p[0] + mx[3] * q.p[1] + mx[5]] as [number, number],
      })) : g));
      setPending(owners);
      guessed = true;
      // ⚠ The app's own mark, beside `__pdcadRefresh` and `__pdcadScene`: WHEN the drafter saw
      // the object land, as against when the server said it had. `qa/ux_survey.py` reads it.
      const t0 = performance.now();
      (window as unknown as Record<string, unknown>).__pdcadOptimistic = {
        n: owners.size, at: t0, paint: null,
      };
      // ⚠⚠ Two rAFs, for the reason `refresh` gives: the number a drafter feels is the FIRST
      // FRAME that shows the object where they put it, not the state update that schedules it.
      // ⚠⚠⚠ **AND THIS NUMBER IS UNDER SUSPICION.** Anchoring it to the paint that actually
      // draws the moved objects — the way `__pdcadGhost` now is — reported 165-196 ms where
      // this reports 30-38. The ghost's paint-anchored figure was confirmed against the
      // framebuffer; this one has never been photographed, so neither number is trustworthy
      // until a MOVE is. See the note beside `stamp` in `Canvas2D`.
      // ⚠ `paint` is stamped by `Canvas2D`'s paint — the one that actually draws the moved
      // objects — and by nothing here. A fixed pair of rAFs fired at 35 ms, before the GPU scene
      // carrying the move was patched at 77, so it timed a state update and called it a picture.
      void t0;
    }

    // ⚠ The same door `authorBody` has, for `qa/optimistic_shot.py`: hold the POST back so the
    // optimistically-transformed picture can be photographed before the server's own delta
    // replaces it. It delays the REQUEST and nothing else, and is `undefined` in a drafter's
    // browser. See `__pdcadDelayDraw`.
    const heldC = (window as unknown as Record<string, unknown>).__pdcadDelayCommit as
      number | undefined;
    if (guessed && heldC && heldC > 0) await new Promise((r) => setTimeout(r, heldC));
    try {
      const rep = await editDrawing(s.id, op, handles, params);
      // ⚠⚠ **EVERY OP THAT REPORTS A COUNT NEEDS A LINE HERE, and ARRAY's was missing** — so
      // an array that made 30 objects reported "6 objects arrayd": `rep.arrayed` was unread,
      // the chain fell through to 0, and… no. It reported 6, because `rep.changed` was absent
      // and the fallback found nothing, leaving the SELECTION size showing through the sentence
      // the drafter reads. A count that is quietly the wrong count is worse than none.
      const n = rep.moved ?? rep.copied ?? rep.erased ?? rep.offset ?? rep.changed
                ?? rep.joined ?? rep.arrayed ?? rep.stretched ?? rep.drawn
                ?? rep.exploded ?? 0;
      // ⚠⚠ **"0 objects exploded" IS NOT AN ANSWER, AND `api.ts` SAYS SO ABOUT `detail`.**
      // Every op that declines with a reason puts it there — "select a block reference to
      // explode", "no cutting edges", "that object is not in the world plane" — and `commit`
      // read none of them, so the whole Modify panel could refuse a command and report a
      // COUNT instead of the reason. The pick commands have surfaced `rep.detail` all along
      // (see the TRIM/EXTEND site); this is the same sentence for the selection commands.
      // ⚠ OVERKILL does two things and a single count hides one of them: a drafter who ran it
      // on a consultant's file wants to know that seven walls were MERGED as much as that
      // twelve duplicates went.
      const merged = (rep as { merged?: number }).merged ?? 0;
      sayRef.current?.(n === 0 && !merged && rep.detail
        ? `  ${rep.detail}`
        : `  ${n} object${n === 1 ? "" : "s"} ${VERB[op] ?? `${op}d`}`
          + (op === "overkill" && merged ? `, ${merged} overlapping run${merged === 1 ? "" : "s"} merged` : ""));
      // ⚠⚠ **AN OP THAT REPLACED ITS INPUTS LEAVES THE SELECTION HOLDING DEAD HANDLES.** JOIN
      // erases four pieces and makes one polyline; the old handles are gone, so the very next
      // command reported "0 objects" against a selection that looked perfectly normal on
      // screen. AutoCAD leaves the RESULT selected, which is also what a drafter wants — the
      // next thing you do after joining is usually to that polyline.
      // ⚠ NOT after OVERKILL: its `handles` are the objects it DELETED, so re-selecting them
      // would leave the drafter holding a set of dead handles — the very bug this block fixes
      // for JOIN.
      if (rep.handles?.length && (rep.joined || rep.erased) && op !== "overkill") {
        setSel(rep.handles.map((h) => ({ handle: h, kind: "LWPOLYLINE", layer: s.drawLayer,
                                         aci: 256 })));
      }
      if (rep.refused_count) {
        // ⚠ The COMMAND'S OWN VERB. "8 could not be moved" after an OFFSET names an operation
        // the drafter did not run, and sends them looking for a MOVE that never happened.
        const verb = VERB[op] ?? `${op}d`;
        sayRef.current?.(`  ${rep.refused_count} could not be ${verb}: `
                         + Object.entries(rep.refused || {}).map(([k, v]) => `${v} ${k}`).join(", "));
      }
      // ⚠ A copy that had to drop a shared link says so — an XCLIP or an extension dictionary
      // belongs to the original and cannot be shared by two entities.
      if (rep.links_dropped) {
        sayRef.current?.(`  ${rep.links_dropped} shared link(s) not copied — see BUILD-PLAN.md`);
      }
      if (op === "erase" || clears) setSel([]);
      // ⚠⚠ **THE REPORT CARRIES THE DELTA.** Every Modify command comes through here, so this
      // one line is what turns a 46 MB refetch into an 88.6 KB splice for MOVE, COPY, ERASE,
      // ROTATE, SCALE and MIRROR. `refresh` falls back to refetching whenever the report has no
      // delta, which is most other operations and every case the server declined.
      // ⚠⚠ **A GUESS THE SERVER DID NOT CONFIRM MUST BE THROWN AWAY, NOT LEFT ON SCREEN.**
      // `refresh` returns early when the report says the picture was KEPT and splices when there
      // is a delta; either way, if we drew something optimistically and the report carries no
      // delta for this space, the only honest thing on screen is the server's own list.
      if (guessed && !keptBy(rep) && !deltaFor(rep, live.current.space ?? "Model")) {
        noteDl("refetch:guessed-no-delta");
        setDl(await loadDisplay(s.id, live.current.space ?? "Model"));
      } else if (guessed && keptBy(rep)) {
        noteDl("refetch:guessed-kept");
        setDl(await loadDisplay(s.id, live.current.space ?? "Model"));
      } else if (!refreshNow(rep)) {
        // ⚠⚠⚠ **NO `await` WHEN THE DELTA PATH CAN DO IT ALL, BECAUSE THE `await` IS A RENDER.**
        // React batches what one task changes; `await refresh(rep)` put the splice in a task of
        // its own and the `finally` below in a third, so one edit re-rendered the studio FOUR
        // times — 42-55 ms of the ~145 ms task on `ysl-hn-center-2025`, each render dragging a
        // ribbon render with it. `refreshNow` returns true when it has done the whole job, and
        // then the splice, the transcript line and the cleared spinner all land together.
        // ⚠ A REFETCH still awaits: it is a network call, and there is nothing to batch it with.
        await refreshFetch();
      }
    } catch (e) {
      // ⚠ The edit failed and the screen is showing a move that never happened. Refetch, always.
      if (guessed) { try { setDl(await loadDisplay(s.id, live.current.space ?? "Model")); } catch { /* fail() says it */ } }
      fail(e);
    } finally { setPending(null); setBusy(null); committing.current -= 1; }
  }, [refresh, fail]);

  /** PEDIT and its sub-menu. ⚠ Three shapes, and the mode decides which:
   *   · `close` `open` `reverse` and PJOIN commit straight away on the selection;
   *   · `width` asks for a number on the command line;
   *   · `addvertex` `delvertex` `arc` `straight` ask for a POINT on the drawing, because
   *     "which segment" is a place, not an index a drafter could type. */
  /** ZOOM ▸ one of its options. ⚠ Every branch is something that already works — this is the
   *  KEYBOARD ROUTE to them, which is how a fast drafter reaches any of them. */
  const runZoom = useCallback((what: string, f?: number) => {
    if (what === "all" || what === "extents") { api.current?.zoomExtents(); return; }
    if (what === "window") {
      setZoomWant(null);
      setMode("zoomwin");
      sayRef.current?.("Specify first corner:");
      return;
    }
    if (what === "scale") {
      if (!f || !(f > 0)) { sayRef.current?.("  a scale factor is a number greater than zero"); return; }
      api.current?.zoomBy(f);
      return;
    }
    // ⚠⚠ **A POINT, THEN A HEIGHT — the shape all three share.** BricsCAD asks for the centre (or
    // the lower-left / lower-right corner) and then "magnification or height"; a height in
    // drawing units is what the number means, and it is the one a drafter types on a site plan.
    if (what === "center" || what === "left" || what === "right") {
      setZoomAnchor({ anchor: what, p: null });
      sayRef.current?.(what === "center" ? "Specify center point:"
                       : what === "left" ? "Specify lower left corner point:"
                       : "Specify lower right corner point:");
      return;
    }
    if (what === "previous") {
      const was = viewHist.current.pop();
      if (!was) { sayRef.current?.("  No previous view."); return; }
      lastView.current = was;
      api.current?.setView(was);
      return;
    }
    // ⚠ BricsCAD's `zoom In` and `zoom Out` are a factor of two about the view centre — the same
    // step its own toolbar buttons take, and what a drafter expects from typing Z then I.
    if (what === "in") { api.current?.zoomBy(2); return; }
    if (what === "out") { api.current?.zoomBy(0.5); return; }
    // ⚠⚠ **ZOOM ENtity fits the SELECTION, and it needs a selection.** BricsCAD asks you to pick
    // one; here the selection already exists, which is pdcad's noun-verb order and the same
    // answer. Refused in words when nothing is picked rather than silently zooming to extents —
    // a command that quietly does something else is worse than one that declines.
    if (what === "entity") {
      const s2 = live.current;
      const own = new Set(s2.sel.map((q) => q.handle));
      if (!own.size) { sayRef.current?.("  Select an entity first."); return; }
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (const r of (s2.dl?.polys ?? []) as unknown[][]) {
        if (!own.has(r[3] as string)) continue;
        const pts = r[2] as number[];
        for (let i = 0; i + 1 < pts.length; i += 2) {
          if (pts[i] < x0) x0 = pts[i];
          if (pts[i] > x1) x1 = pts[i];
          if (pts[i + 1] < y0) y0 = pts[i + 1];
          if (pts[i + 1] > y1) y1 = pts[i + 1];
        }
      }
      // ⚠ A margin, because an object filling the frame edge to edge reads as clipped.
      const mx = Math.max((x1 - x0) * 0.06, 1e-6), my = Math.max((y1 - y0) * 0.06, 1e-6);
      if (!Number.isFinite(x0) || !api.current?.fitBox(x0 - mx, y0 - my, x1 + mx, y1 + my)) {
        sayRef.current?.("  that selection has nothing to zoom to");
      }
      return;
    }
    sayRef.current?.(`  ZOOM has no ${what} option here`);
  }, []);
  zoomRef.current = runZoom;

  const pedit = useCallback((op: string, extra?: Record<string, unknown>) => {
    const s = live.current;
    if (!s.id) return;
    if (op === "splinedit") { splineditRef.current?.(extra); return; }
    if (!s.sel.length) { sayRef.current?.("  Select a polyline first."); return; }
    const mode = String(extra?.mode ?? "");
    if (op === "pjoin") { void commit("pjoin", {}); return; }
    // ⚠⚠ **TYPED `PEDIT` ASKS FOR ITS OPTION, BECAUSE THAT IS WHAT PEDIT IS.** The ribbon button
    // is a flyout and the flyout entries have no command names, so `PEDIT ⏎` reached the parent
    // and found no handler — the one command in AutoCAD that is a sub-menu was the one command
    // here you could not type. It now prints AutoCAD's own option line and takes a letter.
    if (!mode) {
      setPedWant({ op: "pedit", mode: "", kind: "option" });
      setTyped("");
      // ⚠ AutoCAD's own order, with the two this app adds — Add/Delete vertex, which AutoCAD
      // hides behind an `Edit vertex` sub-mode — at the end rather than in the middle.
      sayRef.current?.("Enter an option [Close/Open/Width/Join/Fit/Decurve/Ltype gen/Reverse/"
                       + "Add vertex/Delete vertex/Arc/Straighten]:");
      return;
    }
    if (PEDIT_POINT.has(mode)) {
      setPedWant({ op: "pedit", mode, kind: "point" });
      sayRef.current?.(PEDIT_PROMPT[mode] ?? "Specify a point on the polyline:");
      return;
    }
    if (mode === "width") {
      setPedWant({ op: "pedit", mode, kind: "width" });
      setTyped("");
      sayRef.current?.("Specify new width for all segments:");
      return;
    }
    void commit("pedit", { mode });
  }, [commit]);
  peditRef.current = pedit;

  /** SPLINEDIT — PEDIT's shape exactly, on the entity that is second-commonest in this office's
   *  drawings. ⚠ `degree` asks for a NUMBER, so it reuses the same typed-value prompt PEDIT's
   *  WIDTH uses; `addcv`/`delcv` ask for a POINT, because "which control vertex" is a place. */
  const splinedit = useCallback((extra?: Record<string, unknown>) => {
    const s = live.current;
    if (!s.id) return;
    if (!s.sel.length) { sayRef.current?.("  Select a spline first."); return; }
    const mode = String(extra?.mode ?? "");
    if (!mode) {
      setPedWant({ op: "splinedit", mode: "", kind: "option" });
      setTyped("");
      sayRef.current?.("Enter an option [Close/Open/Fit data/Purge/dEgree/"
                       + "Add vertex/delete verteX]:");
      return;
    }
    if (SPLINEDIT_POINT.has(mode)) {
      setPedWant({ op: "splinedit", mode, kind: "point" });
      sayRef.current?.(SPLINEDIT_PROMPT[mode] ?? "Specify a point on the spline:");
      return;
    }
    if (mode === "degree") {
      setPedWant({ op: "splinedit", mode, kind: "width" });
      setTyped("");
      sayRef.current?.("Enter new degree for spline <3>:");
      return;
    }
    void commit("splinedit", { mode });
  }, [commit]);
  splineditRef.current = splinedit;

  /** The letter typed at PEDIT's option prompt. ⚠ JOIN is a different OP, not a mode — it makes
   *  one object out of several — so it is routed rather than passed through. */
  const acceptPeditOption = useCallback(() => {
    const key = live.current.typed.trim().toLowerCase();
    const which = pedWantRef.current?.op ?? "pedit";
    setPedWant(null); setTyped("");
    if (which === "splinedit") {
      const m = SPLINEDIT_OPTION[key];
      if (!m) { sayRef.current?.(`  "${key}" is not a SPLINEDIT option`); return; }
      splineditRef.current?.({ mode: m });
      return;
    }
    if (which === "zoom") {
      // ⚠⚠ **ENTER ALONE IS THE DEFAULT, WHICH IS WHAT THE `<Extents>` IN THE PROMPT PROMISES.**
      // AutoCAD's angle brackets are a contract: pressing Enter does that thing. A prompt that
      // shows a default and then refuses an empty answer is the same lie as a lit dead button.
      if (!key) { zoomRef.current?.("extents"); return; }
      // ⚠ A bare number is a SCALE FACTOR — `2X` and `0.5` are how a drafter zooms by feel, and
      // AutoCAD takes both. Checked before the letter table so `2` is not read as an option.
      const f = Number(key.replace(/x$/, ""));
      if (Number.isFinite(f) && f > 0) { zoomRef.current?.("scale", f); return; }
      const z = ZOOM_OPTION[key];
      if (!z) { sayRef.current?.(`  "${key}" is not a ZOOM option`); return; }
      zoomRef.current?.(z);
      return;
    }
    const mode = PEDIT_OPTION[key];
    if (!mode) { sayRef.current?.(`  "${key}" is not a PEDIT option`); return; }
    if (mode === "join") { peditRef.current?.("pjoin", {}); return; }
    peditRef.current?.("pedit", { mode });
  }, []);

  /** PEDIT ▸ Width — the typed number, applied to every segment of every selected polyline. */
  const acceptPeditWidth = useCallback(() => {
    const s = live.current;
    const w = Number(s.typed);
    const which = pedWantRef.current?.op ?? "pedit";
    // ⚠ Read the ride-along BEFORE the reset. A ref lags a render, so reading it afterwards
    // happens to work today and would stop the moment this runs in a batch with anything that
    // re-renders first.
    const rode = pedWantRef.current?.mode ?? "";
    setPedWant(null); setTyped("");
    if (which === "zoom") {
      const at = zoomAnchorRef.current;
      zoomAnchorRef.current = null;
      setZoomAnchor(null);
      if (!at?.p) return;
      if (!(w > 0)) { sayRef.current?.("  a view height is a number greater than zero"); return; }
      if (!api.current?.zoomTo(at.p.x, at.p.y, w, at.anchor)) {
        sayRef.current?.("  the canvas has no size to zoom in");
        return;
      }
      sayRef.current?.(`  zoomed to a ${w} high view`);
      return;
    }
    if (which === "splinedit") {
      // ⚠ An INTEGER, and the server refuses anything outside 1–11 with the reason. Rounding a
      // typed 3.5 to 4 silently would give a curve the drafter did not ask for.
      if (!Number.isInteger(w) || w < 1 || w > 11) {
        sayRef.current?.("  a spline's degree is a whole number from 1 to 11"); return;
      }
      void commit("splinedit", { mode: "degree", degree: w });
      return;
    }
    if (which === "patharray") {
      const pw = live.current.pathWant;
      setPathWant(null);
      if (!pw) return;
      if (!Number.isInteger(w) || w < 2) {
        sayRef.current?.("  a path array needs a whole number of items, 2 or more"); return;
      }
      // ⚠ The path handle rode across in `mode`, because `PedWant` already carries a string and a
      // second field for one command would be a field every other command ignores.
      void (async () => {
        const s3 = live.current;
        if (!s3.id) return;
        try {
          const rep = await editDrawing(s3.id, "array", pw.items.map((p) => p.handle),
                                        { kind: "path", count: w, path: rode, align: true });
          sayRef.current?.(`  ${rep.arrayed ?? 0} copies along the path`);
          await refresh(rep);
        } catch (e) { fail(e); }
      })();
      return;
    }
    if (!Number.isFinite(w) || w < 0) { sayRef.current?.("  a width is a number, 0 or more"); return; }
    void commit("pedit", { mode: "width", width: w });
    // ⚠⚠ **SAID OUT LOUD, BECAUSE THE CANVAS DRAWS CENTRELINES.** A polyline's WIDTH is real
    // geometry — it is in the DXF and it plots as a thick band, verified on an A4 at 1:20 — but
    // this canvas flattens every entity to a path and does not fill it. A drafter who sets a
    // width and sees no change would reasonably conclude the command did nothing.
    if (w > 0) sayRef.current?.("  width is drawn on the PLOT, not on this screen");
  }, [commit]);

  const step = useCallback(async (which: "undo" | "redo") => {
    const s = live.current;
    if (!s.id) return;
    try {
      const rep = await (which === "undo" ? undoDrawing(s.id) : redoDrawing(s.id));
      sayRef.current?.(`  ${rep.detail ?? `${which.toUpperCase()} ${rep.op ?? ""}`.trim()}`);
      // ⚠ Undo and redo carry a delta too — `edits._inverse_touch` reads the handles out of the
      // inverse record, so Ctrl+Z splices exactly as an edit does.
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** ⚠ Fetched only for ONE selected object, and cleared the moment that stops being true. A
   *  stale grip square is an invitation to drag geometry that is no longer selected. */
  useEffect(() => {
    setHotGrip(null);
    const s = live.current;
    if (!s.id || sel.length !== 1) { setGrips(null); return; }
    // ⚠⚠⚠ **NOT WHILE A GUESS IS IN FLIGHT — THE SERVER STILL HAS THE OLD GEOMETRY.** This is
    // keyed on `dl`, and an optimistic MOVE changes `dl` at once; the refetch that followed
    // asked a server which had not seen the edit yet and put the grips back where the object
    // USED to be, overwriting the ones `commit` had just transformed with the same matrix.
    // `qa/optimistic_shot.py` photographed it: the line already moved and its handles still
    // marking the old position for the whole round trip, which reads as the edit not taking.
    // ⚠ `pending` clears when the authoritative delta lands, and `dl` changes with it — so the
    // real grips are fetched then, one render later, and agree.
    if (pending && pending.size) return;
    let alive = true;
    loadGrips(s.id, sel[0].handle)
      .then((g) => { if (alive) setGrips(g.length ? g : null); })
      .catch(() => { if (alive) setGrips(null); });
    return () => { alive = false; };
  }, [sel, id, dl, pending]);

  /** Author one entity. ⚠ No selection — DRAW is the one command that creates from nothing. */
  /** ⚠ A ref, because `commitText` is declared BELOW `author` and a new MTEXT is authored from
   *  the editor's Save — the same temporal-dead-zone shape `sayRef` solves. */
  const authorRef = useRef<((k: string, p: Pt[], x?: Record<string, unknown>) => void)
                           | undefined>(undefined);

  const authorBody = useCallback(async (kind: string, pts: Pt[],
                                        extra: Record<string, unknown> = {}) => {
    const s = live.current;
    if (!s.id) return;
    // ⚠⚠ **MVIEW IS NOT A DRAW.** It arms like one — two corners on the paper — but it makes a
    // WINDOW in a sheet, which is its own op with its own undo, and `do_draw` has never heard of
    // a VIEWPORT. Routing it here rather than teaching DRAW about it keeps `DRAW_KINDS` a list
    // of things that are geometry.
    if (kind === "MVIEW") {
      if (!s.space) { sayRef.current?.("  MVIEW works on a layout — open a sheet tab first");
                      return; }
      try {
        const rep = await editDrawing(s.id, "mview", [],
                                      { layout: s.space, points: pts.map((p) => [p.x, p.y, 0]),
                                        ...extra });
        sayRef.current?.(`  viewport ${rep.window?.[0]}×${rep.window?.[1]} mm at 1:${rep.scale}`);
        await refresh(rep);
      } catch (e) { fail(e); }
      return;
    }
    // ⚠⚠⚠ **THREE COMMANDS THAT ARE EDITS, NOT DRAWS — AND ALL THREE WERE ALREADY ON THE
    // SERVER.** DIMCONTINUE and DIMBASELINE add one dimension off the end of one already on the
    // drawing (`edits.do_dimchain`); QDIM measures a whole selection at once (`edits.do_qdim`).
    // Each takes objects that are ALREADY PICKED plus one click, so they cannot go through
    // `do_draw`, which authors from points alone. `api.ts` has typed all three since they
    // landed; nothing on this side ever sent one.
    // ⚠ CONTINUE and BASELINE take exactly ONE dimension — the server refuses anything else in
    // a sentence — so the ref the draw loop resolved IS the handle. QDIM takes the whole
    // selection, which `ref` has kept alive for it.
    const CHAIN: Record<string, EditOp> = {
      DIMCONTINUE: "dimcont", DIMBASELINE: "dimbase", QDIM: "qdim",
    };
    if (CHAIN[kind]) {
      const one = pts[pts.length - 1];
      const handles = kind === "QDIM"
        ? live.current.sel.map((x) => x.handle)
        : [String(extra.ref)];
      if (!handles.length || !handles[0]) {
        sayRef.current?.(`  ${kind} needs something selected first`);
        return;
      }
      try {
        const rep = await editDrawing(s.id, CHAIN[kind], handles,
                                      { point: [one.x, one.y, 0], layer: s.drawLayer,
                                        space: s.space ?? "Model" });
        sayRef.current?.(`  ${rep.made ?? rep.drawn ?? 1} dimension`
                         + `${(rep.made ?? 1) === 1 ? "" : "s"} added`);
        await refresh(rep);
      } catch (e) { fail(e); }
      return;
    }
    // ⚠⚠ **HOLD THE PICTURE, THEN POST.** See `ghost`. The band `Canvas2D` has been drawing all
    // through the gesture keeps being drawn — same renderer, same points, cursor no longer
    // appended — so the object stays where the drafter put it instead of blinking out for the
    // round trip. ⚠ Only the kinds `Canvas2D` can draw settled; anything else keeps the old
    // behaviour rather than being drawn wrong.
    // ⚠ `kind` IS the `DrawDef` kind the band was drawn from — `rect: { kind: "RECT", … }` —
    // so there is nothing to look up. MVIEW, the one id where the two differ, returned above.
    // ⚠⚠ **NOT AN ARCED POLYLINE.** `Canvas2D` draws a PLINE's band as straight segments — it
    // has no bulge branch — so for a polyline drawn with `A` the band is already an
    // approximation, and holding that approximation on screen AFTER the command is over is a
    // different thing from showing it during: one is "here is where your clicks are", the other
    // is "here is your object", and only the second is a claim. The bulges are the server's, by
    // design (`_tangent_bulges`), so the honest picture until it answers is no picture.
    // ⚠⚠ **THE TOKEN IS WHAT MAKES TWO IN FLIGHT SAFE.** Drawing a wall fast puts a second
    // segment's POST on the wire before the first has answered; without it the first report to
    // land would clear the SECOND segment's ghost, and the drafter would watch a line they had
    // just drawn blink out because a different one arrived. Each draw clears only its own.
    const mine = ++ghostSeq.current;
    if (GHOST_KINDS.has(kind) && !(extra.arcs as number[] | undefined)?.length) {
      const d = live.current.draw;
      setGhosts((gs) => [...gs, { kind, pts, seq: mine,
                 rect: { dims: d?.dims, square: d?.square, rot: rectRot(d?.opt) } }]);
      // ⚠⚠⚠ **WHEN THE DRAFTER SEES THE OBJECT, WHICH IS NOT WHEN THE SERVER ANSWERS.** The
      // ghost above is the drawn shape held on screen through the round trip, so for these kinds
      // the picture is complete one frame after the last click — and `qa/bench_actions.py` was
      // timing until the SERVER's row replaced it, reporting a LINE at 150 ms on
      // `ysl-hn-center-2025` against BricsCAD's 28. That is the same fault this file records for
      // MOVE, which publishes `__pdcadOptimistic` for exactly this reason, and it had no
      // equivalent on the draw path.
      // ⚠⚠ **IT IS ONLY HONEST IF THE GHOST IS THE OBJECT.** `qa/ghost_shot.py` holds it to
      // that: the framebuffer while the ghost is up must equal the framebuffer once the server's
      // row has landed. A ghost that merely looks about right would make this mark a lie.
      // ⚠ Two rAFs, as `refresh` and the optimistic MOVE both use: the number is the FIRST FRAME
      // that shows it, not the state update that schedules it.
      // ⚠ `paint` is filled in by `Canvas2D`'s paint — by the one that actually draws this
      // ghost, one frame later, which is when it is composited. A fixed pair of rAFs here read
      // a whole frame too long whenever the canvas painted in the same frame as the click.
      (window as unknown as Record<string, unknown>).__pdcadGhost =
        { kind, seq: mine, at: performance.now(), paint: null };
    } else {
      // ⚠ No ghost for this kind — the mark has to say so rather than keep the last one, or a
      // harness reads a HATCH's draw time off the LINE before it.
      (window as unknown as Record<string, unknown>).__pdcadGhost = null;
    }
    const dropGhost = () => setGhosts((gs) => gs.filter((g) => g.seq !== mine));
    // ⚠⚠⚠ **A DOOR FOR `qa/ghost_shot.py`, AND IT DELAYS ONLY THE POST.** The ghost above is on
    // screen within two frames and the server answers in about fifty milliseconds, so the window
    // in which the two pictures can be COMPARED is shorter than a screenshot takes — the gate
    // read "the server answered before the first grab" on every case and proved nothing. This
    // widens that window on demand. It changes nothing about what is drawn, only when the
    // request leaves, and it is `undefined` in a drafter's browser.
    const held = (window as unknown as Record<string, unknown>).__pdcadDelayDraw as
      number | undefined;
    if (held && held > 0) await new Promise((r) => setTimeout(r, held));
    try {
      const rep = await editDrawing(s.id, "draw", [],
                                    { kind, points: pts.map((p) => [p.x, p.y, 0]),
                                      layer: s.drawLayer,
                                      // ⚠⚠ **THE SPACE, OR A TITLE BLOCK LANDS IN THE MODEL.**
                                      // Everything drawn while a layout is open is paper-space
                                      // geometry — a frame, a north point, a sheet number, a
                                      // note. Filing it in model space puts it at paper-
                                      // millimetre coordinates in the corner of a plan eighty
                                      // metres wide, where it is invisible on both.
                                      space: s.space ?? "Model", ...extra });
      sayRef.current?.(`  ${rep.kind ?? kind} drawn on layer ${s.drawLayer}`
                       + (s.space ? ` of layout "${s.space}"` : ""));
      // ⚠⚠⚠ **`refresh(rep)`, NOT `refresh()` — THE REPORT CARRIES THE DELTA.** Eighteen call
      // sites did `const rep = await editDrawing(...)` and then threw `rep` away, so every one of
      // them refetched the whole display list for an edit the server had already described.
      // Measured on drawing 1158 (63,147 entities) drawing one CIRCLE: the POST is 306 ms and
      // carries a **29 KB** Model delta; the refetch it triggered was **6.93 MB and 2,579 ms**,
      // and the GPU scene was then rebuilt whole (433,306 instances). Four seconds to draw a
      // circle, of which the drawing itself was 306 ms.
      // ⚠ Passing the report is strictly safe: `refresh` falls back to refetching whenever there
      // is no delta for THIS space — which is exactly what `refresh()` did unconditionally.
      await refresh(rep);
      // ⚠ AFTER the refresh, never before: clearing it first puts the gap back, one frame wide
      // instead of a round trip wide, and a one-frame flicker on every line is still a flicker.
      dropGhost();

    } catch (e) { dropGhost(); fail(e); }
  }, [refresh, fail]);

  const author = useCallback(async (kind: string, pts: Pt[],
                                    extra: Record<string, unknown> = {}) => {
    const s = live.current;
    if (!s.id) return;
    // ⚠⚠⚠ **A DRAW IS AN EDIT IN FLIGHT TOO, and this counter did not know it.** `commit`
    // increments it and every MODIFY goes through `commit`; a DRAW comes through here, so
    // `__pdcadCmd` reported a LINE as finished while the engine was still writing it — the
    // caller's next command then raced it, and a 20-command burst counted 19 or 21. Same
    // counter, both paths, or the queue is pacing off half the truth.
    // ⚠⚠ **INSIDE THIS FUNCTION, NOT A WRAPPER AROUND IT.** Wrapping it in a second
    // `useCallback([])` froze the inner one at its first render — `refresh` and `fail` are in
    // its deps — and six e2e scenarios failed at once with stale closures. A counter is not
    // worth a second identity for a callback this file passes everywhere.
    committing.current += 1;
    try {
      return await authorBody(kind, pts, extra);
    } finally { committing.current -= 1; }
  }, [authorBody]);
  authorRef.current = author;

  /** ⚠⚠ **A NEW MTEXT OPENS THE EDITOR, THE WAY AutoCAD DOES — AND NOTHING IS CREATED UNTIL
   *  IT IS SAVED.** The text used to be typed at the command line, one line at a time with `\n`
   *  for a new line: this app's own gesture, and the reason a note with two paragraphs was a
   *  chore. AutoCAD asks for the BOX, opens the editor inside it, and creates nothing if you
   *  close it empty — which is also why the object cannot be authored first: the server refuses
   *  an MTEXT with no text, and it is right to.
   */
  const newMtext = useCallback((pts: Pt[], height: number) => {
    const xs = pts.map((q) => q.x); const ys = pts.map((q) => q.y);
    const box = { x: Math.min(...xs), y: Math.min(...ys),
                  w: Math.abs(xs[1] - xs[0]), h: Math.abs(ys[1] - ys[0]) };
    setTextEdit({
      // ⚠ An EMPTY handle is what says "this one does not exist yet" — `commitText` reads it.
      spec: { handle: "", kind: "MTEXT", text: "", height, width: box.w, rotation: 0,
              attachment: 1, line_spacing: 1, bg_fill: 0, bg_colour: 0, bg_scale: 1.5 },
      box,
    });
    sayRef.current?.("  type the note, then Save — or close it to draw nothing");
  }, []);

  /** A DRAW tool's next point. */
  const onDrawPoint = useCallback((p: Pt) => {
    const d = live.current.draw;
    if (!d) return;
    const def = defOf(d.mode, live.current.dimKind, givenFor(d.mode, live.current));
    if (!def) return;
    let pts = [...d.pts, p];
    // ⚠ A fixed-direction XLINE/RAY is complete at its FIRST point — the second is derived, not
    // asked for. The offset is scaled off the drawing so the direction survives rounding on a
    // site plan as well as on a detail; the entity is infinite either way, so only the
    // DIRECTION of this vector is ever read.
    // ⚠ RECTANG with Dimensions: the second click chooses the QUADRANT and nothing else. Its
    // distance from the first corner is discarded, which is the whole point — the sides were
    // already given. ⚠ A click exactly on the first corner would give a sign of zero and collapse
    // the rectangle, so zero counts as positive.
    // ⚠⚠ **`Square`, `Dimensions` AND `Rotated` ALL LAND HERE, THROUGH THE FUNCTION THE RUBBER
    // BAND ALSO USES.** They were three separate blocks and the band knew about none of them —
    // so the drafter dragged one shape and got another. `rectCorners` is the single rule; corner
    // 2 is the opposite one, which is what the server is sent.
    // ⚠ The rotation goes to the server as well: it projects the two points back into the same
    // frame, so what arrives is exactly the `a` and `b` computed here.
    if (d.mode === "rect" && pts.length === 2
        && (d.dims || d.square || rectRot(d.opt))) {
      const cs = rectCorners(pts[0], pts[1],
                             { dims: d.dims, square: d.square, rot: rectRot(d.opt) });
      pts = [pts[0], { ...pts[1], x: cs[2].x, y: cs[2].y }];
    }
    // ⚠⚠ **BISECT TAKES THREE PICKS AND DRAWS THROUGH THE FIRST.** Vertex, then a point on each
    // leg; the construction line runs along the bisector of that angle. The catalogue says
    // `points: 2` because that is the ordinary gesture, so this intercepts before the count is
    // consulted at all.
    if (d.bisect) {
      if (pts.length < 3) {
        setDraw({ ...d, pts });
        sayRef.current?.(pts.length === 1 ? "  Specify angle start point:"
                                          : "  Specify angle end point:");
        return;
      }
      const [v, a1, b1] = pts;
      const n1 = Math.hypot(a1.x - v.x, a1.y - v.y), n2 = Math.hypot(b1.x - v.x, b1.y - v.y);
      // ⚠ A leg of no length has no direction, and normalising it is a division by zero that
      // arrives as a NaN construction line — drawn, selectable, and invisible.
      if (n1 < 1e-9 || n2 < 1e-9) {
        sayRef.current?.("  that point is on the vertex, so there is no angle to bisect");
        setDraw(null); setMode("select");
        return;
      }
      const bx = (a1.x - v.x) / n1 + (b1.x - v.x) / n2;
      const by = (a1.y - v.y) / n1 + (b1.y - v.y) / n2;
      const bn = Math.hypot(bx, by);
      // ⚠ Two legs pointing exactly opposite sum to zero: the "angle" is a straight line and its
      // bisector is undefined — there are two, at right angles, and guessing one is worse than
      // saying so.
      if (bn < 1e-9) {
        sayRef.current?.("  those two legs are opposite, so the bisector is undefined");
        setDraw(null); setMode("select");
        return;
      }
      const step = Math.max(1, (live.current.extent || 1000) / 10);
      pts = [v, { ...v, x: v.x + (bx / bn) * step, y: v.y + (by / bn) * step }];
    }
    if (d.dir && pts.length === 1) {
      const step = Math.max(1, (live.current.extent || 1000) / 10);
      pts = [...pts, { ...p, x: p.x + d.dir.x * step, y: p.y + d.dir.y * step }];
    }
    // ⚠⚠ **A RECTANGULAR CLOUD IS TWO CLICKS, NOT TWO CLICKS AND AN ENTER.** The corners say
    // everything; waiting for Enter as the unbounded gesture does would make the named option
    // slower than the unnamed one it replaces.
    if (d.mode === "revcloud" && d.shape === "rect" && pts.length === 2) {
      const def2 = defOf(d.mode, live.current.dimKind, "");
      if (def2?.asks?.length
          && def2.asks.every((k) => (d.pre || {})[k.key] !== undefined)) {
        setDraw(null); setMode("select");
        void author(def2.kind, pts, { ...(d.pre || {}), ...(d.ref ? { ref: d.ref } : {}) });
        return;
      }
      setDraw({ ...d, pts, ask: 0, got: { ...(d.pre || {}) } });
      setTyped(String(defaultAsk(def, 0, live.current.extent)));
      sayRef.current?.(askPrompt(def, 0, defaultAsk(def, 0, live.current.extent)));
      return;
    }
    // ⚠⚠ **A TEXT TAKES ONE POINT UNLESS IT IS ALIGNED OR FITTED, AND THE CATALOGUE SAYS ONE.**
    // The count is a property of the JUSTIFICATION rather than of the tool, so it is decided
    // here — the same reading `do_draw` makes on the server, and for the same reason.
    if (d.mode === "text" && TEXT_SPANS.has(Number((d.pre || {}).halign) || 0)
        && pts.length < 2) {
      setDraw({ ...d, pts });
      sayRef.current?.(def.prompts[Math.min(pts.length, def.prompts.length - 1)]);
      return;
    }
    if (def.points > 0 && pts.length >= def.points) {
      // ⚠ A tool that still has typed answers to collect does NOT run yet. TEXT has its point
      // and still needs a height and a string.
      if (def.asks?.length) {
        // ⚠ `pre` rides in — an answer given at the point prompt (TEXT's justification,
        // REVCLOUD's arc length) belongs in the same bag the typed asks fill.
        setDraw({ ...d, pts, ask: 0, got: { ...(d.pre || {}) } });
        setTyped(String(defaultAsk(def, 0, live.current.extent)));
        sayRef.current?.(askPrompt(def, 0, defaultAsk(def, 0, live.current.extent)));
        return;
      }
      const solved = solveGiven(d.mode, givenFor(d.mode, live.current), pts, 0);
      if (typeof solved === "string") {
        // ⚠ A refusal ends the command and says why, which is what AutoCAD does — it does not
        // sit there collecting more points for an arc that cannot exist.
        sayRef.current?.(`  ${solved}`);
        setDraw(null);
        setMode("select");
        return;
      }
      void author(def.kind, solved, { ...(d.ref ? { ref: d.ref } : {}), ...(d.opt || {}) });
      // ⚠ Recorded HERE, from the canonical points that were actually sent, rather than from
      // the picks: several givens solve to an arc whose end is nowhere near the last click.
      setCont(contOf(def.kind, solved));
      // ⚠⚠ **LINE CONTINUES FROM ITS OWN END, WHICH IS WHAT AutoCAD'S LINE DOES.** It is not a
      // polyline — each segment is its own LINE entity — but the gesture runs on until Escape,
      // and a LINE tool that stops after one segment makes a drafter re-pick the corner they
      // are already standing on.
      // ⚠⚠ **POINT STAYS ARMED TOO, AND THE DRAW MENU EVEN CALLS IT "Multiple Point".**
      // AutoCAD's POINT places one per click until Escape. Unlike LINE it does NOT carry the
      // last point forward — each click is its own entity from nothing.
      // ⚠⚠ **CIRCLE's `Multiple` RE-ARMS THE SAME WAY, AND KEEPS THE GIVEN.** BricsCAD's
      // `Multiple circles` draws circle after circle until Escape — a drafter setting out
      // columns. Unlike LINE it does NOT carry the last point forward: each circle has its own
      // centre, and carrying one would put the next circle's centre on the last one's rim.
      const again = d.mode === "line" || d.mode === "point" || !!d.multi;
      setDraw(again ? { mode: d.mode, multi: d.multi,
                        pts: d.mode === "line" ? [p] : [] } : null);
      if (!again) setMode("select");
      else sayRef.current?.(def.prompts[Math.min(1, def.prompts.length - 1)]);
      return;
    }
    // ⚠⚠ **THE SEGMENT INDEX IS THE OLD LENGTH MINUS ONE, NOT THE NEW ONE.** Segment `i` runs
    // from vertex `i` to vertex `i+1`, so the click that just landed closed segment
    // `d.pts.length - 1`. Off by one here puts every arc on the segment after the one the
    // drafter drew — geometry that passes any test counting vertices or bulges.
    const arcs = d.arcing && d.pts.length
      ? [...(d.arcs || []), d.pts.length - 1] : (d.arcs || []);
    setDraw({ ...d, pts, arcs });
    sayRef.current?.(d.arcing ? PROMPTS.arcNext
                              : def.prompts[Math.min(pts.length, def.prompts.length - 1)]);
  }, [author]);

  /** Enter, with FILLET's radius or CHAMFER's distance in the box. */
  const acceptPickScalar = useCallback(() => {
    const s = live.current;
    const c = s.objCmd;
    if (!c) return;
    const def = PICKS[c.name];
    const n = s.typed === "" ? def.scalar?.def ?? 0 : Number(s.typed);
    if (!Number.isFinite(n)) return;
    setObjCmd({ ...c, held: n });
    setTyped("");
    sayRef.current?.(def.prompts[0]);
  }, []);

  /** Enter, during a DRAW. ⚠ Two jobs: it finishes an unbounded POLYLINE, and it accepts the
   *  typed answer a tool is waiting for. Both are what Enter does in AutoCAD. */
  const finishDraw = useCallback(() => {
    const s = live.current;
    const d = s.draw;
    if (!d) return;
    const def = defOf(d.mode, s.dimKind, givenFor(d.mode, s));
    if (!def) return;
    // ⚠⚠ **THE PROMPT HAS BEEN ASKING FOR A COORDINATE SINCE THE COMMAND LINE EXISTED.**
    // `Specify next point:` invites `@3450<30`, and nothing could hear it — `AcadCommandLine`
    // runs every line through `resolveCommand`, so a typed coordinate came back as
    // *Unknown command "@3450<30"*. Same defect as a snap that is lit and does nothing: the
    // app asked a question it could not take the answer to.
    // ⚠ Only while COLLECTING POINTS. Once `ask` is set the tool is after a height or a
    // string, and `100,50` is a legitimate piece of text.
    // ⚠⚠ **BEFORE `parseTyped`, BECAUSE `8,5` IS A CHAMFER PAIR AND NOT A COORDINATE.** Running
    // the coordinate parser first would place a rectangle corner at (8, 5) and swallow the
    // answer to the question actually on screen.
    if (d.optWant) {
      const raw = s.typed.trim();
      const nums = raw.split(/[,\s]+/).filter(Boolean).map(Number);
      // ⚠⚠ **A ROTATION AND AN ELEVATION MAY BE NEGATIVE; A DISTANCE MAY NOT.** -30 degrees and
      // a slab 150 below datum are both ordinary, and refusing them as "not a distance" would
      // make the option useless for half the cases it exists for. A chamfer of -5 is still a
      // typo.
      const signed = d.optWant === "rot" || d.optWant === "elevation";
      if (!raw || !nums.length
          || nums.some((v) => !Number.isFinite(v) || (!signed && v < 0))) {
        sayRef.current?.(signed ? "  that is not a number" : "  that is not a distance");
        return;
      }
      // ⚠⚠ **THE TWO DIMENSION ANSWERS CHAIN AND DO NOT GO IN `opt`.** Length asks width next;
      // width returns to the corner prompt. They live on the draw state because the server never
      // sees them — see `RECT_OPT`.
      // ⚠ Two chained answers, exactly like Dimensions: the AREA, then one side. The other side
      // is `area / side`, which is why a side of zero has to be refused — it is a division by
      // zero that arrives as an Infinity-wide rectangle, drawn and selectable and off the sheet.
      if (d.optWant === "area") {
        if (!(nums[0] > 0)) { sayRef.current?.("  an area has to be more than zero"); return; }
        setDraw({ ...d, dims: { l: 0, w: 0, a: nums[0] }, optWant: "arealen", square: false });
        setTyped("");
        sayRef.current?.(RECT_OPT_PROMPT.arealen);
        return;
      }
      if (d.optWant === "arealen") {
        if (!(nums[0] > 0)) { sayRef.current?.("  a side has to be more than zero"); return; }
        const area = d.dims?.a ?? 0;
        setDraw({ ...d, dims: { l: nums[0], w: area / nums[0] }, optWant: undefined });
        setTyped("");
        sayRef.current?.(def.prompts[Math.min(d.pts.length, def.prompts.length - 1)]);
        return;
      }
      if (d.optWant === "dimlength") {
        setDraw({ ...d, square: false,
                  dims: { l: nums[0], w: (d.dims?.w ?? nums[0]) }, optWant: "dimwidth" });
        setTyped("");
        sayRef.current?.(RECT_OPT_PROMPT.dimwidth);
        return;
      }
      if (d.optWant === "dimwidth") {
        setDraw({ ...d, dims: { l: (d.dims?.l ?? nums[0]), w: nums[0] }, optWant: undefined });
        setTyped("");
        sayRef.current?.(def.prompts[Math.min(d.pts.length, def.prompts.length - 1)]);
        return;
      }
      const opt = { ...(d.opt || {}) };
      // ⚠ AutoCAD's own exclusivity: setting a fillet clears the chamfer and the other way
      // round, because a corner cannot be both and the last thing you said is what you meant.
      if (d.optWant === "fillet") delete opt.chamfer;
      if (d.optWant === "chamfer") delete opt.fillet;
      opt[d.optWant] = d.optWant === "chamfer"
        ? [nums[0], nums.length > 1 ? nums[1] : nums[0]] : nums[0];
      // ⚠ Remembered for the NEXT rectangle too, which is the whole point of the change.
      if (d.mode === "rect") rectOpt.current = opt;
      setDraw({ ...d, opt, optWant: undefined });
      setTyped("");
      sayRef.current?.(def.prompts[Math.min(d.pts.length, def.prompts.length - 1)]);
      return;
    }
    // ⚠⚠ **BEFORE `parseTyped`, FOR THE SAME REASON THE CHAMFER PAIR IS.** `30` at this prompt
    // is an ANGLE; the coordinate parser would read it as a direct distance and place a point.
    // ⚠ A word sub-prompt — SPLINE's `Knots`, REVCLOUD's `Style`. Empty Enter takes the first
    // option, which is what the `<default>` in the prompt promises.
    if (d.subWant) {
      const w = d.subWant;
      const want = s.typed.trim().toLowerCase();
      const hit = !want ? w.opts[0]
                        : w.opts.find(([ab]) => want.startsWith(ab.toLowerCase()));
      if (!hit) {
        sayRef.current?.(`  answer ${w.opts.map(([, v]) => v).join(" or ")}`);
        return;
      }
      setTyped("");
      setDraw({ ...d, subWant: undefined, pre: { ...(d.pre || {}), [w.key]: hit[1] } });
      sayRef.current?.(`  ${w.label} ${hit[1]} — `
                       + `${def.prompts[Math.min(d.pts.length, def.prompts.length - 1)]}`);
      return;
    }
    // ⚠ TEXT's style name. `?` lists what the DRAWING defines — AutoCAD's own option, and it
    // beats a drafter guessing a name and being told it is not one.
    if (d.styleWant) {
      const want = s.typed.trim();
      setTyped("");
      if (want === "?") {
        void (async () => {
          if (!s.id) return;
          try {
            const rep = await loadTextStyles(s.id);
            sayRef.current?.("  " + (rep.styles || [])
              .map((t) => `${t.name}${t.used ? ` (${t.used})` : ""}`).join(", "));
          } catch { sayRef.current?.("  could not read the style table"); }
        })();
        return;                                   // ⚠ still asking — a list is not an answer
      }
      setDraw({ ...d, styleWant: false });
      if (!want) {                                // Enter alone keeps the current style
        sayRef.current?.(def.prompts[Math.min(d.pts.length, def.prompts.length - 1)]);
        return;
      }
      void (async () => {
        if (!s.id) return;
        try {
          const rep = await editDrawing(s.id, "setvar", [], { name: "TEXTSTYLE", value: want });
          // ⚠ The SERVER's answer, not the typed name: it decides whether the drawing defines
          // that style, and echoing the request would report a style that was never set.
          sayRef.current?.(`  TEXTSTYLE = ${rep.value}`);
          // ⚠ Re-read, so the ribbon's style picker and this prompt cannot disagree.
          const got = await loadSysvars(s.id);
          setSysvars(got.vars);
        } catch (e) { fail(e); }
        sayRef.current?.(def.prompts[0]);
      })();
      return;
    }
    // ⚠ SPLINE's `[Fit/CV]`. The sub-prompt owns this keystroke while it is open; an empty
    // answer keeps the tool that is already armed, which is what the `<default>` promises.
    if (d.askMethod) {
      const want = s.typed.trim().toLowerCase();
      if (want && !want.startsWith("f") && !want.startsWith("c")) {
        sayRef.current?.("  answer Fit or CV");
        return;
      }
      const mode = !want ? d.mode : want.startsWith("f") ? "spline" : "splinecv";
      setTyped("");
      setDraw({ ...d, mode, askMethod: false });
      const next = defOf(mode, s.dimKind, "");
      sayRef.current?.(`  ${mode === "spline" ? "Fit" : "CV"} — `
                       + `${next?.prompts[Math.min(d.pts.length, next.prompts.length - 1)] ?? ""}`);
      return;
    }
    // ⚠ REVCLOUD's arc length, answered UP FRONT. It goes in `pre`, not in `got`, because the
    // ask phase has not started — and `pre` is what makes that phase skip itself later.
    if (d.preWant) {
      const raw = s.typed.trim();
      const n = raw === "" || !Number.isFinite(Number(raw))
        ? defaultAsk(def, 0, s.extent) : Number(raw);
      setTyped("");
      setDraw({ ...d, pre: { ...(d.pre || {}), [d.preWant]: n }, preWant: undefined });
      sayRef.current?.(def.prompts[Math.min(d.pts.length, def.prompts.length - 1)]);
      return;
    }
    if (d.angWant) {
      const raw = s.typed.trim();
      const deg = raw === "" ? 0 : Number(raw);
      if (!Number.isFinite(deg)) {
        sayRef.current?.("  that is not an angle");
        return;
      }
      const t = (deg * Math.PI) / 180;
      setDraw({ ...d, angWant: false, dir: { x: Math.cos(t), y: Math.sin(t) } });
      setTyped("");
      sayRef.current?.(d.mode === "ray" ? "  Specify start point:" : "  Specify a point:");
      return;
    }
    if (d.ask == null && s.typed.trim()) {
      const at = Math.min(d.pts.length, def.prompts.length - 1);
      // ⚠⚠ **A TYPED COORDINATE IS IN THE UCS — THAT IS WHAT A UCS IS FOR.** `100,50` at a
      // rotated origin means 100 along THAT x. Everything `parseTyped` sees is mapped in and its
      // answer is mapped back out, so the parser stays pure arithmetic that knows no UCS: the
      // relative, polar and last-point forms then all follow for free, because they are computed
      // from a `last` that is already in the same frame.
      const u = s.ucs;
      const r0 = parseTyped(s.typed, {
        last: d.pts.length ? worldToUcs(d.pts[d.pts.length - 1], u) : null,
        cursor: s.cursor ? worldToUcs(s.cursor, u) : s.cursor,
        keywords: keywordsOf(def.prompts[at] ?? ""),
      });
      const r = r0.kind === "point" ? { ...r0, pt: ucsToWorld(r0.pt, u) } : r0;
      // ⚠⚠ **AFTER `parseTyped`, SO THE COMMAND'S OWN KEYWORD WINS.** At ARC's `[Center]` prompt
      // `CEN` is the Center OPTION and not the centre snap, and AutoCAD resolves it that way:
      // the option is what the prompt is offering. `parseTyped` returns `keyword` for those, so
      // reaching here means the prompt did not claim the word.
      // ⚠ Only for `none` and `error` — a typed `PAR` is not a coordinate, and a real coordinate
      // must never be reinterpreted as a snap name.
      if (r.kind !== "keyword" && r.kind !== "point") {
        const ov = overrideOf(s.typed);
        if (ov !== null) {
          setTyped("");
          setSnapOnce(ov);
          sayRef.current?.(ov === "none" ? "  _non" : `  _${s.typed.trim().toLowerCase()} of`);
          return;
        }
      }
      if (r.kind === "error") {
        // ⚠ Said, not swallowed. `Number("")` is 0, so eating `10,,4` would place a point at
        // (10, 0) — a silent wrong answer, which is the worst of the three outcomes.
        sayRef.current?.(`  ${r.why}`);
        return;
      }
      if (r.kind === "keyword") {
        setTyped("");
        const g = CIRCLE_KEYWORD[r.word];
        // ⚠⚠⚠ **`d.mode === "circle"` WAS MISSING AND EVERY CIRCLE OPTION SAID "NOT BUILT YET".**
        // Typing CIRCLE arms mode `circle`, which hard-wires the centre-radius given; the flyout
        // arms `circv`. Only `circv` was routed here, so the prompt printed
        // `[3P/2P/Ttr (tan tan radius)]` and all three answered *"3P is not built yet"* — on the
        // second-most-used draw command, with the givens built and reachable from the ribbon the
        // whole time. `Diameter` answered nothing at all.
        // ⚠ Measured, not reasoned: `CIRCLE ⏎ 3P ⏎` in the running app. A keyword table that
        // CONTAINS the word tells you nothing about whether anything reads it.
        // ⚠⚠ **`turn Arc into circle` IS A CONVERSION, NOT A GIVEN.** It takes the ARC that is
        // already selected — noun-verb, the same grammar TTR and TTT use and the same one every
        // modify command in this app uses — and replaces it. Nothing is collected afterwards.
        if (r.word === "turn Arc into circle"
            && (d.mode === "circv" || d.mode === "circle")) {
          // ⚠ The selection as it stood when CIRCLE armed — arming clears it, and this option
          // is about an object chosen BEFORE the command.
          const picked = s.sel.length ? s.sel : armSel.current;
          if (!picked.length) {
            // ⚠ Says what to do, not that something is wrong. A prompt that only refuses leaves
            // the drafter guessing which half of the sentence they got wrong.
            sayRef.current?.("  select the arc first, then CIRCLE and A");
            return;
          }
          setDraw(null); setMode("select");
          void (async () => {
            if (!s.id) return;
            try {
              const rep = await editDrawing(s.id, "arc2circle",
                                            picked.map((q) => q.handle), {});
              sayRef.current?.(`  ${rep.converted} arc${rep.converted === 1 ? "" : "s"} `
                               + "turned into a circle");
              await refresh(rep);
            } catch (e) { fail(e); }
          })();
          return;
        }
        // ⚠ `Multiple` is not a given — it changes what happens AFTER the circle is drawn, so it
        // sits beside the given rather than replacing it. Answered for either mode.
        if (r.word === "Multiple" && (d.mode === "circv" || d.mode === "circle")) {
          setDraw({ ...d, multi: true });
          sayRef.current?.("  Multiple — " + (def.prompts[d.pts.length] ?? def.prompts[0]));
          return;
        }
        if (g && (d.mode === "circv" || d.mode === "circle")) {
          const next = CIRCLE_GIVENS[g];
          setCircGiven(g);
          // ⚠⚠ **`Diameter` IS ASKED AFTER THE CENTRE IS DOWN, AND THE CENTRE MUST SURVIVE.**
          // `cd` takes ONE point and then a number, so a centre already picked satisfies it
          // outright — dropping it would make the drafter re-pick the point they are standing
          // on, which is the whole reason the option exists at that step.
          if (next.points > 0 && d.pts.length >= next.points && next.ask) {
            const nd = defOf("circv", s.dimKind, g);
            const dv = nd ? defaultAsk(nd, 0, s.extent) : 0;
            setDraw({ ...d, mode: "circv", pts: d.pts.slice(0, next.points), ask: 0, got: {} });
            setTyped(String(dv));
            sayRef.current?.(nd ? askPrompt(nd, 0, dv) : next.prompts[0]);
            return;
          }
          // ⚠ TTR and TTT come from the SELECTION, not from points — `setCircGiven` routes them
          // through `tanWant` and leaves the draw alone, so this must not stomp on it.
          if (TANGENT_GIVENS.has(g)) { givenRef.current?.(g); return; }
          setDraw({ ...d, mode: "circv", pts: [] });
          sayRef.current?.(next.prompts[0]);
          return;
        }
        // ⚠⚠ **ELLIPSE's `[Arc/Center]`.** `Center` swaps the given in place — the tool stays
        // armed and any point already picked is kept, because at the first prompt there is none.
        // `Arc` is a different TOOL, `ellipsearc`, which asks the same first three points and
        // then a start and an end angle; switching modes rather than givens is what keeps that
        // honest.
        if (d.mode === "ellipse" && (r.word === "Center" || r.word === "Arc")) {
          if (r.word === "Center") {
            setEllGiven("c");
            sayRef.current?.(ELLIPSE_GIVENS.c.prompts[
              Math.min(d.pts.length, ELLIPSE_GIVENS.c.prompts.length - 1)]);
          } else {
            const arc = defOf("ellipsearc", s.dimKind, "");
            setDraw({ ...d, mode: "ellipsearc" });
            sayRef.current?.(arc ? arc.prompts[Math.min(d.pts.length, arc.prompts.length - 1)]
                                 : "Specify axis endpoint of elliptical arc:");
          }
          return;
        }
        // ⚠⚠ **ARC HAS ADVERTISED `[Center]` AND `[Center/End]` SINCE `ARC_GIVENS` WAS
        // WRITTEN, AND NOTHING ANSWERED THEM.** The 31 keywords removed from `DRAWS` were
        // removed for exactly this reason; these two survived the trim because they live in
        // `givens.ts`, which was not looked at. The givens behind them — `cse`, `sce`, `sed` —
        // have been built the whole time, so this is wiring, not new geometry.
        // ⚠⚠⚠ **THE SAME DEFECT CIRCLE HAD, AND THE OPTION SWEEP FOUND IT THE FIRST TIME IT
        // RAN.** Typing ARC arms mode `arc`; the flyout arms `arcv`; only `arcv` was routed
        // here, so ARC's `[Center]` printed and answered *"Center is not built yet"* — with
        // `cse` built in `ARC_GIVENS` the whole time. `arc` and `arc3` ARE the `3p` given and
        // `arcctr` IS `cse`; `givenOf` has said so since it was written, and this line did not
        // ask it.
        const gnow = d.mode === "arcv" ? (givenFor("arcv", s) || "3p")
                   : d.mode === "arc" || d.mode === "arc3" ? "3p"
                   : d.mode === "arcctr" ? "cse" : "";
        const to = gnow ? ARC_KEYWORD[`${gnow}:${at}:${r.word}`] : undefined;
        if (to) {
          setArcGiven(to);
          const next = ARC_GIVENS[to];
          // ⚠ The MODE moves to `arcv` as well, or `givenFor` keeps reporting the hard-wired
          // given of the mode we came from and the next prompt is the one we just left.
          // ⚠ The points already picked are KEPT: `[Center/End]` is offered at the SECOND step,
          // when a start point is down, and dropping it is the one thing that makes the option
          // worse than not having it.
          setDraw({ ...d, mode: "arcv" });
          sayRef.current?.(next.prompts[Math.min(d.pts.length, next.prompts.length - 1)]);
          return;
        }
        // ⚠ AutoCAD's `U` steps ONE point back and stays in the command — it is not the drawing
        // undo, and routing it there would roll back the last thing COMMITTED instead.
        // ⚠⚠ **`Select` PICKS A DIFFERENT BASE DIMENSION**, which is what AutoCAD's own
        // DIMCONTINUE offers and what a drafter needs when a run of dimensions restarts on the
        // far side of an opening. Until now the base could only be the selection, so continuing
        // from a different one meant ending the command, picking, and starting again.
        if (r.word === "Select" && (d.mode === "dimcont" || d.mode === "dimbase")) {
          setBasePick(true);
          sayRef.current?.("Select base dimension:");
          return;
        }
        if (r.word === "Undo") {
          if (!d.pts.length) { sayRef.current?.("  nothing to undo — no point has been picked"); return; }
          const back = d.pts.slice(0, -1);
          // ⚠ And forget the arc on the segment being undone, or a straight re-pick keeps the
          // bulge from the arc that used to be there.
          setDraw({ ...d, pts: back, arcs: (d.arcs || []).filter((i) => i < back.length - 1) });
          sayRef.current?.(def.prompts[Math.min(back.length, def.prompts.length - 1)]);
          return;
        }
        // ⚠⚠ **PLINE's `A` DOES NOT PLACE ANYTHING — IT CHANGES WHAT THE NEXT CLICK MEANS.**
        // That is why it flips a flag on the draw state rather than running anything, and why
        // `L` has to exist beside it: without a way back, one arc turns the whole rest of the
        // polyline into arcs, which is a trap rather than a mode.
        if ((r.word === "Arc" || r.word === "Line") && def.points < 0) {
          if (r.word === "Arc" && !d.pts.length) {
            sayRef.current?.("  an arc needs a direction to leave in — pick the start point "
                             + "first");
            return;
          }
          const on = r.word === "Arc";
          setDraw({ ...d, arcing: on });
          sayRef.current?.(on ? PROMPTS.arcNext
                              : PROMPTS.polyNext);
          return;
        }
        // ⚠ RECTANG's three options all ASK for a number and then come back to the corner
        // prompt — they set the command up, they do not draw anything. `opt` rides along to the
        // server, where `_rect_corners` does the geometry.
        // ⚠⚠ **XLINE AND RAY: `[Horizontal/Vertical]` NEEDS NO SECOND POINT.** The direction is
        // the answer, so one click finishes the command — which is what BricsCAD does and why a
        // drafter types H before every gridline. `dir` rides on the draw state and
        // `onDrawPoint` synthesises the through point from it.
        if ((r.word === "Horizontal" || r.word === "Vertical")
            && (d.mode === "guide" || d.mode === "ray")) {
          setDraw({ ...d, bisect: false,
                    dir: r.word === "Horizontal" ? { x: 1, y: 0 } : { x: 0, y: 1 } });
          sayRef.current?.(d.mode === "ray" ? "  Specify start point:" : "  Specify a point:");
          return;
        }
        // ⚠ `Angle` asks for the number FIRST and the point second, which is BricsCAD's order and
        // the useful one: the angle is the thing being reused across a run of gridlines.
        if (r.word === "Angle" && (d.mode === "guide" || d.mode === "ray")) {
          setDraw({ ...d, angWant: true, bisect: false, dir: undefined });
          sayRef.current?.("  Angle of xline (0):");
          return;
        }
        // ⚠ `Bisect` clears `dir`: a leftover direction would synthesise the second point at the
        // FIRST pick and finish the command before the two legs were ever asked for.
        if (r.word === "Bisect" && (d.mode === "guide" || d.mode === "ray")) {
          setDraw({ ...d, bisect: true, dir: undefined, angWant: false, pts: [] });
          sayRef.current?.("  Specify angle vertex point:");
          return;
        }
        // ⚠⚠ **`Parallel` — THE ONE XLINE OPTION THAT NEEDS AN OBJECT, WHICH IS WHY IT WAS THE
        // LAST OF THE FIVE.** The other four are answered from the keyboard; this one makes the
        // next CLICK an argument rather than a point, the way TRIM's click is.
        if (r.word === "Parallel" && (d.mode === "guide" || d.mode === "ray")) {
          setDraw({ ...d, parWant: true, bisect: false, angWant: false, dir: undefined, pts: [] });
          sayRef.current?.("  Select a line to be parallel to:");
          return;
        }
        // ⚠⚠ **SPLINE's `Knots` AND REVCLOUD's `Style` — TWO WORD SUB-PROMPTS, ONE MECHANISM.**
        // ⚠ `Knots` really does change the curve, which I claimed it did not before measuring:
        // through the same five points the run's midpoint lands at (300.0, 400.0) uniform,
        // (369.7, 331.9) chord and (334.3, 358.9) square-root chord.
        if (r.word === "Knots" && (d.mode === "spline" || d.mode === "splinecv")) {
          setDraw({ ...d, subWant: { key: "knots", label: "knots",
                                     opts: [["c", "chord"], ["sq", "sqrt_chord"],
                                            ["u", "uniform"]] } });
          sayRef.current?.("  Enter knot parameterization [Chord/SQrt chord/Uniform] <Chord>:");
          return;
        }
        if (r.word === "Style" && d.mode === "revcloud") {
          setDraw({ ...d, subWant: { key: "style", label: "style",
                                     opts: [["n", "normal"], ["c", "calligraphy"]] } });
          sayRef.current?.("  Enter cloud style [Normal/Calligraphy] <Normal>:");
          return;
        }
        // ⚠⚠ **REVCLOUD's AND SPLINE's `Entity` — BOTH CONVERSIONS, BOTH NOUN-VERB.** They read
        // `armSel` for the same reason CIRCLE's three do: arming the tool clears the selection,
        // so a typed `REVCLOUD ⏎ E ⏎` would otherwise ask for an object the drafter had just
        // picked. See the note on `armSel`.
        if (r.word === "Entity" && (d.mode === "revcloud" || d.mode === "spline"
                                    || d.mode === "splinecv")) {
          const picked = s.sel.length ? s.sel : armSel.current;
          const what = d.mode === "revcloud" ? "the object to draw the cloud round"
                                             : "the polyline to turn into a spline";
          if (!picked.length) {
            sayRef.current?.(`  select ${what} first, then this command and E`);
            return;
          }
          const op: EditOp = d.mode === "revcloud" ? "revcloudentity" : "splineentity";
          setDraw(null); setMode("select");
          void (async () => {
            if (!s.id) return;
            try {
              // ⚠ The METHOD rides along: SPLINE's Entity makes a curve THROUGH the corners
              // and SPLINECV's makes one PULLED by them. Converting under one name and drawing
              // the other looks like a rounding error rather than a wrong reading.
              const rep = await editDrawing(s.id, op, picked.map((q) => q.handle),
                                            { ...(d.pre || {}),
                                              ...(d.mode === "splinecv" ? { method: "cv" }
                                                                        : {}) });
              const n = rep.clouded ?? rep.converted ?? 0;
              sayRef.current?.(`  ${n} object${n === 1 ? "" : "s"} converted`);
              await refresh(rep);
            } catch (e) { fail(e); }
          })();
          return;
        }
        // ⚠⚠ **SPLINE's `[Method]` — Fit against CV, which are two DIFFERENT OBJECTS to edit for
        // the rest of the drawing's life.** `spline` places the points the curve passes THROUGH,
        // `splinecv` the points that pull it; a drafter who picks the wrong one gets grips that
        // do not do what they expect. Both have been built all along and the prompt never said so.
        if (r.word === "Method" && (d.mode === "spline" || d.mode === "splinecv")) {
          setDraw({ ...d, askMethod: true });
          sayRef.current?.(`  Enter spline creation method [Fit/CV] `
                           + `<${d.mode === "spline" ? "Fit" : "CV"}>:`);
          return;
        }
        // ⚠⚠ **TEXT's THREE JUSTIFICATIONS.** They need nothing but the keystroke: the point
        // that follows becomes the ALIGNMENT point rather than the insert, which is what
        // justification IS in DXF. `pre` carries the pair into the draw spec the same way
        // REVCLOUD's arc length does.
        // ⚠ `Middle` is halign 4 with NO valign — AutoCAD's MIDDLE is its own justification,
        // centred both ways, not "centre horizontally plus middle vertically". Sending (1, 2)
        // for it puts the text half a character-height out.
        // ⚠⚠ **`use defined Style` SETS THE SAME `TEXTSTYLE` THE RIBBON'S PICKER WRITES.** A
        // second place to choose a font is a second place for the two to disagree about which
        // one the next label gets — and a drafter who set it here and saw the ribbon still
        // showing the old name would trust neither.
        if (r.word === "use defined Style" && d.mode === "text") {
          setDraw({ ...d, styleWant: true });
          const now = (s.sysvars ?? []).find((v) => v.name === "TEXTSTYLE")?.value;
          sayRef.current?.(`  Enter style name or [?] <${now ?? "Standard"}>:`);
          return;
        }
        if (TEXT_JUSTIFY[r.word] && (d.mode === "text")) {
          const [ha, va] = TEXT_JUSTIFY[r.word];
          setDraw({ ...d, pre: { ...(d.pre || {}), halign: ha, valign: va } });
          sayRef.current?.(TEXT_SPANS.has(ha)
            ? "  Specify the start of the line the text spans:"
            : `  Specify ${r.word.split(" ")[0].toLowerCase()} point of text:`);
          return;
        }
        // ⚠ REVCLOUD's two gestures, NAMED. They were already both reachable — by how many
        // points happened to arrive — which is not the same as being offered.
        // ⚠⚠ **`Freehand` IS THE ONLY GESTURE IN THIS APP THAT IS NOT A RUN OF PICKS.** Hold the
        // button and the cloud follows the pointer; let go and it closes. That is why the canvas
        // needed `onTrail` — nothing else here reads a stroke.
        if (r.word === "Freehand" && d.mode === "revcloud") {
          setDraw({ ...d, freehand: true, shape: undefined, pts: [] });
          sayRef.current?.("  Hold the left button and trace the cloud; let go to close it.");
          return;
        }
        if ((r.word === "Rectangular" || r.word === "Polygonal") && d.mode === "revcloud") {
          const shape = r.word === "Rectangular" ? "rect" as const : "poly" as const;
          setDraw({ ...d, shape, pts: [] });
          sayRef.current?.(shape === "rect" ? "  Specify first corner of the cloud:"
                                            : "  Specify first point of the cloud:");
          return;
        }
        // ⚠ `Arc length` up front. The value lands in `pre` and the post-point ask is then
        // SKIPPED — asking twice for the same number is how an option becomes a nuisance.
        if (r.word === "Arc length" && d.mode === "revcloud") {
          setDraw({ ...d, preWant: "arc" });
          setTyped(String(defaultAsk(def, 0, s.extent)));
          sayRef.current?.(askPrompt(def, 0, defaultAsk(def, 0, s.extent)));
          return;
        }
        // ⚠⚠ **`Square` TAKES NO NUMBER, WHICH IS WHY IT IS NOT IN `RECT_OPT`.** It is a mode:
        // the second click still chooses a direction and a size, and the LONGER of its two
        // offsets becomes both sides — AutoCAD's own reading, and the one that lets a drafter
        // drag a square out rather than type one.
        // ⚠ It clears `dims`, and `Dimensions` clears it back. A rectangle cannot be both a
        // stated length-by-width and a square, and the last thing said is what was meant — the
        // same exclusivity Chamfer and Fillet already have.
        if (r.word === "Square" && d.mode === "rect") {
          setDraw({ ...d, square: true, dims: undefined });
          sayRef.current?.("  Square — " + (def.prompts[d.pts.length] ?? def.prompts[0]));
          return;
        }
        if (RECT_OPT[r.word] && d.mode === "rect") {
          const k = RECT_OPT[r.word];
          setDraw({ ...d, optWant: k });
          sayRef.current?.(RECT_OPT_PROMPT[k]);
          return;
        }
        // ⚠ `closed` is a field `do_draw` has taken since it was written (`spec["closed"]`), so
        // this is the polyline the server already knows how to make — not a ring of segments
        // with a duplicate last point, which is what "close it myself" would have produced and
        // which OFFSET and TRIM both treat differently.
        if (r.word === "Close" && def.points < 0) {
          if (d.pts.length < 3) {
            sayRef.current?.("  a closed polyline needs at least three points");
            return;
          }
          void author(def.kind, d.pts, { closed: true,
                                         ...(d.arcs?.length ? { arcs: d.arcs } : {}),
                                         ...(d.opt || {}) });
          setDraw(null);
          setMode("select");
          return;
        }
        sayRef.current?.(`  ${r.word} is not built yet`);
        return;
      }
      if (r.kind === "point") {
        setTyped("");
        onDrawPoint(r.pt);
        return;
      }
      // ⚠⚠⚠ **`CIRCLE ⏎ 200,200 ⏎ 150 ⏎` DREW NOTHING AND SAID NOTHING — for want of a mouse.**
      // A bare number is DIRECT DISTANCE ENTRY, which needs a direction, and `parseTyped`
      // answers `number` rather than `point` when there is no cursor to take one from. Every
      // branch below then declines it and the tail of this function sets `draw` to null: the
      // command ended, silently, having drawn nothing. Measured with the pointer parked off the
      // canvas — 0 of 20 CIRCLEs committed there and 20 of 20 with the pointer over it, which
      // is not a distinction AutoCAD makes and not one a drafter would guess.
      // ⇒ At a RADIUS prompt the direction is meaningless — a circle is the same circle
      // whichever way the radius points — so the number IS the answer. Anywhere else it still
      // needs a direction, and this says so instead of throwing the command away.
      if (r.kind === "number") {
        const ask0 = def.prompts[at] ?? "";
        if (/radius/i.test(ask0) && d.pts.length) {
          const base = d.pts[d.pts.length - 1];
          setTyped("");
          onDrawPoint({ x: base.x + r.value, y: base.y });
          return;
        }
        sayRef.current?.("  a bare number is a distance from the last point — move the pointer "
                         + "to give it a direction, or type x,y");
        return;
      }
      // ⚠⚠⚠ **A WORD AT A POINT PROMPT KILLED THE COMMAND WITHOUT A SOUND.** `parseTyped`
      // answers `none` for anything that is neither a keyword, a coordinate nor a number — and
      // every branch below declines it, so the tail of this function set `draw` to null and the
      // tool was simply gone. POINT repeats like AutoCAD's does, so a burst of them typed
      // `POINT` at its own `Specify a point:` prompt: 10 of 20 committed and the other 10 ended
      // the tool and then leaked their coordinates to the command line as *Unknown command
      // "50370,50230"*. AutoCAD's answer is a sentence and the prompt again.
      if (r.kind === "none") {
        sayRef.current?.("  Point or option keyword required.");
        sayRef.current?.(def.prompts[at] ?? "");
        setTyped("");
        return;
      }
    }
    // ⚠⚠ **AN UNBOUNDED TOOL THAT ALSO ASKS FOR TEXT NEEDS ENTER TWICE, AND THE FIRST ONE MUST
    // NOT COMMIT.** A LEADER takes points until Enter and THEN takes its note; treating the
    // first Enter as "run it" drew a leader with no text at all and lost the note the drafter
    // was about to type.
    if (def.points < 0 && def.asks?.length && d.ask == null) {
      if (d.pts.length < 2) { sayRef.current?.("  a leader needs at least two points"); return; }
      // ⚠ A POLYGONAL cloud is not a rectangle: two points name a diagonal, and closing a
      // polygon through two of them gives a line. Named gestures get held to their names.
      if (d.mode === "revcloud" && d.shape === "poly" && d.pts.length < 3) {
        sayRef.current?.("  a polygonal cloud needs at least three points");
        return;
      }
      // ⚠⚠ **EVERY ASK ALREADY ANSWERED UP FRONT MEANS THERE IS NOTHING TO ASK.** Asking again
      // for the arc length the drafter just typed is how BricsCAD's order becomes a nuisance
      // instead of a convenience.
      if (def.asks.every((k) => (d.pre || {})[k.key] !== undefined)) {
        setDraw(null); setTyped(""); setMode("select");
        void author(def.kind, d.pts, { ...(d.pre || {}), ...(d.ref ? { ref: d.ref } : {}) });
        return;
      }
      setDraw({ ...d, ask: 0, got: { ...(d.pre || {}) } });
      setTyped("");
      sayRef.current?.(askPrompt(def, 0, defaultAsk(def, 0, s.extent)));
      return;
    }
    if (def.asks?.length && d.ask != null) {
      const ask = def.asks[d.ask];
      const raw = s.typed;
      const val: unknown = ask.kind === "number"
        ? (raw === "" || !Number.isFinite(Number(raw)) ? defaultAsk(def, d.ask, s.extent) : Number(raw))
        : raw;
      const got = { ...(d.got || {}), [ask.key]: val };
      const next = d.ask + 1;
      if (next < def.asks.length) {
        setDraw({ ...d, ask: next, got });
        setTyped(ask.kind === "number" ? "" : String(defaultAsk(def, next, s.extent)));
        sayRef.current?.(askPrompt(def, next, defaultAsk(def, next, s.extent)));
        return;
      }
      // ⚠ The ask path commits too — CIRCLE's `Diameter` ends here rather than in `onDrawPoint`,
      // and `Multiple` has to mean the same thing whichever way the circle was specified.
      setDraw(d.multi ? { mode: d.mode, multi: true, pts: [] } : null);
      setTyped("");
      if (!d.multi) setMode("select");
      // ⚠⚠ **A FLYOUT GIVEN'S TYPED ANSWER IS PART OF THE GEOMETRY, NOT A PROPERTY.** "Start,
      // Centre, Angle" sends three POINTS to the server; the 90 the drafter typed has already
      // been spent working out where the third one is. Passing it on as well would put an
      // `a: 90` in the draw spec that `do_draw` has never heard of.
      const g = givenFor(d.mode, s);
      if (g) {
        const solved = solveGiven(d.mode, g, d.pts, Number(val) || 0);
        if (typeof solved === "string") { sayRef.current?.(`  ${solved}`); return; }
        void author(def.kind, solved);
        return;
      }
      // ⚠ MTEXT's last answer is its HEIGHT, and then the editor opens — nothing is drawn yet.
      // See `newMtext`: an empty editor closes without leaving an object, which is AutoCAD's
      // answer and the server's too (it refuses an MTEXT with no text).
      if (def.kind === "MTEXT" && !("text" in got)) {
        newMtext(d.pts, Number(got.height) || 0);
        return;
      }
      void author(def.kind, d.pts, { ...got, ...(d.ref ? { ref: d.ref } : {}) });
      return;
    }
    if (def.points < 0 && d.pts.length >= 2) {
      // ⚠⚠ **`pre` RIDES HERE TOO, AND IT DID NOT.** An answer given at the POINT prompt —
      // SPLINE's `Knots`, REVCLOUD's arc length and `Style` — is collected before any point is
      // picked, and an unbounded tool with no `asks` commits down this path. It was dropped, so
      // `Knots` was accepted, echoed and thrown away: chord and uniform drew the identical
      // curve and the option looked built.
      void author(def.kind, d.pts, { ...(d.arcs?.length ? { arcs: d.arcs } : {}),
                                     ...(d.pre || {}), ...(d.opt || {}) });
    }
    setDraw(null);
    setMode("select");
  }, [author, onDrawPoint]);

  /** HATCH or HATCHEDIT. ⚠ ONE dialog: which one it is is entirely determined by whether the
   *  selection is hatches or boundaries, so asking would be asking a question the app can
   *  already answer. */
  /** GROUP, UNGROUP, GROUPEDIT, PICKSTYLE, ATTDEF and ATTSYNC.
   *
   * ⚠ Six commands with one entry point because each is a short prompt over the selection, and
   * six near-identical callbacks is how two of them come to disagree about what a group is. */
  const runGroupish = useCallback(async (name: string) => {
    const s = live.current;
    if (!s.id) { sayRef.current?.("  No drawing is open."); return; }
    if (name === "pickstyle") {
      // ⚠ AutoCAD's PICKSTYLE — whether clicking one member selects the whole group. A real
      // toggle over a real flag; the drawing stores it per group in code 71.
      setGroupPick((v) => {
        sayRef.current?.(`  group selection ${v ? "off" : "on"}`);
        return !v;
      });
      return;
    }
    try {
      if (name === "group") {
        if (s.sel.length < 2) { sayRef.current?.("  Select at least two objects to group.");
                                return; }
        const rep = await editDrawing(s.id, "group", s.sel.map((p) => p.handle), {});
        sayRef.current?.(`  ${rep.grouped ?? 0} objects grouped as ${rep.name}`);
      } else if (name === "ungroup") {
        const g = (await loadGroups(s.id)).groups
          .find((x) => s.sel.some((p) => x.members.includes(p.handle)));
        if (!g) { sayRef.current?.("  Select an object that is in a group."); return; }
        const rep = await editDrawing(s.id, "ungroup", [], { handle: g.handle });
        // ⚠ Says what it did NOT do, because that is the surprising half: the objects stay.
        sayRef.current?.(`  group ${rep.name} removed — its ${g.members.length} objects are `
                         + "unchanged");
      } else if (name === "groupedit") {
        const g = (await loadGroups(s.id)).groups
          .find((x) => s.sel.some((p) => x.members.includes(p.handle)));
        if (!g) { sayRef.current?.("  Select an object that is in a group."); return; }
        const add = s.sel.filter((p) => !g.members.includes(p.handle)).map((p) => p.handle);
        if (!add.length) { sayRef.current?.("  every selected object is already in that group");
                           return; }
        const rep = await editDrawing(s.id, "groupedit", add,
                                      { handle: g.handle, mode: "add" });
        sayRef.current?.(`  ${rep.changed ?? 0} added — ${g.name} now has ${rep.members}`);
      } else if (name === "attdef") {
        const ins = s.sel.find((p) => p.kind === "INSERT");
        if (!ins) { sayRef.current?.("  Select a block reference to define an attribute on.");
                    return; }
        // ⚠ AutoCAD's own command-line ATTDEF order: the tag, then the prompt, then the
        // default, then where it sits. Asked one at a time in the dynamic-input box, which is
        // the same machinery PEDIT's options and SETVAR use.
        setAttWant({ handle: ins.handle, step: "tag", tag: "", prompt: "", def: "" });
        setTyped("");
        sayRef.current?.("Enter attribute tag name:");
        return;
      } else {
        const ins = s.sel.find((p) => p.kind === "INSERT");
        if (!ins) { sayRef.current?.("  Select a block reference to synchronise."); return; }
        const rep = await editDrawing(s.id, "attsync", [], { handle: ins.handle });
        sayRef.current?.(`  ${rep.synced ?? 0} reference(s) synchronised — `
                         + `${rep.added ?? 0} attribute(s) added, ${rep.removed ?? 0} removed, `
                         + `${rep.kept ?? 0} value(s) kept`);
      }
      await refresh();
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** Enter, during ATTDEF's prompt chain. */
  const acceptAtt = useCallback(() => {
    const s = live.current;
    const w = s.attWant;
    if (!w) return;
    const typed = s.typed.trim();
    setTyped("");
    if (w.step === "tag") {
      if (!typed) { sayRef.current?.("  an attribute needs a tag"); return; }
      setAttWant({ ...w, tag: typed.toUpperCase(), step: "prompt" });
      sayRef.current?.("Enter attribute prompt:");
      return;
    }
    if (w.step === "prompt") {
      setAttWant({ ...w, prompt: typed, step: "def" });
      sayRef.current?.("Enter default attribute value:");
      return;
    }
    setAttWant(null);
    setAttPlace({ handle: w.handle, tag: w.tag, prompt: w.prompt, def: typed });
    sayRef.current?.("Specify start point of attribute:");
  }, []);

  /** The click that places a new attribute definition. */
  const onAttPoint = useCallback(async (p: Pt) => {
    const s = live.current;
    const w = s.attPlace;
    setAttPlace(null);
    if (!s.id || !w) return;
    try {
      const rep = await editDrawing(s.id, "attdef", [], {
        handle: w.handle, tag: w.tag, prompt: w.prompt, default: w.def,
        point: [p.x, p.y, 0], layer: s.drawLayer });
      // ⚠ Says what it did NOT do, because AutoCAD does not either and the difference surprises
      // people: a new definition appears on the NEXT insertion, and existing copies need
      // ATTSYNC. Discovering that by finding forty unchanged blocks is worse than being told.
      sayRef.current?.(`  ${rep.tag} defined on ${rep.block} — ${rep.attributes} attribute(s). `
                       + "Run ATTSYNC to add it to blocks already placed.");
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** SETVAR, or a variable typed as a command. ⚠ AutoCAD prints the CURRENT value in the
   *  prompt — `Enter new value for LTSCALE <1.0000>:` — because that is usually the question
   *  being asked, and a prompt that does not say it forces a second command to find out. */
  const openVar = useCallback(async (name: string, then?: string) => {
    const s = live.current;
    if (!s.id) { sayRef.current?.("  No drawing is open."); return; }
    try {
      const got = await loadSysvars(s.id);
      setSysvars(got.vars);
      if (!name) {
        setVarWant({ name: "", now: null });
        setTyped("");
        sayRef.current?.("Enter variable name or [?]:");
        return;
      }
      const row = got.vars.find((v) => v.name === name.toUpperCase());
      if (!row) { sayRef.current?.(`  ${name.toUpperCase()} is not a variable this app reads`);
                  return; }
      setVarWant({ name: row.name, now: row.value, then });
      setTyped("");
      sayRef.current?.(`Enter new value for ${row.name} <${String(row.value)}>:`
                       + `\n  ${row.reads}`);
    } catch (e) { fail(e); }
  }, [fail]);

  /** Enter, at a SETVAR prompt. ⚠ Two stages: the NAME, then the VALUE. */
  const acceptVar = useCallback(() => {
    const s = live.current;
    const w = s.varWant;
    if (!w) return;
    const typed = s.typed.trim();
    if (!w.name) {
      setTyped("");
      if (typed === "?") {
        // ⚠ AutoCAD's own `?` option. Listing what is honoured beats a drafter guessing at a
        // name and being told it is not one.
        sayRef.current?.("  " + (s.sysvars ?? []).map((v) => `${v.name} = ${v.value}`)
                                  .join("\n  "));
        setVarWant(null);
        return;
      }
      void openVar(typed);
      return;
    }
    setVarWant(null);
    setTyped("");
    // ⚠ Enter alone keeps the current value — and still moves on to the next question, because
    // ELEV asks two and a drafter who keeps the elevation still wants to be asked the thickness.
    if (typed === "") { if (w.then) void openVar(w.then); return; }
    const row = (s.sysvars ?? []).find((v) => v.name === w.name);
    const value: string | number = typeof row?.default === "string" ? typed : Number(typed);
    if (typeof value === "number" && !Number.isFinite(value)) {
      sayRef.current?.(`  ${typed} is not a number`);
      return;
    }
    // ⚠⚠ **NOT THROUGH `commit`, WHICH BAILS ON AN EMPTY SELECTION.** Every other op acts on
    // picked objects, so that guard is right for them and wrong for this one: SETVAR changes
    // the DOCUMENT, and requiring something to be selected first made it silently do nothing.
    void (async () => {
      if (!s.id) return;
      try {
        const rep = await editDrawing(s.id, "setvar", [], { name: w.name, value });
        sayRef.current?.(`  ${rep.name} = ${rep.value}  (was ${rep.was ?? "unset"})`);
        // ⚠⚠ **APPLIED HERE, NOT ON THE NEXT OPEN.** A drafter who types `PICKBOX 3` expects
        // the next click to use it. The server says which store the variable came from; the
        // user-scoped ones go straight into the canvas, and SNAPUNIT into the grid step.
        if ((rep as { where?: string }).where === "user") {
          applyMine({ [String(rep.name)]: Number(rep.value) });
        } else if (String(rep.name) === "SNAPUNIT") {
          setSnapUnit(Number(rep.value) || SNAP_UNIT);
        } else if (String(rep.name) === "LWDISPLAY") {
          // ⚠ Same rule as PICKBOX above: a drafter who types `LWDISPLAY 1` expects the pen
          // weights to appear, not to appear after a reload — and the status-bar button has to
          // light with them, or the drawing and the button disagree about the same fact.
          setTog((t) => ({ ...t, lwt: !!Number(rep.value) }));
        }
        // ⚠⚠⚠ **THE CACHED LIST IS WHAT EVERY OTHER READER SEES, AND IT WAS NEVER UPDATED.**
        // `sysvars` is fetched once when the drawing opens; SETVAR wrote the variable and left
        // the list alone, so a drafter who typed `HPSCALE 40` got a HATCH dialog still offering
        // the value from page load. Same for TEXTSTYLE and UCSFOLLOW, which read it too.
        // Seen in a screenshot: HPSCALE set to 40, the dialog opening at the old one.
        setSysvars((rows) => (rows ?? []).map((v) => (
          v.name === String(rep.name)
            ? { ...v, value: rep.value as SysVar["value"], set: true }
            : v)));
        await refresh(rep);            // ⚠ WITH the report: most variables keep the picture
        if (w.then) void openVar(w.then);
      } catch (e) { fail(e); }
    })();
  }, [openVar, refresh, fail]);

  /** Finish a SELECT gesture and report what it caught, in AutoCAD's own words. */
  /** The empty Enter that ENDS `Select objects:` — and runs the verb that was waiting on it.
   *
   * ⚠⚠⚠ **IT USED TO ANSWER "Invalid option keyword."** The prompt renders a `DynIn` which takes
   * the focus, and its `onEnter` handed whatever was typed to `selectOption` UNCONDITIONALLY — so
   * an empty Enter, which is how a drafter closes a selection, was read as an option keyword of
   * `""`. The window-level handler further down has always had the right branch; it simply never
   * got the keystroke. ⇒ **verb-first was unreachable for every modify command**: type EXPLODE,
   * pick the objects, press Enter, and the app printed an error and asked again. Found by
   * `qa/bench_actions.py` needing a drafter-sized EXPLODE.
   * ⚠ Shared by both callers rather than repeated, because the two drifting apart is precisely
   * how one of them came to be missing this.
   */
  const closeSelect = useCallback(() => {
    const w = live.current.selWant;
    setSelWant(null);
    const n = live.current.sel.length;
    sayRef.current?.(`  ${n} found`);
    // ⚠⚠ **AN EMPTY ENTER ENDS THE COMMAND, IT DOES NOT RUN IT ON NOTHING.** AutoCAD's ERASE
    // with no objects picked answers and stops; running a MOVE over an empty selection would ask
    // for a base point for a set that does not exist.
    if (w?.then && n) runCmdRef.current?.(w.then[0], w.then[1]);
  }, []);

  const applySelect = useCallback((picks: Pick[]) => {
    // ⚠⚠⚠ **THE PENDING COMMAND SURVIVES THE OPTION.** `selectThen` parks the verb in
    // `selWant.then` and the empty Enter below runs it — but every keyword option used to clear
    // `selWant` on its way out, so by the time the drafter pressed Enter there was nothing left
    // holding the command. `EXPLODE` then `L` answered "1 found" and then nothing, forever, and
    // the same was true of Window, Crossing, Fence, WPolygon, CPolygon, ALL and Previous: eight
    // of the nine ways of answering `Select objects:` lost the verb, and only clicking objects
    // one at a time worked. ⇒ keep it, and re-prompt — which is also what AutoCAD does, because
    // a selection stays open for more picks until an empty Enter closes it.
    const pend = live.current.selWant?.then;
    setSelWant(pend ? { mode: null, pts: [], then: pend } : null);
    setSel((c) => {
      // ⚠ AutoCAD's SELECT ADDS to the set rather than replacing it — that is what makes
      // "window this corner, then fence those pipes" one selection instead of two.
      prevSel.current = c;
      const by = new Map(c.map((p) => [p.handle, p]));
      for (const p of picks) by.set(p.handle, p);
      return [...by.values()];
    });
    sayRef.current?.(`  ${picks.length} found`);
    if (pend) sayRef.current?.(SELECT_PROMPT);
  }, []);

  /** One click during a SELECT gesture. */
  const onSelectPoint = useCallback((p: Pt) => {
    const w = live.current.selWant;
    if (!w?.mode) return;
    const pts = [...w.pts, p];
    const m = w.mode;
    if (m.points === 2 && pts.length === 2) {
      // ⚠ W and C are the drag gesture with the direction FORCED, which is the whole reason
      // they exist as options: a drafter who needs a crossing but must drag left-to-right past
      // an obstacle types C and drags whichever way they like.
      const poly = [pts[0].x, pts[0].y, pts[1].x, pts[0].y, pts[1].x, pts[1].y, pts[0].x, pts[1].y];
      applySelect(api.current?.selectPoly(poly, m.label === "Window" ? "wpoly" : "cpoly") ?? []);
      return;
    }
    setSelWant({ mode: m, pts });
    sayRef.current?.(m.prompts[Math.min(pts.length, m.prompts.length - 1)] ?? "");
  }, [applySelect]);

  /** Enter, during a SELECT gesture — closes the fence or the polygon. */
  const finishSelect = useCallback(() => {
    const w = live.current.selWant;
    if (!w?.mode) { setSelWant(null); return; }
    const m = w.mode;
    const need = m.label === "Fence" ? 2 : 3;
    if (w.pts.length < need) {
      sayRef.current?.(`  a ${m.label.toLowerCase()} needs at least ${need} points`);
      setSelWant(null);
      return;
    }
    const poly = w.pts.flatMap((q: Pt) => [q.x, q.y]);
    const how = m.label === "Fence" ? "fence" : m.label === "WPolygon" ? "wpoly" : "cpoly";
    applySelect(api.current?.selectPoly(poly, how) ?? []);
  }, [applySelect]);

  /** The letter typed at "Select objects:". ⚠ Four of the eight need no picks at all. */
  const selectOption = useCallback((typed: string) => {
    const s = live.current;
    const m = selectModeOf(typed);
    if (!m) {
      // ⚠ AutoCAD's own wording. A silent re-prompt reads as a keystroke that was dropped.
      sayRef.current?.(`  Invalid option keyword.\n${SELECT_PROMPT}`);
      return;
    }
    // ⚠ the same rule as `applySelect`: the option answers the prompt, it does not end the
    // command that is waiting on it.
    const pend = s.selWant?.then;
    const stay = () => {
      setSelWant(pend ? { mode: null, pts: [], then: pend } : null);
      if (pend) sayRef.current?.(SELECT_PROMPT);
    };
    if (m.label === "All") { A.selectAll(); stay(); return; }
    if (m.label === "Previous") {
      const was = prevSel.current;
      if (!was.length) { sayRef.current?.("  No previous selection set."); stay(); return; }
      // ⚠⚠ **FILTERED AGAINST WHAT IS STILL THERE.** A previous set that names erased objects
      // would put handles into the next command that no longer exist — and the server would
      // answer "0 found" about a selection the palette says has six things in it.
      const live2 = new Set<string>();
      for (const p of s.dl?.polys ?? []) live2.add(p[3]);
      for (const t of s.dl?.texts ?? []) live2.add(t[7]);
      for (const mk of s.dl?.marks ?? []) live2.add(mk[4]);
      for (const r of s.dl?.rays ?? []) live2.add(r[7]);
      const keep = was.filter((p) => live2.has(p.handle));
      setSel(keep);
      sayRef.current?.(`  ${keep.length} found`
                       + (keep.length < was.length ? `  (${was.length - keep.length} erased)` : ""));
      stay();
      return;
    }
    if (m.label === "Last") {
      // ⚠ The last object CREATED, which is the highest handle — handles are allocated in
      // order by `_next_handle`, so this is the drawing's own record of what came last.
      // ⚠⚠ **FROM `everything()`, WHICH WALKS ALL FOUR CHANNELS.** This read `dl.polys` alone, so
      // a last object that was a TEXT, a POINT, a block of points or a ray was invisible to it —
      // and the answer was not "No last object", it was the highest-handled POLYLINE in the
      // drawing. A silently WRONG target handed to whatever command was waiting on the selection:
      // draw a note, type ERASE, press L, and something else goes. `Previous` already walked all
      // four, and `everything()` exists precisely so the list is written once.
      const last = everything().sort(
        (a, b) => parseInt(a.handle, 16) - parseInt(b.handle, 16)).pop();
      if (!last) { sayRef.current?.("  No last object."); stay(); return; }
      setSel([last]);
      sayRef.current?.("  1 found");
      stay();
      return;
    }
    setSelWant({ mode: m, pts: [] });
    sayRef.current?.(m.prompts[0] ?? "");
  }, []);

  /** Open the in-place editor on one text entity.
   *
   *  ⚠⚠ **THE RAW STRING COMES FROM THE SERVER, NOT FROM THE DISPLAY LIST.** `dl.texts` holds
   *  `plain_text()` — every inline code already stripped — so seeding the editor from it and
   *  writing the result back would silently delete a client's formatting the first time
   *  somebody fixed a typo. Measured: 77% of the MTEXT stored here carries at least one code.
   *  One extra request per double-click is the price, and it is nothing. */
  /** ⚠ A ref, because `author` is declared ABOVE the editor opener and a new MTEXT has to reach
   *  it — the same temporal-dead-zone shape `sayRef` solves. */
  const openTextEditorRef = useRef<((h: string, b: TextBox) => void) | undefined>(undefined);

  const openTextEditor = useCallback(async (handle: string, box: TextBox) => {
    const s = live.current;
    if (!s.id) return;
    try {
      setTextEdit({ spec: await loadText(s.id, handle), box });
    } catch (e) { fail(e); }
  }, [fail]);
  openTextEditorRef.current = openTextEditor;

  /** Save what the editor holds. ⚠ ONE `setprop`, so it is ONE undo step — a drafter who
   *  retypes a note and changes its height expects Ctrl+Z to put back what was there, not to
   *  peel the two apart. */
  const commitText = useCallback(async (text: string, props: Partial<TextSpec>) => {
    const s = live.current;
    const t = s.textEdit;
    setTextEdit(null);
    if (!s.id || !t) return;
    if (!t.spec.handle) {
      // ⚠ A NEW note. Nothing was drawn when the box was dragged, so an empty save draws
      // nothing at all — AutoCAD's own answer to closing its editor without typing.
      if (!text.trim()) { sayRef.current?.("  nothing typed — no note drawn"); return; }
      const b = t.box;
      void authorRef.current?.("MTEXT",
        [{ x: b.x, y: b.y + b.h }, { x: b.x + b.w, y: b.y }],
        { text, height: props.height ?? t.spec.height });
      return;
    }
    const params: Record<string, unknown> = { ...props };
    if (text !== t.spec.text) params.text = text;
    if (!Object.keys(params).length) return;              // nothing changed — not an edit
    try {
      const rep = await editDrawing(s.id, "setprop", [t.spec.handle], params);
      sayRef.current?.(rep.changed
        ? `  ${t.spec.kind} updated (${Object.keys(params).sort().join(", ")})`
        : `  ${rep.detail ?? "nothing changed"}`);
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** One click while HATCH is armed for internal points. ⚠ Stays armed. */
  const onHatchPoint = useCallback(async (p: Pt) => {
    const s = live.current;
    const want = s.hatchWant;
    if (!s.id || !want) return;
    try {
      // ⚠⚠ **THE SAME PICK ANSWERS TWO COMMANDS.** BOUNDARY and the internal-point HATCH ask
      // the identical question — "what encloses this point?" — and `boundary.face_at` answers
      // it once. Arming a second point-collector for BOUNDARY would be a second gesture for one
      // act, and the two would drift over which face they think you meant.
      const asBoundary = (want as { kind?: string }).kind === "boundary";
      const rep = await editDrawing(s.id, asBoundary ? "boundary" : "hatchpoint", [],
                                    { point: [p.x, p.y, 0], pattern: want.pattern,
                                      solid: want.solid, scale: want.scale, angle: want.angle,
                                      gradient: want.gradient,
                                      layer: s.drawLayer, space: s.space });
      sayRef.current?.(asBoundary
        ? `  traced ${Math.round(rep.area ?? 0).toLocaleString()} sq units`
          + (rep.holes ? ` with ${rep.holes} island${rep.holes === 1 ? "" : "s"}` : "")
          + ` — ${rep.drawn ?? 0} polyline${(rep.drawn ?? 0) === 1 ? "" : "s"}`
        : `  filled ${Math.round(rep.area ?? 0).toLocaleString()} sq units with `
          + `${rep.pattern}`
          + (rep.holes ? `, ${rep.holes} island${rep.holes === 1 ? "" : "s"}` : ""));
      await refresh(rep);
    } catch (e) {
      // ⚠ NOT disarmed on a refusal. "That point is not inside a closed area" is the commonest
      // outcome — a wall with a gap in it — and making the drafter re-run the whole command to
      // try one pixel to the left is how a working tool feels broken.
      fail(e);
    }
  }, [refresh, fail]);

  const applyHatch = useCallback(async (s: HatchSpec) => {
    const st2 = live.current;
    if (!st2.id) return;
    // ⚠⚠ **A GRADIENT ALWAYS ARMS THE POINT PICKER, EVEN WITH A SELECTION.** The branch below
    // routes a selection to `hatch`/`hatchedit`, neither of which carries the gradient fields —
    // so filling a selected boundary with a gradient would have quietly produced a plain
    // pattern hatch. AutoCAD's GRADIENT asks for an internal point too.
    if (!st2.sel.length || s.gradient) {
      // ⚠ Armed and STAYS armed: AutoCAD fills one area per click until you press Enter, and a
      // drafter hatching a floor plan clicks eleven rooms without re-running the command.
      setHatchDlg(false);
      setHatchWant(s);
      sayRef.current?.(s.gradient
        ? `Pick a point inside the area to fill with the ${s.gradient.name} gradient:`
          + "  (Enter or Esc when done)"
        : "Pick internal point:  (Enter or Esc when done)");
      return;
    }
    const editing = st2.sel.every((p) => p.kind === "HATCH");
    setHatchDlg(false);
    try {
      const rep = await editDrawing(st2.id, editing ? "hatchedit" : "hatch",
                                    st2.sel.map((p) => p.handle),
                                    { pattern: s.pattern, solid: s.solid,
                                      scale: s.scale, angle: s.angle,
                                      layer: st2.drawLayer });
      const n = rep.hatched ?? rep.changed ?? 0;
      sayRef.current?.(n ? `  ${n} ${editing ? "hatch(es) changed"
                                             : `boundar${n === 1 ? "y" : "ies"} filled with `
                                               + `${rep.pattern}`}`
                         : `  ${rep.detail ?? "nothing to hatch"}`);
      if (rep.refused_count) {
        sayRef.current?.("  " + Object.entries(rep.refused || {})
          .map(([k, v]) => `${v} ${k}`).join(", ") + " could not be a boundary");
      }
      if (!editing && rep.handles?.length) setSel([]);
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** The base or insertion point for a block, clicked on the drawing. */
  /** One `underlay` edit, on the SELECTED underlays or on all of them. ⚠ All of them when
   *  nothing is picked, which is what AutoCAD's UFADE does — it is a per-drawing control there. */
  const underlayOp = useCallback(async (spec: Record<string, unknown>,
                                        said: (r: EditReport) => string) => {
    const s2 = live.current;
    if (!s2.id) return;
    try {
      const rep = await editDrawing(s2.id, "underlay", s2.sel.map((x) => x.handle), spec);
      sayRef.current?.(said(rep));
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** The second corner of an underlay clip. ⚠ Two picks, and the first is remembered HERE
   *  rather than in `imgWant`: that state also carries a File, and a clip has no file. */
  const onClipPoint = useCallback((p: Pt) => {
    const w = live.current.clipWant;
    if (!w) return;
    if (!w.pts.length) {
      setClipWant({ pts: [p] });
      sayRef.current?.("Specify opposite corner:");
      return;
    }
    const a = w.pts[0];
    setClipWant(null);
    void underlayOp({ clip: [[a.x, a.y], [p.x, a.y], [p.x, p.y], [a.x, p.y]] },
                    () => "  underlay clipped");
  }, [underlayOp]);

  /** A measurement, said the way a drafter reads it. ⚠ mm² AND m², because a floor area in
   *  square millimetres is a number nobody can check against a schedule. */
  const sayMeasure = useCallback((r: Measured) => {
    if (r.kind === "distance") {
      sayRef.current?.(`  distance ${(r.distance ?? 0).toFixed(1)}  `
        + `ΔX ${(r.dx ?? 0).toFixed(1)}  ΔY ${(r.dy ?? 0).toFixed(1)}  `
        + `angle ${(r.angle ?? 0).toFixed(2)}°`);
      return;
    }
    if (r.kind === "angle") {
      sayRef.current?.(`  angle ${(r.angle ?? 0).toFixed(2)}°  `
        + `(reflex ${(r.reflex ?? 0).toFixed(2)}°)`);
      return;
    }
    if (r.kind === "radius") {
      sayRef.current?.(`  ${r.entity} · radius ${(r.radius ?? 0).toFixed(1)}  `
        + `diameter ${(r.diameter ?? 0).toFixed(1)}  `
        + `circumference ${(r.circumference ?? 0).toFixed(1)}`);
      return;
    }
    const a = r.area ?? 0;
    const refused = Object.entries(r.refused || {})
      .map(([k, n]) => `${n} ${k}`).join(", ");
    sayRef.current?.(`  area ${a.toFixed(1)} mm²  =  ${(a / 1e6).toFixed(3)} m²  ·  `
      + `perimeter ${(r.perimeter ?? 0).toFixed(1)}`
      // ⚠⚠ SAID. "3.2 m²" for an open polyline the drafter thought was closed is how a quantity
      // ends up wrong on a schedule — the area IS the chord-closed one.
      + (r.open ? `  ⚠ ${r.open} of them were NOT closed — measured chord-closed` : "")
      + (refused ? `  (no area: ${refused})` : ""));
  }, []);

  const runMeasure = useCallback(async (handles: string[],
                                        spec: Record<string, unknown>) => {
    const id2 = live.current.id;
    if (!id2) return;
    try { sayMeasure(await measureApi(id2, handles, spec)); }
    catch (e) { fail(e); }
  }, [sayMeasure, fail]);

  const onMeasPoint = useCallback((p: Pt) => {
    const w = live.current.measWant;
    if (!w) return;
    const pts = [...w.pts, p];
    // ⚠ AREA takes points until Enter; DIST and ANGLE know how many they want.
    if (w.want > 0 && pts.length >= w.want) {
      setMeasWant(null);
      // ⚠⚠ **PAN BY TWO POINTS IS NOT A MEASUREMENT AND RIDES HERE ANYWAY** — it is the same
      // gesture (two picked points, snaps and ortho and all), and giving it its own collector
      // would be a second copy of this one. `view.x/y` is the WORLD POINT AT THE CANVAS CENTRE
      // (`toScreen`), so moving the drawing under the hand means moving the view the OTHER way:
      // the point picked first ends up where the second was.
      if (w.kind === "panpts") {
        const was = lastView.current;
        if (was) {
          api.current?.setView({ ...was, x: was.x + (pts[0].x - pts[1].x),
                                y: was.y + (pts[0].y - pts[1].y) });
        }
        return;
      }
      void runMeasure([], { kind: w.kind, points: pts.map((q) => [q.x, q.y]) });
      return;
    }
    setMeasWant({ ...w, pts });
    sayRef.current?.(w.kind === "area"
      ? `Specify next corner point or press Enter to total (${pts.length} so far):`
      : "Specify next point:");
  }, [runMeasure]);

  const onBreakPoint = useCallback((p: Pt) => {
    const w = live.current.breakWant;
    if (!w) return;
    const pts = [...w.pts, p];
    if (pts.length < w.want) {
      setBreakWant({ ...w, pts });
      sayRef.current?.("Specify second break point:");
      return;
    }
    setBreakWant(null);
    const s2 = live.current;
    if (!s2.id || !s2.sel.length) return;
    void (async () => {
      try {
        const rep = await editDrawing(s2.id!, "break", [s2.sel[0].handle],
                                      { points: pts.map((q) => [q.x, q.y]) });
        sayRef.current?.(rep.became
          ? `  broken — what is left is an ${rep.became}`
          : `  broken into ${rep.pieces ?? 1} piece${(rep.pieces ?? 1) === 1 ? "" : "s"}`);
        setSel([]);
        await refresh(rep);
      } catch (e) { fail(e); }
    })();
  }, [refresh, fail]);

  const onBlockPoint = useCallback(async (p: Pt) => {
    const s = live.current;
    const want = s.blockWant;
    if (!want || !s.id) return;
    setBlockWant(null);
    try {
      // ⚠⚠ **A BLOCK FROM ANOTHER DRAWING IS A DIFFERENT COMMAND.** `insert` refuses a name
      // this drawing has never heard of — correctly — so DESIGNCENTER fetches the definition
      // and its nested definitions FIRST and hands them over with the placement.
      const rep = want.mode === "create"
        ? await editDrawing(s.id, "block", s.sel.map((x) => x.handle),
                            { name: want.name, base: [p.x, p.y, 0], layer: s.drawLayer })
        : want.from != null
          ? await editDrawing(s.id, "importblock", [],
                              { name: want.name, point: [p.x, p.y, 0], scale: want.scale,
                                rotation: want.rotation, layer: s.drawLayer,
                                blocks: (await loadBlockDef(want.from, want.name)).blocks })
          : await editDrawing(s.id, "insert", [],
                              { name: want.name, point: [p.x, p.y, 0], scale: want.scale,
                                rotation: want.rotation, layer: s.drawLayer });
      const carried = rep.blocks_carried ?? 0;
      sayRef.current?.(want.mode === "create"
        ? `  ${rep.blocked ?? 0} object(s) → block "${rep.block}", one instance left in place`
        : want.from != null
          ? `  ${rep.block} placed`
            + (carried ? `, ${carried} definition${carried === 1 ? "" : "s"} carried across`
                       : " — this drawing already had that block, and it was kept as it is")
          : `  ${rep.block} inserted`);
      // ⚠ AutoCAD leaves the new INSTANCE selected after BLOCK, not the geometry that went into
      // it — that geometry no longer exists as loose objects and a stale selection would point
      // at rows the command just moved into the definition.
      setSel([]);
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** One object handed to a TRIM / EXTEND / FILLET / CHAMFER. */
  /** MATCHPROP: the first pick is the source, every one after it is painted.
   *  ⚠ Layer, colour, linetype and lineweight — the four AutoCAD's MATCHPROP copies by default.
   *  It does NOT copy geometry or text content, which is why this reads the properties off the
   *  display list's pick rather than fetching the source entity. */
  const onMatchPick = useCallback(async (p: Pick) => {
    const s2 = live.current;
    if (!s2.id) return;
    if (!matchFrom.current) {
      matchFrom.current = p;
      sayRef.current?.(`Select destination object(s): source is ${p.kind} on ${p.layer}`);
      return;
    }
    const from = matchFrom.current;
    if (p.handle === from.handle) return;
    try {
      // ⚠ the report is KEPT and passed on — see the note in the PICKS handler above. MATCHPROP
      // threw it away and refetched the whole list to paint one object a different colour.
      const rep = await editDrawing(s2.id, "setprop", [p.handle],
                                    { layer: from.layer, aci: from.aci });
      sayRef.current?.(`  1 object painted from ${from.kind} on ${from.layer}`);
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  const onPickObject = useCallback(async (p: Pick, at?: Pt) => {
    const s = live.current;
    const c = s.objCmd;
    if (!c || !s.id || !s.dl) return;
    const def = PICKS[c.name];
    if (def.scalar && c.held === undefined) {
      sayRef.current?.(`  ${def.scalar.label} first — type it and press Enter`);
      return;
    }
    if (def.sidePoint) {
      // ⚠ ONE object, then its OWN side. The command stays armed afterwards, which is what makes
      // "offset both faces of a wall outwards" two clicks each instead of one click for both.
      setSideWant({ handle: p.handle, dist: c.held ?? 0 });
      sayRef.current?.(def.prompts[1]);
      return;
    }
    const got = [...c.got, p];
    const ats = [...c.at, at ?? { x: 0, y: 0 }];
    if (def.objects > 0 && got.length < def.objects) {
      setObjCmd({ ...c, got, at: ats });
      sayRef.current?.(def.prompts[Math.min(got.length, def.prompts.length - 1)]);
      return;
    }
    try {
      let rep;
      if (def.objects < 0) {
        // ⚠⚠ **THE CUTTING EDGES ARE FILTERED HERE, NOT ON THE SERVER.** AutoCAD's modern TRIM
        // uses every visible object; sending 3,767 handles would make the server load all of
        // them for one click. The display list already holds the bounding boxes and a box that
        // does not touch the target's cannot cross it — an exact filter, not a guess.
        // ⚠⚠ **EXTEND DOES NOT WANT TRIM'S LIST — see `reachHandles`.** A cutting edge must
        // already cross the object; a boundary to extend TO must not. Sending TRIM's set here
        // meant EXTEND could only ever reach something it was touching already.
        const { handles, capped } = def.op === "extend"
          ? reachHandles(s.dl, p.handle, at ?? { x: 0, y: 0 })
          : nearbyHandles(s.dl, p.handle);
        if (capped) sayRef.current?.("  ⚠ too many candidate edges — only the first 400 used");
        rep = await editDrawing(s.id, def.op as EditOp, [],
                                { handle: p.handle, point: [at?.x ?? 0, at?.y ?? 0, 0],
                                  edges: handles });
      } else {
        rep = await editDrawing(s.id, def.op as EditOp, got.map((g) => g.handle),
                                { radius: c.held ?? 0, picks: ats.map((q) => [q.x, q.y]) });
        setObjCmd({ ...c, got: [], at: [] });   // ⚠ AutoCAD's FILLET repeats with the same radius
      }
      const n = rep.trimmed ?? rep.extended ?? rep.cornered ?? 0;
      sayRef.current?.(n ? `  ${def.op} ok${rep.split ? " — split into two" : ""}`
                         : `  ${rep.detail ?? "nothing to do"}`);
      if (rep.refused_count) {
        sayRef.current?.("  " + Object.entries(rep.refused || {})
          .map(([k, v]) => `${v} ${k}`).join(", ") + " refused");
      }
      // ⚠⚠⚠ **`refresh(rep)`, NOT `refresh()` — and `rep` was right there.** The report carries
      // the delta; without it `refresh` refetches the WHOLE display list and the GPU scene is
      // rebuilt from nothing. Measured on drawing 1158, that was **312-395 ms of scene work
      // after every TRIM, EXTEND, FILLET, OFFSET and CHAMFER** — the five commands a drafter
      // uses most after MOVE — while the engine's own answer was 3-4 KB and already in hand.
      // ⚠ It hid well: `felt` stays small because these commands paint optimistically, so the
      // only symptom was the work column. `qa/bench_actions.py` now prints the route
      // (`[DL refetch:refreshx1]`), which is what made it findable at all.
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** A freehand stroke, one sample at a time; `null` when the button comes up.
   *
   * ⚠⚠ **THINNED AS IT ARRIVES, NOT AFTERWARDS.** A pointer sends hundreds of moves a second and
   * a cloud traced across a sheet would arrive as thousands of vertices — a polyline nobody can
   * edit, out of a gesture that looks casual. One sample per `FREEHAND_STEP` screen pixels keeps
   * the shape and the object.
   * ⚠ The step is in SCREEN pixels, so it is the same smoothness whatever the zoom. In drawing
   * units it would be a scribble on a detail and a straight line on a site plan.
   */
  const onTrail = useCallback((p: Pt | null) => {
    const s = live.current;
    const d = s.draw;
    if (!d?.freehand) return;
    if (p) {
      const last = d.pts[d.pts.length - 1];
      // ⚠ The canvas's LIVE view object, which is the only place the current scale is — the
      // studio keeps `lastView` for ZOOM Previous and that is the PREVIOUS one.
      const step = FREEHAND_STEP / (api.current?.view?.scale || 1);
      if (last && Math.hypot(p.x - last.x, p.y - last.y) < step) return;
      setDraw({ ...d, pts: [...d.pts, p] });
      return;
    }
    // ⚠ Three points is the least that encloses anything. A stroke shorter than that is a slip
    // of the hand, and drawing a cloud from it would leave something the drafter has to find.
    if (d.pts.length < 3) {
      setDraw(null); setMode("select");
      sayRef.current?.("  that stroke is too short to make a cloud");
      return;
    }
    const def = defOf("revcloud", s.dimKind, "");
    setDraw(null);
    setMode("select");
    if (def) void author(def.kind, d.pts, { ...(d.pre || {}) });
  }, [author]);

  /** Drag the hot grip to `p`. ⚠ The ROLE goes with it — a circle's quadrant grip resizes and
   *  its centre grip moves, and the server cannot tell which from the index alone. */
  const dropGrip = useCallback(async (p: Pt) => {
    const s = live.current;
    const i = s.hotGrip;
    if (!s.id || i == null || !s.grips || !s.sel.length) return;
    setHotGrip(null);
    try {
      // ⚠⚠ **THE REPORT, or dragging one grip refetches the whole drawing.** See the note in
      // the `PICKS` handler: `refresh()` without it finds no delta and reloads the entire display
      // list. This is the commonest direct manipulation in the app.
      const gripRep = await editDrawing(s.id, "grip", [], {
        handle: s.sel[0].handle, index: i, role: s.grips[i].role, point: [p.x, p.y, 0],
      });
      sayRef.current?.(`  ${s.grips[i].role === "whole" ? "moved" : "stretched"}`);
      await refresh(gripRep);
      // ⚠ Re-read the grips: the geometry moved, so where they ARE moved with it.
      setGrips(await loadGrips(s.id, s.sel[0].handle));
    } catch (e) { fail(e); }
  }, [refresh, fail]);

  /** A command's next point. ⚠ Already snapped — `Canvas2D` sends the marker's point, not the
   *  pointer's, because that is the one the drafter lined up. */
  const onPoint = useCallback((rawPt: Pt, typedIn = false) => {
    // ⚠ ORTHO, POLAR and grid SNAP applied HERE, once, before any consumer sees the point.
    // Doing it per consumer would be sixteen chances to forget one.
    // ⚠⚠ **EXCEPT A TYPED ONE.** `0,0` means the origin whether or not ORTHO is on; constraining
    // it would move a coordinate the drafter stated exactly. `parseTyped` has already applied
    // the constraint where it belongs — to direct distance entry, whose whole meaning is "this
    // far in the direction the cursor is already constrained to".
    const p = typedIn ? rawPt : applyConstraint(rawPt);
    // ⚠⚠ **A SINGLE-SHOT SNAP IS SPENT BY THE PICK IT WAS TYPED FOR.** Cleared here, at the one
    // place every point arrives, rather than in each of the sixteen consumers below — the same
    // argument the constraint line above makes. An override that outlived its pick would be a
    // running snap the drafter never turned on and cannot see.
    if (snapOnceRef.current !== null) setSnapOnce(null);
    const polar = polarWantRef.current;
    if (polar) {
      setPolarWant(null);
      void commit("array", { kind: "polar", count: polar.count, angle: polar.angle,
                             centre: [p.x, p.y, 0] });
      return;
    }
    const pw = pedWantRef.current;
    if (pw && pw.kind === "point") {
      setPedWant(null);
      if (pw.op === "splinedit") {
        void commit("splinedit", { mode: pw.mode, at: [p.x, p.y, 0] });
        return;
      }
      // ⚠ `at` for every one of them — the server resolves a point to the nearest SEGMENT for
      // arc/straight/addvertex and the nearest VERTEX for delvertex, because those are
      // different questions and only it knows the polyline's vertex list.
      void commit("pedit", { mode: pw.mode, at: [p.x, p.y, 0] });
      return;
    }
    // ⚠ Before every other point consumer: while SELECT is collecting a fence, a click is a
    // fence point and nothing else. Putting this after `draw` would let a half-armed tool eat it.
    if (live.current.selWant?.mode) { onSelectPoint(p); return; }
    if (live.current.clipBase) {
      const c2 = live.current.clip;
      setClipBase(false);
      if (c2) setClip({ ...c2, base: p });
      sayRef.current?.(`  ${c2?.handles.length ?? 0} objects copied — paste places by that point`);
      return;
    }
    if (live.current.mode === "paste") {
      void pasteAt(p);
      setMode("select");
      return;
    }
    if (live.current.breakWant) { onBreakPoint(p); return; }
    if (live.current.measWant) { onMeasPoint(p); return; }
    if (live.current.clipWant) { onClipPoint(p); return; }
    // ⚠⚠ **BEFORE the other point consumers**, because ZOOM is transparent in AutoCAD: it runs
    // during another command and hands control back. Putting it after them would let a running
    // draw tool eat the corner.
    // ⚠ Before the window branch and for the same reason it is before the draw tools: ZOOM is
    // transparent, so its own prompt must eat the point rather than whatever is running under it.
    if (zoomAnchorRef.current && !zoomAnchorRef.current.p) {
      const at = { ...zoomAnchorRef.current, p: { x: p.x, y: p.y } };
      zoomAnchorRef.current = at;
      setZoomAnchor(at);
      setPedWant({ op: "zoom", mode: "height", kind: "width" });
      setTyped("");
      sayRef.current?.("Enter magnification or height:");
      return;
    }
    if (live.current.zoomWant !== null || live.current.mode === "zoomwin") {
      const first = live.current.zoomWant;
      if (!first) {
        setZoomWant(p);
        sayRef.current?.("Specify opposite corner:");
        return;
      }
      setZoomWant(null);
      setMode("select");
      const dx = Math.abs(p.x - first.x), dy = Math.abs(p.y - first.y);
      // ⚠ The CANVAS fits it — see `fitBox`. It is the only thing that knows how many pixels it
      // has, and a caller computing the scale would be writing the view maths a second time.
      if (!api.current?.fitBox(first.x, first.y, p.x, p.y)) {
        sayRef.current?.("  that window has no area");
        return;
      }
      sayRef.current?.(`  zoomed to a ${dx.toFixed(0)} x ${dy.toFixed(0)} window`);
      return;
    }
    if (live.current.ucsWant) {
      const w = live.current.ucsWant;
      if (!w.origin) {
        setUcsWant({ origin: p });
        sayRef.current?.("Specify point on X-axis <accept>:");
        return;
      }
      setUcsWant(null);
      const next = ucsFromPoints(w.origin, p, live.current.ucs);
      pushUcs(next);
      sayRef.current?.(`  origin ${next.x.toFixed(1)}, ${next.y.toFixed(1)}`
                       + (Math.abs(next.rot) > 1e-12
                          ? ` at ${(next.rot * 180 / Math.PI).toFixed(1)}°` : ""));
      return;
    }
    const sw = live.current.sideWant;
    if (sw) {
      setSideWant(null);
      const s3 = live.current;
      if (!s3.id) return;
      void (async () => {
        try {
          const rep = await editDrawing(s3.id!, "offset", [sw.handle],
                                        { distance: sw.dist, side: [p.x, p.y, 0] });
          sayRef.current?.(rep.offset
            ? `  1 object offset by ${sw.dist}`
            : `  ${Object.entries(rep.refused || {}).map(([k, v]) => `${v} ${k}`).join(", ")
               || "nothing to offset"} refused`);
          await refresh(rep);
          // ⚠ ARMED AGAIN, distance kept — AutoCAD's OFFSET runs until Escape, and re-typing the
          // distance for every wall is the difference between the command and a chore.
          sayRef.current?.(PICKS.offset.prompts[0]);
        } catch (e) { fail(e); }
      })();
      return;
    }
    if (live.current.tanWant) { onTanPoint(p); return; }
    if (live.current.imgWant) { onImagePoint(p); return; }
    if (live.current.hatchWant) { void onHatchPoint(p); return; }
    if (live.current.attPlace) { void onAttPoint(p); return; }
    if (live.current.blockWant) { void onBlockPoint(p); return; }
    if (live.current.hotGrip != null) { void dropGrip(p); return; }
    if (live.current.draw) { onDrawPoint(p); return; }
    const c = live.current.cmd;
    if (!c) return;
    const def = COMMANDS[c.name];
    // ⚠ A click before the number has been given is not a point — OFFSET wants the distance
    // first, and taking the click as the side would offset by whatever was in the box.
    if (def.scalarFirst && c.held === undefined) {
      sayRef.current?.(`  ${def.scalar?.label ?? "value"} first — type it and press Enter`);
      return;
    }
    const pts = [...c.pts, p];
    if (pts.length < def.points) {
      setCmd({ ...c, pts });
      setTyped("");
      sayRef.current?.(def.prompts[pts.length] ?? "");
      return;
    }
    // ⚠ The LAST point is the cursor, not a stored one: `params` is written to read the live
    // position so the committed value is exactly the one the preview was showing.
    const raw = live.current.typed === "" ? null : Number(live.current.typed);
    const n = c.held ?? (Number.isFinite(raw as number) ? raw : null);
    setTyped("");
    // ⚠⚠ **COPY IN MULTIPLE MODE STAYS ARMED, SAME BASE POINT, SAME SELECTION.** That is the
    // whole of the option: one base, many destinations, until Escape.
    // ⚠ And it must NOT clear the selection — `commit`'s third argument does that for anything
    // that CREATES, which copy does. Clearing after the first destination would leave the command
    // armed over nothing and every further click would report "0 found".
    if (c.name === "copy" && c.multi) {
      setCmd({ ...c, pts: c.pts });
      sayRef.current?.(def.prompts[c.pts.length] ?? "");
      void commit(def.op, def.params(pts.slice(0, -1), p, n), false);
      return;
    }
    setCmd(null);
    void commit(def.op, def.params(pts.slice(0, -1), p, n), def.creates);
  }, [commit]);

  /** The click that finishes TTR, TTT or ARC Continue.
   *
   * ⚠⚠ **THE CLICK IS THE DISAMBIGUATION, NOT A GUESS.** TTR fits up to four circles and TTT
   * exactly four — the incircle and three excircles — and every one of them is a correct answer
   * to what was asked. AutoCAD decides from where you clicked on each object; pdcad already has
   * the objects, so this click says which answer. Picking the first, or the smallest, would be
   * right about a quarter of the time and silent the rest.
   */
  const onTanPoint = useCallback((p: Pt) => {
    const w = live.current.tanWant;
    if (!w) return;
    if (w.given === "ttr" && !(w.r > 0)) {
      // ⚠ A click before the radius is not the disambiguation — same rule OFFSET follows, where
      // a click before the distance would offset by whatever was in the box.
      sayRef.current?.("  radius first — type it and press Enter");
      return;
    }
    setTanWant(null);
    setTyped("");
    if (w.given === "cont") {
      const from = live.current.cont;
      if (!from) { sayRef.current?.("  nothing to continue from"); return; }
      const solved = continueArc(from, p);
      if (typeof solved === "string") { sayRef.current?.(`  ${solved}`); return; }
      void author("ARC", solved);
      setCont(contOf("ARC", solved));
      return;
    }
    const all = w.given === "ttr"
      ? ttrAll(w.objs[0], w.objs[1], w.r)
      : tttAll(w.objs[0], w.objs[1], w.objs[2]);
    if (!all.length) {
      // ⚠ Says WHY there is no answer. Two parallel lines 10 apart have no circle of radius 3
      // tangent to both, and that is a fact about the picks rather than a failure of the solve.
      sayRef.current?.(w.given === "ttr"
        ? `  no circle of radius ${w.r} is tangent to both — try a different radius`
        : "  those three lines have no circle touching all three; two of them are parallel");
      return;
    }
    const got = nearest(all, p);
    if (!got) { sayRef.current?.("  no circle fits those picks"); return; }
    sayRef.current?.(`  ${all.length} circle${all.length > 1 ? "s" : ""} fit — drew the one `
                     + `nearest the click, r ${got.r.toFixed(3)}`);
    void author("CIRCLE", [{ x: got.x, y: got.y }, { x: got.x + got.r, y: got.y }]);
  }, [author]);

  /** The two corners of an attached image's frame.
   *
   * ⚠⚠ **A RECTANGLE, NOT AN INSERTION POINT.** AutoCAD's IMAGEATTACH asks for a point and a
   * scale, which needs the drafter to know how many drawing units one pixel should be. Two
   * corners is the same information in the form they already have — and it is the same gesture
   * as RECT, so nothing new has to be learned.
   */
  const onImagePoint = useCallback((p: Pt) => {
    const w = live.current.imgWant;
    if (!w) return;
    if (!w.pts.length) {
      setImgWant({ ...w, pts: [p] });
      sayRef.current?.("Specify opposite corner:");
      return;
    }
    const a = w.pts[0];
    setImgWant(null);
    setMode("select");
    const dwgId = live.current.id;
    if (!dwgId) return;
    void (async () => {
      const body = new FormData();
      body.append("file", w.file);
      body.append("x0", String(a.x));
      body.append("y0", String(a.y));
      body.append("x1", String(p.x));
      body.append("y1", String(p.y));
      if (live.current.space) body.append("space", live.current.space);
      body.append("layer", live.current.drawLayer || "0");
      try {
        if (w.kind === "pdf") {
          // ⚠ Page 1. AutoCAD's dialog lists the pages; the report says how many there are, so
          // a drafter who wanted page 7 of a tender set is told the set has 12 rather than
          // finding out by attaching the cover sheet and wondering.
          body.append("page", String(w.page ?? 1));
          const rep = await attachPdf(dwgId, body);
          const more = (rep.pages ?? 1) > 1
            ? ` — that PDF has ${rep.pages} pages; this is page ${rep.page}` : "";
          sayRef.current?.(`  ${rep.underlay} attached at `
                           + `${rep.size?.[0]?.toFixed(0)} x ${rep.size?.[1]?.toFixed(0)}${more}`);
        } else {
          const rep = await attachImage(dwgId, body);
          sayRef.current?.(`  ${rep.image} attached, ${rep.pixels?.[0]}x${rep.pixels?.[1]} px`);
        }
        await refresh();
      } catch (e) { fail(e); }
    })();
  }, [refresh]);

  /** Enter, with TTR's radius. ⚠ Its own accept rather than `commitTyped`: this does not run
   *  anything, it stores the number and moves on to the click that says which circle — the same
   *  two-step shape OFFSET's scalar-first commands have. */
  const acceptTanRadius = useCallback(() => {
    const w = live.current.tanWant;
    if (!w || w.given !== "ttr") return;
    const n = Number(live.current.typed);
    if (!Number.isFinite(n) || n <= 0) { sayRef.current?.("  a radius must be positive"); return; }
    setTanWant({ ...w, r: n });
    setTyped("");
    sayRef.current?.("Click near the circle you want:");
  }, []);

  /** Enter, with whatever is in the dynamic-input box. ⚠ AutoCAD commits a typed angle or factor
   *  on Enter without a second click, and a drafter who has typed 90 will press it. */
  const commitTyped = useCallback(() => {
    const s = live.current;
    const c = s.cmd;
    if (!c) return;
    const def = COMMANDS[c.name];
    // ⚠⚠ **THE KEYWORD IS READ BEFORE THE NUMBER, because at this box they arrive the same
    // way.** `[Reference]` was printed by ROTATE and SCALE from the day they were written and
    // nothing anywhere read the letter — the same "keyword the app cannot HEAR" that the 31
    // removed from `DRAWS` were. The guard in `test/keywords.test.ts` did not walk `COMMANDS`,
    // so it never said so.
    const opts = keywordsOf(def.prompts[def.prompts.length - 1] ?? "");
    if (s.typed !== "" && matchKeyword(s.typed, opts) === "Reference") {
      setCmd({ ...c, ref: null });
      setTyped("");
      sayRef.current?.(REF_ASK[c.name]?.[0] ?? "Specify reference value:");
      return;
    }
    const n = s.typed === "" ? null : Number(s.typed);
    if (!def.scalar || n === null || !Number.isFinite(n)) return;
    // ⚠⚠ **REFERENCE IS TWO NUMBERS AND A SUBTRACTION — or a DIVISION, and which one is the
    // command's business, not this function's.** AutoCAD asks for what the thing measures now
    // and what it should measure; a drafter uses it to rotate a wall onto a grid line without
    // working out the difference, which is exactly the arithmetic they came here to avoid.
    if (c.ref === null) {
      setCmd({ ...c, ref: n });
      setTyped("");
      sayRef.current?.(REF_ASK[c.name]?.[1] ?? "Specify new value:");
      return;
    }
    if (c.ref !== undefined) {
      // ⚠ A reference LENGTH of 0 has no factor — every new length is infinitely bigger. Said
      // by name rather than dividing and shipping an Infinity to the server.
      if (c.name === "scale" && c.ref === 0) {
        sayRef.current?.("  Value must be positive and nonzero.");
        setTyped("");
        return;
      }
      const value = c.name === "scale" ? n / c.ref : n - c.ref;
      const at2 = s.cursor ?? c.pts[0];
      setCmd(null);
      setTyped("");
      void commit(def.op, def.params(c.pts, at2, value), def.creates);
      return;
    }
    // ⚠ For a scalar-FIRST command, Enter stores the number and moves on to the click rather
    // than running anything — pressing it once must not offset by a distance with no side.
    if (def.scalarFirst && c.held === undefined) {
      setCmd({ ...c, held: n });
      setTyped("");
      sayRef.current?.(def.prompts[0]);
      return;
    }
    const at = s.cursor ?? c.pts[0];
    setCmd(null);
    setTyped("");
    void commit(def.op, def.params(c.pts, at, n), def.creates);
  }, [commit]);

  // ── what the chrome is allowed to ask for ───────────────────────────────────────────────────
  /** Names the ribbon has asked for and nobody has written — reported ONCE each. See the
   *  Proxy at the end of `A`. */
  /** A typed POINT at a modify command's point prompt — `MOVE ⏎ 0,0 ⏎ @1000,0 ⏎`.
   *
   * ⚠⚠ **THE COMMONEST KEYSTROKE SEQUENCE IN AutoCAD AND IT HAD NOWHERE TO GO.** `parseTyped`
   * was only ever reached from `finishDraw`, so typed coordinates worked for LINE and CIRCLE and
   * not for MOVE, COPY, ROTATE or SCALE — the drafter typed `0,0` at "Specify base point:" and
   * the command line answered *Unknown command "0,0"*. A drawing moved by eye instead of by
   * number is the difference between a CAD program and a drawing program.
   * ⚠ Deliberately the same parse as `finishDraw`: the UCS mapping in and out, the command's own
   * keywords first, and the snap override after. Two parsers would disagree about `@` on the day
   * one of them was changed.
   */
  const finishCmdPoint = useCallback(() => {
    const s = live.current;
    const c = s.cmd;
    if (!c) return;
    const def = COMMANDS[c.name];
    if (!def) return;
    // ⚠⚠⚠ **AN EMPTY ENTER IS AN ANSWER, NOT A NO-OP — `<use first point as displacement>`.**
    // MOVE, COPY and STRETCH all print that default and nothing kept it. It has to be answered
    // HERE, in the function the keystroke actually reaches: during a command the focus is the
    // dynamic-input box (`INPUT#dynin`, measured), whose Enter calls this — and `Studio`'s own
    // window listener returns early for any INPUT, so a branch there could never see it. That
    // was the whole reason the first implementation of this looked right and did nothing.
    // ⚠ The base is the LAST point picked, which is what all three measure from (`p[0]` for MOVE
    // and COPY, `p[2]` for STRETCH). Handing `2·base` to `onPoint` makes `params`' own
    // `c − base` come out as exactly `base`, so the ORDINARY commit path runs — preview,
    // optimistic local draw and all — instead of a second implementation of the command.
    // ⚠⚠ Doubled in the UCS, not the world: the vector a drafter typed is in the coordinate
    // system they are working in, and on a rotated origin the world doubling goes elsewhere.
    if (!s.typed.trim()) {
      const at0 = c.pts.length;
      if (at0 > 0 && at0 === def.points - 1
          && /use first point as displacement/i.test(def.prompts[at0] ?? "")) {
        const u0 = s.ucs;
        const bU = worldToUcs(c.pts[at0 - 1], u0);
        onPoint(ucsToWorld({ ...bU, x: bU.x * 2, y: bU.y * 2 }, u0), true);
      }
      return;
    }
    const at = Math.min(c.pts.length, def.prompts.length - 1);
    const u = s.ucs;
    // ⚠ The sub-prompt owns this keystroke while it is open — see COPY's `[mode]` below. An
    // empty answer takes BricsCAD's own default, which is Multiple.
    if (c.askMode) {
      const want = s.typed.trim().toLowerCase();
      if (want && !want.startsWith("m") && !want.startsWith("s")) {
        sayRef.current?.("  answer Single or Multiple");
        return;
      }
      const multi = !want || want.startsWith("m");
      setTyped("");
      setCmd((c0) => (c0 ? { ...c0, askMode: false, multi } : c0));
      sayRef.current?.(`  ${multi ? "Multiple" : "Single"} — ${def.prompts[c.pts.length] ?? ""}`);
      return;
    }
    const r0 = parseTyped(s.typed, {
      last: c.pts.length ? worldToUcs(c.pts[c.pts.length - 1], u) : null,
      cursor: s.cursor ? worldToUcs(s.cursor, u) : s.cursor,
      keywords: keywordsOf(def.prompts[at] ?? ""),
    });
    if (r0.kind === "error") { sayRef.current?.(`  ${r0.why}`); return; }
    if (r0.kind === "keyword") {
      // ⚠⚠⚠ **`[Displacement]` — AutoCAD'S AND BricsCAD'S "the vector, not two points".** Typing
      // D at the base-point prompt means the NEXT thing given is the displacement itself, and
      // the command ends there. Implemented by putting the base point at the UCS ORIGIN: the
      // command's own `params` then computes `c − base`, which with base at the origin IS the
      // vector — so the ordinary point path, the ordinary preview and the optimistic local draw
      // all run unchanged, instead of a second implementation of MOVE beside the first.
      // ⚠ The origin is taken in the CURRENT UCS, not the world: a displacement a drafter types
      // is in the coordinate system they are working in.
      // ⚠⚠ **COPY's `[mode]` — one copy or many.** A sub-prompt: the keywords at this step
      // become Single and Multiple until it is answered, which is why `finishCmdPoint` reads
      // `askMode` before the prompt's own list.
      if (r0.word === "mOde" && c.name === "copy") {
        setTyped("");
        setCmd((c0) => (c0 ? { ...c0, askMode: true } : c0));
        sayRef.current?.("  Enter a copy mode option [Single/Multiple] <Multiple>:");
        return;
      }
      if (r0.word === "Displacement") {
        setTyped("");
        setCmd((c0) => (c0 ? { ...c0, pts: [ucsToWorld({ x: 0, y: 0 }, u)] } : c0));
        sayRef.current?.("  Specify displacement <0,0,0>:");
        return;
      }
      // ⚠ `[Reference]` is answered by `commitTyped`. Handing it over keeps one implementation.
      commitTyped();
      return;
    }
    if (r0.kind !== "point") {
      const ov = overrideOf(s.typed);
      if (ov !== null) {
        setTyped("");
        setSnapOnce(ov);
        sayRef.current?.(ov === "none" ? "  _non" : `  _${s.typed.trim().toLowerCase()} of`);
        return;
      }
      sayRef.current?.("  that is not a point");
      return;
    }
    setTyped("");
    onPoint(ucsToWorld(r0.pt, u), true);
  }, [commitTyped, onPoint]);

  const toldRef = useRef<Set<string>>(new Set());

  const A = useMemo(() => {
    const real: Record<string, (...a: any[]) => any> = {
      // ⚠⚠⚠ **THE STATUS BAR WAS DECORATIVE.** Every one of these was called by a button and
      // defined by nothing, so the Proxy above turned it into silence: measured in the running
      // app, **F8 lit ORTHO and the ORTHO button beside it did nothing.** The toggles
      // themselves have always been real — `TOGGLES`'s own comment says they are "implemented,
      // not stubbed" — it was the twelve controls that set them that were missing.
      // ⚠ Written as a table rather than twelve near-identical closures: they differ only in
      // which key they flip, and a list of pairs cannot drift out of step with itself the way
      // twelve hand-written setters did.
      ...Object.fromEntries(([
        ["setShowGrid", "grid"], ["setSnapMode", "snapmode"], ["setOrtho", "ortho"],
        ["setPolar", "polar"], ["setOtrack", "otrack"],
        ["setDynin", "dynin"], ["setInferCon", "infer"], ["setShowAlpha", "transparency"],
        ["setIso", "iso"], ["setAnnoMon", "annomon"],
      ] as [string, keyof Toggles][]).map(([name, key]) => [
        name,
        (want?: boolean) => setTog((t) => ({ ...t, [key]: want === undefined ? !t[key] : !!want })),
      ])),
      /** ⚠⚠ **NOT IN THE TABLE ABOVE, BECAUSE IT IS WRITTEN BACK TO THE DRAWING.** Pen weights
       *  are AutoCAD's `LWDISPLAY`, kept in the header, so a drafter who turns them on and
       *  reopens the file must find them on — the same reason `OSMODE` is written back when the
       *  snap drop-up changes. Two places holding the same fact is how they drift apart. */
      setLwt: (want?: boolean) => setTog((t) => {
        const next = want === undefined ? !t.lwt : !!want;
        if (id) void editDrawing(id, "setvar", [], { name: "LWDISPLAY", value: next ? 1 : 0 })
          .catch(() => undefined);
        return { ...t, lwt: next };
      }),
      /** ⚠ The one toggle that carries a VALUE rather than a flag — see `polarAng`. */
      setPolarAng: (a: number) => setPolarAng(a > 0 ? a : 90),
      /** HATCHEDIT — the dialog the Hatch tool already uses, opened on an existing fill.
       *  ⚠ The `hatchedit` op and the dialog both existed; only the ribbon button between them
       *  was missing, so pressing Modify ▸ Edit Hatch did nothing at all. */
      hatchEdit: () => {
        const picked = live.current.sel;
        if (!picked.length || !picked.every((q) => q.kind === "HATCH")) {
          sayRef.current?.("  select one or more hatches first");
          return;
        }
        setHatchDlg(true);
      },
      /** TEXTEDIT — the in-place editor a double-click opens, from the ribbon.
       *  ⚠ AutoCAD has both, and a drafter who has already selected the note reaches for the
       *  button rather than double-clicking something they have picked. */
      editNote: () => {
        const picked = live.current.sel.filter(
          (q) => ["TEXT", "MTEXT", "ATTRIB", "ATTDEF"].includes(q.kind));
        if (picked.length !== 1) {
          sayRef.current?.(picked.length
            ? "  TEXTEDIT edits one note at a time"
            : "  select a piece of text first, or double-click it");
          return;
        }
        // ⚠ The box is where the editor is PUT, and the canvas is the only thing that knows it
        // in screen pixels — a dialog in the middle of the screen hides the drawing the note
        // annotates, which is the whole reason this editor is in-place.
        void openTextEditor(picked[0].handle,
                            api.current?.textBoxOf?.(picked[0].handle)
                            ?? { x: 120, y: 120, w: 320, h: 90 });
      },
      /** SAVE. ⚠⚠ There is nothing to save and saying so is the point: every edit is already a
       *  row in Postgres with its own undo record, which is why the drawing survives a browser
       *  reload and AutoCAD's does not. A Save button that silently does nothing teaches a
       *  drafter that their work might not be safe. */
      /** **Ctrl+S / QSAVE — and it means two different things in the two modes.**
       *
       * ⚠⚠⚠ **ON THE SERVER PATH THERE IS NOTHING TO SAVE, AND IN LOCAL MODE THERE IS EVERYTHING
       * TO SAVE.** Every server edit is already written; a drawing taken into this tab is not,
       * and until `saveToServer` existed the only way back was `uploadToServer` — the IMPORT
       * endpoint — which makes a NEW drawing: open *Plan A*, press Ctrl+S, get *Plan A (1)*.
       * That is why local mode could not be a default, and it is why this is Ctrl+S rather than
       * a new command name nobody would press.
       * ⚠ A conflict is SPOKEN, never retried. The server refuses when somebody else has saved
       * since this tab took the drawing, and retrying with `force` would be this app deciding
       * whose work to destroy.
       */
      save: () => {
        const id = live.current.id;
        if (!id || !localFor(id)) {
          sayRef.current?.("  already saved — every edit is written as it is made, "
                           + "and the history is on the Drawing History palette");
          return;
        }
        if (id < 0) {
          sayRef.current?.("  This drawing came from a file — SAVEDWG writes it back to disk.");
          return;
        }
        void (async () => {
          try {
            sayRef.current?.("  saving to the server…");
            const got = await saveToServer(id);
            const kb = (got.bytes / 1024).toLocaleString(undefined,
                                                         { maximumFractionDigits: 0 });
            sayRef.current?.(`  ${got.name} — saved to the server, ${kb} KB (${got.ms} ms)`);
          } catch (e) {
            const c = e as Error & { conflict?: boolean };
            if (c?.conflict) {
              sayRef.current?.(`  ⚠ ${c.message}`);
              sayRef.current?.("  Your edits are still here. SAVEDWG writes them to a file.");
              return;
            }
            fail(e);
          }
        })();
      },
      /** UPDATEFIELD — what every field in this drawing would read now.
       *  ⚠ It reports. See `backend/drawing/fields.py`: DXF stores a field's EVALUATED result
       *  in the note's own text with nothing marking where it sits, so rewriting one would
       *  delete whatever label shares the string with it. */
      updateFields: () => {
        const dwgId = live.current.id;
        if (!dwgId) { sayRef.current?.("  open a drawing first"); return; }
        void (async () => {
          try {
            const r = await loadFields(dwgId);
            const carried = Object.entries(r.carried || {})
              .map(([k, n]) => `${n} ${k}`).join(", ");
            sayRef.current?.(`  ${r.fields.length} field${r.fields.length === 1 ? "" : "s"}: `
              + `${r.evaluable} this app can read, ${r.stale} no longer matching their own text`
              + (carried ? ` · carried, not evaluated: ${carried}` : ""));
            for (const f of r.fields.filter((x) => x.stale).slice(0, 8)) {
              sayRef.current?.(`    ${f.var} on ${f.layout} reads "${f.value}" — the note says `
                               + `"${f.shows.slice(0, 40)}"`);
            }
          } catch (e) { fail(e); }
        })();
      },
      /** VPLOCK — the selected window's Display locked flag. */
      vpLock: (want: boolean) => {
        const v = live.current.pickedVp;
        if (!v || !live.current.id) { sayRef.current?.("  select one viewport first"); return; }
        void (async () => {
          try {
            await editDrawing(live.current.id!, "vplock", [v.h], { lock: want });
            sayRef.current?.(`  viewport display ${want ? "locked" : "unlocked"}`);
            await refresh();
          } catch (e) { fail(e); }
        })();
      },
      /** IMAGEATTACH — open the file picker; the rectangle comes after the file. */
      attachImage: () => {
        if (!live.current.id) { sayRef.current?.("  open a drawing first"); return; }
        pickKind.current = "image";
        if (imgPick.current) imgPick.current.accept = "image/*";
        imgPick.current?.click();
      },
      // ── the underlay controls ───────────────────────────────────────────────────────────
      /** UFADE. ⚠⚠ **THE RIBBON PASSES 0–1 AND THE DXF STORES 0–100**, and the slider shows
       *  AutoCAD's `90 - v` because more fade is less picture. Two conversions, both at this
       *  boundary, so nothing downstream has to remember which end it is holding. */
      setUnderlay: (v: number) => {
        void underlayOp({ fade: Math.round(Math.max(0, Math.min(1, v)) * 100) },
                        (r) => `  underlay fade ${r.fade}%`);
      },
      /** ADJUST ▸ Monochrome. ⚠ ENTITY data (group 280 bit 2), so it goes out in the DXF and
       *  every reader sees it — unlike FRAME, which is this screen's business only. */
      setUnderlayMono: (on: boolean) => {
        void underlayOp({ mono: !!on },
                        () => `  underlay ${on ? "monochrome" : "full colour"}`);
      },
      /** FRAME. ⚠ The one underlay control that genuinely IS a view setting — see `uframes` in
       *  `display.py`. The frame stays in `polys` either way, or the underlay could not be
       *  picked, which is a different thing from invisible. */
      setUnderlayFrame: (on: boolean) => {
        setTog((t) => ({ ...t, uframe: !!on }));
        sayRef.current?.(`  underlay frames ${on ? "on" : "off"}`);
      },
      /** XCLIP on an underlay — drag the rectangle to keep.
       *  ⚠ The callback the chrome passes is IGNORED: it writes to the design app's plan, which
       *  pdcad has no equivalent of. The clip is entity data here and goes through the server. */
      startClip: () => {
        if (!live.current.id) { sayRef.current?.("  open a drawing first"); return; }
        setClipWant({ pts: [] });
        setMode("select");
        sayRef.current?.("Specify first corner of the clipping rectangle:");
      },

      // ── measuring, and BREAK ────────────────────────────────────────────────────────────
      /** AREA. ⚠⚠ **PICKED POINTS ARE THE PRIMARY ROUTE, NOT A FALLBACK.** Most rooms in this
       *  office's drawings have no closed object to select — they are four overshooting wall
       *  lines — which is why AutoCAD's AREA opens with "Specify first corner point" too. */
      measureArea: () => {
        const s2 = live.current;
        if (!s2.id) { sayRef.current?.("  open a drawing first"); return; }
        if (s2.sel.length) {
          void runMeasure(s2.sel.map((x) => x.handle), { kind: "area" });
          return;
        }
        setMeasWant({ kind: "area", want: 0, pts: [] });
        setMode("select");
        sayRef.current?.("Specify first corner point or press Enter to total the selection:");
      },
      /** DIST — two points, and it reports the deltas and the angle beside the length, which is
       *  what AutoCAD prints and what a drafter checks a wall against. */
      measureDist: () => {
        if (!live.current.id) { sayRef.current?.("  open a drawing first"); return; }
        setMeasWant({ kind: "distance", want: 2, pts: [] });
        setMode("select");
        sayRef.current?.("Specify first point:");
      },
      /** **-PAN: a base point and a second point, and the view shifts by that displacement.**
       *
       * ⚠⚠ Its ribbon button armed a mode called `panpts` and **NOTHING IN THE FRONTEND ANSWERED
       * TO IT** — a live button whose note described the gesture precisely and which did nothing
       * at all when pressed. `qa/command_survey.py` scored it BUILT, because arming a mode lights
       * the button and that is an observable change; only `test/routing.test.ts` can see it. */
      panPoints: () => {
        if (!live.current.id) { sayRef.current?.("  open a drawing first"); return; }
        setMeasWant({ kind: "panpts", want: 2, pts: [] });
        setMode("select");
        sayRef.current?.("Specify base point:");
      },
      /** MEASUREGEOM ▸ Angle — three points, the VERTEX second. ⚠ Three rather than two lines:
       *  two lines make four angles and AutoCAD has to ask which. */
      measureAngle: () => {
        if (!live.current.id) { sayRef.current?.("  open a drawing first"); return; }
        setMeasWant({ kind: "angle", want: 3, pts: [] });
        setMode("select");
        sayRef.current?.("Specify first point (the VERTEX is the second):");
      },
      /** MEASUREGEOM ▸ Radius — reads a selected circle or arc. */
      measureRadius: () => {
        const s2 = live.current;
        if (!s2.sel.length) { sayRef.current?.("  Select a circle or an arc first."); return; }
        void runMeasure(s2.sel.map((x) => x.handle), { kind: "radius" });
      },
      /** BREAK / BREAKATPOINT. ⚠ ONE object — the two points can only be on one of them, which
       *  is why the server refuses a longer selection by name rather than guessing. */
      breakObject: (atPoint?: boolean) => {
        const s2 = live.current;
        if (s2.sel.length !== 1) {
          sayRef.current?.(s2.sel.length
            ? "  BREAK works on one object at a time — select just one."
            : "  Select the object to break first.");
          return;
        }
        setBreakWant({ want: atPoint ? 1 : 2, pts: [] });
        setMode("select");
        sayRef.current?.(atPoint ? "Specify the point to split at:"
                                 : "Specify first break point:");
      },

      /** GRADIENT — a hatch with a gradient fill instead of a pattern.
       *
       *  ⚠⚠ **THIS SENT ONE HARDCODED BLUE.** `{name: "LINEAR", colour1: "#3070c0"}`, with a
       *  comment saying a second colour "needs a picker this app does not have yet". The
       *  SERVER has taken nine gradient names, two colours and a sweep angle since the day it
       *  was written — so eight of the nine were unreachable and every gradient in every
       *  drawing was the same blue. The picker was the only missing piece, and it is 40 lines
       *  in the dialog that was already open for HATCH.
       */
      fillGradient: () => {
        if (!live.current.id) { sayRef.current?.("  open a drawing first"); return; }
        setHatchDlg("gradient");
      },
      /** BOUNDARY — trace the area you click in. ⚠ The same gesture as the internal-point
       *  hatch, and deliberately so: one question, one walker, two answers. */
      traceBoundary: () => {
        if (!live.current.id) { sayRef.current?.("  open a drawing first"); return; }
        // ⚠ The pattern fields are unused by BOUNDARY and are filled anyway:  is
        // one shape, and an optional-everything variant of it would let a real HATCH be armed
        // with no pattern.
        setHatchWant({ kind: "boundary", pattern: "", solid: false, scale: 1, angle: 0 });
        setMode("select");
        sayRef.current?.("Pick a point inside the area to trace:");
      },
      /** CENTERMARK. ⚠ Reads DIMCEN when no size is given, and its SIGN chooses the shape. */
      centreMark: () => {
        const s2 = live.current;
        if (!s2.sel.length) { sayRef.current?.("  Select a circle or an arc first."); return; }
        void (async () => {
          try {
            const rep = await editDrawing(s2.id!, "centremark",
                                          s2.sel.map((x) => x.handle),
                                          { layer: s2.drawLayer });
            sayRef.current?.(`  ${rep.drawn ?? 0} lines · ${rep.note ?? ""}`);
            await refresh(rep);
          } catch (e) { fail(e); }
        })();
      },
      /** CENTERLINE — the bisector of two picked lines. */
      centreLine: () => {
        const s2 = live.current;
        if (s2.sel.length !== 2) {
          sayRef.current?.("  Select exactly two lines to find the middle of.");
          return;
        }
        void (async () => {
          try {
            const rep = await editDrawing(s2.id!, "centreline",
                                          s2.sel.map((x) => x.handle),
                                          { layer: s2.drawLayer });
            sayRef.current?.(`  centreline drawn · ${rep.note ?? ""}`);
            await refresh(rep);
          } catch (e) { fail(e); }
        })();
      },

      /** Remove Clip. ⚠ RESETS to the whole page rather than deleting the boundary — see
       *  `do_underlay`. Deleting it would leave this app with no page size at all. */
      clearUnderlayClip: () => {
        void underlayOp({ clip: null }, () => "  the whole page is shown again");
      },

      /** ADCENTER — browse another drawing's blocks. ⚠ A dialog, and the placement point is
       *  asked for on the DRAWING afterwards, the same shape BLOCK and INSERT use. */
      designCenter: () => {
        if (!live.current.id) { sayRef.current?.("  open a drawing first"); return; }
        setAdcOpen(true);
      },
      /** PDFATTACH — the same gesture, a different `accept` and a different endpoint.
       *  ⚠⚠ **MEASURED BEFORE IT WAS BUILT: `PDFUNDERLAY` IS IN 207 OF THIS OFFICE'S 18,241
       *  DRAWINGS AND `DWFUNDERLAY` IN NONE.** That is why there is no DWFATTACH beside this. */
      attachPdf: () => {
        if (!live.current.id) { sayRef.current?.("  open a drawing first"); return; }
        pickKind.current = "pdf";
        if (imgPick.current) imgPick.current.accept = "application/pdf,.pdf";
        imgPick.current?.click();
      },
      zoomFit: () => api.current?.zoomExtents(),
      zoomExtents: () => api.current?.zoomExtents(),
      zoomIn: () => api.current?.zoomBy(1.4),
      zoomOut: () => api.current?.zoomBy(1 / 1.4),
      open: () => setOpenDlg(true),
      regen: () => { if (current) void open(current); },
      setDark: (v: boolean) => setDark(!!v),
      /** ⚠⚠ **EVERY MODIFY BUTTON IN THE RIBBON ARRIVES HERE**, because `handlers.ts` sends all
       *  of `OP_OF` to `A.runCmd`. Only two are built — so the rest SAY they are not, instead of
       *  looking alive and doing nothing. That is the same rule as the object snaps: this app's
       *  worst failure mode is a control that claims to work. */
      runCmd: (name: string, extra?: Record<string, unknown>) => {
        // ⚠⚠ **PICKFIRST 0 MEANS A SELECTION MADE BEFORE THE COMMAND IS NOT ITS SELECTION.**
        // AutoCAD then asks *Select objects:* even when something is picked, and a few offices
        // set it that way so a stray pick cannot become a stray edit. The set is DROPPED here,
        // once, rather than at each of the branches below — every one of them reads
        // `live.current.sel` and would otherwise have to remember.
        if (!pickFirst.current && live.current.sel.length) {
          setSel([]);
          live.current.sel = [];
          sayRef.current?.("  PICKFIRST is 0 — select objects for this command");
        }
        // ⚠⚠ **`extra` WAS BEING DROPPED, AND THAT IS WHY EVERY PEDIT ENTRY SAID "not built
        // yet".** `handlers.ts` has routed `mod.pedit.close` → `["pedit", {mode: "close"}]`
        // since before pdcad had a `pedit` op; this signature took one argument, so the mode
        // never arrived and the name fell through to the "not built" branch — a command that
        // WAS wired, reporting that it was not.
        if (name === "pedit" || name === "pjoin" || name === "splinedit") {
          pedit(name, extra); return;
        }
        // ⚠⚠ **ARRAY IS A DIALOG, NOT A GESTURE**, because rows, columns and two spacings are
        // four numbers and no run of clicks says them. AutoCAD asks in a box too.
        if (name === "array") {
          if (!live.current.sel.length) { sayRef.current?.("  Select objects first."); return; }
          setArrayDlg(String(extra?.kind ?? "rect") === "polar" ? "polar" : "rect");
          return;
        }
        // ⚠ ERASE takes no points, so it is not in `COMMANDS` — but it IS a command name, and
        // `selectThen("erase")` hands it back here after a verb-first `Select objects:`. Without
        // this it would answer "ERASE is not built yet" about the one modify command that has
        // worked since the first week.
        if (name === "erase") {
          if (!live.current.sel.length) { sayRef.current?.("  Select objects first."); return; }
          void commit("erase", {});
          return;
        }
        // ⚠⚠ **EXPLODE — THE `BREAKLINE` BUG A FOURTH TIME, AND ON A COMMAND WITH A WORKING
        // SERVER HALF SINCE `do_explode` LANDED.** `edits.py` has had `op == "explode"` and a
        // full undo (erase the parts, restore the reference) all along, `api.ts` declares
        // `exploded`, `catalog.ts` puts it on the Modify panel with the accelerator X, and
        // `handlers.ts` routes `mod.explode` → `["explode"]`. Nothing in the STUDIO answered
        // that name: EXPLODE takes no points, so it is not in `COMMANDS`, so `BUILT` did not
        // have it, so the live-looking ribbon button and the X key both said "EXPLODE is not
        // built yet" — about the command AutoCAD users press to get at a block's contents.
        // Same fix as ERASE directly above: a selection command with no points needs a line
        // here or the table lookup below denies it exists.
        // ⚠⚠ **OVERKILL IS THE ONE COMMAND FOR WHICH AN EMPTY SELECTION IS AN ANSWER.**
        // Everything else here refuses with "Select objects first"; OVERKILL with nothing
        // selected means the WHOLE drawing, which is what a drafter running it on a file from a
        // consultant means, and what AutoCAD's own `ALL` answer to its prompt does.
        // ⚠ `clears`, like EXPLODE: the objects it deleted are gone, so a kept selection would
        // hold dead handles and the next command would report 0 against a set that still looks
        // normal on screen.
        if (name === "overkill") {
          void commit("overkill", { ...(extra ?? {}) }, true);
          return;
        }
        if (name === "explode") {
          if (!live.current.sel.length) { sayRef.current?.("  Select objects first."); return; }
          // ⚠ `clears` — the references it exploded no longer exist, so a kept selection would
          // hold dead handles and the next command would report "0 objects" against a set that
          // still looks normal on screen. That is the JOIN bug this file documents above.
          void commit("explode", {}, true);
          return;
        }
        // ⚠⚠ **SETBYLAYER, WHICH IS A `setprop` AND NEEDS NO OP OF ITS OWN.** `handlers.ts`
        // sends it here as `["setprop", {aci: 256, linetype: "BYLAYER", lineweight: -1}]` —
        // the three properties AutoCAD's SETBYLAYER resets. Kept general rather than
        // special-cased to that one id: `setprop` is the op the Properties palette already
        // uses, and any ribbon entry that wants to write a fixed set of properties onto the
        // selection should reach it the same way.
        if (name === "setprop") {
          if (!live.current.sel.length) { sayRef.current?.("  Select objects first."); return; }
          void commit("setprop", { ...(extra ?? {}) });
          return;
        }
        // ⚠⚠⚠ **RENDERER — WHICH BACKEND IS ACTUALLY DRAWING, ON THIS MACHINE, RIGHT NOW.**
        // Nothing could answer that from outside the app, and it is the first question every
        // support call and every benchmark asks. `qa/bench_desktop.py` measured the DESKTOP app
        // at three to seven times BricsCAD on draws where browser mode is at two, and the only
        // difference is the shell — Tauri uses WebKitGTK on Linux, the browser is Chromium. A
        // webview that has quietly fallen back to Canvas2D would explain the whole gap, and
        // there was no way to find out but to add this: the answer goes on the COMMAND LINE,
        // where a screenshot can read it without any access to the page.
        if (name === "renderer") {
          const w = window as unknown as Record<string, unknown>;
          const sc = w.__pdcadScene as { instances?: number } | undefined;
          const gl = w.__pdcadGL as { kind?: string; renderer?: string } | undefined;
          const ed = (w.__pdcadEdits as Array<Record<string, unknown>> | undefined)?.slice(-1)[0];
          const sp = (ed?.split ?? {}) as Record<string, number>;
          const s2 = (sc ?? {}) as Record<string, unknown>;
          // ⚠⚠ **WHY WebGL2 AND NOT WebGPU, on this machine, right now.** `WEBGPU_READY` is true
          // and `select()` prefers WebGPU — but only where `canPresentWebGpu()` draws a real
          // triangle and reads it back. The owner saw `webgl2` in a screenshot and asked the
          // right question; the answer is a property of the SHELL, not of the app, and it was
          // not printed anywhere. `navigator.gpu` absent means the webview has no WebGPU at all.
          const gpuApi = typeof (navigator as unknown as { gpu?: unknown }).gpu !== "undefined";
          sayRef.current?.(
            `  canvas: ${gl ? `${gl.kind} — ${gl.renderer}` : "2D (no GPU backend)"}`
            + ` · navigator.gpu ${gpuApi ? "present" : "ABSENT"}`
            + (w.__pdcadWebGpuWhy ? ` · no WebGPU: ${w.__pdcadWebGpuWhy}` : "")
            + ` · scene ${(s2.instances as number)?.toLocaleString() ?? "—"} instances`
            + ` · ${standalone() ? "desktop" : "browser"}`);
          // ⚠⚠ **AND THE LAST EDIT'S SPLIT, because on the desktop there is no other way to get
          // it.** `qa/bench_desktop.py` can see a stopwatch and a screenshot and nothing else —
          // no DOM, no `window`, no devtools — so the numbers that told browser mode where its
          // milliseconds went were simply unavailable for the SHIPPED binary. Printing them on
          // the command line is the whole of the instrument.
          const all = (w.__pdcadEdits as unknown[] | undefined) ?? [];
          // ⚠⚠ **THE COUNT IS PRINTED ALWAYS, AND `qa/bench_desktop.py` READS IT.** A burst of
          // 20 commands that committed 3 of them looks exactly like one that committed 20 —
          // that is how every desktop number this project published came out of a screen that
          // changed because the prompt echoed. The harness now reads this line before and after
          // a burst and refuses a row whose edits did not arrive.
          sayRef.current?.(
            `  edits ${(w.__pdcadEditCount as number) ?? all.length}`
            + ` · last edit: ${ed ? `${Math.round(ed.ms as number)} ms` : "—"}`
            + (sp.engine ? ` (engine ${Math.round(sp.engine)}, module ${sp.engineMs ?? "—"})` : "")
            + ` · scene build ${s2.build ?? "—"} ms`
            + ` [cuts ${s2.cuts ?? "—"} lines ${s2.lines ?? "—"} fills ${s2.fills ?? "—"}]`
            + ` upload ${s2.upload ?? "—"}`
            + (s2.patched ? ` · PATCHED ${s2.patched}` : ` · rebuilt${s2.patchWhy ? `: ${s2.patchWhy}` : ""}`));
          // ⚠⚠⚠ **AND HOW OFTEN THE SHELL LETS US PAINT — the frame clock.** The desktop app
          // measures 3–8× the SAME bundle in Chrome (`/var/tmp/webview_tax.py`: LINE 18.2 vs
          // 50.8 ms, POINT 19.2 vs 150.2, on the same drawing with the same clock), and the
          // spread is bimodal, which a slower CPU path cannot produce and a frame that arrives
          // late can. Every edit here ends in `requestAnimationFrame`, so if WebKitGTK paces
          // frames differently every command pays the difference — and the two numbers below
          // are the only way to find out from inside a binary with no devtools.
          //
          //   steady — the interval between consecutive frames while a loop is running. 16.7 ms
          //            is a 60 Hz display keeping up; 33 is half rate; a large p90 is a stall.
          //   cold   — request → callback for a SINGLE frame from an idle page, which is what an
          //            edit actually pays. A shell that only wakes on the next vsync charges a
          //            whole frame here even when `steady` looks perfect.
          const fr: number[] = [];
          let prev = performance.now();
          const cold = (n: number, out: number[]) => {
            if (n === 0) {
              const c = out.slice().sort((a, b) => a - b);
              const st = fr.slice(1).sort((a, b) => a - b);
              const q = (v: number[], p: number) => v[Math.min(v.length - 1, Math.floor(v.length * p))] ?? 0;
              sayRef.current?.(
                `  frames: steady med ${q(st, 0.5).toFixed(1)} ms`
                + ` p90 ${q(st, 0.9).toFixed(1)} max ${(st[st.length - 1] ?? 0).toFixed(1)}`
                + ` · cold med ${q(c, 0.5).toFixed(1)} max ${(c[c.length - 1] ?? 0).toFixed(1)}`
                + ` (${st.length}+${c.length})`);
              return;
            }
            // ⚠ The gap matters: back-to-back one-shots ARE a loop, and would measure `steady`
            // a second time. 40 ms is longer than a 60 Hz frame, so each request starts idle.
            setTimeout(() => {
              const t0 = performance.now();
              requestAnimationFrame(() => { out.push(performance.now() - t0); cold(n - 1, out); });
            }, 40);
          };
          const tick = (t: number) => {
            fr.push(t - prev); prev = t;
            if (fr.length < 60) requestAnimationFrame(tick); else cold(10, []);
          };
          requestAnimationFrame(tick);
          return;
        }
        // ⚠⚠ **LOCAL — take this drawing into the tab.** One request for the rows, and after it
        // every read and every edit is answered by `pdcad-core` compiled to wasm: no network,
        // no latency, and the drawing keeps working if the link does not. The routing itself is
        // in `api.ts`, so nothing else in this file knows or needs to.
        // ⚠ It says what it did, both ways. A drafter must always know whether their work is on
        // the server or in the tab, and a mode that switched silently would be a trap.
        if (name === "local") {
          const s0 = live.current;
          if (!s0.id) { sayRef.current?.("  Open a drawing first."); return; }
          if (localFor(s0.id)) { sayRef.current?.("  This drawing is already local."); return; }
          void (async () => {
            sayRef.current?.("  taking the drawing into this tab…");
            try {
              const t0 = performance.now();
              // ⚠ the NAME is the autosave's key (`opfs.ts`), so it is the drawing's own and
              // not a placeholder — `live` does not carry it, the LIST row does.
              const row = s0.rows.find((r) => r.id === s0.id);
              await takeOver(s0.id!, row?.name || `drawing ${s0.id}`);
              const ms = Math.round(performance.now() - t0);
              sayRef.current?.(`  LOCAL — this drawing now lives in the browser (${ms} ms). `
                               + "Edits are yours until you save.");
              await refresh({});
            } catch (e) { fail(e); }
          })();
          return;
        }
        // ⚠⚠⚠ **SERVER — THE WAY BACK, AND WITHOUT IT `LOCAL` IS A TRAP.** The browser engine
        // runs a fraction of the server's commands; a drafter who takes a drawing into the tab
        // and then needs TRIM, FILLET or a hatch has nowhere to go, and every edit they have made
        // is in the tab rather than on the server. This hands the drawing back.
        // ⚠⚠ **IT SAVES FIRST, ALWAYS.** `forgetLocal` closes the store: doing that before the
        // save would throw away every edit made since the take-over, silently, on a command whose
        // name sounds like a view setting. If the save is refused the drawing STAYS local and the
        // refusal is spoken — the drafter still has their work and can write it to a file.
        if (name === "server") {
          const id = live.current.id;
          if (!id) { sayRef.current?.("  Open a drawing first."); return; }
          if (!localFor(id)) {
            sayRef.current?.("  This drawing is already on the server.");
            return;
          }
          if (id < 0) {
            sayRef.current?.("  This drawing came from a file and has no place on the server "
                             + "yet — UPLOAD puts it in the list, SAVEDWG writes it to disk.");
            return;
          }
          void (async () => {
            try {
              sayRef.current?.("  saving to the server before handing it back…");
              const got = await saveToServer(id);
              await forgetLocal(id);
              sayRef.current?.(`  ${got.name} — saved and handed back. Every command is the `
                               + "server's again.");
              await refresh({});
            } catch (e) {
              const c = e as Error & { conflict?: boolean };
              if (c?.conflict) {
                sayRef.current?.(`  ⚠ ${c.message}`);
                sayRef.current?.("  The drawing is STILL in this tab and your edits are safe. "
                                 + "SAVEDWG writes them to a file.");
                return;
              }
              fail(e);
            }
          })();
          return;
        }
        // ⚠⚠ **OPENDWG — a file from the drafter's own disk, with no request at all.** The
        // reader, the import, the context and the picture are all `pdcad-core` compiled to
        // wasm; the drawing gets a NEGATIVE id, which nothing on the server can answer for, so
        // a call that leaked would 404 loudly rather than touch somebody else's file.
        if (name === "opendwg") {
          void (async () => {
            try { await openPicked(); } catch (e) { fail(e); }
          })();
          return;
        }
        // ⚠⚠ **SAVEDXF — the file is written in this tab.** The other half of OPENDWG: a
        // drawing that never reached the server can still be handed to a client, because the
        // DXF writer is the engine's own (`engine::todxf`) and runs in the browser.
        // ⚠ It says WHICH of the three things happened — written back, downloaded, or refused —
        // because "Saved" over a download that went to ~/Downloads is how a drafter ends up
        // editing yesterday's file.
        // ⚠⚠⚠ **SAVE — THE DRAWING BACK ONTO THE ONE IT WAS OPENED FROM.** Until this existed
        // the only way out of local mode was SAVEDXF/SAVEDWG to the drafter's own disk, or
        // `uploadToServer`, which posts to the IMPORT endpoint and therefore makes a NEW drawing:
        // open *Plan A*, press save, get *Plan A (1)* beside it. That is why local mode could not
        // be anybody's default, and it is the whole reason this command is here.
        // ⚠⚠ **A CONFLICT IS SPOKEN, NOT RETRIED.** The server refuses (409) when somebody else
        // has saved the drawing since this tab took it, and the sentence it sends names them and
        // when. Retrying with `force` on the drafter's behalf would be this app deciding whose
        // work to destroy.
        if (name === "savedxf") {
          void (async () => {
            const id = live.current.id;
            if (!localFor(id)) {
              sayRef.current?.("  SAVEDXF saves a drawing this tab holds. Use the download "
                               + "button for one the server has.");
              return;
            }
            try {
              const got = await saveToDisk(id!);
              const kb = (got.bytes / 1024).toLocaleString(undefined,
                                                           { maximumFractionDigits: 0 });
              sayRef.current?.(
                got.how === "written" ? `  ${got.name} — written back, ${kb} KB (${got.ms} ms)`
                : got.how === "downloaded" ? `  ${got.name} — downloaded, ${kb} KB. `
                    + "This browser cannot write back to the file you opened."
                : `  ${got.name} — not saved: the browser refused permission to write.`);
            } catch (e) { fail(e); }
          })();
          return;
        }
        // ⚠⚠ **SAVEDWG — the file the drafter handed over, given back as ITSELF.** Every reader
        // takes a DXF, which is why SAVEDXF was enough to call phase 3 done; but a client who
        // sends a .dwg and gets a .dxf back has to explain it to whoever they forward it to.
        // ⚠⚠ **AND IT SAYS WHAT THE WRITER LEFT OUT.** The engine counts every entity it cannot
        // build yet, by kind, and a save that quietly drops a wall's worth of ink is worse than
        // one that refuses — so the number is spoken, not swallowed.
        if (name === "savedwg") {
          void (async () => {
            const id = live.current.id;
            if (!localFor(id)) {
              sayRef.current?.("  SAVEDWG saves a drawing this tab holds. Use the download "
                               + "button for one the server has.");
              return;
            }
            try {
              const got = await saveDwgToDisk(id!);
              const kb = (got.bytes / 1024).toLocaleString(undefined,
                                                           { maximumFractionDigits: 0 });
              sayRef.current?.(
                got.how === "written" ? `  ${got.name} — written back, ${kb} KB (${got.ms} ms)`
                : got.how === "downloaded" ? `  ${got.name} — downloaded, ${kb} KB. `
                    + "This browser cannot write back to the file you opened."
                : `  ${got.name} — not saved: the browser refused permission to write.`);
              const rep = got.report as { skipped?: Record<string, number> } | null;
              const lost = Object.entries(rep?.skipped ?? {})
                .sort((a, b) => b[1] - a[1]).slice(0, 3);
              const n = Object.values(rep?.skipped ?? {}).reduce((a, b) => a + b, 0);
              if (n > 0) {
                sayRef.current?.(`  ⚠ ${n.toLocaleString()} entities are not in it: `
                                 + lost.map(([k, v]) => `${k} ${v.toLocaleString()}`).join(", ")
                                 + ". Save as DXF to keep everything.");
              }
            } catch (e) { fail(e); }
          })();
          return;
        }
        // ⚠⚠ **UPLOAD — phase 5's storage, and the step that was missing from the desktop app.**
        // A drawing edited in this tab had nowhere to go but the drafter's own disk; on a machine
        // with no server that file is the ONLY copy there is. This writes the .dwg here and posts
        // it to the import endpoint, so it lands in the office list with its layers, blocks and
        // sheets. ⚠ It makes a NEW drawing every time, exactly as Import does — and says so,
        // because a drafter finding two of everything in the list is how that is learnt otherwise.
        if (name === "upload") {
          void (async () => {
            const id = live.current.id;
            if (!localFor(id)) {
              sayRef.current?.("  UPLOAD sends a drawing this tab holds to the server. "
                               + "This one is already there.");
              return;
            }
            if (standalone()) {
              sayRef.current?.("  There is no server to send it to — this app is running on "
                               + "its own. Use SAVEDWG to write the file.");
              return;
            }
            try {
              const got = await uploadToServer(id!);
              const kb = (got.bytes / 1024).toLocaleString(undefined,
                                                          { maximumFractionDigits: 0 });
              sayRef.current?.(`  ${got.name} — sent to the server as drawing ${got.id}, `
                               + `${kb} KB (${got.ms} ms). It is a NEW drawing; sending it `
                               + "again makes another.");
              const rep = got.report as { skipped?: Record<string, number> } | null;
              const n = Object.values(rep?.skipped ?? {}).reduce((a, b) => a + b, 0);
              if (n > 0) {
                sayRef.current?.(`  ⚠ ${n.toLocaleString()} entities are not in it — `
                                 + "SAVEDXF keeps everything.");
              }
            } catch (e) { fail(e); }
          })();
          return;
        }
        if (PICKS[name]) { armPick(name); return; }
        const def = COMMANDS[name];
        if (!def) { sayRef.current?.(`  ${name.toUpperCase()} is not built yet`); return; }
        // ⚠⚠⚠ **VERB FIRST HERE TOO — `handlers.op` LEARNED THIS AND THIS PATH DID NOT.**
        // `MIRROR ⏎ L ⏎` answered *"Select objects first."*, threw the command away, and the
        // `L` meant for the SELECT prompt then resolved as the LINE command: the transcript
        // read `_LINE / Specify first point: / Unknown command "0,0"`. MOVE, COPY, ROTATE and
        // SCALE all worked, because the ribbon sends those through `op` — MIRROR, TRIM, EXTEND
        // and BREAK come through `startPick`, which lands here. One rule for every modify
        // command instead of two that disagree about the commonest way AutoCAD is driven.
        if (!live.current.sel.length) {
          (real.selectThen as (n: string, e?: Record<string, unknown>) => void)(name, extra);
          return;
        }
        setCmd({ name, pts: [] });
        setTyped("");
        // ⚠ A scalar-first command's FIRST prompt is the number, not its point prompt.
        sayRef.current?.(def.scalarFirst && def.scalar
          ? `Specify offset distance or [Through/Erase/Layer] <${def.scalar.def.toFixed(4)}>:`
          : def.prompts[0]);
      },
      /** ⚠ `handlers.ts` sends TRIM, EXTEND, BREAK and MIRROR through `startPick`. MIRROR is a
       *  point command; TRIM and EXTEND pick OBJECTS. BREAK is not built and says so. */
      startPick: (name: string) => {
        if (COMMANDS[name]) { (real.runCmd as (n: string) => void)(name); return; }
        if (PICKS[name]) { armPick(name); return; }
        sayRef.current?.(`  ${name.toUpperCase()} is not built yet`);
      },
      remove: () => {
        if (!live.current.sel.length) { sayRef.current?.("  Select objects first."); return; }
        void commit("erase", {});
      },
      setDrawLayer: (n: string) => setDrawLayer(String(n || "0")),

      // ── paper space ─────────────────────────────────────────────────────────────────────
      newLayout: () => setLayoutDlg("new"),
      /** DIMSTYLE — AutoCAD's Dimension Style Manager. */
      dimStyle: () => {
        if (!live.current.id) { sayRef.current?.("  No drawing is open."); return; }
        setDimDlg(true);
      },
      /** STYLE — AutoCAD's Text Style Manager. ⚠ This button used to open the DESIGN app's Pen
       *  Styles palette, whose note is true of the app the chrome came from and false here. */
      textStyle: () => {
        if (!live.current.id) { sayRef.current?.("  No drawing is open."); return; }
        setTxtDlg(true);
      },
      /** BATTMAN — a block's attribute DEFINITIONS, printed at the command line.
       *  ⚠ Not a dialog, deliberately: the VALUES are already rows in the Properties palette,
       *  which is where a drafter changes them. What BATTMAN adds is the tags, prompts and
       *  defaults of a definition — including blocks nothing has placed yet — and that is a
       *  list to read, not a form to fill in. */
      blockAtts: () => {
        const s = live.current;
        if (!s.id) { sayRef.current?.("  No drawing is open."); return; }
        // ⚠ Always the whole list. Narrowing to the selected block's definition needs the
        // block NAME, which a `Pick` does not carry — and the list is the useful answer anyway:
        // BATTMAN exists to say what tags exist before you place something.
        void loadBlockAtts(s.id).then((j) => {
          const groups = j.blocks ?? (j.block ? [{ block: j.block, attributes: j.attributes ?? [] }] : []);
          if (!groups.length) {
            sayRef.current?.("  no block in this drawing defines an attribute");
            return;
          }
          for (const g of groups) {
            sayRef.current?.(`  ${g.block}: ` + g.attributes.map(
              (a) => `${a.tag}${a.constant ? " (constant)" : ""}`
                     + (a.prompt ? ` — "${a.prompt}"` : "")).join(" · "));
          }
        }).catch(fail);
      },
      /** XREF — AutoCAD's External References palette. */
      xrefs: () => {
        if (!live.current.id) { sayRef.current?.("  No drawing is open."); return; }
        setXrefDlg(true);
      },
      // ⚠ Rename and delete act on the layout that is OPEN, which is the one whose tab is lit.
      // AutoCAD's are on the tab's own right-click menu for the same reason; asking which sheet
      // in a dialog would be asking a question the screen has already answered.
      renameLayout: () => {
        if (!live.current.space) {
          sayRef.current?.("  open a layout tab first — model space cannot be renamed");
          return;
        }
        setLayoutDlg("rename");
      },
      deleteLayout: () => {
        if (!live.current.space) {
          sayRef.current?.("  open a layout tab first — model space cannot be deleted");
          return;
        }
        setLayoutDlg("delete");
      },
      /** VPSCALE — 1:n for the selected windows. ⚠ Asked on the COMMAND LINE, not in a dialog:
       *  it is one number, and AutoCAD's own viewport scale is set without one. */
      vpScale: () => {
        const s = live.current;
        const vps = s.sel.filter((p) => p.kind === "VIEWPORT");
        if (!vps.length) { sayRef.current?.("  select a viewport first"); return; }
        setVpAsk(vps.map((p) => p.handle));
        setTyped("");
        sayRef.current?.(`Specify viewport scale 1:  (${vps.length} selected)`);
      },
      /** ⚠ `handlers.ts` sends Create Block here rather than to the chrome's own `makeBlock` —
       *  see the note there: that one maps handles through `Number()` and has nowhere to ask
       *  for a base point. */
      makeBlock: () => {
        if (!live.current.sel.length) {
          sayRef.current?.("  Select the objects to make into a block first.");
          return;
        }
        setBlockDlg("create");
      },
      setDimKind: (k: string) => setDimKind(String(k || "ortholinear")),
      // ⚠⚠ **THE `Center` MENU ENTRY USED TO DRAW AN AXIS-END ELLIPSE.** `draw.ellipse.center`
      // mapped to the plain `ellipse` mode and there was no given to set, so the first click —
      // meant as the middle — was taken as a rim point. See `ELLIPSE_GIVENS`.
      // ⚠ Sets the MODE as well, exactly like `setCircGiven` — `ellg` in `handlers.ts` overrides
      // the generic `tool()` run, so nothing else would arm the tool.
      setEllGiven: (g: string) => { setEllGiven(g === "c" ? "c" : "axis"); setMode("ellipse"); },
      // ⚠ `arcg`/`circg` in `handlers.ts` set the GIVEN and leave the mode to us — see
      // `acadOfMode`, which reads `arcv`/`circv` back out to light the right flyout entry.
      setArcGiven: (g: string) => {
        const name = String(g || "3p");
        if (name === "cont") {
          const from = live.current.cont;
          if (!from) {
            sayRef.current?.("  nothing has been drawn to continue from");
            return;
          }
          setTanWant({ given: "cont", objs: [], r: 0 });
          setMode("select");
          sayRef.current?.("Specify end point of arc:");
          return;
        }
        setArcGiven(name);
        setMode("arcv");
      },
      setCircGiven: (g: string) => {
        const name = String(g || "3p");
        if (TANGENT_GIVENS.has(name)) {
          // ⚠⚠ **NOUN-VERB, WHICH IS THIS APP'S GRAMMAR AND NOT A SHORTCUT.** AutoCAD prompts
          // "Select first object:"; pdcad selects first and runs second everywhere else, and a
          // modal object prompt in one command would be the odd one out. So the objects come
          // from the selection and the click that follows says WHICH of the answers was meant —
          // which is the same disambiguation AutoCAD gets from where you clicked on each object.
          const want = name === "ttt" ? 3 : 2;
          // ⚠ Same reason as `turn Arc into circle`: reached from the ribbon the selection is
          // still there, and reached by typing `CIRCLE ⏎ TTR ⏎` the tool has already armed and
          // wiped it. Both routes must see the objects the drafter picked.
          const picked = live.current.sel.length ? live.current.sel : armSel.current;
          if (picked.length !== want) {
            sayRef.current?.(`  select ${want} object${want > 1 ? "s" : ""} first, then `
                             + `${name.toUpperCase()} — ${picked.length} selected`);
            return;
          }
          const objs = picked.map((q) => objOf(live.current.dl, q.handle, q.kind));
          const bad = picked.filter((q, i) => !objs[i]);
          if (bad.length) {
            // ⚠ Names the OBJECT that cannot be used, not just the count. "3 lines" is a
            // requirement a drafter can act on; "invalid selection" is not.
            sayRef.current?.(`  ${name.toUpperCase()} needs `
                             + (name === "ttt" ? "three straight LINES" : "lines, arcs or circles")
                             + ` — ${bad.map((q) => q.kind).join(", ")} cannot be used`);
            return;
          }
          const good = objs as TanObj[];
          if (name === "ttt" && good.some((o) => o.kind !== "line")) {
            // ⚠⚠ With a circle among the three this is the APOLLONIUS problem — eight
            // solutions and a fistful of degenerate cases. Refusing beats a circle that
            // touches two of the three and looks right until somebody measures it.
            sayRef.current?.("  TTT takes three straight lines; with a circle among them this "
                             + "is the Apollonius problem, which is not built");
            return;
          }
          // ⚠⚠ **THE RADIUS IS PROMPTED FOR, NOT REQUIRED IN ADVANCE.** The first version made
          // a drafter type it before pressing the button, which is a rule nobody can guess and
          // which AutoCAD does not have — its TTR asks *after* the objects. So an empty box
          // arms the prompt instead of refusing.
          const typedR = Number(live.current.typed);
          const r = name === "ttr" && Number.isFinite(typedR) ? typedR : 0;
          setTanWant({ given: name as "ttr" | "ttt", objs: good, r });
          setTyped("");
          setMode("select");
          sayRef.current?.(name === "ttr" && !(r > 0)
            ? "Specify radius of circle, then press Enter:"
            : "Click near the circle you want:");
          return;
        }
        setCircGiven(name);
        setMode("circv");
      },
      undo: () => { void step("undo"); },
      redo: () => { void step("redo"); },
      /** ⚠ From the DISPLAY LIST, not from a server query. Everything drawable is exactly what
       *  a drafter means by "all", and it already excludes the layers that are off. */
      selectAll: () => {
        const got = everything();
        setSel(got);
        sayRef.current?.(`  ${got.length} found`);
      },
      /** VERB FIRST — press ERASE with nothing picked and AutoCAD asks `Select objects:`.
       *
       * ⚠⚠ **THIS IS HALF OF HOW AutoCAD IS DRIVEN AND THE APP REFUSED IT.** `handlers.op`
       * answered "Select objects first." and stopped, so every modify command could only be
       * reached noun-first. A drafter who types `E` and starts picking — which is the older and
       * still the commoner habit — got a refusal and lost the command.
       * ⚠ The whole select grammar comes with it, because it is the same state: ALL, Previous,
       * Last, Fence, WPolygon, CPolygon, window and crossing all answer this prompt already.
       */
      selectThen: (name: string, extra?: Record<string, unknown>) => {
        setMode("select");
        setSelWant({ mode: null, pts: [], then: [name, extra] });
        setTyped("");
        sayRef.current?.(SELECT_PROMPT);
      },
      /** QSELECT's pickers. ⚠⚠ **THE RIBBON ASKS THIS WHILE IT IS RENDERING**, so it must be
       *  cheap and it must never throw — see the Proxy below, which existed precisely because
       *  this one was missing and printed `selectableKinds is not built yet` into AutoCAD's
       *  command history on every single load, before the drafter had typed anything.
       *  ⚠ `wallKinds` is always empty. It is a DESIGN-APP concept that came across with the
       *  dialog; pdcad has DXF entity kinds and nothing else, and inventing walls here would be
       *  a second vocabulary inside a CAD app. */
      selectableKinds: () => ({
        kinds: live.current.selKinds.kinds,
        wallKinds: [] as string[],
        hasBlocks: live.current.selKinds.hasBlocks,
      }),
      /** QSELECT — everything of a kind, or on a layer, or both. */
      selectBy: (f: { type?: string; layer?: number | null; wallKind?: string }) => {
        const rows = live.current.rows;
        const want = f.layer == null ? "" : (rows.find((r) => r.id === f.layer)?.name || "");
        const kind = (f.type || "").toUpperCase();
        const got = everything().filter(
          (p) => (!kind || p.kind === kind || (kind === "BLOCK" && p.kind === "INSERT"))
                 && (!want || p.layer === want));
        setSel(got);
        // ⚠ AutoCAD's own wording, and it says ZERO out loud rather than silently doing nothing:
        // an empty result is the commonest QSELECT outcome and the one a drafter must notice.
        sayRef.current?.(`  ${got.length} found`);
      },
      /** SELECTSIMILAR — everything matching the selection's KIND and LAYER, which is what
       *  AutoCAD's default `SELECTSIMILAR` settings (`SIMILARITYMODE` = layer + name) mean. */
      selectSimilar: () => {
        const sel = live.current.sel;
        if (!sel.length) { sayRef.current?.("  Select an object first."); return; }
        const keys = new Set(sel.map((p) => `${p.kind}|${p.layer}`));
        const got = everything().filter((p) => keys.has(`${p.kind}|${p.layer}`));
        setSel(got);
        sayRef.current?.(`  ${got.length} found`);
      },
      /** FIND — selects every text whose content contains `q`, and RETURNS the count, because
       *  the dialog prints it. ⚠ Case-insensitive: AutoCAD's FIND is, and a drafter hunting a
       *  room name does not know how it was typed. */
      findText: (q: string) => {
        const d = live.current.dl;
        const needle = String(q || "").trim().toLowerCase();
        if (!d || !needle) return 0;
        const by = new Map<string, Pick>();
        for (const t of d.texts) {
          const ly = d.layers[t[0]];
          if (!ly || !ly.on || ly.frozen) continue;
          if (!String(t[6] ?? "").toLowerCase().includes(needle)) continue;
          by.set(t[7], { handle: t[7], kind: t[8], layer: ly.name, aci: t[1] });
        }
        setSel([...by.values()]);
        sayRef.current?.(`  ${by.size} found`);
        return by.size;
      },
      /** The layers the SELECTION touches — the Layers panel greys its per-selection tools
       *  (isolate, freeze, off, lock) on this. */
      selectionLayers: () => [...new Set(live.current.sel.map((p) => p.layer))],
      layerCount: () => live.current.rows.length,
      /** How many objects sit on each layer — the Layer palette's count column, and what makes
       *  LAYDEL able to say what it is about to erase. */
      layerShapeIds: () => {
        const by: Record<string, number> = {};
        for (const p of everything()) by[p.layer] = (by[p.layer] || 0) + 1;
        return by;
      },

      /** BEDIT — open a block's definition on the drawing, edit it, save it back.
       *
       *  ⚠⚠ **THE CHROME'S OWN `beditOpen`/`beditClose` POST TO A REJECTING STUB.** They were
       *  written against the design app's endpoint and its answer shape (`w_mm`, `d_mm`, a
       *  catalogue `code`) — none of which pdcad has. This is the same repointing BLOCK and
       *  WBLOCK needed: the live path is an edit op, so BEDIT gets undo, the command line and
       *  the drawing's own tools for free.
       *  ⚠ There is NO second canvas, and that is the design. The definition is placed where
       *  its instance stood and edited with the drawing's ordinary tools.
       */
      beditOpen: () => {
        const s2 = live.current;
        if (!s2.id) return { why: "No drawing is open." };
        const sel = s2.sel.filter((p) => p.kind === "INSERT");
        if (sel.length !== 1) {
          return { why: "Select ONE placed block to edit its definition." };
        }
        void (async () => {
          try {
            const r = await editDrawing(s2.id!, "bedit", [sel[0].handle], { mode: "open" });
            sayRef.current?.(`  ${r.opened} — ${r.objects ?? 0} object(s) on the drawing. `
                             + "Edit them, then Save Block.");
            setBedit(String(r.opened ?? ""));
            setSel([]);
            await refresh();
          } catch (e) { fail(e); }
        })();
        return { n: 1 };
      },

      /** Save Block / Discard. ⚠ Saving changes EVERY placed copy — that is what a block is,
       *  and the message says the number so it is a decision rather than a surprise. */
      beditClose: (save: boolean) => {
        const s2 = live.current;
        if (!s2.id) return { why: "No drawing is open." };
        void (async () => {
          try {
            const r = await editDrawing(s2.id!, "bedit", [],
                                        { mode: save ? "save" : "cancel" });
            sayRef.current?.(save
              ? `  ${r.saved} saved — ${r.objects ?? 0} object(s), `
                + `${r.instances ?? 0} placed copy(s) updated`
              : `  ${r.cancelled} — edit discarded, the block is as it was`);
            setBedit(null);
            await refresh();
          } catch (e) { fail(e); }
        })();
        return { n: 1 };
      },

      /** CHSPACE — move the selection between model space and a paper layout.
       *
       *  ⚠⚠⚠ **THE RIBBON REFUSED THIS WITH THE DESIGN APP'S REASON.** "there is one space
       *  here — Model" is true of `/opt/dsn`, and `models.Space`'s own docstring says so —
       *  pdcad has real layouts precisely so this command means something. Measured: 971
       *  paper-space layouts across 144 drawings.
       *  ⚠ The SPACE the drafter is looking at is sent as `to`. The server cannot know it, and
       *  its fallback — the first layout that has a window — is right often and not always.
       */
      /** EXPORTLAYOUT — the OPEN layout as a new drawing, flattened. See `edits.do_exportlayout`.
       *
       *  ⚠⚠ **THE LAYOUT IS THE ONE ON SCREEN, NOT THE FIRST ONE ON THE SERVER.** The endpoint's
       *  fallback picks the first layout that HAS a viewport, which is right when nobody said —
       *  and wrong every time somebody is looking at the third sheet. The browser is the only
       *  thing that knows which tab is open, exactly as `chspace`'s `to` exists for.
       *  ⚠ No `refresh()`. It creates a SECOND drawing and leaves this one untouched, so
       *  reloading the current display list would say nothing about what happened. */
      exportLayout: () => {
        const s2 = live.current;
        if (!s2.id) return { why: "No drawing is open." };
        if (s2.space && /^model$/i.test(s2.space)) {
          return { why: "EXPORTLAYOUT exports a LAYOUT — open a sheet tab first." };
        }
        void (async () => {
          try {
            const r = await exportLayoutApi(s2.id!, s2.space || undefined);
            sayRef.current?.(
              `  ${r?.name} — ${r?.model_objects ?? 0} object(s) at ${r?.scale}x through the `
              + `viewport, ${r?.sheet_objects ?? 0} from the sheet, `
              + `${r?.outside_the_viewport ?? 0} outside it left behind`);
            if (r?.kept_whole_across_the_border) {
              sayRef.current?.(`  ⚠ ${r.kept_whole_across_the_border} object(s) cross the `
                + "viewport border and were kept WHOLE — AutoCAD would cut them");
            }
          } catch (err: any) {
            sayRef.current?.(`  ${err?.message || "EXPORTLAYOUT failed"}`);
          }
        })();
        return {};
      },
      chspace: () => {
        const s2 = live.current;
        if (!s2.id) return { why: "No drawing is open." };
        const sel = s2.sel.map((p) => p.handle);
        if (!sel.length) return { why: "Select the objects to move between spaces." };
        void (async () => {
          try {
            const r = await editDrawing(s2.id!, "chspace", sel, {});
            sayRef.current?.(`  ${r?.n ?? 0} object(s) moved from ${r?.from} to ${r?.to}`
              + `, scaled ${r?.scale}x through the viewport`);
            await refresh();
          } catch (e) { fail(e); }
        })();
        return { n: sel.length };
      },

      /** GEOMCONSTRAINT / AUTOCONSTRAIN — apply one geometric constraint, once.
       *
       *  ⚠⚠ **THE RIBBON HAS CALLED THIS SINCE THE PARAMETRIC TAB WAS LIFTED AND IT DID NOT
       *  EXIST.** `handlers.ts` does `A.constrain?.(kind)` and then `if (!r) return;` — so with
       *  no action defined, all ten geometric constraints returned `undefined` and RETURNED
       *  SILENTLY. Ten live-looking buttons, no error, no transcript line, nothing moved.
       *  ⚠ It was invisible to `tsc` because `AcadContext.A` is typed `any`, and invisible to
       *  the command survey because a command that says nothing looks exactly like one that
       *  armed a tool — which is the hole `qa/command_survey.py` now closes.
       *  ⚠ Returns `{n}` or `{why}`, the same shape as `joinText` below, because that is what
       *  the handler already reads.
       */
      constrain: (kind: string) => {
        const s2 = live.current;
        if (!s2.id) return { why: "No drawing is open." };
        const sel = s2.sel.map((p) => p.handle);
        // ⚠ HORIZONTAL, VERTICAL and AUTOCONSTRAIN measure each line against an AXIS, so they
        // need no reference and take one object. The other seven are relative to the first.
        // ⚠ Kept in step with `edits.AXIS_ONLY` — the server refuses either way, but refusing
        // here is the difference between a sentence and a round trip that ends in one.
        const axisOnly = kind === "horizontal" || kind === "vertical" || kind === "auto";
        if (sel.length < (axisOnly ? 1 : 2)) {
          return { why: axisOnly
            ? "Select the objects to straighten."
            : "Select two or more objects — the first one is the reference." };
        }
        void (async () => {
          try {
            const r = await editDrawing(s2.id!, "constrain", sel, { kind });
            const n = Number(r?.n ?? 0);
            sayRef.current?.(n
              ? `  ${n} object${n === 1 ? "" : "s"} constrained`
              : "  nothing to change — the selection already satisfies it");
            await refresh();
          } catch (e) { fail(e); }
        })();
        return { n: sel.length - (axisOnly ? 0 : 1) };
      },

      /** TXT2MTXT / TEXTJOIN — the selected notes into ONE multi-line note.
       *
       *  ⚠⚠ **IN THE ORDER THEY READ ON THE SHEET, NOT THE ORDER THEY WERE TYPED.** Top to
       *  bottom, then left to right. A drafter joining a column of notes expects the column
       *  back; joining by creation order gives a paragraph in whatever sequence somebody typed
       *  them in over the years, which is the one order that is never right.
       *  ⚠ The Y comparison has a TOLERANCE of half a line: two notes on the same line of a
       *  schedule differ by a rounding error in Y, and sorting them strictly would interleave
       *  the columns.
       *  ⚠ It ERASES the originals, which is what TXT2MTXT does — and the undo is one step
       *  because the draw and the erase are two ops in one command, not two commands.
       */
      joinText: () => {
        const s2 = live.current;
        const d = s2.dl;
        if (!s2.id || !d) return { why: "No drawing is open." };
        const want = new Set(s2.sel.map((p) => p.handle));
        const rows = d.texts
          .filter((t) => want.has(t[7]) && String(t[6] ?? "").length)
          .map((t) => ({ handle: t[7], x: t[2], y: t[3], h: t[4], text: String(t[6]) }));
        if (rows.length < 2) return { why: "Select two or more notes to join." };
        const line = Math.max(...rows.map((r) => r.h)) * 0.6;
        rows.sort((a, b) => (Math.abs(a.y - b.y) > line ? b.y - a.y : a.x - b.x));
        const top = rows[0];
        const body = rows.map((r) => r.text).join("\\P");
        void (async () => {
          try {
            await editDrawing(s2.id!, "draw", [],
                              { kind: "MTEXT", points: [[top.x, top.y, 0]], text: body,
                                height: top.h, layer: s2.sel[0]?.layer || s2.drawLayer,
                                space: s2.space ?? "Model" });
            const er = await editDrawing(s2.id!, "erase", rows.map((r) => r.handle), {});
            sayRef.current?.(`  ${rows.length} notes joined into one`);
            setSel([]);
            await refresh(er);
          } catch (e) { fail(e); }
        })();
        return { n: rows.length };
      },

      // ── the property setters ─────────────────────────────────────────────────────────────
      // ⚠⚠ **THESE WERE THE PROPERTIES PANEL'S OWN DROPDOWNS AND NONE OF THEM WROTE ANYTHING.**
      // Colour, lineweight, linetype and transparency are four live `select` controls the ribbon
      // has rendered since it was lifted, each calling an action the studio never defined — so
      // choosing a value moved the dropdown and left the drawing alone.
      // ⚠ On the SELECTION, not on what is about to be drawn: the strip above the drawing is the
      // other one, and the two are different commands in AutoCAD too.
      setPen: (what: string, value: unknown) => {
        const s2 = live.current;
        if (!s2.sel.length) { sayRef.current?.("  Select objects first."); return; }
        // ⚠ "" is ByLayer for all three, and ByLayer is a REAL value — 256 for a colour, -1 for
        // a lineweight, the name `ByLayer` for a linetype — not an absence.
        const p: Record<string, unknown> =
          what === "colour" ? { aci: value === "" ? 256 : Number(value) }
          : what === "lw" ? { lineweight: value === "" ? -1 : Number(value) }
          : what === "ltype" ? { linetype: value === "" ? "ByLayer" : String(value) }
          : {};
        if (!Object.keys(p).length) { sayRef.current?.(`  ${what} is not a property here`); return; }
        void commit("setprop", p);
      },
      setAlpha: (pct: number) => {
        if (!live.current.sel.length) { sayRef.current?.("  Select objects first."); return; }
        void commit("setprop", { transparency: Math.max(0, Math.min(90, Math.round(pct))) });
      },
      /** TEXTHEIGHT — returns the count, because the chrome prints it. */
      setTextHeight: (mm: number) => {
        const s2 = live.current;
        const n = s2.sel.filter((p) => TEXTISH.has(p.kind)).length;
        if (!n) return 0;
        void commit("setprop", { height: mm });
        return n;
      },
      /** PDMODE — how a POINT is drawn. ⚠ A SYSVAR, so it lives in the drawing and comes back
       *  with it; AutoCAD keeps it there too and that is why a colleague's file looks different. */
      setPdmode: (m: number) => {
        const s2 = live.current;
        if (!s2.id) return;
        void (async () => {
          try {
            const rep = await editDrawing(s2.id!, "setvar", [], { name: "PDMODE", value: Number(m) });
            sayRef.current?.(`  PDMODE = ${Number(m)}`);
            await refresh(rep);
          } catch (e) { fail(e); }
        })();
      },
      setCycling: (on: boolean) => setTog((t) => ({ ...t, cycling: !!on })),
      setViewPref: (pref: { dark?: boolean }) => {
        if (typeof pref?.dark === "boolean") setDark(pref.dark);
      },

      // ── the UCS ─────────────────────────────────────────────────────────────────────────
      /** UCSWORLD — back to the drawing's own 0,0, with the move on the Previous stack. */
      resetAxes: () => {
        if (isWorldUcs(live.current.ucs)) { sayRef.current?.("  already at the world origin"); return; }
        pushUcs(WORLD_UCS);
        sayRef.current?.("  origin 0.0, 0.0 — world");
      },
      /** UCSPREV — one step back up the stack.
       *  ⚠⚠ **NOT THE SAME OFFER AS UCS WORLD, WHICH IS WHY BOTH EXIST.** A drafter who moved
       *  the origin onto a rotated wall to detail it wants the PREVIOUS origin back, not the
       *  corner of the site. */
      ucsPrev: () => {
        const st2 = ucsStack.current;
        if (!st2.length) { sayRef.current?.("  no previous origin"); return; }
        const back = st2[st2.length - 1];
        ucsStack.current = st2.slice(0, -1);
        setUcsDepth(ucsStack.current.length);
        setUcs(back);
        if (live.current.id != null) {
          void editDrawing(live.current.id, "ucs", [],
                           { origin: [back.x, back.y, 0], rot: back.rot }).catch(() => {});
        }
        followUcs(back);
        sayRef.current?.(`  origin ${back.x.toFixed(1)}, ${back.y.toFixed(1)}`
                         + (Math.abs(back.rot) > 1e-12
                            ? ` at ${(back.rot * 180 / Math.PI).toFixed(1)}°` : " — world"));
      },
      /** UCSMAN — save, restore and delete named origins.
       *  ⚠ Called with NO arguments it REPORTS, which is what the shell's prompt reads to list
       *  what exists before asking. One entry point rather than two, so the list the drafter is
       *  shown and the store the name resolves against cannot drift apart. */
      ucsNamed: (name?: string, op?: string) => {
        let saved: Record<string, Ucs> = {};
        try { saved = JSON.parse(localStorage.getItem(UCS_KEY) || "{}") || {}; } catch { saved = {}; }
        const names = Object.keys(saved).sort();
        if (!name) return { names, current: live.current.ucs };
        const key = name.trim();
        if (!key) return { names, current: live.current.ucs };
        if (op === "delete") {
          if (!(key in saved)) { sayRef.current?.(`  no saved origin called "${key}"`); return { names }; }
          delete saved[key];
          localStorage.setItem(UCS_KEY, JSON.stringify(saved));
          return { did: "deleted", name: key, names: Object.keys(saved).sort() };
        }
        // ⚠ A KNOWN name RESTORES and an unknown one SAVES — the shell's prompt says so, and
        // asking "save or restore?" as a second question for a one-word answer is a dialog.
        const hit = Object.keys(saved).find((k) => k.toLowerCase() === key.toLowerCase());
        if (hit) {
          pushUcs(saved[hit]);
          return { did: "restored", name: hit, names };
        }
        saved[key] = live.current.ucs;
        localStorage.setItem(UCS_KEY, JSON.stringify(saved));
        return { did: "saved", name: key, names: Object.keys(saved).sort() };
      },

      /** ARRAYPATH — distribute the selection along a picked curve, spaced by ARC LENGTH.
       *  ⚠⚠ **TWO PICKS, IN ORDER, WHICH IS WHY IT ARMS RATHER THAN RUNS.** The path is the
       *  SECOND argument and is not part of the selection — arraying it along itself is the
       *  mistake this shape prevents. */
      pathArray: () => {
        const s2 = live.current;
        if (!s2.sel.length) { sayRef.current?.("  Select the objects to array first."); return; }
        setPathWant({ items: s2.sel });
        sayRef.current?.("Select the path curve:");
      },
      /** PASTEBLOCK — the next paste wraps its objects in ONE anonymous block. */
      clipPasteBlock: () => {
        if (!live.current.clip) { sayRef.current?.("  The clipboard is empty."); return {}; }
        setPasteBlock(true);
        setMode("paste");
        return {};
      },

      /** MLEADERALIGN — line the selected leaders' NOTES up in a column.
       *  ⚠ Returns the count; the chrome prints it and warns when it is zero. */
      alignLeaders: () => {
        const s2 = live.current;
        const n = s2.sel.filter((p) => p.kind === "LEADER").length;
        if (n < 2) return 0;
        void commit("leaderalign", {});
        return n;
      },

      /** MLEADERCOLLECT — merge the selected tags into one note, keeping every arrow.
       *  ⚠ Returns `{n}` or `{why}` — the chrome prints one and warns with the other, and a
       *  refusal from the server (a multi-line note) has to reach the drafter as a sentence. */
      collectLeaders: () => {
        const s2 = live.current;
        const n = s2.sel.filter((p) => p.kind === "LEADER").length;
        if (n < 2) return { why: "Select two or more tags to collect." };
        void commit("leadercollect", {});
        return { n };
      },

      /** DIMSPACE — even out the gaps in a stack of linear dimensions.
       *  ⚠ Returns the count synchronously because the chrome prints it; the edit is a round
       *  trip and a refusal still surfaces through `fail`. */
      dimSpace: () => {
        const s2 = live.current;
        const n = s2.sel.filter((p) => p.kind === "DIMENSION").length;
        if (n < 2) { sayRef.current?.("  Select two or more linear dimensions."); return 0; }
        void commit("dimspace", {});
        return n;
      },

      // ── ISOLATEOBJECTS / UNISOLATEOBJECTS ────────────────────────────────────────────────
      isolate: () => {
        const s2 = live.current;
        if (!s2.sel.length) { sayRef.current?.("  Select the objects to isolate."); return; }
        const keep = new Set(s2.sel.map((p) => p.handle));
        // ⚠ Everything ELSE, computed from the display list rather than remembered — a drafter
        // can isolate twice in a row, and the second one has to narrow the first rather than
        // replace it.
        const gone = new Set(hiddenRef.current);
        for (const p of everything()) if (!keep.has(p.handle)) gone.add(p.handle);
        setHidden(gone);
        sayRef.current?.(`  ${keep.size} object${keep.size === 1 ? "" : "s"} isolated — `
                         + `${gone.size} hidden`);
      },
      unisolate: () => {
        const n = hiddenRef.current.size;
        setHidden(new Set());
        sayRef.current?.(n ? `  ${n} objects back` : "  nothing is isolated");
      },

      // ── the clipboard ────────────────────────────────────────────────────────────────────
      clipCopy: () => {
        const s2 = live.current;
        if (!s2.sel.length) { sayRef.current?.("  Select objects first."); return; }
        const hs = s2.sel.map((p) => p.handle);
        setClip({ handles: hs, cut: false, base: cornerOf(hs), from: s2.id });
        sayRef.current?.(`  ${hs.length} object${hs.length === 1 ? "" : "s"} copied`);
        // ⚠ The geometry is fetched NOW, not at paste time: by then this drawing may be closed,
        // and a clipboard that empties when you switch tabs is not a clipboard.
        void (async () => {
          try {
            const r = await editDrawing(s2.id!, "clipcopy", hs, {});
            setClip((c) => (c && c.handles === hs
              ? { ...c, rows: r.rows, blocks: r.blocks,
                  base: { x: (r.base || [0, 0])[0], y: (r.base || [0, 0])[1] } } : c));
          } catch { /* ⚠ the same-drawing paste still works without it — say nothing */ }
        })();
      },
      clipCut: () => {
        const s2 = live.current;
        if (!s2.sel.length) { sayRef.current?.("  Select objects first."); return; }
        const hs = s2.sel.map((p) => p.handle);
        // ⚠⚠ **THE OBJECTS GO NOW.** They used to stay on screen until the paste, which then
        // MOVED them — this app's own invention, and one it had to explain in the command line
        // every time. AutoCAD removes them at CUTCLIP and PASTECLIP inserts a copy; the bar is
        // AutoCAD's behaviour, so `clipcut` takes the payload and erases in ONE undoable step.
        void (async () => {
          try {
            const r = await editDrawing(s2.id!, "clipcut", hs, {});
            setClip({ handles: [], cut: true, rows: r.rows, blocks: r.blocks,
                      base: { x: (r.base || [0, 0])[0], y: (r.base || [0, 0])[1] },
                      from: s2.id });
            setSel([]);
            sayRef.current?.(`  ${hs.length} object${hs.length === 1 ? "" : "s"} cut`);
            await refresh(r);
          } catch (e) { fail(e); }
        })();
      },
      /** COPYBASE — copy, then ask for the point the paste will place BY. */
      clipCopyBase: () => {
        const s2 = live.current;
        if (!s2.sel.length) { sayRef.current?.("  Select objects first."); return; }
        setClip({ handles: s2.sel.map((p) => p.handle), cut: false, base: { x: 0, y: 0 },
                  from: s2.id });
        setClipBase(true);
        sayRef.current?.("Specify base point:");
      },
      clipPasteOrig: () => {
        const c2 = live.current.clip;
        if (!c2) { sayRef.current?.("  The clipboard is empty."); return; }
        // ⚠ A CUT pasted back where it came from is a no-op, and saying so beats an undo step
        // that changed nothing.
        if (c2.cut) { sayRef.current?.("  they are already there — a cut pasted at its own "
                                       + "coordinates moves nothing"); setClip(null); return; }
        void pasteAt(c2.base);
      },
      clipPasteSpec: () => {
        const c2 = live.current.clip;
        if (!c2) { sayRef.current?.("  The clipboard is empty."); return; }
        void pasteAt(c2.base, true);
      },

      // ── MATCHPROP ────────────────────────────────────────────────────────────────────────
      // ⚠⚠ **IT ARMS A MODE, IT DOES NOT ACT ON THE SELECTION.** MATCHPROP needs a source object
      // and then a run of targets — two gestures, not a command with an argument — which is why
      // `handlers.ts` marks it `arms: true` and clears the selection first.
      armMatchProp: () => {
        matchFrom.current = null;
        setMatching(true);
        setSel([]);
        sayRef.current?.("Select source object:");
      },

      // ── the layer tools: LAYCUR, LAYMCH, COPYTOLAYER, LAYDEL ─────────────────────────────
      // ⚠⚠ **THESE WERE LIT AND DEAD.** The ribbon greys a control when `handlers.ts` gives it
      // no `run` — but the run calls `A.moveToLayer(...)`, and Studio defined no such action, so
      // the button looked alive, printed a line into the command history and moved nothing. The
      // greying is one layer too high to catch it; see the Proxy at the end of this file, which
      // is what made it visible at all.
      // ⚠ They return the count SYNCHRONOUSLY because the chrome prints it, and the edit itself
      // is a round trip. The number is the selection size — right whenever the server agrees,
      // and a refusal still surfaces through `fail`.
      moveToLayer: (to: string) => {
        const n = live.current.sel.length;
        if (!n || !to) return 0;
        void commit("setprop", { layer: String(to) });
        return n;
      },
      copyToLayer: (to: string) => {
        const s2 = live.current;
        const n = s2.sel.length;
        if (!n || !to || !s2.id) return 0;
        // ⚠⚠ **THE COPY LANDS ON TOP OF THE ORIGINAL, WHICH IS THE WHOLE POINT OF THE COMMAND.**
        // COPYTOLAYER is "keep the setting-out and get a copy on the plotted layer" — a
        // displacement would put the copy somewhere the drafter has to move it back from.
        // ⚠ Two ops rather than one, so the second addresses the handles the FIRST made. Copying
        // and then setting the layer on the SELECTION would move the originals instead.
        void (async () => {
          try {
            const made = await editDrawing(s2.id!, "copy", s2.sel.map((p) => p.handle),
                                           { delta: [0, 0, 0] });
            const hs: string[] = made.handles || [];
            const lrep = hs.length
              ? await editDrawing(s2.id!, "setprop", hs, { layer: String(to) })
              : undefined;
            sayRef.current?.(`  ${hs.length} object${hs.length === 1 ? "" : "s"} copied to ${to}`);
            await refresh(lrep);
          } catch (e) { fail(e); }
        })();
        return n;
      },
      /** LAYDEL — erase everything on the selection's layers, not just the selection.
       *  ⚠ It says the count first. This is the one layer command that destroys work, and the
       *  number is usually far larger than what is selected — which is exactly the surprise. */
      eraseLayer: () => {
        const s2 = live.current;
        if (!s2.id || !s2.sel.length) { sayRef.current?.("  Select an object on the layer first."); return 0; }
        const want = new Set(s2.sel.map((p) => p.layer));
        const doomed = everything().filter((p) => want.has(p.layer));
        if (!doomed.length) return 0;
        void (async () => {
          try {
            const delRep = await editDrawing(s2.id!, "erase", doomed.map((p) => p.handle), {});
            sayRef.current?.(`  ${doomed.length} objects erased from `
                             + `${[...want].join(", ")}`);
            setSel([]);
            await refresh(delRep);
          } catch (e) { fail(e); }
        })();
        return doomed.length;
      },

      // ── ZOOM Previous ────────────────────────────────────────────────────────────────────
      /** `ZOOM ⏎` — AutoCAD's option prompt, which is the keyboard route to Previous, Window and
       *  a scale factor. ⚠ The ribbon button still fits the drawing in one click; see
       *  `typedRun` in `AutoCadShell.fire`. */
      zoomPrompt: () => {
        setPedWant({ op: "zoom", mode: "", kind: "option" });
        setTyped("");
        sayRef.current?.(ZOOM_PROMPT);
      },
      canViewPrev: () => viewHist.current.length > 0,
      viewPrev: () => {
        const was = viewHist.current.pop();
        if (!was) { sayRef.current?.("  No previous view."); return; }
        lastView.current = was;          // ⚠ or the restore is itself recorded on the next change
        api.current?.setView(was);
      },
      // ⚠ A real toggle over a real list — the drop-up's fourteen ticks read straight off it.
      toggleSnap: (n: string) => {
        // ⚠⚠ **AND IT IS WRITTEN BACK TO `OSMODE`, BECAUSE THAT IS WHERE AutoCAD KEEPS IT.**
        // The running snaps are a bitmask in the drawing's header, not an app preference —
        // reopening a drawing must restore the snaps it was saved with, and a drafter who types
        // `OSMODE 4133` must see the drop-up change. Two places holding the same fact is how
        // they come to disagree.
        setSnaps((v) => {
          const next = v.includes(n) ? v.filter((x) => x !== n) : [...v, n];
          const id = live.current.id;
          if (id) void editDrawing(id, "setvar", [], { name: "OSMODE", value: maskOf(next) })
                        .catch(() => undefined);
          return next;
        });
        // ⚠⚠ **A SNAP THAT IS TICKED AND DOES NOTHING IS THE DEFECT THIS WHOLE FILE EXISTS TO
        // FIX** — the drop-up claimed five running snaps for a week while nothing snapped. Six
        // of the fourteen are still not built, so ticking one SAYS SO rather than lighting up
        // and lying. `snap.ts` owns the list, so this cannot drift from what is implemented.
        if (!IMPLEMENTED.has(n)) sayRef.current?.(`  ${n} snap is not built yet`);
      },
    };
    // ⚠ Published for the mode effect, which runs ABOVE this and cannot see `real`. Assigned
    // here rather than inside `runCmd`, where it would only exist after the first call — which
    // is the one call that needs it.
    runCmdRef.current = real.runCmd as (n: string, e?: Record<string, unknown>) => void;
    // ⚠⚠⚠ **THE FALLBACK USED TO BE `() => ({})` — A FUNCTION THAT SILENTLY DID NOTHING — AND
    // THAT MADE FIFTY RIBBON BUTTONS LIVE AND DEAD AT THE SAME TIME.** Audited 2026-09-03: the
    // ribbon calls 73 actions and this object defined 23 of them, so every other button was
    // clickable, un-greyed, and answered with silence. Verified in the running app: **F8 lights
    // ORTHO and the ORTHO button beside it does nothing.** That is precisely the failure this
    // whole shell is built to prevent — a grey with a reason teaches something, a live control
    // that does nothing teaches that the app is broken.
    //
    // ⚠ It still returns an object, because call sites read a field off the result. What it no
    // longer does is stay quiet: an action nobody has written now SAYS SO, once per press, in
    // the place AutoCAD would have printed a prompt. And it is named, so the next person can
    // grep for it.
    return new Proxy(real, {
      get: (t, p) => {
        if (typeof p !== "string") return undefined;
        const got = t[p];
        if (got) return got;
        return () => {
          // ⚠⚠ **DEFERRED, AND THAT IS NOT TIDINESS — SAYING IT INLINE CRASHED THE APP.**
          // Some of these names are QUERIES the ribbon calls while it is building itself
          // (`A.canViewPrev()`, `A.selectionLayers()`), so speaking straight away wrote state
          // during the render that was asking, React answered with *#301: Too many
          // re-renders*, and the whole page went blank. A `setTimeout` puts the message after
          // the render that provoked it.
          // ⚠ Once per name, because a query is asked on every render and a command line that
          // repeats the same line forever is noise rather than information.
          if (!toldRef.current.has(p)) {
            toldRef.current.add(p);
            setTimeout(() => sayRef.current?.(`  ${p} is not built yet`), 0);
          }
          return {};
        };
      },
    });
  }, [current, open, commit, step]);

  /** The FIRST underlay in the drawing, which is what one slider and one toggle can honestly
   *  represent. ⚠ From `images`, not from a separate query: an attached page draws through that
   *  channel and carries its own fade and monochrome flag on the row. */
  const under = useMemo(
    // ⚠ By KIND, never by file name. An IMAGE attached from a file called `plan.pdf` is not an
    // underlay, and a name heuristic would light UFADE and Monochrome on a photograph.
    () => (dl?.images ?? []).find((r) => (r[14] || "").endsWith("UNDERLAY")) ?? null,
    [dl]);

  const st = useMemo(() => ({
    ...tog, dark, dirty: false,
    // ⚠⚠ **`st.cursor`, NOT `T.coords`.** `shell.tsx` builds the status bar's X,Y,Z readout out
    // of `st?.cursor` and prints an em dash when it is absent — so the one number on that bar
    // which is supposed to move continuously sat at "—" and the app read as frozen.
    cursor, snap: snapHit,
    // ⚠⚠ **THE COORDINATE BOX READS IN THE UCS, WHICH IS THE POINT OF SETTING ONE.** `cursor`
    // stays WORLD because every preview, every commit and every hit test is world — publishing
    // one field in two frames under one name is how a UCS bug becomes invisible.
    cursorUcs: cursor ? worldToUcs(cursor, ucs) : null,
    ucs, ucsWorld: isWorldUcs(ucs),
    selected: sel.length ? sel[0].handle : null,
    selShapes: sel.map((p) => p.handle),
    // ⚠ The NAME of the block open for editing, or null. On `st` rather than on `A`: the
    // actions object is memoised without the drawing's state on purpose — `shell.tsx` measured
    // 3.48 ms of handler rebuilding for a mouse that had moved four pixels — so a value that
    // changes belongs on the state object beside `selected`, not on the action table.
    bedit,
    workspace: "Drafting & Annotation", units: "mm", actions: A,
  }), [tog, dark, A, cursor, sel, snapHit, ucs, under, bedit]);

  const T = useMemo(() => ({
    dark, showGrid: tog.grid, ortho: tog.ortho, polar: tog.polar, otrack: tog.otrack,
    polarAng,
    snapMode: tog.snapmode, lwt: tog.lwt, dynin: tog.dynin, inferCon: tog.infer,
    drawLayer, dimKind, pen: 7, iso: tog.iso, cycling: tog.cycling,
    // ⚠ Read by the status bar to light the button and to choose which of the two commands it
    // runs. Hardcoded absent until now, so the toggle could never come back on.
    isolated: hidden.size > 0,
    // ⚠ Read by `handlers.ts` to grey the three Paste buttons since the ribbon was lifted, and
    // hardcoded to 0 until now — so Paste was permanently dead whatever was on the clipboard.
    clipCount: clip?.handles.length ?? 0,
    // ⚠ `showAlpha` and `annoMon` were READ by the status bar and never published, so both
    // drew permanently dim whatever the toggle said. Same shape as `canContinueArc`.
    showAlpha: tog.transparency, annoMon: tog.annomon,
    // ⚠ Written at last, after being READ by `handlers.ts` since the ribbon was lifted. ARC
    // Continue was greyed saying "nothing has been drawn yet" however much had been drawn.
    canContinueArc: !!cont,
    // ⚠ `undefined` means "no single window is selected", which is a DIFFERENT state from
    // "selected and unlocked" — the ribbon greys the first and toggles the second.
    vpLock: pickedViewport ? !!pickedViewport.lock : undefined,
    // ⚠ An ARRAY — see SNAPS_ON. Master OSNAP off means none are running, which is what F3 does.
    snaps: tog.osnap ? snaps : [],
    // ⚠ The status bar reads the live pointer from here — AutoCAD's coordinate box is the one
    // thing on that bar that moves continuously, and a static 0,0 reads as a frozen program.
    coords: cursor ? [cursor.x, cursor.y] : null,
    // ⚠ FRAME is the ONE underlay control that genuinely is a view setting — AutoCAD's `FRAME`
    // system variable. Fade, monochrome and the clip all go out in the DXF; whether the outline
    // is stroked on this screen does not.
    uframe: tog.uframe,
    // ⚠ Published so the ribbon can GREY UCS World and UCS Previous when there is nothing for
    // them to do. A lit button that reports "already at the world origin" is the defect class
    // this app keeps finding; saying it in advance costs two booleans.
    ucsWorld: isWorldUcs(ucs), ucsCanPrev: ucsDepth > 0,
    // ⚠⚠⚠ **THESE FIVE WERE READ BY `handlers.ts` AND NEVER PUBLISHED, AND THAT BECAME A LIE.**
    // The four underlay controls sat grey saying "this drawing has no underlay to frame" — dead
    // but harmless while pdcad had no underlay concept, and FALSE the moment PDFATTACH landed
    // and the 207 client drawings with a PDFUNDERLAY started drawing. A control that states
    // something untrue about the drawing is worse than one that is merely dead.
    // ⚠ `images` covers BOTH — an attached page draws through that channel — and `underlays`
    // covers the client ones we cannot resolve, which are still underlays for these purposes.
    hasUnderlay: !!under,
    // ⚠ The FADE of the FIRST one, which is what a single slider can honestly represent. Two
    // underlays at different fades show the first; moving it sets both, which is what AutoCAD's
    // UFADE does — it is a per-drawing control there.
    // ⚠⚠ `handlers.ts` reads this as 0–1 and shows `90 - v*100` on the slider; the display list
    // carries AutoCAD's own 0–100. Converted HERE, once, at the boundary between the two.
    underlay: (under?.[12] ?? 0) / 100,
    underlayMono: !!under?.[13],
    // ⚠ `hasClip` is about the CLIP, not about having a boundary path: every underlay this app
    // attaches has one (its page), and calling that "clipped" would light Remove Clip on an
    // unclipped page. See `do_underlay`, where removing it is refused for the same reason.
    hasClip: false,
  }), [tog, dark, cursor, snaps, drawLayer, dimKind, cont, pickedViewport, polarAng, clip, hidden,
       ucs, ucsDepth]);

  // ⚠ A REF, filled below rather than passed — see `Panels.tsx` and `chrome/shell-types.ts`.
  const panels = useRef<any>(null);
  panels.current = {
    inspector: () => <Properties sel={sel} dl={dl} drawing={current} onSet={setProp}
                                onAttr={setAttr}
                                // ⚠ Acts on the SELECTION, so a sheet with two underlays can
                                // have them set apart — unlike the ribbon's slider, which is
                                // UFADE and is per drawing.
                                onUnderlay={(spec) => void underlayOp(
                                  spec, (r) => `  underlay changed (${r.changed ?? 0})`)} />,
    panelBody: (which: string) =>
      (which === "layers"
        ? <Layers dl={dl} rows={layerRows} onPatch={patchLayer}
                  vpFreeze={pickedViewport ? new Set(pickedViewport.off) : null}
                  onVpFreeze={vpFreeze} /> : null),
  };

  /** STYLE — write a text style and redraw. ⚠ Changing one changes every label that uses it,
   *  so the report says how many that is rather than leaving it to be discovered. */
  const applyTextStyle = useCallback(async (name: string,
                                            vars: Record<string, string | number>) => {
    if (!id) return;
    try {
      const rep = await editDrawing(id, "textstyle", [], { name, vars });
      sayRef.current?.(`  style ${rep.style} set — ${rep.affects ?? 0} pieces of text use it`);
      await refresh(rep);
    } catch (e) { fail(e); }
  }, [id, refresh]);

  /** VPLAYER — freeze or thaw one layer in the selected window. */
  const vpFreeze = useCallback((name: string, freeze: boolean) => {
    const v = pickedViewport;
    if (!v || !id) return;
    void (async () => {
      try {
        const rep = await editDrawing(id, "vplayer", [v.h], { layers: [name], freeze });
        sayRef.current?.(`  ${name} ${freeze ? "frozen in" : "thawed in"} `
                         + `${rep.viewports ?? 0} viewport`);
        await refresh(rep);
      } catch (e) { fail(e); }
    })();
  }, [pickedViewport, id, refresh]);

  /** The drag preview: how far the selection would move if you clicked now.
   *  ⚠ `cursor` is the SNAPPED point — see `Canvas2D.onCursor` — so the preview lands exactly
   *  where the marker says it will, which is the entire reason a drafter trusts a snap. */
  const preview = useMemo(() => {
    if (!cmd || !cmd.pts.length || !cursor) return null;
    const def = COMMANDS[cmd.name];
    const n = typedStore.v === "" ? null : Number(typedStore.v);
    const m: Affine = def.preview(cmd.pts, cursor, Number.isFinite(n as number) ? n : null);
    // ⚠ The rubber band runs from the LAST point picked to the cursor. For MOVE that is the
    // displacement; for MIRROR it is the mirror line itself, which is the thing being chosen.
    return { m, from: cmd.pts[cmd.pts.length - 1], to: cursor };
    // ⚠⚠ `typedGen`, not `typed` — the buffer is not state any more (see `typedStore`), and this
    // is the ONE place a render still has to follow it: MOVE's rubber band takes a typed
    // displacement, ROTATE's a typed angle. `setTyped` bumps the counter only while a modify
    // command has a base point down, so typing a coordinate at a DRAW prompt — where nothing on
    // screen depends on the text but the box itself — costs no render at all.
  }, [cmd, cursor, typedGen]);

  /** What the DRAG would commit right now, shown greyed in the dynamic-input box. */
  const livePreviewNumber = useMemo(() => {
    if (!cmd || !cursor || !cmd.pts.length) return null;
    // ⚠⚠ **NOTHING GREYED IN WHILE `[Reference]` IS BEING ANSWERED.** The box is taking a
    // reference angle, not a rotation, and showing the angle the CURSOR is at would be a
    // suggested answer to a different question — a drafter who pressed Enter on it would rotate
    // the wall by the number the drag happened to be at.
    if (cmd.ref !== undefined) return null;
    const def = COMMANDS[cmd.name];
    if (!def.scalar) return null;
    const p = def.params(cmd.pts, cursor, null) as Record<string, number>;
    const v = p.angle ?? p.factor;
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  }, [cmd, cursor]);

  const ctx: ShellContext = useMemo(() => ({
    active: true,
    // ⚠⚠ **THE DOCUMENT'S LAYER ROWS, NOT THE DISPLAY LIST'S.** `shell.tsx`'s `patchLayers`
    // calls `designLayersApi.update(l.id, …)`, and `dl.layers` has no `id` — every layer command
    // in the chrome would have PATCHed `/api/layers/undefined/`. The display list is the render
    // state; this is the table.
    plan: current ? { ...current, layers: layerRows } : null,
    planId: id ?? 0, mode, setMode,
    // ⚠ The 3D viewport reports a pick; the selection lives HERE, which is what Properties,
    // the ribbon's context tabs and every SETPROP read. A Pick needs no more than this.
    pick3d: (picks) => setSel(picks.map(
      (p) => ({ handle: p.handle, kind: p.kind, layer: p.layer, aci: p.aci })) as any),
    st, A, T,
    // ⚠⚠⚠ **THIS IGNORED ITS ARGUMENT AND SIGNED THE DRAFTER OUT, WHATEVER IT WAS ASKED FOR.**
    // It was written for `file.exit`, which really does mean sign out here — the studio IS the
    // app, so there is no drawings list to go back to. But ELEVEN handlers in the lifted chrome
    // call `c.nav(...)` with one of the design app's own routes: `/design/plans` (File ▸ Open,
    // File ▸ Close All, Window ▸ Close All), `/design/<id>` (File ▸ Close, File ▸ Properties,
    // Window ▸ Close), `/design/<id>?tab=qty` (Data Extraction, Extract Table) and
    // `/design/render/<id>` (Render, Render Window). Every one of them LOGGED THE USER OUT —
    // `File ▸ Open` in an AutoCAD replacement ended the session and threw away the drawing.
    // ⇒ Only `/signout` signs out. Anything else is a route this app does not have, and it says
    // so in the command line rather than acting on a guess.
    nav: (to?: string) => {
      if (!to || to === "/signout") { void onSignOut(); return; }
      sayRef.current?.(`  ${to} is not a screen in pdcad`);
    },
    // ⚠⚠⚠ **THIS SWALLOWED EVERY FAILURE IN THE LIFTED CHROME — SEVENTEEN BUTTONS' WORTH.**
    // `void fn()` with no `.catch` turns a rejected promise into an unhandled rejection: the
    // console gets a line nobody is reading and the drafter gets NOTHING. Thirteen shell
    // actions post to endpoints pdcad does not have (`api.ts`'s `soon(...)`, which rejects on
    // purpose and says so in a sentence written to be read), and every one of those sentences
    // was going into the void — so BEDIT, the lights, the surfaces, INTERFERE and mesh
    // conversion all read as buttons that do nothing at all.
    // ⚠ The stub's own message is the refusal; there is nothing better to say here than what it
    // already says. `fail` puts it in the transcript AND raises the toast.
    run: (_k: string, fn: () => Promise<any>) => { void fn().catch(fail); },
    // ⚠⚠ **BOTH, AND IT USED TO BE ONLY THE TOAST.** `warn` is what every ribbon handler calls
    // to refuse — "Select two or more objects", "Select a viewport first" — and a toast is
    // TRANSIENT: a drafter who looked at the drawing while clicking never sees it, and it
    // leaves no record to scroll back to. AutoCAD puts every refusal in the command line, and
    // so does the rest of this app (`sayRef`). The toast stays because it is what catches the
    // eye; the transcript line is what can be re-read.
    // ⚠ Found because the command survey could not see a single constraint refusal: ten
    // handlers correctly returning a reason, and nothing in the history to prove it.
    warn: (m: string) => { toast(m); sayRef.current?.(`  ${m}`); },
    user: who.name,
    exportAs: async (fmt) => {
      if (!id) return toast("No drawing is open.");
      if (fmt === "dxf") { window.location.href = `/api/drawings/${id}/export/`; return; }
      // ⚠⚠ **`as=`, NOT `format=` — DRF owns `format` and answers 404 to a renderer it does not
      // have.** ⚠ And this is a fetch rather than a navigation, because the server may REFUSE:
      // a DWG that lost geometry comes back 409 with the counts, and `window.location.href`
      // would show the drafter a blank error page instead of the reason.
      if (fmt === "dwg") {
        toast("Writing the DWG, then reading it back to check it…");
        try {
          const r = await fetch(`/api/drawings/${id}/export/?as=dwg`,
                                { credentials: "same-origin" });
          if (r.status === 409) {
            const j = await r.json();
            toast(`DWG refused: ${j.detail}`);
            return;
          }
          if (!r.ok) { toast(`DWG export failed (${r.status}).`); return; }
          const blob = await r.blob();
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `${current?.name || "drawing"}.dwg`;
          a.click();
          URL.revokeObjectURL(a.href);
          // ⚠ SAID EVERY TIME, AND IT NAMES THE WRITER. Since 2026-09-15 the file is
          // written by pdcad's own codec (LibreDWG is GPL and cannot ship in the desktop app);
          // LibreDWG remains the fallback for a kind the codec cannot build yet. Either way the
          // check is the writer's own reader agreeing with itself, which a drafter sending the
          // file to a client should know until one has been opened in AutoCAD.
          const by = r.headers.get("X-CAD-Dwg-Writer") ?? "libredwg";
          toast(`${r.headers.get("X-CAD-Dwg-Records") ?? "?"} objects written as `
                + `${r.headers.get("X-CAD-Dwg-Version") ?? "?"} and read back intact. `
                + (by === "pdcad"
                   ? "Written by pdcad's own codec. ⚠ Not yet checked by AutoCAD itself."
                   : `Written by LibreDWG (${r.headers.get("X-CAD-Dwg-Fallback")
                      ?? "asked for"}). ⚠ Not yet checked by AutoCAD itself.`));
        } catch { toast("DWG export failed."); }
        return;
      }
      // ⚠ PDF goes through the PLOT endpoint, which is the only thing that knows about scale.
      // Fitted, because Export has no dialog to ask in — Plot does.
      // ⚠⚠ **A DOWNLOAD, NOT `window.open`.** The popup version produced no FILE: a blocker
      // stops it outright, and where it is allowed the drafter gets a tab to save from by hand
      // rather than the file AutoCAD's Export gives them. It also made EXPORTPDF unmeasurable —
      // no download event, no DOM change — so it sat in the survey's SILENT bucket. DXF and DWG
      // both already hand over a file; this is the odd one out, not a house style.
      if (fmt === "pdf") {
        toast("Plotting to PDF…");
        try {
          const r = await fetch(`/api/drawings/${id}/plot/?page=A3&landscape=auto&scale=0`,
                                { credentials: "same-origin" });
          if (!r.ok) { toast(`PDF plot failed (${r.status}).`); return; }
          const blob = await r.blob();
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `${current?.name || "drawing"}.pdf`;
          a.click();
          URL.revokeObjectURL(a.href);
          toast("PDF written.");
        } catch { toast("PDF plot failed."); }
        return;
      }
      return toast(`pdcad plots to PDF and exports DXF — "${fmt}" is not built yet.`);
    },
    reload: async () => { await refreshLayers(); },
    // ⚠ The chrome's tab ids are numeric, so the INDEX is the id and the name rides along —
    // see `ShellContext.layouts`. Model is not in this list: the chrome always draws its tab.
    layouts: layoutTabs,
    onSpace: goSpace,
    // ⚠ AutoCAD's TILEMODE, resolved server-side to a name — see `sysvars.opens_in`. The chrome
    // owns the tab, so it has to be told which one to light; setting the studio's own `space`
    // in `open()` was overruled by the chrome's next effect.
    openSpace: space,
    // ⚠ Ctrl+P. AutoCAD opens the Plot dialog rather than printing straight away, and so does
    // this — the scale is a decision, not a default.
    print: () => plotRef.current?.("drawing"),
    armDef: () => {}, openPalette: () => setOpenDlg(true),
    panels,
  }), [current, dl, id, mode, st, A, T, toast, open, layerRows, patchLayer,
       layoutTabs, goSpace, space]);

  const chrome = useAutoCadStudioChrome(ctx);
  sayRef.current = chrome.say;
  // ⚠ Beside `sayRef` and for the same reason: the key handler binds ONCE, so reading
  // `chrome.fire` from the closure would call the dispatcher this component had on its first
  // render. Ctrl+1 would then open a palette in a chrome nobody is looking at.
  fireRef.current = chrome.fire;
  // ⚠ Assigned every render beside `sayRef`, for the same reason: `pushUcs` is declared above
  // `refresh` and reaches it through this.
  refreshRef.current = refresh;
  // ⚠ Same not-hoisted problem `sayRef` solves: `finishDraw` is declared long before `A`, and a
  // typed keyword must do EXACTLY what the flyout entry does — including TTR's noun-verb check
  // and its radius prompt. Reimplementing that beside it would be a second copy to drift.
  givenRef.current = A.setCircGiven as ((g: string) => void) | undefined;
  plotRef.current = chrome.openPlot;

  /** Is a prompt waiting for something to be TYPED? Every typed answer in this app is a
   *  `DynIn` showing `typed`, and this is the list of states that render one. */
  const wantsTyped = () => {
    const L = live.current;
    return !!(L.draw || L.cmd || L.attWant || L.varWant || L.selWant || L.objCmd
              || (L.tanWant?.given === "ttr" && !(L.tanWant.r > 0))
              || pedWantRef.current || vpAskRef.current);
  };

  /** ⚠⚠⚠ **THE INPUT QUEUE — keys typed before the prompt they answer exists.**
   *
   *  A command takes ~30 ms to arm: the chrome fires it, the studio sets its state, React
   *  commits, and only then does the `DynIn` mount and focus itself. In that window
   *  `document.activeElement` is BODY and a keystroke has nowhere to go — it is not queued, not
   *  echoed, not refused. It is lost.
   *
   *  ⚠ This is not theoretical and it is not small. `LINE ⏎ 0,0 ⏎ 1000,0 ⏎ Esc` typed at 4 ms
   *  per key commits NOTHING; at 30 ms it commits a line (`/var/tmp/keyland.py` logs the
   *  focused element per key). `qa/bench_desktop.py` types at 4 ms — so every desktop number
   *  this project has published against BricsCAD timed a screen that changed because the prompt
   *  echoed, not because anything was drawn.
   *
   *  ⇒ Keys that arrive with nowhere to go are HELD and replayed when the prompt opens, which
   *  is what AutoCAD's command engine has always done: it owns the keyboard, and a box on the
   *  screen is only where the characters are shown. ⚠ Held only in the 800 ms after an Enter —
   *  a drafter idly pressing a key on the canvas must not have it turn up inside a command they
   *  start a minute later. */
  /** How many edits are in flight. ⚠ A COUNTER rather than a flag: two commits can overlap, and
   *  a flag would be cleared by whichever finished first. Read by the input queue. */
  const committing = useRef(0);
  const keyQ = useRef<string[]>([]);
  const armedAt = useRef(0);
  /** ⚠ Set while the queue is replaying, so the Enter it dispatches is not queued again — that
   *  is a loop, not a keystroke. */
  const replaying = useRef(false);
  const draining = useRef(false);
  /** When the drain started waiting for something — a prompt to open, or a commit to land. */
  const waited = useRef(0);
  /** What the command state looked like when the last Enter was dispatched. ⚠ The next step
   *  cannot run until React has committed the answer to that one; this is how the drain knows
   *  it has, without guessing at a number of milliseconds. */
  const lastSig = useRef<string | null>(null);
  /** What the drain is waiting for, for the log: `commit`, `sig` or `arm`. */
  const held = useRef("");
  /** An Enter has gone to the command line and a prompt is expected to open. */
  const arming = useRef(false);
  const drainRef = useRef<() => void>(() => {});

  /** ⚠⚠ **THE QUEUE DRAINS ONE STEP PER FRAME, AND THAT IS NOT A STYLE CHOICE.**
   *
   *  An Enter is answered by handlers that read `live.current.typed` — state, which holds the
   *  characters only AFTER React has committed them. Draining the whole queue in one pass hands
   *  every Enter an empty string. A frame per step also paces the replay at the speed the app
   *  can actually work: a scene rebuild pushes the next frame out, so the queue never runs
   *  ahead of the drawing. That is what BricsCAD's LISP arm gets from its own command queue,
   *  and it is the only way the two can be timed with one stopwatch.
   *
   *  ⚠ Keys are DISPATCHED, not reimplemented — the window handler above has thirty-odd
   *  branches deciding what Enter means at each prompt, and the chrome's command line owns what
   *  a letter means at `Command:`. A second copy of either decision would be wrong within a
   *  week. A synthetic event is untrusted, which matters only for the browser's own default
   *  actions; every branch this replay reaches is ours.
   *  ⚠⚠ The text a command line takes has to go in through the NATIVE value setter and an
   *  `input` event: React tracks the last value it wrote, and assigning `el.value` directly is
   *  invisible to it — the box shows the text and `onChange` never fires, so Enter submits the
   *  empty string. */
  const cmdBox = () =>
    document.querySelector('[data-testid="acad-cmdinput"]') as HTMLInputElement | null;
  const typeIntoCmd = (txt: string) => {
    const el = cmdBox();
    if (!el) return;
    const set = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
    set?.call(el, el.value + txt);
    el.dispatchEvent(new Event("input", { bubbles: true }));
  };
  /** The command state the drain watches for a change. ⚠ Not a deep compare and not meant to
   *  be one: every Enter this replays moves one of these, and that is all it has to see. */
  const cmdSig = () => {
    const L = live.current;
    const w = window as unknown as Record<string, unknown>;
    return `${L.draw ? `${L.draw.pts.length}.${L.draw.ask ?? -1}.${L.draw.mode}` : "-"}`
      + `|${L.cmd ? `${L.cmd.pts.length}.${L.cmd.name}` : "-"}|${L.mode}`
      + `|${L.selWant ? 1 : 0}${L.objCmd ? 1 : 0}${L.attWant ? 1 : 0}${L.varWant ? 1 : 0}`
      // ⚠⚠ **THE SELECTION AND THE EDIT COUNT BELONG IN IT.** A modify is answered in five
      // steps — `MOVE ⏎ L ⏎ ⏎ 0,0 ⏎ 200,0 ⏎` — and two of them change neither the command nor
      // the mode: `L` fills the SELECTION, and the last Enter COMMITS. Without these the drain
      // read "nothing happened", waited out its budget and then pressed the next Enter into a
      // prompt that had moved on: COPY committed 11 of 20 and MIRROR 12.
      + `|${L.sel.length}|${(w.__pdcadEditCount as number) ?? 0}`;
  };
  /** ⚠⚠⚠ **A TIMER, NOT A FRAME — the drain paced the whole benchmark.** One step per
   *  `requestAnimationFrame` is 16.7 ms a step, and a LINE is four steps, so the replay was
   *  adding ~70 ms to every command in a burst: the desktop harness read 316 ms for a LINE whose
   *  edit costs 71 and whose frames were a clean 16.7 throughout. A `setTimeout(0)` yields to the
   *  event loop — which is all that is needed, because React's scheduler runs on a
   *  `MessageChannel` and commits BEFORE the timer fires. */
  /** ⚠⚠ **A `MessageChannel`, NOT `setTimeout(0)` — a timer waits for the frame, a port message
   *  does not.** Both are macrotasks, but the browser services rAF and its paint before the
   *  timer queue, so every step of the replay was costing a frame it did not need: the gaps
   *  between steps had a 9 ms median with a 54 ms p90 while a Studio render is 2.8 ms. This is
   *  the same mechanism React's own scheduler uses, for the same reason. */
  const chan = useRef<MessageChannel | null>(null);
  if (!chan.current && typeof MessageChannel !== "undefined") {
    chan.current = new MessageChannel();
    chan.current.port1.onmessage = () => drainRef.current();
  }
  const soon = () => {
    if (chan.current) chan.current.port2.postMessage(0);
    else setTimeout(() => drainRef.current(), 0);
  };
  /** How many steps one tick may run. ⚠ A bound, not a target: a queue that never yields would
   *  freeze the tab if a prompt ever answered itself. A command is four or five steps. */
  const STEPS_PER_TICK = 12;
  const drain = () => {
    for (let n = 0; n < STEPS_PER_TICK; n++) {
      if (drainStep() !== "again") return;
    }
    soon();
  };
  /** ⚠⚠⚠ **ONE STEP — and it returns whether the next one can run in the SAME TASK.**
   *
   *  Every answer a command takes used to cost its own trip through the event loop: dispatch a
   *  key, yield, let React commit, come back. That is four or five trips for `LINE ⏎ 0,0 ⏎
   *  1000,0 ⏎ Esc`, ~11 ms each, and it is most of what a typed command costs. It was necessary
   *  while the command state only existed as React state — a handler reading `live.current`
   *  would have seen the PREVIOUS point count. It is not necessary now that `setDraw`, `setCmd`
   *  and the typed buffer all write the ref synchronously: the next Enter can be delivered
   *  immediately, React batches every update in the task, and the screen is painted once at the
   *  end instead of once per keystroke.
   *
   *  ⚠ Two things still have to yield, because they are genuinely asynchronous:
   *    * a command ARMING — the chrome fires it, the studio sets a mode, and a React EFFECT
   *      opens the prompt. Nothing here can make that land sooner.
   *    * an edit COMMITTING — the engine runs in a worker. Feeding the next command in early
   *      answers the prompt that is still on screen (see `committing`). */
  const drainStep = (): "again" | "yield" => {
    const q = keyQ.current;
    if (!q.length) {
      // ⚠⚠ **NOT IDLE UNTIL THE EDIT HAS LANDED.** `(command …)` in AutoLISP returns when the
      // command has RUN, not when its last keystroke was accepted — a caller that drew twenty
      // lines in a loop and measured the wall time would otherwise be timing the parser.
      if (committing.current > 0) { soon(); return "yield"; }
      draining.current = false; waited.current = 0; arming.current = false; lastSig.current = null;
      publishPending();
      if (waiters.current.length) {
        const w = waiters.current; waiters.current = [];
        w.forEach((f) => f());
      }
      return "yield";
    }
    publishPending();
    // ⚠⚠ **THE PREVIOUS ENTER'S ANSWER HAS TO LAND FIRST.** `finishDraw` reads `live.current`,
    // which is assigned during render: dispatching the next Enter before React has committed
    // would hand it the previous step's point count and put the second point where the first
    // one went. ⚠ Bounded — an Enter that legitimately changes nothing (a refused coordinate)
    // must not stall the queue.
    // ⚠⚠ **NOTHING IS TYPED INTO A COMMAND THAT IS STILL COMMITTING.** The keystroke after one
    // is an answer to the prompt that comes AFTER it — `L` naming the object COPY just made —
    // and feeding it in early answers the prompt that is still on screen.
    if (committing.current > 0 && performance.now() - (waited.current || performance.now()) < 3000) {
      if (!waited.current) { waited.current = performance.now(); held.current = "commit"; }
      soon(); return "yield";
    }
    if (lastSig.current !== null) {
      if (cmdSig() === lastSig.current && performance.now() - waited.current < 250) {
        held.current = held.current || "sig"; soon(); return "yield";
      }
      lastSig.current = null;
    }
    const ready = wantsTyped();
    // ⚠ A command submitted at the command line is ARMING until its prompt opens; the stamp is
    // set by the capture listener, and the drain is the only thing that clears it.
    if (!ready && !arming.current && armedAt.current > 0
        && performance.now() - armedAt.current < 400) arming.current = true;
    // ⚠ A command submitted at the command line takes ~30 ms to open its prompt. Waiting for it
    // — rather than deciding by a clock — is what keeps `0,0` out of the command line, where it
    // would come back as *Unknown command "0,0"*. Three frames, then the command line has it:
    // ZOOM's `E` and every other option letter IS answered there, and waiting forever would
    // hang the queue on them.
    // ⚠⚠⚠ **A COORDINATE IS NEVER A COMMAND NAME, SO IT IS NEVER GIVEN TO THE COMMAND LINE.**
    // The app itself refuses one there — *Unknown command "753,487"* — so a replay that gives up
    // waiting and types it at the prompt box is choosing the one destination that CANNOT work.
    // Measured before this rule, a 20x burst in the browser: CIRCLE committed 0, POINT 10,
    // MOVE 11, MIRROR 0, all of them with a leaked coordinate in the transcript. The cause is
    // that a command takes longer to arm when the app is busy — a scene rebuild is ~160 ms, ten
    // frames — and four frames of patience was a guess about a fast machine.
    // ⚠ A LETTER is genuinely ambiguous: `E` answers ZOOM's option prompt AND names a command.
    // Those still get the short wait, because waiting on one that was never going to open a
    // prompt would hang the queue.
    let peek = "";
    for (const c of q) {
      if (c === "Enter" || c === "Escape") break;
      peek = c === "Backspace" ? peek.slice(0, -1) : peek + c;
    }
    const answerish = /[,@<]/.test(peek) || /^[-+.\d]/.test(peek);
    if (!ready && (arming.current || answerish)) {
      if (!waited.current) waited.current = performance.now();
      // ⚠ Time, not attempts: a `setTimeout(0)` is under a millisecond, so a count would be a
      // number nobody could reason about. A coordinate waits as long as a busy app needs — two
      // seconds — because there is nowhere else for it to go; an ambiguous LETTER waits 60 ms
      // and is then the command line's, which is where ZOOM's `E` is answered.
      if (performance.now() - waited.current < (answerish ? 2000 : 60)) {
        held.current = held.current || "arm"; soon(); return "yield";
      }
    }
    if (ready) { arming.current = false; armedAt.current = 0; }
    const k = q[0];
    // ⚠ The replay's own transcript. Nothing in the app reads it; `/var/tmp/keyland.py` does,
    // and without it a queue that misroutes one key is indistinguishable from one that dropped
    // it. Bounded, like `__pdcadEdits`.
    {
      const w = window as unknown as Record<string, unknown>;
      const lg = (w.__pdcadQlog ||= []) as unknown[];
      lg.push({ k, t: Math.round(performance.now()), ready, arming: arming.current,
                // ⚠ What the step WAITED for, and how long. Without this the gaps between steps
                // are a number with no cause attached — and the cause is the whole question.
                wait: waited.current ? Math.round(performance.now() - waited.current) : 0,
                held: held.current || "",
                left: q.length,
                typed: live.current.typed, mode: live.current.mode,
                draw: !!live.current.draw, cmd: !!live.current.cmd });
      if (lg.length > 512) lg.splice(0, lg.length - 512);
    }
    waited.current = 0;
    held.current = "";
    if (k === "Enter" || k === "Escape") {
      q.shift();
      // ⚠⚠ **TAKEN BEFORE THE DISPATCH, and that is not a detail.** The command state is now
      // mirrored into `live` synchronously, so a signature read AFTER the key has been
      // delivered is already the new one — the drain then compares it against itself, decides
      // nothing has happened, and waits out its whole budget. Measured the moment the mirror
      // landed: every Enter waited 250 ms.
      const was = cmdSig();
      const was0 = live.current.typed;
      // ⚠⚠⚠ **NOT CLEARED BEFORE THE DISPATCH ANY MORE, AND THE REASON IS THE STORE.** While
      // `typed` was React state, `setTyped("")` here only SCHEDULED a clear — `live.current.typed`
      // still held the text when the handler read it a moment later, which is what made the
      // order look safe. `typedStore` is synchronous, so the same line wiped the coordinate
      // before `finishDraw` could read it and every command ended in silence: `LINE ⏎ 0,0 ⏎`
      // cancelled itself with an empty buffer. ⇒ Dispatch first; clear afterwards ONLY if the
      // handler did not put something there itself, which is what PEDIT's option prompt and a
      // refused coordinate do.
      replaying.current = true;
      try {
        if (ready || k === "Escape") {
          // ⚠⚠⚠ **AT THE BOX IF THERE IS ONE, AND THAT IS NOT INTERCHANGEABLE WITH `window`.**
          // Each `DynIn` carries its OWN `onEnter` — ROTATE's angle accepts a Reference keyword
          // there, PEDIT's option letter, the attribute editor's value — and the window handler
          // knows a different, overlapping set. Replaying every Enter at the window made
          // `ROTATE ▸ R` stop asking for a reference while five other scenarios went green: the
          // key has to arrive where a real one would have, and then bubble to the window like a
          // real one does.
          const dyn = document.querySelector('[data-testid="dynin"]') as HTMLElement | null;
          const ev = new KeyboardEvent("keydown", { key: k, bubbles: true, cancelable: true });
          (dyn ?? window).dispatchEvent(ev);
        } else {
          arming.current = true;
          armedAt.current = performance.now();
          cmdBox()?.dispatchEvent(new KeyboardEvent("keydown",
                                                    { key: k, bubbles: true, cancelable: true }));
        }
      } finally { replaying.current = false; }
      if (live.current.typed === was0) live.current.typed = "";
      // ⚠ The next step waits for this one to be committed — see `lastSig`.
      lastSig.current = was;
      waited.current = performance.now();
    } else {
      let txt = "";
      while (q.length && q[0] !== "Enter" && q[0] !== "Escape") {
        const c = q.shift()!;
        txt = c === "Backspace" ? txt.slice(0, -1) : txt + c;
      }
      if (txt) {
        if (ready) {
          // ⚠⚠⚠ **THE REF IS WRITTEN AS WELL AS THE STATE, AND WITHOUT THAT NOTHING WORKS.**
          // Every Enter handler reads `live.current.typed`, which is assigned during RENDER —
          // and React 19 schedules a render from a `requestAnimationFrame` callback through its
          // own scheduler, which does not always commit before the NEXT frame. Measured: the
          // drain typed `0,0`, the following frame pressed Enter, and `live.current.typed` was
          // still `''` — so `finishDraw` ended the command instead of taking the point, and the
          // transcript blamed the coordinate. The ref is the value the command engine reads;
          // the state is what the box on screen shows.
          const next = live.current.typed + txt;
          live.current.typed = next;
          setTyped(next);
        } else { typeIntoCmd(txt); }
      }
    }
    // ⚠⚠⚠ **THE NEXT STEP RUNS IN THIS TASK ONLY WHILE ONE COMMAND IS ANSWERING ITS OWN
    // PROMPTS.** Collapsing everything was measured and it was wrong: a burst with no yield at
    // all put coordinates on the command line — *Unknown command "870,330"* — and ran some
    // commands twice (29 of 20 CIRCLEs). Two boundaries are genuinely asynchronous and have to
    // be waited for, whatever the state says: a command ARMING opens its prompt in a React
    // effect, and an edit COMMITTING runs in a worker. Between them — `0,0 ⏎ 1000,0 ⏎` at a
    // prompt that is already open — nothing is asynchronous any more, because the state those
    // handlers read is mirrored into `live` as it is set.
    // ⚠⚠ **ONLY WHERE THE STATE IS MIRRORED.** `setDraw`, `setCmd` and the typed buffer write
    // `live` synchronously, so a POINT prompt can take its next answer in this task. A SELECT
    // prompt cannot: `selWant` and the selection itself are ordinary React state, so `MOVE ⏎ L ⏎
    // ⏎` read a selection that had not landed and the modify commands committed 1 of 20 while
    // every draw stayed at 20. The rule is the mirror, not the prompt.
    const L = live.current;
    // ⚠ A DRAW tool's point prompt only. A modify command's scalar — ROTATE's angle, SCALE's
    // factor — is accepted through state this does not mirror (`held`), and running its steps
    // in one task committed exactly half of a 20-command burst. Draws are 20 of 20 under this
    // rule and they are the bulk of both the benchmark and the drafting.
    const mirrored = !!L.draw && !L.cmd && !L.selWant && !L.objCmd && !L.attWant && !L.varWant
                     && !pedWantRef.current && !vpAskRef.current;
    if (!mirrored || committing.current > 0 || arming.current) { soon(); return "yield"; }
    return "again";
  };
  drainRef.current = drain;
  /** ⚠⚠⚠ **NOBODY CAN SEE A PREVIEW THAT IS ALREADY ANSWERED.** While keys are still queued and
   *  a command is mid-way, the rubber band between two of its prompts is drawn for an instant
   *  and replaced — 6 ms of canvas per step, four steps a command, for a picture no eye is
   *  quick enough to catch. `Canvas2D` reads this and re-schedules instead of painting.
   *  ⚠ ONLY mid-command: the moment a command finishes, `draw` and `cmd` are null and the paint
   *  goes through. So every command still redraws exactly once — which is what BricsCAD's arm
   *  does, and a skip that swallowed those would make the comparison a lie.
   *  ⚠ And it re-schedules rather than dropping the frame, so the screen can never be left
   *  stale by a queue that empties without another state change. */
  const publishPending = () => {
    const w = window as unknown as Record<string, unknown>;
    w.__pdcadInputPending =
      keyQ.current.length > 0 && (live.current.draw || live.current.cmd) ? 1 : 0;
  };
  const pushKey = (k: string) => {
    keyQ.current.push(k);
    publishPending();
    if (!draining.current) {
      draining.current = true;
      soon();
    }
  };

  /** ⚠⚠⚠ **A DOOR INTO THE COMMAND STREAM THAT IS NOT A KEYBOARD.**
   *
   *  Everything that drives this app from outside has had to pretend to be a drafter's fingers:
   *  `qa/bench_desktop.py` types coordinates with `xdotool`, the e2e suite types them through
   *  playwright, and BricsCAD's own arm — the one every comparison is against — does nothing of
   *  the sort. `pddrawloop.lsp` calls `(command "_.LINE" p1 p2 "")` and the program runs it. So
   *  every number this project has put beside BricsCAD's has included a keyboard path that
   *  BricsCAD never paid, and there was no way to subtract it.
   *
   *  ⇒ `window.__pdcadCmd("LINE", "0,0", "1000,0", "")` runs one command and resolves when it
   *  has COMMITTED. It goes through the same queue the keyboard feeds — one stream, one set of
   *  rules, one place where a prompt is answered — so it cannot drift from what a drafter gets.
   *  ⚠ An empty string is an empty Enter, which is what AutoLISP's `(command … "")` means.
   *
   *  This is the seam AutoLISP's `(command …)` will marshal out to: the interpreter never learns
   *  what a command is, and the studio never learns what a LISP form is. */
  const waiters = useRef<(() => void)[]>([]);

  /** ⚠⚠⚠ **THE FAST PATH: a command that is CALLED does not have to be TYPED.**
   *
   *  `__pdcadCmd` used to push the command's NAME through the queue as characters, so a
   *  programmatic command paid for the command line's resolution, a chrome round trip and a
   *  synthetic Enter per answer — each one a trip through the event loop. Measured on drawing 46:
   *  78.9 ms a LINE against BricsCAD's 40.8, with the app's own edit at 15.6 of it.
   *
   *  ⇒ Resolve the name here (`resolveCommand` is the same table the command line uses — not a
   *  second one), fire it, wait for its prompt to OPEN because that is a React effect and cannot
   *  be hurried, and then answer every prompt IN ONE TASK. `setDraw`/`setCmd`/the typed buffer
   *  all write `live` synchronously, so each answer sees the previous one's state without a
   *  render in between.
   *  ⚠ Falls back to the queue for anything that does not arm a typed prompt — ZOOM's option
   *  letter is answered at the command line and always was. A fast path that silently dropped
   *  those would be a fast path that loses commands.
   */
  // ⚠⚠⚠ **NOT EVERY COMMAND OPENS A TYPED PROMPT, AND WAITING 400 ms FOR ONE COST ZOOM 660 ms.**
  // `ZOOM ⏎ E` is answered at the COMMAND LINE — there is no `DynIn` for an option letter — so
  // the wait ran to its budget, the fast path gave up, and the queue then ran the whole command
  // a second time. Measured on drawing 10951: ZOOM extents 660 ms with the scene untouched, no
  // request made and 0.4 ms of canvas. ⇒ Wait briefly for a prompt; if none opens, answer where
  // the command line is and carry on, rather than starting again.
  /** Wait for a fired command to arm its point tool. `false` when it never does.
   *
   *  ⚠⚠⚠ **THIS 90 ms IS THE WHOLE OF THE "ZOOM IS SLOW" NUMBER, AND ZOOM IS NOT SLOW.**
   *  A command that takes its answers at the COMMAND LINE — ZOOM's `E`, an option letter, a
   *  keyword — never arms, so `runCommandFast` pays the budget in full before it types the
   *  answer, and for ZOOM the view does not move until that answer lands. Measured on
   *  `ysl-hn-center-2025`, to the painted picture:
   *
   *      typed / ribbon  ZOOM E    119-122 ms      the wheel a drafter turns    7-18 ms
   *
   *  Every frame in between is a healthy 16 ms and the canvas's own work is 7 (`geomMs` 6,
   *  `gpuMs` 0.9) — there is no work to remove, only a timeout to stop waiting for.
   *  ⚠ `qa/bench_session.py` reported 110 ms for ZOOM all through 2026-09-20 and that number was
   *  this timeout, not the drawing. It now measures the wheel beside it.
   *
   *  ⇒ The fix is to ASK whether a command arms rather than discover it by waiting, which needs
   *  the catalog to say so per command.
   *
   *  ⚠⚠⚠ **AN EARLY EXIT WAS TRIED AND REVERTED — IT BREAKS A CLICKED LINE.** Giving up once the
   *  app has settled without arming (`> 40 ms` with `committing`/`keyQ` empty) does what it says
   *  on the typed path — ZOOM extents **110 ms -> 62** with SELECT ALL unharmed at 14 — and then
   *  `qa/bench_mouse.py`, which types the command word and CLICKS the points, reports:
   *
   *      with it      LINE  0 of 10, then 1 of 10     CIRCLE RECTANG PLINE all 10 of 10
   *      without it   LINE 10 of 10, twice
   *
   *  `armWait` is reached from `runCommandFast` and nothing else, and that bench never calls
   *  `__pdcadCmd` — so the mechanism is NOT understood, and four runs say the effect is real.
   *  Shipping a change to the command-input path whose failure mode is "the line you drew did
   *  not appear", on evidence that contradicts the call graph, is not a trade worth 48 ms.
   *  ⚠ Whoever picks this up: the win is real and so is the break. Find the mechanism first —
   *  `qa/bench_mouse.py --only LINE --n 10` reproduces it in about four minutes.
   */
  /** How long a command may take to arm before `armWait` concludes it never will. ⚠ Past the
   *  ~30 ms the input-queue note records for arming, and well under the 90 ms budget it
   *  replaces as the usual exit. */
  const EARLY_MS = 40;
  const armWait = (budget = 90) => new Promise<boolean>((done) => {
    const t0 = performance.now();
    const look = () => {
      // ⚠ Checked on a TIMEOUT and not on animation frames: an rAF version noticed an arm a
      // frame late and cost SELECT ALL 16 ms of its 14. A command that arms must be seen the
      // instant it does; only the giving-up needs a clock.
      if (wantsTyped()) { done(true); return; }
      if (performance.now() - t0 > budget) { done(false); return; }
      // ⚠⚠ **STOP WAITING ONCE THE APP HAS SETTLED WITHOUT ARMING.** The budget was paid in FULL
      // by every command that takes its answers at the command line, because nothing told it to
      // give up — 90 of the 110 ms a ribbon ZOOM cost. `committing`/`keyQ` empty is the same
      // "nothing in flight" test `idleWait` uses: a command still arming is not idle. The budget
      // stays as the CEILING, so this only ever returns the same answer sooner.
      // ⚠ And the answers loop re-asks `wantsTyped()` for EVERY answer, so a command that arms
      // late still has its answer delivered to the prompt rather than the command box.
      if (performance.now() - t0 > EARLY_MS
          && committing.current <= 0 && !keyQ.current.length) { done(false); return; }
      setTimeout(look, 0);
    };
    setTimeout(look, 0);
  });
  const idleWait = () => new Promise<void>((done) => {
    const look = () => {
      if (committing.current <= 0 && !keyQ.current.length) { done(); return; }
      setTimeout(look, 0);
    };
    setTimeout(look, 0);
  });
  const sendKey = (k: string) => {
    replaying.current = true;
    try {
      const dyn = document.querySelector('[data-testid="dynin"]') as HTMLElement | null;
      (dyn ?? window).dispatchEvent(
        new KeyboardEvent("keydown", { key: k, bubbles: true, cancelable: true }));
    } finally { replaying.current = false; }
  };
  const runCommandFast = async (name: string, answers: string[]): Promise<boolean> => {
    // ⚠ The split, published for `/var/tmp/apibench.py`. Three phases and nothing between them:
    // ARM is a React effect, ANSWER is one task, COMMIT is the worker. Guessing which one is the
    // cost has been wrong twice today.
    const mark = (window as unknown as Record<string, unknown>).__pdcadCmdSplit =
      { arm: 0, fire: 0, answer: 0, commit: 0 } as Record<string, number>;
    const t0 = performance.now();
    const { id } = resolveCommand(name);
    if (!id) return false;
    // ⚠ A live command first, for the same reason the queue path does it: POINT repeats, and its
    // prompt would take the next command as an answer.
    // ⚠⚠ **AND THE CANCEL HAS TO COMMIT BEFORE THE NEW COMMAND IS FIRED.** Escape sets the mode
    // back to `select`; firing in the SAME task then sets it to `point` again, React compares
    // against the last COMMITTED value — still `point` — sees no change, and the mode effect
    // never runs, so the tool never arms. Measured: POINT committed 1 of 20 and took 2,349 ms a
    // command, all of it `armWait` timing out and falling back to the queue.
    if (wantsTyped()) {
      sendKey("Escape");
      await new Promise((r) => setTimeout(r, 0));
    }
    const tf = performance.now();
    fireRef.current?.(id);
    mark.fire = performance.now() - tf;
    const armed = await armWait();
    if (!armed) {
      // ⚠ No prompt: the command takes its answers at the command line (ZOOM's `E`, an option
      // letter, a keyword). Type them there and let the app's own resolution do the rest.
      for (const a of answers) {
        if (a === "\u001b") { sendKey("Escape"); continue; }
        if (wantsTyped()) { live.current.typed = a; sendKey("Enter"); continue; }
        typeIntoCmd(a);
        await new Promise((r) => setTimeout(r, 0));
        cmdBox()?.dispatchEvent(new KeyboardEvent("keydown",
                                                  { key: "Enter", bubbles: true, cancelable: true }));
        await new Promise((r) => setTimeout(r, 0));
      }
      await idleWait();
      mark.arm = performance.now() - t0;
      return true;
    }
    mark.arm = performance.now() - t0;
    const t1 = performance.now();
    for (let ai = 0; ai < answers.length; ai++) {
      const a = answers[ai];
      // ⚠ An Escape ENDS a command that is still running — LINE draws each segment as it goes
      // and keeps asking — so it is sent whatever the state is, and never counts as an answer.
      if (a === "\u001b") { sendKey("Escape"); continue; }
      // ⚠⚠ **CHECKED BEFORE THE ANSWER IS USED, NOT AFTER.** The test used to run after the
      // Enter and included `committing.current > 0` — which became true the moment a DRAW
      // started counting as an edit in flight, so every LINE and POINT bailed to the queue and
      // ran a SECOND time: 4,545 ms a command and 1 edit in 20 through `qa/bench_lisp.py`. What
      // matters is whether a prompt is still open to take THIS answer.
      if (!wantsTyped()) {
        const rest = answers.slice(ai).filter((x) => x !== "" && x !== "\u001b");
        if (rest.length) return false;                 // real answers left — let the queue try
        break;
      }
      live.current.typed = a;
      sendKey("Enter");
    }
    mark.answer = performance.now() - t1;
    const t2 = performance.now();
    await idleWait();
    mark.commit = performance.now() - t2;
    return true;
  };

  const runCommandApi = (name: string, answers: string[]) => new Promise<void>((resolve) => {
    // ⚠⚠⚠ **A COMMAND STARTS FROM `Command:`, AND SOME COMMANDS DO NOT END THEMSELVES.** POINT
    // repeats until it is cancelled — AutoCAD's own behaviour — so `(command "_.POINT" p)`
    // leaves the tool armed, and the NEXT call's NAME then arrives as an answer to that prompt.
    // Measured through `qa/bench_lisp.py`: with POINT ahead of them in the run, ELLIPSE
    // committed 60 edits for 20 commands and TEXT took 762 ms each; alone, 20 and 136.
    // ⚠ Only when something IS running — an Escape at `Command:` clears the selection, and a
    // programmatic command must not have that side effect.
    if (wantsTyped()) pushKey("Escape");
    // ⚠ The command NAME goes in as keys, exactly as a typed one does — the command line is
    // where a name is resolved (`ALIASES`, the exact-match rule, the option namespace), and a
    // second resolver here would be a second set of answers to "what does `L` mean".
    for (const ch of name) pushKey(ch);
    pushKey("Enter");
    for (const a of answers) {
      for (const ch of String(a)) pushKey(ch);
      pushKey(a === "\u001b" ? "Escape" : "Enter");
    }
    waiters.current.push(resolve);
  });

  /** ⚠⚠⚠ **WHILE A COMMAND IS RUNNING THE COMMAND ENGINE OWNS THE KEYBOARD — every key, from
   *  wherever focus happens to be.** Three boxes can hold it during one `LINE`: the chrome's
   *  command line, nothing at all (`fire` BLURS it the moment a tool arms — see
   *  `releaseKeyboard`), and then the coordinate box when it mounts about 30 ms later. Keys
   *  that arrived in between were lost, and keys that arrived at the command line were read as
   *  command names: the transcript said *expected x,y* and then *Unknown command "100000,0"*
   *  for a `LINE` whose coordinates were typed 4 ms apart.
   *
   *  ⚠ CAPTURE, and that is the whole point. React's own `onKeyDown` runs at the root before a
   *  bubbling window listener, so the command line had already submitted `0,0` as a command by
   *  the time anything here could refuse it. A capture listener on `window` runs first, and
   *  `stopPropagation` keeps the key out of every box that would have misread it.
   *
   *  ⚠ It takes the key only while a command is running, arming, or a replay is in flight.
   *  Otherwise the command line behaves exactly as it always has — typing `LINE` at
   *  `Command:` is not part of any stream. */
  /** ⚠⚠⚠ **AutoLISP — the language every AutoCAD office already has files in, and the only way
   *  to compare this app with BricsCAD on equal terms.**
   *
   *  `qa/reference/pddrawloop.lsp` drives BricsCAD with `(command "_.LINE" p1 p2 "")`. pdcad has
   *  only ever been driven by xdotool, so every published comparison had a keyboard path in
   *  pdcad's column that BricsCAD never paid — 30-50 ms a command, measured through
   *  `__pdcadCmd`. Running the SAME FILE against both is the end of that.
   *
   *  ⚠ The host is where the two languages meet: `(command …)` becomes the queue's answers,
   *  `getvar`/`setvar` the app's variables, `princ` a line at the command line. The interpreter
   *  knows none of it — see `lisp/eval.ts`.
   *  ⚠ Built LAZILY. A drafter who never types a bracket never pays for it, and the studio
   *  mounts before the drawing does. */
  const lispRef = useRef<Lisp | null>(null);
  /** Sysvars the interpreter keeps itself. ⚠⚠ `USERS1`-`USERS5` are how a driver script passes
   *  arguments to itself — `pddrawloop.lsp` reads the KIND and the COUNT out of them — and this
   *  app's own sysvar table is the DRAWING's, fetched from the engine. The two are different
   *  things and mixing them would write a benchmark's scratch value into a client's file. */
  const lispVars = useRef(new Map<string, LispValue>([
    ["CMDECHO", 0], ["BLIPMODE", 0], ["OSMODE", 0],
    ["USERS1", ""], ["USERS2", ""], ["USERS3", ""], ["USERS4", ""], ["USERS5", ""],
  ]));
  const getLisp = () => {
    if (lispRef.current) return lispRef.current;
    lispRef.current = new Lisp({
      command: (name, answers) =>
        (window as unknown as { __pdcadCmd: (n: string, ...a: string[]) => Promise<void> })
          .__pdcadCmd(name, ...answers),
      getvar: (name) => {
        // ⚠⚠ **`DATE` IS A JULIAN DAY WITH A FRACTION, and every timing script in this repo
        // multiplies it by 86,400,000 to get milliseconds.** Returning a date-shaped integer
        // would make every measured interval zero — which reads as a very fast program.
        if (name === "DATE") return 2440587.5 + Date.now() / 86_400_000;
        if (name === "MILLISECS") return Date.now();
        if (lispVars.current.has(name)) return lispVars.current.get(name) ?? null;
        const row = (live.current.sysvars ?? []).find((v) => v.name === name);
        return row ? (row.value as LispValue) : null;
      },
      setvar: (name, value) => { lispVars.current.set(name, value); },
      say: (line) => sayRef.current?.(line),
      // ⚠ A browser cannot write `/tmp`. The text goes where a harness can read it, and the
      // command line says so — a script that believes it wrote a file and did not is worse
      // than one that could not open it.
      file: (path, text) => {
        const w = window as unknown as Record<string, unknown>;
        const files = (w.__pdcadLispFiles ||= {}) as Record<string, string>;
        files[path] = text;
        sayRef.current?.(`  wrote ${path} (${text.length} bytes, in this tab)`);
      },
    });
    return lispRef.current;
  };
  const runLisp = async (src: string) => {
    try {
      const out = await getLisp().run(src);
      if (out !== null) sayRef.current?.(`  ${lispPrint(out)}`);
    } catch (e) {
      // ⚠ Said, with the message. A macro that fails silently is a macro nobody can fix.
      sayRef.current?.(`  ; error: ${e instanceof Error ? e.message : String(e)}`);
    }
  };
  if (chrome.evalLisp) chrome.evalLisp.current = (src: string) => { void runLisp(src); };
  if (chrome.lispCmd) chrome.lispCmd.current = (name: string) => {
    const up = name.toUpperCase();
    // ⚠ APPLOAD and LOAD are answered here rather than in the catalogue because they exist to
    // bring LISP in, and until a file is loaded there is nothing else in this namespace.
    if (up === "APPLOAD" || up === "LOAD") { lspPick.current?.click(); return true; }
    const fn = lispRef.current?.commands.get(up);
    if (!fn) return false;
    sayRef.current?.(`_${up}`);
    void fn.call([]).catch((e) =>
      sayRef.current?.(`  ; error: ${e instanceof Error ? e.message : String(e)}`));
    return true;
  };

  // ⚠ Published on `window` rather than passed down: the callers are outside React entirely —
  // `qa/` harnesses, and the LISP interpreter, which will call it from wasm through a host
  // binding. ⚠ Assigned every render so it closes over the current queue, never a stale one.
  {
    const w = window as unknown as Record<string, unknown>;
    w.__pdcadCmd = async (name: string, ...answers: string[]) => {
      // ⚠ The fast path first; the queue is the fallback and the reference behaviour.
      if (await runCommandFast(name, answers)) return;
      await runCommandApi(name, answers);
    };
    // ⚠⚠ **THE SPACE, FOR A HARNESS — and there is no other way in, which is the point.**
    // `goSpace` is reachable only by clicking a layout tab, so every benchmark measured
    // whichever space the drawing happened to open in. Drawing 10951 opens on sheet `E1.0`,
    // which owns 46 entities, and `qa/bench_session.py` reported those 46 under a header
    // saying "1,021,581 objects" for as long as it existed.
    // ⚠ `null` is Model space, exactly as `goSpace` takes it.
    w.__pdcadSpace = (name: string | null) =>
      goSpace(name === "Model" || name === null ? null : name);
    // ⚠⚠⚠ **REGEN, TIMED TO THE FRAME — because "there is nothing to regenerate" is an answer
    // about the architecture, not a number, and BricsCAD's arm reports a real one.** This does
    // the work REGEN names here: the whole scene rebuilt from the display list, uploaded, and on
    // screen. ⚠ It waits for `__pdcadScene.at` to MOVE before the two frames, because the build
    // runs in a `.then` off a layout effect — an rAF pair alone would resolve before it started
    // and report a regeneration of a million polylines as one frame.
    w.__pdcadRegen = () => new Promise<number>((resolve) => {
      const t0 = performance.now();
      api.current?.regen();
      const look = () => {
        const sc = w.__pdcadScene as { at?: number } | undefined;
        if (sc && (sc.at ?? 0) > t0) {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => resolve(performance.now() - t0)));
          return;
        }
        if (performance.now() - t0 > 120_000) { resolve(-1); return; }
        setTimeout(look, 0);
      };
      setTimeout(look, 0);
    });
    // ⚠ `qa/bench_lisp.py` runs `pddrawloop.lsp` through this — the same file BricsCAD runs.
    w.__pdcadLisp = (src: string) => getLisp().run(src);
    // ⚠ The PRINTER too: `qa/lisp_check.py` compares against AutoLISP's own output format, and
    // a check that formats the answer itself is checking its own formatter.
    w.__pdcadLispPrint = (v: unknown) => lispPrint(v as LispValue);
  }

  useEffect(() => {
    const cap = (e: KeyboardEvent) => {
      if (replaying.current || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const testid = el?.getAttribute?.("data-testid") ?? "";
      const onCmd = testid === "acad-cmdinput";
      const stream = el?.tagName === "BODY" || testid === "dynin" || onCmd;
      if (!stream) return;
      const k = e.key;
      // ⚠ SPACE is never taken: it ends a command at every prompt, cycles the grip modes, and
      // is a literal space inside a text answer. Three meanings, none of them "a character".
      const mine = k === "Enter" || k === "Escape" || k === "Backspace"
                   || (k.length === 1 && k !== " ");
      if (!mine) return;
      const arming = armedAt.current > 0 && performance.now() - armedAt.current < 400;
      // ⚠⚠⚠ **THE COMMAND LINE IS TAKEN OVER ONLY INSIDE THE GAP, NEVER WHILE A COMMAND RUNS.**
      // Stealing every key from it broke six e2e scenarios at once: this app deliberately lets
      // a drafter type a NEW command at the box while a tool is still armed — `BREAK` typed
      // during an unfinished LINE is how half the suite drives it, and AutoCAD allows it too.
      // So the box keeps its keys except in the ~30 ms after a submit, which is the one window
      // where a coordinate typed there is certainly meant for the prompt that is opening.
      if (onCmd && !arming && !keyQ.current.length) {
        if (k === "Enter") armedAt.current = performance.now();
        return;
      }
      if (!wantsTyped() && !arming && !keyQ.current.length) {
        // ⚠ Nothing is running. The command line takes it — but an Enter there SUBMITS, and a
        // prompt may be about to open, so the stamp that makes the next keystroke wait for it
        // is set here and nowhere else.
        if (k === "Enter" && testid === "acad-cmdinput") armedAt.current = performance.now();
        return;
      }
      e.preventDefault(); e.stopPropagation();
      pushKey(k);
    };
    window.addEventListener("keydown", cap, true);
    return () => window.removeEventListener("keydown", cap, true);
  });

  // ⚠ AutoCAD's own function keys. A drafter's fingers know these before they know the ribbon,
  // and F8 in particular is pressed dozens of times an hour without looking.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      // ⚠ Except ESCAPE. AutoCAD's command line has focus most of the time, and a drafter who
      // has typed half a command and wants out presses ESC — which would otherwise be swallowed
      // by the input and leave the command armed with no way to cancel it.
      // ⚠⚠ **AND EXCEPT THE FUNCTION KEYS, WHICH IS NOT A DETAIL — see the note above this
      // effect.** The coordinate box holds focus for the whole of a draw command, which is
      // exactly when ORTHO gets pressed; without this, F8 would go dead at the one moment a
      // drafter reaches for it and would work perfectly whenever they did not need it. In
      // AutoCAD F1-F12 fire whatever has focus.
      const always = e.key === "Escape" || /^F([1-9]|1[0-2])$/.test(e.key);
      if ((tag === "INPUT" || tag === "TEXTAREA") && !always) return;
      const flip = (k: keyof Toggles) => {
        e.preventDefault(); setTog((t) => ({ ...t, [k]: !t[k] }));
      };
      if (e.ctrlKey && e.key.toLowerCase() === "o") { e.preventDefault(); setOpenDlg(true); return; }
      // ⚠⚠ **CTRL+Z IS THE MOST-PRESSED KEY IN A CAD PROGRAM** and it goes to the SERVER here,
      // because that is where the edit happened — see `models.DrawingEdit`.
      if (e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault(); void step("undo"); return;
      }
      if (e.ctrlKey && (e.key.toLowerCase() === "y"
                        || (e.shiftKey && e.key.toLowerCase() === "z"))) {
        e.preventDefault(); void step("redo"); return;
      }
      if (e.ctrlKey && e.key.toLowerCase() === "a") {
        e.preventDefault(); A.selectAll(); return;
      }
      // ⚠⚠⚠ **A KEYSTROKE TYPED WHILE NOTHING HAS FOCUS IS A KEYSTROKE LOST, AND THE WHOLE
      // DESKTOP BENCHMARK WAS MEASURING THAT.** Every typed answer in this app lives in a
      // `DynIn` that focuses itself when it mounts — so between a command starting and its box
      // appearing, `document.activeElement` is BODY for about 30 ms, and anything typed in that
      // window goes nowhere at all. Measured with a capture-phase logger (`/var/tmp/keyland.py`)
      // on `LINE ⏎ 0,0 ⏎ 1000,0 ⏎ Esc`:
      //
      //   typed at 4 ms/key   L I N E ⏎ → cmdinput, then EVERY key → BODY, 0 edits committed
      //   typed at 30 ms/key  the same keys → INPUT#dynin, 1 edit committed
      //
      // ⇒ `qa/bench_desktop.py` and `/var/tmp/webview_tax.py` both send their bursts at 4 ms,
      // so the "commands" they timed never drew anything; the screen changed because the prompt
      // echoed, and the stopwatch stopped on that. Every desktop-against-BricsCAD number this
      // project has published came out of that hole.
      //
      // The fix is the one AutoCAD has always had: the command engine owns the keyboard, not
      // whichever box happens to be mounted. A printable key with a prompt waiting goes into
      // `typed` wherever focus is — and `DynIn` shows `typed`, so the box catches up the moment
      // it appears.
      // ⚠ SPACE is deliberately NOT here: it ends a command at every prompt, cycles the grip
      // modes below, and is a literal space inside a text answer. Three meanings, none of them
      // "append a character".
      // ⚠ Delete erases, which is AutoCAD's own binding and the one a drafter reaches for
      // before they ever find the ERASE command.
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault(); A.remove(); return;
      }
      // ⚠⚠ ESC CANCELS THE COMMAND FIRST, then the selection — AutoCAD's order exactly. Pressing
      // it once during a MOVE must not also throw away the objects you spent time picking.
      // ⚠⚠ ENTER FINISHES AN UNBOUNDED TOOL. AutoCAD's POLYLINE runs until you say stop, and
      // the only way to say stop is Enter or a right-click — a tool with no ending is one a
      // drafter escapes out of, losing the polyline they just drew.
      // ⚠⚠ **SELECT'S ENTER COMES FIRST, AND IT MEANS TWO THINGS.** With a mode running it
      // closes the fence or polygon; with the prompt still waiting for a letter it ENDS the
      // command with whatever is already picked — which is what Enter does at AutoCAD's own
      // "Select objects:".
      if (e.key === "Enter" && live.current.attWant) {
        e.preventDefault(); acceptAtt(); return;
      }
      if (e.key === "Enter" && live.current.hatchWant) {
        e.preventDefault(); setHatchWant(null); sayRef.current?.("Command:"); return;
      }
      if (e.key === "Enter" && live.current.measWant?.kind === "area") {
        e.preventDefault();
        const w = live.current.measWant;
        setMeasWant(null);
        // ⚠ Enter with no points falls back to the SELECTION, which is what "or press Enter to
        // total the selection" promises at the prompt.
        if (w.pts.length >= 3) {
          void runMeasure([], { kind: "area", points: w.pts.map((q) => [q.x, q.y]) });
        } else if (live.current.sel.length) {
          void runMeasure(live.current.sel.map((x) => x.handle), { kind: "area" });
        } else {
          sayRef.current?.("  an area needs at least three points, or a selection");
        }
        return;
      }
      if (e.key === "Enter" && live.current.varWant) {
        e.preventDefault(); acceptVar(); return;
      }
      // ⚠⚠ **A NUMBER TYPED BEFORE THE FIRST CORNER CHOOSES THE PDF PAGE.** PDFATTACH placed
      // page 1 and reported the other eleven afterwards — by which point the drafter had
      // already dragged a rectangle for the wrong page. Only BEFORE the first corner: once a
      // corner is down the gesture is about the frame, and a number then means nothing.
      if (e.key === "Enter" && live.current.imgWant?.kind === "pdf"
          && !live.current.imgWant.pts.length && live.current.typed.trim()) {
        e.preventDefault();
        const w = live.current.imgWant;
        const n = Number(live.current.typed);
        setTyped("");
        const cap = w.pages ?? 0;
        if (!Number.isInteger(n) || n < 1 || (cap && n > cap)) {
          sayRef.current?.(cap ? `  that PDF has ${cap} page${cap === 1 ? "" : "s"}`
                               : "  a page is a whole number, 1 or more");
          return;
        }
        setImgWant({ ...w, page: n });
        sayRef.current?.(`  page ${n}${cap ? ` of ${cap}` : ""}`
                         + " — now pick the first corner of the frame");
        return;
      }
      // ⚠⚠ **UCS OFFERED `[World/Previous]` AND NOTHING COULD HEAR THE LETTER** — while both
      // options had working handlers, reachable from the ribbon, since the UCS landed. The
      // typed path for a point step belongs to DRAW tools (`onDrawPoint`), and `ucsWant` is not
      // one, so a letter typed at this prompt fell through to `resolveCommand` and came back as
      // *Unknown command "W"*. One branch, and the prompt tells the truth again.
      if (e.key === "Enter" && live.current.ucsWant && live.current.typed.trim()) {
        const word = matchKeyword(live.current.typed, keywordsOf(PROMPTS.ucs));
        if (word) {
          e.preventDefault();
          setTyped("");
          setUcsWant(null);
          // ⚠ The SAME handlers the ribbon buttons call — UCSWORLD and UCSPREV — rather than a
          // second implementation that would drift from them.
          if (word === "World") (A as any).resetAxes();
          else (A as any).ucsPrev();
          return;
        }
      }
      if (e.key === "Enter" && live.current.selWant) {
        e.preventDefault();
        const w = live.current.selWant;
        if (w.mode) finishSelect();
        else if (live.current.typed.trim()) { selectOption(live.current.typed); setTyped(""); }
        else closeSelect();
        return;
      }
      if (e.key === "Enter" && live.current.draw) { e.preventDefault(); finishDraw(); return; }
      // ⚠⚠⚠ **`<use first point as displacement>` IS A PROMISE AND NOTHING KEPT IT.** MOVE, COPY
      // and STRETCH all print it, and pressing ENTER there did nothing whatsoever. In AutoCAD it
      // means: move the selection BY the base point read as a vector from the origin — which is
      // how a drafter makes an exact move, typing the vector at the first prompt and pressing
      // Enter rather than hunting for a second point 500 mm away.
      // ⚠ It escaped `test/keywords.test.ts` because that guard walks `[A/B/C]` OPTIONS, and a
      // `<default>` is not one. The guard is widened in the same edit — see it.
      // ⚠ The base is the LAST point picked, which is what all three measure from (`p[0]` for
      // MOVE and COPY, `p[2]` for STRETCH). Handing `2·base` to `onPoint` makes `params`' own
      // `c − base` come out as exactly `base`, so the ORDINARY commit path runs — including the
      // optimistic local draw — instead of a second implementation of the same command.
      // ⚠⚠ **DOUBLED IN THE UCS, NOT IN THE WORLD.** The vector a drafter typed is in the
      // current UCS; on a rotated origin, doubling the world point would displace along the
      // wrong axis and the wall would land somewhere nobody asked for.
      if (e.key === "Enter" && !live.current.typed.trim() && live.current.cmd) {
        const c0 = live.current.cmd;
        const d0 = COMMANDS[c0.name];
        const at0 = c0.pts.length;
        if (d0 && at0 === d0.points - 1 && at0 > 0
            && /use first point as displacement/i.test(d0.prompts[at0] ?? "")) {
          // ⚠ Delegated, not duplicated — `finishCmdPoint` owns this now. This branch only
          // catches the case where the CANVAS has focus rather than the dynamic-input box.
          e.preventDefault(); finishCmdPoint(); return;
        }
      }
      // ⚠ After the `draw` branch and before the scalar ones: a modify command at a POINT step
      // with something typed. The box above owns Enter while it has focus; this is the same
      // accept for a keystroke that arrived with focus somewhere else.
      if (e.key === "Enter" && live.current.cmd && live.current.typed.trim()
          && COMMANDS[live.current.cmd.name]
          && live.current.cmd.pts.length < COMMANDS[live.current.cmd.name].points
          && !(COMMANDS[live.current.cmd.name].scalar
               && (COMMANDS[live.current.cmd.name].scalarFirst
                     ? live.current.cmd.held === undefined
                     : live.current.cmd.pts.length > 0))) {
        e.preventDefault(); finishCmdPoint(); return;
      }
      if (e.key === "Enter" && vpAskRef.current) { e.preventDefault(); acceptVpScale(); return; }
      if (e.key === "Enter" && pedWantRef.current?.kind === "width") {
        e.preventDefault(); acceptPeditWidth(); return;
      }
      if (e.key === "Enter" && pedWantRef.current?.kind === "option") {
        e.preventDefault(); acceptPeditOption(); return;
      }
      if (e.key === "Enter" && live.current.tanWant?.given === "ttr"
          && !(live.current.tanWant.r > 0)) {
        // ⚠ The `DynIn` above owns Enter while it has focus; this is the same accept for a
        // drafter who typed into the canvas instead, which is what happens after clicking the
        // ribbon button rather than the box.
        e.preventDefault(); acceptTanRadius(); return;
      }
      if (e.key === "Enter" && live.current.objCmd) {
        e.preventDefault();
        // ⚠ Enter accepts the radius if one is still wanted; otherwise it ENDS the command,
        // which is how AutoCAD's repeating TRIM stops.
        if (live.current.objCmd.held === undefined && PICKS[live.current.objCmd.name].scalar) {
          acceptPickScalar();
        } else {
          setObjCmd(null);
          sayRef.current?.("Command:");
        }
        return;
      }
      if (e.key === "Escape") {
        // ⚠ ESC drops a hot grip FIRST, leaving the object selected — AutoCAD's order. Pressing
        // it once must not also throw away the object whose corner you were about to move.
        if (live.current.attWant || live.current.attPlace) {
          setAttWant(null); setAttPlace(null); sayRef.current?.("*Cancel*"); return;
        }
        if (live.current.hatchWant) { setHatchWant(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.tanWant) { setTanWant(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.matching) { setMatching(false); matchFrom.current = null;
                                     sayRef.current?.("*Cancel*"); return; }
        if (live.current.zoomWant !== null || live.current.mode === "zoomwin") {
          setZoomWant(null); setMode("select"); sayRef.current?.("*Cancel*"); return;
        }
        if (live.current.breakWant) { setBreakWant(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.measWant) { setMeasWant(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.clipWant) { setClipWant(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.ucsWant) { setUcsWant(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.pasteBlock) { setPasteBlock(false); setMode("select");
                                       sayRef.current?.("*Cancel*"); return; }
        if (live.current.clipBase) { setClipBase(false); sayRef.current?.("*Cancel*"); return; }
        if (live.current.imgWant) { setImgWant(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.varWant) { setVarWant(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.selWant) { setSelWant(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.blockWant) { setBlockWant(null); sayRef.current?.("*Cancel*"); return; }
        // ⚠ PAN leaves on Esc like every AutoCAD mode. Placed with the other modal exits rather
        // than at the top: a hot grip or an armed prompt is the more urgent thing to drop.
        if (live.current.mode === "pan") { setMode("select"); sayRef.current?.("*Cancel*"); return; }
        if (polarWantRef.current) { setPolarWant(null); sayRef.current?.("*Cancel*"); return; }
        // ⚠ A ZOOM Center waiting for its point is its own state, so the pedWant branch below
        // does not see it and Escape would leave the next click zooming.
        zoomAnchorRef.current = null;
        setZoomAnchor(null);
        if (pedWantRef.current) {
          // ⚠ ARRAYPATH's count prompt IS a pedWant. Cancelling it has to drop the armed path
          // pick as well, or the next object click is eaten by a command already cancelled.
          if (pedWantRef.current.op === "patharray") setPathWant(null);
          setPedWant(null); sayRef.current?.("*Cancel*"); return;
        }
        // ⚠ AFTER the pedWant branch, not before: while ARRAYPATH's count prompt is up BOTH are
        // set, and the prompt is the thing in front of the drafter — ESC has to cancel that one.
        if (live.current.pathWant) { setPathWant(null); sayRef.current?.("*Cancel*"); return; }
        // ⚠ OFFSET's side question is cancelled FIRST and the command survives, the way ESC
        // on a mis-typed snap does: a drafter who picked the wrong object should get the
        // *"Select object to offset"* prompt back, not lose the distance they typed.
        if (live.current.sideWant) {
          setSideWant(null);
          sayRef.current?.(PICKS.offset.prompts[0]);
          return;
        }
        if (live.current.objCmd) { setObjCmd(null); sayRef.current?.("*Cancel*"); return; }
        if (live.current.hotGrip != null) { setHotGrip(null); sayRef.current?.("*Cancel*"); return; }
        // ⚠ An armed single-shot snap is dropped FIRST and the command survives — ESC on a
        // mis-typed `MID` must not also throw away the polyline being drawn.
        if (snapOnceRef.current !== null) { setSnapOnce(null); return; }
        if (live.current.draw) {
          // ⚠ ESC during a DRAW abandons it — AutoCAD's *Cancel*. The points are not committed:
          // a half-drawn polyline is not a polyline.
          // ⚠⚠ And it FORGETS the selection the tool armed with. Without this, a drafter who
          // picked an arc, started CIRCLE, changed their mind, and typed `CIRCLE ⏎ A ⏎` an hour
          // later would convert an arc they no longer had selected — a command acting on
          // something nobody currently sees highlighted is the worst kind of surprise.
          armSel.current = [];
          setDraw(null); setMode("select"); sayRef.current?.("*Cancel*"); return;
        }
        if (live.current.cmd) { setCmd(null); sayRef.current?.("*Cancel*"); return; }
        // ⚠⚠ **AND A MODE THAT OUTLIVED ITS `draw` IS CLEARED HERE, OR THE COMMAND IS DEAD.**
        // The draw branch above is the only other place that resets `mode`, and it runs only
        // while a draw is live — so any path that nulls `draw` on its own (a space change did)
        // left the mode armed with nothing on screen. Typing that command again then set the
        // mode to the value React already held, the arming effect never ran, and nothing the
        // drafter could do brought it back. ESC means *no command* in AutoCAD; it has to mean
        // that here whatever is left over.
        if (live.current.mode !== "select") setMode("select");
        setSel([]);
        return;
      }
      // ⚠⚠ **SPACE CYCLES THE GRIP MODES — AutoCAD's five, in its order.** STRETCH, MOVE,
      // ROTATE, SCALE, MIRROR, and round again. Handled BEFORE the function keys and after the
      // ESC block, because while a grip is hot SPACE means this and nothing else.
      // ⚠ The four that are not STRETCH are handed to the ORDINARY command machinery with the
      // grip as `pts[0]` — the base point already picked. Preview, typed angles, the Reference
      // option and the undo record are then the existing ones rather than a second copy.
      if (e.key === " " && gripEditRef.current) {
        e.preventDefault();
        const ge = gripEditRef.current;
        const next = (ge.mode + 1) % GRIP_MODES.length;
        const m = GRIP_MODES[next];
        setGripEdit({ at: ge.at, mode: next });
        if (m.cmd === null) {
          // ⚠ Back to STRETCH: the hot grip has to come back, because that is the mode that
          // drags a single vertex rather than transforming the whole selection.
          setCmd(null);
          setHotGrip(gripIdxRef.current);
        } else {
          setHotGrip(null);
          setCmd({ name: m.cmd, pts: [ge.at] });
          setTyped("");
        }
        sayRef.current?.(`** ${m.label} **\n${m.prompt}`);
        return;
      }
      if (e.key === "F3") flip("osnap");
      if (e.key === "F7") flip("grid");
      if (e.key === "F8") flip("ortho");
      if (e.key === "F9") flip("snapmode");
      if (e.key === "F10") flip("polar");
      // ⚠⚠ **F11 AND F12 WERE PRINTED ON THE STATUS BAR AND DEAD.** `STATUS_ITEMS` gives
      // `st.otrack` the key F11 and `st.dynin` the key F12, so both tooltips have been promising
      // a shortcut that did nothing since the bar was built — worse than an absent feature,
      // because the app itself says it is there. Found by `qa/ux_survey.py`.
      if (e.key === "F11") flip("otrack");
      if (e.key === "F12") flip("dynin");
      // ⚠ Ctrl+1 is Properties, and AutoCAD's Ctrl+1 TOGGLES it — pressing it with the palette
      // open closes it. Fired through the chrome so the ribbon button and the shortcut share
      // one notion of "open"; see `shell-types.ts`.
      if (e.ctrlKey && e.key === "1") { e.preventDefault(); fireRef.current?.("prop.palette"); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [A, step, finishDraw, finishCmdPoint, acceptPickScalar]);



  // ⚠⚠ **THE GRIP CYCLE ENDS WHEN NOTHING IS HOLDING IT**, and it is expressed as a rule rather
  // than as a line in each of the six places a grip edit can finish — commit, cancel, Escape,
  // a new selection, a new command, a cleared drawing. One of those six WILL be forgotten, and
  // the symptom would be SPACE silently cycling grip modes during an unrelated command.
  useEffect(() => {
    if (hotGrip == null && !cmd && gripEdit) { setGripEdit(null); gripIdxRef.current = null; }
  }, [hotGrip, cmd, gripEdit]);

  // ── the UX probe. ⚠ Read by `qa/ux_survey.py` and by nothing in the app — see `uxprobe.ts`.
  // The canvas publishes what it can see (the view, the band, the grips); this publishes what
  // only the studio knows: how many objects are selected, and which status-bar toggles are on.
  useEffect(() => {
    publishUX({ sel: sel.length, tog: { ...tog } as unknown as Record<string, boolean>,
                hotGrip: hotGrip ?? -1,
                // ⚠ The SPACE on screen — `null` for model, the layout's name for a sheet. See
                // `UxProbe.space`: a lit tab is not the same as a drawn sheet.
                space,
                gripMode: gripEdit ? GRIP_MODES[gripEdit.mode].label : null });
  }, [sel, tog, hotGrip, gripEdit, space]);

  const bg = dark ? "#1a1a1a" : "#ffffff";
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                  background: "#2b2b2b", color: "#e8e8e8", font: "12px/1.4 system-ui",
                  overflow: "hidden" }}>
      {chrome.top}
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {chrome.rail}
        <div style={{ flex: 1, position: "relative", minWidth: 0, background: bg }}>
          {/* ⚠⚠⚠ **`chrome.viewport` HAD A PRODUCER AND NO CONSUMER, AND TWO FEATURES WENT
              THROUGH IT.** `shell.tsx` builds this slot for the paper-space SHEET COMPOSER and
              for the 3D viewer, and `shell-types.ts` describes it as "replace the plan canvas
              entirely" — and nothing here ever rendered it. So clicking a LAYOUT TAB highlighted
              the tab, printed `Layout Layout1…`, switched `space`, fetched the sheet, and left
              MODEL SPACE on screen. Every test passed: the tab state was right, the display list
              for the layout was right, and the picture was of something else.
              ⚠ It REPLACES the canvas rather than sitting over it — that is what the slot's own
              documentation says, and a sheet drawn on top of a live plan canvas would keep
              taking the pointer events meant for it.
              ⚠ The 3D branch of the same slot renders `Model3D`, which is a deliberate STUB
              saying 3D is not built. That notice never reached a drafter either: the ViewCube
              read `3D` over an unchanged 2D drawing. */}
          {/* ⚠⚠⚠ **ONLY THE 3D BRANCH OF THIS SLOT, AND THE `space === null` IS THE WHOLE
              CORRECTION.** `shell.tsx` fills `chrome.viewport` for TWO things: the 3D viewer and
              a `SheetComposer` that is a stub. Rendering it unconditionally put that stub in
              front of LAYOUTS — which have always worked. `goSpace` fetches the sheet's display
              list and `Canvas2D` draws it: paper, printable-area border, viewports onto the
              model, paper-space annotation.
              ⚠ I read a slow compose as a missing feature. A 19,734-object drawing takes longer
              than six seconds to compose a sheet, and the screenshot I judged it by was taken
              while the fetch was still in flight — the busy toast was in the picture and I did
              not read it. `UxProbe.space` exists so a check can ask the app what it is showing
              rather than a photograph.
              ⚠ The 3D half IS worth rendering: there is no 3D data at all, and the notice is the
              honest answer. Before this it never reached a drafter — the ViewCube read `3D` over
              an unchanged 2D drawing. */}
          {(chrome.viewport && space === null) ? (
            // ⚠ AN EXACT BOX, CLIPPED. Both current occupants are `height: 100%` with
            // `padding: 24` and content-box sizing, so each stands 48 px taller than the area it
            // is given; `inset: 0` inside the `position: relative` parent makes the box the area
            // itself whatever the content asks for, and `overflow: hidden` keeps that true for
            // anything put here later.
            <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
              {chrome.viewport}
            </div>
          ) : (<>
          {/* ⚠⚠ **GPU LINEWORK IS ON BY DEFAULT — this comment said the opposite for long
              enough to be believed.** `gpuOn` above is `localStorage.pdcad.gpu !== "0"`, so it
              is on unless a browser has opted OUT; `?gpu=0` turns it off for one load without a
              rebuild. Counted in the running app to settle it: a plain `/?d=…` has TWO canvases.
              ⚠ It draws `polys` and `marks` on a WebGL2 canvas under this one and measures tone
              +0.5% against the 2D canvas. The rest — rays, fills, texts, the grid, the
              selection, grips, snaps, the sheet — is still the 2D context on top, which is why
              being on by default is not the incomplete picture the old wording implied. */}
          <Canvas2D dl={shownDl} dark={dark} showGrid={tog.grid}
                    // ⚠⚠ **PEN WEIGHTS STAND THE GPU CANVAS DOWN.** `glBackend` strokes every
                    // instance at one width and has nowhere to put a second, so with LWDISPLAY
                    // on it would paint hairlines UNDER the weighted strokes and the app would
                    // pay for both renderers. The effect in `Canvas2D` disposes the GL context
                    // when this goes false, which is what makes the flag safe to change here.
                    gpu={gpuOn && !tog.lwt}
                    lwt={tog.lwt}
                    // ⚠ REVCLOUD's `Freehand` is the one tool that reads a STROKE rather than
                    // picks — see `onTrail`.
                    trailing={!!draw?.freehand}
                    onTrail={onTrail}
                    // ⚠ So a renderer the app did not want is SAID, not left in a screenshot.
                    onSay={(l) => sayRef.current?.(l)}
                    onApi={(a) => {
                      api.current = a;
                      // ⚠ Published beside `__pdcadOpen`, `__pdcadRefresh`, `__pdcadScene` and
                      // `__pdcad2d`, and for the same reason: an instrument that cannot place
                      // the view exactly cannot click on a known piece of geometry, and
                      // `qa/bench_actions.py` needs to for TRIM, EXTEND, FILLET and OFFSET —
                      // every one of which asks "which object?", which is a PLACE. Read-only
                      // from the app's side; nothing in the product uses it.
                      (window as unknown as Record<string, unknown>).__pdcadApi = a;
                    }}
                    onView={(v) => {
                      // ⚠⚠ **RECORDED HERE, WHERE EVERY VIEW CHANGE ALREADY ARRIVES.** Pan, wheel
                      // zoom, ZOOM EXTENTS and a layout switch all come through this one
                      // callback, so the history cannot miss one — and `Canvas.setView`
                      // deliberately does not call back, or Previous would push what it just
                      // restored and bounce between two views forever.
                      // ⚠⚠⚠ **THE CANVAS HANDS BACK THE SAME MUTABLE OBJECT EVERY TIME.**
                      // `onWheel` does `view.current.scale *= f` and then `onView?.(view.current)`
                      // — so remembering the reference means comparing it with ITSELF on the next
                      // event: always equal, nothing ever pushed, and ZOOM Previous answering
                      // "No previous view." after four wheel notches. Measured, in the running
                      // app, with the option prompt already working.
                      // ⇒ SNAPSHOT on arrival. Fixing it in the canvas would work too and would
                      // allocate on every pointer-move; the studio is the one that needs a copy.
                      const now = { x: v.x, y: v.y, scale: v.scale };
                      const was = lastView.current;
                      if (was && (Math.abs(was.x - now.x) > 1e-9 || Math.abs(was.y - now.y) > 1e-9
                                  || Math.abs(was.scale - now.scale) > 1e-12)) {
                        viewHist.current.push(was);
                        if (viewHist.current.length > 10) viewHist.current.shift();
                      }
                      lastView.current = now;
                      setView(now);
                    }}
                    // ⚠ The READOUT is constrained too, not just the click. AutoCAD's
                    // coordinate box shows where the point would go, and a box that disagrees
                    // with the rubber band is how you find out ortho is on by misplacing a wall.
                    onCursor={(p, px) => { cursorPx.current = px ?? null;
                                           setCursor(p ? applyConstraint(p) : p); }}
                    uframe={tog.uframe}
                    snaps={running}
                    // ⚠ OTRACK, and the increment its paths run at. AutoCAD's rule: orthogonal
                    // only until POLAR is on, and then the same increments POLAR uses — so the
                    // two toggles cannot end up disagreeing about what a direction is.
                    otrack={tog.otrack}
                    trackAng={tog.polar ? polarAng : 90}
                    onSnap={(h) => { snapRef.current = h; setSnapHit(h); }}
                    // ⚠⚠ **THE RIGHT BUTTON IS ENTER.** A drafter finishes a command with the
                    // hand already on the mouse far more often than by reaching for the ENTER
                    // key, and AutoCAD's default menu is the first thing most of them turn into
                    // plain ENTER. Dispatched as a real keydown rather than reimplemented,
                    // because "what ENTER means" is thirty branches long in the handler above
                    // and a second copy of that logic would drift from the first.
                    // ⚠ Idle — nothing running, nothing selected — repeats the last command,
                    // which is what ENTER does at an empty command line. `repeatLast` is the
                    // chrome's, because `lastId` lives there.
                    onRightClick={() => {
                      const st = live.current;
                      const busy = !!st.cmd || !!st.draw || !!st.objCmd || st.hotGrip != null
                                   || !!st.selWant || !!st.attWant || !!st.hatchWant
                                   || !!st.measWant || !!st.tanWant || !!st.blockWant;
                      if (busy) {
                        window.dispatchEvent(new KeyboardEvent("keydown",
                                                               { key: "Enter", bubbles: true }));
                        return;
                      }
                      if (!chrome.repeatLast?.()) sayRef.current?.("Command:");
                    }}
                    // ⚠ A double-click of the WHEEL is ZOOM Extents, and it goes through
                    // `runZoom` so the view lands on the ZOOM Previous stack — a gesture that
                    // cannot be undone is worse than one that does not exist.
                    onZoomExtentsGesture={() => runZoom("extents")}
                    // ⚠⚠ **EVERY POINT-TAKING STATE HAS TO BE LISTED HERE OR THE CLICK NEVER
                    // ARRIVES.** `Canvas2D` only calls `onPoint` when `picking` is true;
                    // otherwise a click is a SELECTION. PASTECLIP printed "Specify insertion
                    // point:" and then swallowed the click, which reads as a dead command.
                    panning={mode === "pan"}
                    // ⚠⚠ **XLINE/RAY `Parallel` IS THE ONE DRAW STATE THAT MUST NOT PICK
                    // POINTS.** `picking` is what makes the canvas report a POINT instead of a
                    // selection click — so while a draw is armed the click never reaches
                    // `onSelect`, and the branch that reads the line to be parallel to was
                    // unreachable. It drew a construction line at the wrong angle and looked
                    // like the direction being discarded, which is a different bug entirely.
                    // ⚠ `basePick` turns picking OFF for one click: DIMCONTINUE's `Select`
                    // needs an OBJECT while the command is otherwise reading points.
                    picking={!basePick
                             && (!!cmd || (!!draw && !draw.parWant) || hotGrip != null
                                 || !!blockWant
                             || pedWant?.kind === "point" || !!selWant?.mode
                             || !!hatchWant || !!attPlace || !!tanWant || !!imgWant
                             || mode === "paste" || clipBase || !!ucsWant || !!clipWant
                             // ⚠⚠ **ZOOM WINDOW HAS TO BE HERE OR ITS CORNERS NEVER ARRIVE.**
                             // `picking` is what makes the canvas report a POINT instead of a
                             // selection click, and a mode that is armed but not listed here
                             // prompts for a corner and then swallows it as a pick — which is
                             // the same "answers and does nothing" shape as the missing branch
                             // in the arming effect, one layer down.
                             || mode === "zoomwin" || zoomWant !== null
                             // ⚠ ZOOM Center/Left/Right's point, for the same reason
                             || (zoomAnchor !== null && !zoomAnchor.p)
                             // ⚠⚠ OFFSET's SIDE click is a POINT, not a pick — and while the
                             // command is armed the canvas hands clicks to `onPickObject`, so
                             // without this the side click landed on the object again and the
                             // prompt asked for a side twice in a row.
                             || !!sideWant
                             || !!breakWant || !!measWant)}
                    onPoint={onPoint} preview={preview} fit={fitKey}
                    // ⚠ The LAST point given to whichever command is running — a draw tool's
                    // previous click, or a modify command's base. Perpendicular, tangent and
                    // parallel are all "from" snaps and have nothing to work from without it.
                    rasterUrl={(h) => `/api/drawings/${id}/raster/?h=${encodeURIComponent(h)}`}
                    base={draw?.pts.length ? draw.pts[draw.pts.length - 1]
                          : cmd?.pts.length ? cmd.pts[cmd.pts.length - 1] : null}
                    sheet={space ? (dl?.sheets?.[0] ?? null) : null}
                    grips={grips} hotGrip={hotGrip}
                    onEditText={(p, box) => { void openTextEditor(p.handle, box); }}
                    // ⚠ Saying WHICH of the stacked objects is picked, and how many there
                    // are. A silent cycle looks like a click that landed somewhere else.
                    onCycle={(all, i) => {
                      // ⚠⚠ **CYCLING REPLACES, IT DOES NOT ADD.** A plain click ADDS to the set
                      // — that is PICKADD, AutoCAD's default and pdcad's — but the second click
                      // at the SAME spot means "not that one, this one". Letting it add left
                      // both the wall and the note picked and reported "1 found, 2 total",
                      // which is the opposite of what the gesture is for.
                      if (i > 0) {
                        const drop = all[(i + all.length - 1) % all.length].handle;
                        setSel((c) => c.filter((p) => p.handle !== drop));
                      }
                      sayRef.current?.(
                        `  ${all[i].kind} ${all[i].handle} — ${i + 1} of ${all.length} here `
                        + "(click again to cycle)");
                    }}
                    onGrip={(i) => {
                      setHotGrip(i);
                      gripIdxRef.current = i;
                      const g = grips?.[i];
                      setGripEdit(g ? { at: { x: g.p[0], y: g.p[1] }, mode: 0 } : null);
                      // ⚠ AutoCAD's own wording when a grip goes hot.
                      sayRef.current?.("** STRETCH **\nSpecify stretch point or "
                                       + "[Base point/Copy/Undo/eXit]:");
                    }}
                    // ⚠⚠ **THE TWO-CORNER GESTURES THAT ARE NOT `draw` WERE PICKED BLIND.**
                    // XCLIP and the image/underlay frame each ask for a corner and then for the
                    // opposite one with nothing drawn in between — so the drafter finds out what
                    // they asked for after it has happened. Every other two-corner gesture in
                    // this app rubber-bands, and `Canvas2D` has taken a `RECT` since MVIEW.
                    rubber={clipWant?.pts.length ? { kind: "RECT", pts: clipWant.pts }
                          : imgWant?.pts.length ? { kind: "RECT", pts: imgWant.pts }
                          : draw ? { kind: (defOf(draw.mode, dimKind)?.kind ?? "LINE") === "MVIEW"
                                           ? "RECT" : (defOf(draw.mode, dimKind)?.kind ?? "LINE"),
                                     pts: draw.pts,
                                     // ⚠ The options ride along so the band is the shape that
                                     // will be committed — see `rectCorners`.
                                     rect: { dims: draw.dims, square: draw.square,
                                             rot: rectRot(draw.opt) } } : null}
                    ghosts={ghosts}
                    onSelect={(got, shift, at) => {
                      // ⚠⚠ **WHILE A PICK COMMAND IS RUNNING, A CLICK IS AN ARGUMENT, NOT A
                      // SELECTION.** TRIM's click chooses the piece to remove; letting it also
                      // highlight the object would leave a selection behind that the next
                      // command would act on.
                      // ⚠ DIMCONTINUE's `Select`: this click names the base dimension and
                      // the command carries on from it. Anything else picked is ignored with a
                      // sentence rather than silently, because a click that did nothing at a
                      // prompt reads as a dead canvas.
                      if (live.current.basePick) {
                        const dim = got.find((g) => g.kind.startsWith("DIMENSION"));
                        if (!dim) { sayRef.current?.("  that is not a dimension"); return; }
                        setBasePick(false);
                        setSel([dim]);
                        const d2 = live.current.draw;
                        const def2 = d2 && defOf(d2.mode, live.current.dimKind,
                                                 givenFor(d2.mode, live.current));
                        sayRef.current?.(`  continuing from ${dim.kind} on ${dim.layer}`);
                        if (def2) sayRef.current?.(def2.prompts[0]);
                        return;
                      }
                      if (live.current.objCmd) {
                        if (got.length) void onPickObject(got[0], at);
                        return;
                      }
                      // ⚠⚠ **XLINE/RAY `Parallel` EATS THE CLICK AND DOES NOT SELECT.** The pick
                      // is the line to be parallel TO; leaving it highlighted would hand the next
                      // command an object the drafter never chose — the same reason the pick
                      // commands above return early.
                      if (live.current.draw?.parWant) {
                        const dr = got.length
                          ? directionOf(live.current.dl, got[0].handle, at) : null;
                        if (!dr) {
                          sayRef.current?.(got.length
                            ? "  that object has no single direction to be parallel to"
                            : "  nothing there — click the line to be parallel to");
                          return;
                        }
                        setDraw((d0) => (d0 ? { ...d0, parWant: false, dir: dr } : d0));
                        sayRef.current?.(live.current.draw?.mode === "ray"
                                         ? "  Specify start point:" : "  Specify a point:");
                        return;
                      }
                      // ⚠⚠ **MATCHPROP EATS THE CLICK AND STAYS ARMED.** The first pick is the
                      // SOURCE; every one after it is painted, until Escape. Letting the click
                      // also select would leave a highlighted object behind for the next command
                      // to act on — the same reason the pick commands above return early.
                      if (live.current.matching && got.length) {
                        void onMatchPick(got[0]);
                        return;
                      }
                      // ⚠ The path pick eats the click and does NOT change the selection — the
                      // objects being arrayed are still the ones the drafter chose.
                      if (live.current.pathWant && got.length) {
                        const path = got[0];
                        setPedWant({ op: "patharray", mode: path.handle, kind: "width" });
                        setTyped("");
                        sayRef.current?.("Enter number of items:");
                        return;
                      }
                      setSel((c) => {
                      // ⚠⚠ **CLICKING ONE MEMBER OF A GROUP SELECTS THE GROUP.** That is what a
                      // group IS — AutoCAD's PICKSTYLE, on by default, and a group whose
                      // members select one at a time is indistinguishable from no group at all.
                      // ⚠ Per group's own `selectable` flag (code 71), not one global switch:
                      // a drawing can carry both kinds and does.
                      const grown = live.current.groupPick
                        ? expandGroups(got, live.current.groups, live.current.dl)
                        : got;
                      const next = pickadd(c, grown, shift);
                      // ⚠⚠ **AutoCAD SAYS `n found` ON THE COMMAND LINE AND A DRAFTER READS
                      // IT.** It is how you know a crossing caught the two objects behind the
                      // one you meant, before you erase them. The `, m total` half only
                      // appears when the set already had something, which is AutoCAD's own
                      // rule — `3 found` on a fresh pick, `3 found, 8 total` on the next.
                      if (got.length) {
                        chrome.say?.(shift
                          ? `  ${got.length} removed, ${next.length} total`
                          : next.length > got.length
                            ? `  ${got.length} found, ${next.length} total`
                            : `  ${got.length} found`);
                      }
                      return next;
                      });
                    }}
                    sel={selHandles} />
          {/* ⚠⚠ **AutoCAD'S DYNAMIC INPUT, AND IT IS A REAL `<input>` ON PURPOSE.** The chrome
              owns the command line and it holds the keyboard most of the time, so a hand-rolled
              key handler would fight it for every digit. Giving the box focus while a command
              wants a number means typing just works — and Escape still reaches the studio,
              because its own handler is the one exception that reads keys from inputs. */}
          {cmd && COMMANDS[cmd.name]?.scalar
            && (COMMANDS[cmd.name].scalarFirst ? cmd.held === undefined : cmd.pts.length > 0) && (
            <DynIn at={cursorPx.current}
                   // ⚠ The label follows the QUESTION, not the command. A box saying "Angle"
                   // while it is asking for a reference angle and then for a new one is the
                   // same box asking three different things under one name.
                   label={cmd.ref === null ? "Reference"
                        : cmd.ref !== undefined ? "New"
                        : COMMANDS[cmd.name].scalar!.label}
                   suffix={COMMANDS[cmd.name].scalar!.suffix}
                   
                   live={livePreviewNumber}
                   onType={setTyped}
                   onEnter={commitTyped} />
          )}
          {/* ⚠⚠ **THE POINT BOX FOR A MODIFY COMMAND, AND ITS ABSENCE WAS THE REASON
              `MOVE ⏎ 0,0 ⏎` COULD NOT WORK.** The box above takes ROTATE's angle and SCALE's
              factor; nothing took MOVE's base point, so the keystrokes fell through to the
              command line and came back as *Unknown command "0,0"*.
              ⚠ Shown only at a POINT step: when the scalar box above is up, the command is
              asking for a number and two boxes at the cursor would be two answers to one
              question. ⚠ `key` on the point count remounts it after each click, which is what
              puts focus back — the same trick the draw box documents above. */}
          {cmd && COMMANDS[cmd.name] && cmd.pts.length < COMMANDS[cmd.name].points
            && !(COMMANDS[cmd.name].scalar
                 && (COMMANDS[cmd.name].scalarFirst ? cmd.held === undefined
                                                    : cmd.pts.length > 0))
            && tog.dynin && (
            <DynIn key={`cmdpt-${cmd.pts.length}`} at={cursorPx.current} wide
                   label={cmd.pts.length ? "Next" : "Base"} suffix=""
                   hint={cmd.pts.length ? "@dx,dy or d<a" : "x,y"}
                   
                   live={cmd.pts.length && cursor
                         ? Math.hypot(cursor.x - cmd.pts[cmd.pts.length - 1].x,
                                      cursor.y - cmd.pts[cmd.pts.length - 1].y)
                         : null}
                   onType={setTyped} onEnter={finishCmdPoint} />
          )}
          {/* ⚠⚠ **ON THE CANVAS, AT THE TEXT** — that is what "in-place" means and it is the
              whole difference between this and a dialog. A drafter editing a note has to see it
              against the drawing it annotates; a modal in the middle of the screen hides the
              one thing they are checking it against. */}
          {textEdit && (
            <TextEditor spec={textEdit.spec} box={textEdit.box} dark={dark}
                        onCommit={(s, p) => { void commitText(s, p); }}
                        onCancel={() => setTextEdit(null)} />
          )}
          {/* ⚠ The same box, for a DRAW tool's typed answers — a text height, then the string.
              One component rather than two that could drift apart in look or in behaviour. */}
          {draw?.ask != null && defOf(draw.mode, dimKind)?.asks?.[draw.ask] && (
            <DynIn at={cursorPx.current}
                   label={defOf(draw.mode, dimKind)!.asks![draw.ask].label}
                   suffix=""  live={null} text
                   onType={setTyped} onEnter={finishDraw} />
          )}
          {/* ⚠⚠ **THE COORDINATE BOX — AutoCAD'S DYNAMIC INPUT, AND WITHOUT IT THERE WAS
              NOWHERE TO TYPE A POINT.** Every other typed answer in this app already has a
              `DynIn`; the one state that did not was the commonest of all — a tool asking for
              a point. So `Specify next point:` invited `@3450<30` and the keystrokes had no
              destination, which is why it looked like typed coordinates were "not supported"
              rather than "not routed".
              ⚠ `key` on the point count REMOUNTS it after every click, which is what puts
              focus back. Without that, the first canvas click blurs the box and every
              keystroke after it goes nowhere — the feature would work exactly once.
              ⚠ Gated on `dynin`, the toggle that has always been in `TOGGLES` and has always
              been true; a drafter who turns dynamic input off gets AutoCAD's behaviour, which
              is that the command line takes over. */}
          {draw && draw.ask == null && tog.dynin && (
            <DynIn key={`pt-${draw.pts.length}`} at={cursorPx.current} wide
                   label={draw.pts.length ? "Next" : "Point"} suffix=""
                   hint={draw.pts.length ? "@dx,dy or d<a" : "x,y"}
                   
                   live={draw.pts.length && cursor
                         ? Math.hypot(cursor.x - draw.pts[draw.pts.length - 1].x,
                                      cursor.y - draw.pts[draw.pts.length - 1].y)
                         : null}
                   onType={setTyped} onEnter={finishDraw} />
          )}
          {/* ⚠⚠ **THE OPTION LETTER GOES IN THE DYNAMIC-INPUT BOX, NOT THE COMMAND LINE.**
              Typing `F` at the chrome's command line resolves through `ALIASES` to FILLET —
              the selection grammar's letters are answers to a PROMPT, not command names, and
              the two namespaces collide on almost every one of them. PEDIT's option prompt has
              the same shape and the same reason. */}
          {attWant && (
            <DynIn at={cursorPx.current}
                   label={attWant.step === "tag" ? "Tag"
                          : attWant.step === "prompt" ? "Prompt" : "Default"}
                   suffix=""  live={null} text
                   onType={setTyped} onEnter={acceptAtt} />
          )}
          {varWant && (
            <DynIn at={cursorPx.current} label={varWant.name || "Variable"} suffix=""
                    live={null} text onType={setTyped} onEnter={acceptVar} />
          )}
          {/* ⚠⚠ **TTR'S RADIUS NEEDS A BOX TO GO IN.** The first version prompted for it on the
              command line and listened for Enter on the window — but nothing was focused, so
              the digits went nowhere and the prompt sat there refusing every click. Every other
              typed answer in this app is a `DynIn` at the cursor; this is not the one to be
              different. */}
          {/* ⚠ A hidden file input rather than a dialog: the OS picker IS the dialog, and one
              built here would be a worse copy of it. The rectangle is asked for afterwards, on
              the canvas, where a frame belongs. */}
          {/* ⚠⚠ **`accept` IS SET ON THE DOM NODE BY THE COMMAND, NOT RENDERED FROM A REF.**
              A ref does not re-render, so an `accept={pickKind.current ...}` expression here
              would still carry the PREVIOUS command's filter when the picker opened — and a
              file dialog offering images to PDFATTACH looks like a broken command, not a stale
              attribute. One input, because two hidden ones is two refs to keep in step. */}
          {/* ⚠ A testid on a HIDDEN input, which is not decoration: playwright's
              `set_input_files` needs a selector, and PDFATTACH is a gesture no other route can
              exercise — the file picker is the first step of it. */}
          {/* ⚠ Its OWN input rather than a second `accept` on the one above: a picker that
              sometimes takes images and sometimes takes code is one `pickKind` away from
              loading a drafter's photo as a program. */}
          <input ref={lspPick} data-testid="acad-lisppick"
                 type="file" style={{ display: "none" }} accept=".lsp,.LSP"
                 onChange={(ev) => {
                   const f = ev.target.files?.[0];
                   ev.target.value = "";
                   if (!f) return;
                   void f.text().then((src) => {
                     sayRef.current?.(`  loading ${f.name}…`);
                     return runLisp(src);
                   }).then(() => {
                     const names = [...(lispRef.current?.commands.keys() ?? [])];
                     sayRef.current?.(names.length
                       ? `  ${f.name} loaded — ${names.length} command(s): ${names.join(", ")}`
                       : `  ${f.name} loaded`);
                   });
                 }} />
          <input ref={imgPick} data-testid="acad-filepick"
                 type="file" style={{ display: "none" }} accept="image/*"
                 onChange={(ev) => {
                   const f = ev.target.files?.[0];
                   ev.target.value = "";          // ⚠ so the same file can be picked twice
                   if (!f) return;
                   const kind = pickKind.current;
                   setImgWant({ file: f, pts: [], kind, page: 1 });
                   setMode("select");
                   if (kind !== "pdf") {
                     sayRef.current?.("Specify first corner of the image frame:");
                     return;
                   }
                   // ⚠⚠ **THE PAGE COUNT IS READ BEFORE THE FRAME IS DRAWN, NOT AFTER.**
                   // PDFATTACH used to place page 1 and only mention the other eleven in the
                   // report — by which point the drafter had already dragged a rectangle for
                   // the wrong page. The probe rasterises nothing; it reads the page tree.
                   sayRef.current?.("Specify first corner of the underlay frame:");
                   void (async () => {
                     try {
                       const body = new FormData();
                       body.append("file", f);
                       const got = await pdfPages(body);
                       setImgWant((w) => (w && w.file === f ? { ...w, pages: got.pages } : w));
                       if (got.pages > 1) {
                         sayRef.current?.(`  ${f.name} has ${got.pages} pages — page 1 unless `
                                          + "you type another, then pick the corners");
                       }
                     } catch { /* ⚠ silent: the probe is a courtesy and page 1 still works */ }
                   })();
                 }} />
          {tanWant?.given === "ttr" && !(tanWant.r > 0) && (
            <DynIn at={cursorPx.current} label="Radius" suffix=""  live={null}
                   onType={setTyped} onEnter={acceptTanRadius} />
          )}
          {selWant && !selWant.mode && (
            <DynIn at={cursorPx.current} label="Select" suffix=""  live={null}
                   onType={setTyped}
                   // ⚠ an EMPTY Enter closes the selection; only a typed word is an option.
                   onEnter={() => {
                     const t = live.current.typed;
                     setTyped("");
                     if (t.trim()) selectOption(t); else closeSelect();
                   }} />
          )}
          {pedWant?.kind === "option" && (
            <DynIn at={cursorPx.current} label="Option" suffix=""  live={null}
                   onType={setTyped} onEnter={acceptPeditOption} />
          )}
          {pedWant?.kind === "width" && (
            <DynIn at={cursorPx.current} label="Width" suffix=""  live={null}
                   onType={setTyped} onEnter={acceptPeditWidth} />
          )}
          {vpAsk && (
            <DynIn at={cursorPx.current} label="Scale 1:" suffix=""  live={null}
                   onType={setTyped} onEnter={acceptVpScale} />
          )}
          {/* ⚠ FILLET's radius and CHAMFER's distance, asked for BEFORE the two objects. */}
          {objCmd && PICKS[objCmd.name].scalar && objCmd.held === undefined && (
            <DynIn at={cursorPx.current}
                   label={PICKS[objCmd.name].scalar!.label}
                   suffix={PICKS[objCmd.name].scalar!.suffix}
                    live={null}
                   onType={setTyped} onEnter={acceptPickScalar} />
          )}
          {!dl && !busy && <Empty onOpen={() => setOpenDlg(true)} n={rows.length} />}
          {busy && <Toast text={busy} />}
          {err && <Toast text={err} bad />}
          {!busy && !err && note && <Toast text={note} quiet />}
          {chrome.overlay}
          </>)}
        </div>
        {chrome.side}
      </div>
      {chrome.status}
      {arrayDlg && (
        <ArrayDialog kind={arrayDlg}
                     onClose={() => setArrayDlg(null)}
                     onRect={(s: RectSpec) => { setArrayDlg(null); void commit("array",
                       { kind: "rect", ...s }); }}
                     onPolar={(s: PolarSpec) => {
                       setArrayDlg(null); setPolarWant(s);
                       sayRef.current?.("Specify centre point of array:");
                     }} />
      )}
      {xrefDlg && id && (
        <XrefDialog drawingId={id} onClose={() => setXrefDlg(false)}
                    onBind={(block, from) => { setXrefDlg(false); void bindXref(block, from); }} />
      )}
      {dimDlg && id && (
        <DimStyleDialog drawingId={id} onClose={() => setDimDlg(false)}
                        onApply={(n, vars, base) => { setDimDlg(false); void applyDimStyle(n, vars, base); }} />
      )}
      {txtDlg && id && (
        <TextStyleDialog drawingId={id} onClose={() => setTxtDlg(false)}
                         onApply={(n, vars) => { setTxtDlg(false); void applyTextStyle(n, vars); }} />
      )}
      {layoutDlg && (
        <LayoutDialog mode={layoutDlg} current={space ?? undefined}
                      count={layoutDlg === "delete"
                             ? layoutRows.find((l) => l.name === space)?.entities : undefined}
                      taken={layoutRows.map((l) => l.name)}
                      onClose={() => setLayoutDlg(null)} onGo={runLayout} />
      )}
      {openDlg && <OpenDialog rows={rows} onPick={open} onChanged={setRows}
                              onClose={() => setOpenDlg(false)} />}
      {adcOpen && (
        <DesignCenter here={id} onClose={() => setAdcOpen(false)}
          onPlace={(spec) => {
            setAdcOpen(false);
            setBlockWant({ mode: "insert", name: spec.name, scale: spec.scale,
                           rotation: spec.rotation, from: spec.from });
            sayRef.current?.("Specify insertion point:");
          }} />)}
      {blockDlg && id && (
        <BlockDialog mode={blockDlg} drawingId={id} count={sel.length}
                     onClose={() => setBlockDlg(null)}
                     onName={(name, o) => {
                       setBlockDlg(null);
                       setBlockWant({ mode: blockDlg, name, ...o });
                       sayRef.current?.(blockDlg === "create"
                         ? "Specify insertion base point:"
                         : PROMPTS.insert);
                     }} />
      )}
      {hatchDlg && (
        <HatchDialog editing={hatchDlg !== "gradient"
                                && sel.every((p) => p.kind === "HATCH")}
                     count={sel.length}
                     gradient={hatchDlg === "gradient"}
                     // ⚠⚠⚠ **`$HPSCALE` FIRST — the extent heuristic is a TEXT HEIGHT rule and
                     // it is absurd for a hatch.** `extent / 200` on drawing 10919 is **56,566**,
                     // which puts ANSI31's lines 127,000 units apart: on a 1,200 x 800 room
                     // exactly one line lands inside, and the drafter sees a single diagonal
                     // stroke where they asked for hatching. Worse, `$HPSCALE` is the variable
                     // whose entire job is to remember the scale they last hatched at — the
                     // command writes it on every use (`edits.do_hatch`'s `_remember`) — and
                     // this ignored it. Seen in a screenshot: HPSCALE had just been set to 40
                     // and the dialog opened at 56,566.
                     // ⇒ what the drawing remembers, then the heuristic for a drawing that has
                     // never been hatched.
                     defaultScale={(() => {
                       const hp = Number((live.current.sysvars ?? [])
                         .find((v) => v.name === "HPSCALE")?.value);
                       if (Number.isFinite(hp) && hp > 0) return hp;
                       return Math.max(1, Math.round((live.current.extent || 1000) / 200));
                     })()}
                     onApply={(s) => { void applyHatch(s); }}
                     onClose={() => setHatchDlg(false)} />
      )}
    </div>
  );
}

/** AutoCAD's dynamic-input box: the number the command is asking for, at the cursor.
 *
 * ⚠ It shows the DRAGGED value greyed until something is typed, so a drafter can see what the
 * mouse is about to commit rather than guessing from the picture. AutoCAD does the same, and it
 * is the difference between "rotate it roughly" and "rotate it 90".
 */
function DynIn({ at, label, suffix, live, onType, onEnter, wide, hint, text }: {
  at: { x: number; y: number } | null;
  label: string; suffix: string; live: number | null;
  onType: (s: string) => void; onEnter: () => void;
  /** ⚠ A coordinate needs room. `@3450<30` does not fit in the 62 px a text height wants. */
  wide?: boolean;
  /** Shown instead of the live number when there is no distance to show yet. */
  hint?: string;
  /** ⚠⚠ **THIS BOX HOLDS WORDS, SO SPACE MUST STAY A SPACE.** Everywhere else in AutoCAD SPACE
   *  is ENTER — it is how a drafter ends a LINE without reaching for the return key — and a
   *  coordinate, an angle and a scale factor never contain one. An attribute default, a text
   *  string and a style name do. Getting this backwards makes it impossible to type
   *  "Arial Narrow", which is a worse failure than a missing shortcut. */
  text?: boolean;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  //: ⚠ Read here, not passed in: this box is the only thing in the app that shows the buffer,
  //: so it is the only thing that has to render when the buffer changes. See `typedStore`.
  const value = useTyped();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    // ⚠⚠ **AND AGAIN ON THE NEXT TICK, OR THE BOX IS FOCUSED EXACTLY ONCE.** When this box is
    // remounted BY A CLICK — which is what happens after every point a draw tool takes — React
    // flushes this effect DURING the pointer event, and the browser then moves focus to the
    // document on mouseup, silently undoing it. Measured: `document.activeElement` was BODY
    // 900 ms after the click, so the first typed coordinate worked and every one after a click
    // went nowhere. Nothing reports this; the keystrokes simply vanish.
    const t = setTimeout(() => el.focus(), 0);
    // ⚠⚠ **AND IT HANDS FOCUS BACK TO NOBODY, WHICH IS DELIBERATE.** Focusing the command line
    // here was tried and broke six scenarios: this box REMOUNTS after every point, so the
    // command line would take focus between two points of one LINE and the next coordinate
    // would arrive there as *Unknown command "100000,0"*. BODY is this app's resting state —
    // `shell.tsx` routes a letter typed there into the command line through `takeKeyboard`, and
    // the queue in `Studio` catches everything typed while a command is running.
    return () => clearTimeout(t);
  }, []);
  const x = (at?.x ?? 40) + 22, y = (at?.y ?? 40) + 22;
  return (
    <div style={{ position: "absolute", left: x, top: y, zIndex: 45, display: "flex",
                  alignItems: "center", gap: 5, background: "rgba(30,30,30,.94)",
                  border: "1px solid #6d6d6d", borderRadius: 2, padding: "2px 6px",
                  font: "11.5px system-ui", pointerEvents: "auto" }}>
      <span style={{ color: "#9aa0a6" }}>{label}</span>
      <input ref={ref} value={value} data-testid="dynin"
             onChange={(e) => onType(e.target.value)}
             onKeyDown={(e) => {
               // ⚠⚠ **SPACE IS ENTER HERE TOO.** A drafter finishing a polyline presses SPACE,
               // and this box had focus for the whole of every draw command — so the keystroke
               // put a space in the coordinate field and the command carried on. The command
               // line learned this in `AutoCadShell`; the two boxes have to agree, or SPACE
               // means one thing at the prompt and another one keystroke later.
               if (e.key === "Enter" || (e.key === " " && !text)) {
                 e.preventDefault(); onEnter();
               }
             }}
             placeholder={live == null ? (hint ?? "") : live.toFixed(2)}
             style={{ width: wide ? 120 : 62, background: "#1e1e1e", border: "1px solid #555",
                      color: "#e8e8e8", font: "11.5px system-ui", padding: "1px 4px" }} />
      <span style={{ color: "#9aa0a6" }}>{suffix}</span>
    </div>
  );
}

/** ⚠⚠ **THE TESTID IS NOT DECORATION — WITHOUT IT THE APP ANSWERS AND NO INSTRUMENT CAN TELL.**
 *  A toast is a real answer to a drafter: STLOUT's *"pdcad plots to PDF and exports DXF"* is
 *  exactly the explanation the app is supposed to give. `command_survey.py` reads the command
 *  transcript and the SET OF TESTIDS, so a toast with neither was invisible — and STLOUT was
 *  counted among the 20 SILENT commands while doing precisely the right thing. */
function Toast({ text, bad, quiet }: { text: string; bad?: boolean; quiet?: boolean }) {
  return (
    <div data-testid="acad-toast"
         style={{ position: "absolute", left: "50%", top: 14, transform: "translateX(-50%)",
                  background: bad ? "#5c1f1f" : quiet ? "rgba(40,40,40,.9)" : "#333",
                  border: `1px solid ${bad ? "#a44" : "#555"}`, padding: "5px 14px",
                  borderRadius: 3, fontSize: 11.5, zIndex: 40, pointerEvents: "none",
                  maxWidth: "70%", whiteSpace: "nowrap", overflow: "hidden",
                  textOverflow: "ellipsis" }}>
      {text}
    </div>
  );
}

/** ⚠ Says what to do rather than showing a black rectangle. An empty viewport in a CAD program
 *  is indistinguishable from a failed load — this lane has already shipped that mistake once. */
function Empty({ onOpen, n }: { onOpen: () => void; n: number }) {
  return (
    <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center",
                  pointerEvents: "none", zIndex: 5 }}>
      <div style={{ textAlign: "center", color: "#8a8f94", pointerEvents: "auto" }}>
        <div style={{ font: "600 15px system-ui", color: "#c8ccd0" }}>No drawing open</div>
        <div style={{ margin: "6px 0 14px" }}>
          {n} drawing{n === 1 ? "" : "s"} in this database
        </div>
        <button data-testid="empty-open" onClick={onOpen}
                style={{ background: "#3c5a82", border: "1px solid #4a6d9c", color: "#fff",
                         padding: "6px 16px", borderRadius: 3, cursor: "pointer" }}>
          Open…  <span style={{ opacity: .7 }}>Ctrl+O</span>
        </button>
      </div>
    </div>
  );
}
